// ╔══════════════════════════════════════════════════════════════════════════════╗
// ║  YOUR PEAK STACK — by Peak Health Stack                                      ║
// ║  Refactored for production scalability                                        ║
// ║                                                                               ║
// ║  MODULE MAP (split into /src/* when moving to Vite/Next.js):                 ║
// ║  ─────────────────────────────────────────────────────────────────────────── ║
// ║  [DATA]      /data/supplements.js · interactions.js · protocols.js           ║
// ║  [LOGIC]     /logic/interactions.js · sorting.js · ai.js · features.js       ║
// ║  [CONTEXT]   /context/UserContext.jsx · AppContext.jsx                        ║
// ║  [HOOKS]     /hooks/useStack.js · useProtocols.js · useLogs.js               ║
// ║  [SERVICES]  /services/storage.js · api.js · auth.js                         ║
// ║  [COMPONENTS]/components/BuyButtons · Drawer · Builder · Toast · Gates       ║
// ║  [PAGES]     /pages/Stack · Interactions · Log · Trends · Protocols · Shop   ║
// ║  [APP]       App.jsx                                                          ║
// ╚══════════════════════════════════════════════════════════════════════════════╝

import { useState, useCallback, useRef, useEffect, useContext, createContext } from "react";
import PWAInstallBanner from './PWAInstallBanner.jsx';
import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Only enable Sentry after user has accepted the consent banner (UK GDPR best practice).
// On first visit phs_consent is null — banner shows and Sentry stays disabled until accepted.
const _sentryConsent = (() => { try { return localStorage.getItem("phs_consent"); } catch { return null; } })();
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracesSampleRate: 0.2,
  sendDefaultPii: false,
  enabled: _sentryConsent === "true",
});

// ─────────────────────────────────────────────────────────────────────────────
// SUPABASE CLIENT — env vars set in Vercel: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY
const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY;
const AI_INTERNAL_KEY = import.meta.env.VITE_AI_INTERNAL_KEY ?? "";

async function fetchWithRetry(url, options = {}, retryCount = 0) {
  const timeout = options.timeout || 15000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    const method = (options.method ?? "GET").toUpperCase();
    const isIdempotent = method === "GET" || method === "HEAD";
    const isRetryable = isIdempotent && error instanceof TypeError; // AbortError = our timeout fired — don't retry; POST = non-idempotent — don't retry
    if (isRetryable && retryCount < 3) {
      const delay = [500, 1000, 2000][retryCount];
      await new Promise(r => setTimeout(r, delay));
      return fetchWithRetry(url, options, retryCount + 1);
    }
    throw error;
  }
}

async function sbFetch(path, options = {}) {
  // Low-level Supabase REST helper — used by the api layer below
  const url = SUPABASE_URL + "/rest/v1/" + path;
  const headers = {
    "apikey": SUPABASE_ANON,
    "Authorization": getAuthHeader(true),
    "Content-Type": "application/json",
    "Prefer": options.prefer ?? "return=representation",
    ...options.headers,
  };
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    console.warn("[Auth] Session expired");
    sbSession = null;
    storage.remove("phs_token");
    window.dispatchEvent(new CustomEvent("phs:session-expired"));
    return null;
  }
  if (!res.ok && res.status !== 406 && res.status !== 409) { // 409 handled below
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? "Supabase error " + res.status);
  }
  if (res.status === 409) {
    console.warn("[sbFetch] 409 Conflict — write was silently dropped. URL:", url);
    return null;
  }
  if (res.status === 204 || res.status === 406) return null;
  return res.json();
}

async function sbAuth(action, email, password, extras = {}) {
  // Supabase Auth REST helper
  const res = await fetchWithRetry(SUPABASE_URL + "/auth/v1/" + action, {
    method: "POST",
    headers: { "apikey": SUPABASE_ANON, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, ...extras }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error_description ?? data.msg ?? "Auth error");
  return data;
}

async function sbSignOut(accessToken) {
  await fetch(SUPABASE_URL + "/auth/v1/logout", {
    method: "POST",
    headers: { "apikey": SUPABASE_ANON, "Authorization": "Bearer " + accessToken },
  });
}

// In-memory session store (populated after login)
let sbSession = null;
let planPollInterval = null;
// Holds token data when a password-reset (type=recovery) link is clicked — cleared after use
let recoverySession = null;

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ?? (Math.floor(Date.now() / 1000) + 3600);
  } catch { return Math.floor(Date.now() / 1000) + 3600; }
}

function isSessionExpired() {
  if (!sbSession?.expires_at) return false;
  const now = Math.floor(Date.now() / 1000);
  return now > sbSession.expires_at;
}

function getAuthHeader(requireAuth = false) {
  // Always sync from localStorage first — another window (e.g. PWA) may have rotated the token
  const stored = storage.get("phs_token", null);
  if (stored?.access_token && stored.access_token !== sbSession?.access_token) {
    console.log("[Auth] Token updated by another window — syncing sbSession");
    sbSession = stored;
    if (!sbSession.expires_at && sbSession.access_token) {
      sbSession.expires_at = getTokenExpiry(sbSession.access_token);
    }
  }
  if (sbSession?.access_token && !isSessionExpired()) {
    return "Bearer " + sbSession.access_token;
  }
  if (requireAuth) {
    window.dispatchEvent(new CustomEvent("phs:session-expired"));
    throw new Error("Session expired — please sign in again");
  }
  // Fallback to anon key for non-auth reads (e.g. guest mode, post-expiry race window).
  // Safe: every table's RLS policy uses auth.uid() = user_id — anon requests return zero rows.
  return "Bearer " + SUPABASE_ANON;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /data/tokens.js — Design tokens
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const C = {
  navy:"#1B3A5C", navyDeep:"#0f2540",
  // Brand gold system (from the Your Peak Stack logo). `teal`/`tealDark` keys are
  // retained as aliases so the ~200 existing references resolve to gold without churn.
  gold:"#E5B83F", goldDark:"#C2912B", goldLight:"#F5D67A", goldBright:"#F5C84A",
  cream:"#F3ECD9",
  teal:"#E5B83F", tealDark:"#C2912B",
  white:"#FFFFFF", gray400:"#9CA3AF",
  amber:"#F59E0B", red:"#EF4444", green:"#10B981", orange:"#F97316",
  purple:"#8B5CF6", blue:"#3B82F6", pink:"#EC4899",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DESIGN TOKENS — PHS Premium System
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SPACING = {
  xs: "8px",
  sm: "12px",
  md: "16px",
  lg: "24px",
  xl: "32px",
};

const TYPOGRAPHY = {
  h1: { fontSize: "32px", fontWeight: "800", lineHeight: "1.2" },
  h2: { fontSize: "24px", fontWeight: "700", lineHeight: "1.3" },
  h3: { fontSize: "18px", fontWeight: "700", lineHeight: "1.4" },
  h4: { fontSize: "16px", fontWeight: "600", lineHeight: "1.4" },
  body: { fontSize: "14px", fontWeight: "400", lineHeight: "1.6" },
  bodySmall: { fontSize: "13px", fontWeight: "400", lineHeight: "1.5" },
  bodyXS: { fontSize: "12px", fontWeight: "400", lineHeight: "1.4" },
  label: { fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" },
  labelSm: { fontSize: "11px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" },
};

const SHADOWS = {
  sm: "0 2px 4px rgba(0,0,0,0.08)",
  md: "0 4px 12px rgba(0,0,0,0.1)",
  lg: "0 8px 24px rgba(0,0,0,0.12)",
  xl: "0 12px 32px rgba(0,0,0,0.15)",
  hero: "0 20px 40px rgba(229,184,63,0.15)",
  glow: "0 4px 12px rgba(229,184,63,0.3)",
  glowLg: "0 8px 20px rgba(229,184,63,0.4)",
};

const GRADIENTS = {
  primary: "linear-gradient(135deg, #E5B83F 0%, #C2912B 100%)",
  secondary: "linear-gradient(135deg, rgba(229,184,63,0.12) 0%, rgba(229,184,63,0.04) 100%)",
  success: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
  upsell: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
  info: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GLOBAL ANIMATIONS & STYLES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const GLOBAL_STYLES = `
  * { box-sizing: border-box; }
  @keyframes subtleShift {
    0%, 100% { transform: translate(0, 0); }
    25% { transform: translate(20px, -20px); }
    50% { transform: translate(-10px, 10px); }
    75% { transform: translate(10px, -10px); }
  }
  @keyframes growBar { from { width: 0; } to { width: var(--target-width, 100%); } }
  @keyframes growUp { from { height: 0; opacity: 0; } to { height: auto; opacity: 1; } }
  @keyframes pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.7; } }
  @keyframes breathe { 0%, 100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.05); opacity: 1; } }
  @keyframes floatAround {
    0%, 100% { transform: translate(0, 0); }
    25% { transform: translate(20px, -30px); }
    50% { transform: translate(-10px, 20px); }
    75% { transform: translate(30px, 10px); }
  }
  @keyframes drawLine { from { stroke-dashoffset: 300; } to { stroke-dashoffset: 0; } }
  @keyframes slideInUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes phs-growUp { from { height: 0%; opacity: 0; } to { opacity: 1; } }
  @keyframes meshOrb1 {
    0%,100% { transform: translate(0,0); }
    33% { transform: translate(60px,-40px); }
    66% { transform: translate(-30px,50px); }
  }
  @keyframes meshOrb2 {
    0%,100% { transform: translate(0,0); }
    33% { transform: translate(-50px,30px); }
    66% { transform: translate(40px,-60px); }
  }
  @keyframes drawerSlideUp {
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes ldWave {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.3; }
    30% { transform: translateY(-6px); opacity: 1; }
  }
  @keyframes goldShimmer {
    0%,100% { transform: translateX(-100%); }
    50%     { transform: translateX(200%); }
  }
  @keyframes tsUp {
    from { opacity:0; transform:translate(-50%, 16px) scale(0.92); }
    to   { opacity:1; transform:translate(-50%, 0)    scale(1); }
  }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(229,184,63,0.3); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(229,184,63,0.55); }
  * { scrollbar-width: thin; scrollbar-color: rgba(229,184,63,0.3) transparent; }
  button:active { transform: scale(0.97); }
  @media (prefers-reduced-motion: reduce) {
    * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
  }
`;

if (typeof document !== "undefined" && !document.getElementById("phs-global-styles")) {
  const _styleEl = document.createElement("style");
  _styleEl.id = "phs-global-styles";
  _styleEl.textContent = GLOBAL_STYLES;
  document.head.appendChild(_styleEl);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PHS supplement article links + AI text helpers
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const PHS_SUPPLEMENT_LINKS = {
  'Magnesium':           'https://peakhealthstack.com/best-magnesium-supplements',
  'Magnesium Glycinate': 'https://peakhealthstack.com/best-magnesium-supplements',
  'Vitamin D':           'https://peakhealthstack.com/vitamin-d-deficiency-symptoms',
  'Vitamin D3':          'https://peakhealthstack.com/vitamin-d-deficiency-symptoms',
  'Vitamin D3 + K2':     'https://peakhealthstack.com/vitamin-d-deficiency-symptoms',
  'Omega-3':             'https://peakhealthstack.com/best-omega-3-supplements',
  'Fish Oil':            'https://peakhealthstack.com/best-omega-3-supplements',
  'Omega-3 / Fish Oil':  'https://peakhealthstack.com/best-omega-3-supplements',
  'Ashwagandha':         'https://peakhealthstack.com/best-ashwagandha-supplements',
  'Vitamin B12':         'https://peakhealthstack.com/vitamin-b12-deficiency-symptoms',
  'B12':                 'https://peakhealthstack.com/vitamin-b12-deficiency-symptoms',
  'Creatine':            'https://peakhealthstack.com/creatine-benefits-dosage',
  'Zinc':                'https://peakhealthstack.com/zinc-supplements-benefits',
  'CoQ10':               'https://peakhealthstack.com/coq10-supplements-benefits',
  'Iron':                'https://peakhealthstack.com/iron-deficiency-symptoms',
  'Collagen':            'https://peakhealthstack.com/best-collagen-supplements',
  'L-Theanine':          'https://peakhealthstack.com/best-supplements-for-anxiety',
  'Probiotics':          'https://peakhealthstack.com/best-probiotic-supplements',
  'Vitamin C':           'https://peakhealthstack.com/vitamin-c-supplements',
};

// Sanitise AI response text before HTML rendering
function sanitiseText(text) {
  if (!text) return text;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Linkify first occurrence of each supplement name in AI response text
function linkifySupplements(text) {
  if (!text) return text;
  let result = text;
  const linked = new Set();
  Object.entries(PHS_SUPPLEMENT_LINKS).forEach(([name, url]) => {
    if (linked.has(name)) return;
    const regex = new RegExp(`\\b(${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'i');
    if (regex.test(result)) {
      result = result.replace(regex, `<a href="${url}" target="_blank" rel="noopener" style="color:#F5C84A;text-decoration:underline">${name}</a>`);
      linked.add(name);
    }
  });
  return result;
}

// Sanitise then linkify — use for dangerouslySetInnerHTML
function renderAiText(text) {
  return linkifySupplements(sanitiseText(text));
}

// Rich formatter for AI free-text responses. Turns lightweight markdown
// (#/## headings, **bold**, bullet & numbered lists, paragraphs) into polished,
// well-spaced HTML — matching the premium formatting of the Trends analysis.
// Used for the AI Advisor conversation + Weekly/Monthly/Why-Not-Improving reports.
function formatAiText(text) {
  if (!text) return "";
  const esc = sanitiseText(String(text)).replace(/```[a-z]*|```/gi, "").trim();
  const inline = (s) => s.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#F5D67A;font-weight:700">$1</strong>');
  const lines = esc.split(/\r?\n/);
  let html = "", list = null;
  const close = () => { if (list) { html += list === "ul" ? "</ul>" : "</ol>"; list = null; } };
  const heading = (t) => `<div style="font-size:11px;font-weight:700;color:#E5B83F;text-transform:uppercase;letter-spacing:1px;margin:18px 0 9px;padding-bottom:6px;border-bottom:1px solid rgba(229,184,63,0.18)">${inline(t.replace(/[:：*]+$/,""))}</div>`;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { close(); continue; }
    let m;
    if ((m = line.match(/^#{1,6}\s+(.*)$/)))   { close(); html += heading(m[1]); continue; }
    if ((m = line.match(/^\*\*(.+?)\*\*:?$/)))  { close(); html += heading(m[1]); continue; }
    if ((m = line.match(/^[-*•·–]\s+(.*)$/))) {
      if (list !== "ul") { close(); html += '<ul style="margin:4px 0 13px;padding:0;list-style:none">'; list = "ul"; }
      html += `<li style="position:relative;padding:4px 0 4px 20px;color:rgba(255,255,255,0.82);line-height:1.62;font-size:13px"><span style="position:absolute;left:2px;top:3px;color:#E5B83F;font-size:12px">▸</span>${inline(m[1])}</li>`;
      continue;
    }
    if ((m = line.match(/^(\d+)[.)]\s+(.*)$/))) {
      if (list !== "ol") { close(); html += '<ol style="margin:4px 0 13px;padding:0;list-style:none">'; list = "ol"; }
      html += `<li style="position:relative;padding:3px 0 7px 31px;color:rgba(255,255,255,0.82);line-height:1.6;font-size:13px;min-height:23px"><span style="position:absolute;left:0;top:1px;width:21px;height:21px;border-radius:50%;background:rgba(229,184,63,0.16);border:1px solid rgba(229,184,63,0.4);color:#F5D67A;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center">${m[1]}</span>${inline(m[2])}</li>`;
      continue;
    }
    close();
    html += `<p style="margin:0 0 11px;color:rgba(255,255,255,0.82);line-height:1.72;font-size:13px">${inline(line)}</p>`;
  }
  close();
  return linkifySupplements(html);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /data/supplements.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const SUPPLEMENT_DB = {
  "Magnesium Glycinate":             {category:"Mineral",           timing:"Before bed",               dose:"300–400mg",         evidence:"Strong",   tags:["sleep","anxiety","muscle","cramps","recovery"]},
  "Magnesium L-Threonate":           {category:"Mineral",           timing:"Before bed",               dose:"1500–2000mg",       evidence:"Good",     tags:["brain","cognition","memory","sleep"]},
  "Magnesium Citrate":               {category:"Mineral",           timing:"Before bed or with a meal",dose:"200–400mg",         evidence:"Strong",   tags:["sleep","muscle","cramps","constipation","relaxation"]},
  "Magnesium Malate":                {category:"Mineral",           timing:"Morning",                  dose:"300–450mg",         evidence:"Good",     tags:["energy","muscle","fatigue","cramps"]},
  "Magnesium Taurate":               {category:"Mineral",           timing:"Before bed",               dose:"300–400mg",         evidence:"Moderate", tags:["heart","cardiovascular","blood pressure","calm","sleep"]},
  "Magnesium Oxide":                 {category:"Mineral",           timing:"With a meal",              dose:"200–400mg",         evidence:"Limited",  tags:["constipation","cramps"]},
  "Zinc Bisglycinate":               {category:"Mineral",           timing:"With a meal",              dose:"15–25mg",           evidence:"Good",     tags:["immunity","hormone","skin","testosterone","hair"]},
  "Iron Bisglycinate":               {category:"Mineral",           timing:"With a meal (+ vitamin C)",dose:"As directed by GP", evidence:"Strong",   tags:["energy","blood","fatigue","women"]},
  "Calcium Citrate":                 {category:"Mineral",           timing:"With a meal",              dose:"500–1000mg",        evidence:"Strong",   tags:["bone","menopause","women","teeth"]},
  "Selenium":                        {category:"Mineral",           timing:"With a meal",              dose:"100–200mcg",        evidence:"Good",     tags:["thyroid","antioxidant","immunity","hormone"]},
  "Iodine":                          {category:"Mineral",           timing:"Morning",                  dose:"150–300mcg",        evidence:"Good",     tags:["thyroid","metabolism","energy","women"]},
  "Copper Bisglycinate":             {category:"Mineral",           timing:"With a meal",              dose:"1–2mg",             evidence:"Moderate", tags:["connective tissue","tendon","collagen","immunity"]},
  "Potassium Citrate":               {category:"Mineral",           timing:"With a meal",              dose:"200–400mg",         evidence:"Good",     tags:["heart","cramps","blood pressure","muscle"]},
  "Boron":                           {category:"Mineral",           timing:"With a meal",              dose:"3–6mg",             evidence:"Good",     tags:["bone","testosterone","joint","menopause"]},
  "Vitamin D3 + K2":                 {category:"Vitamin",           timing:"Morning with food",        dose:"2000–4000 IU",      evidence:"Strong",   tags:["bone","immunity","mood","testosterone","menopause"]},
  "Vitamin B12":                     {category:"Vitamin",           timing:"Morning",                  dose:"500–1000mcg",       evidence:"Strong",   tags:["energy","nerve","mood","vegan","over 40"]},
  "Vitamin C":                       {category:"Vitamin",           timing:"With a meal",              dose:"500–1000mg",        evidence:"Strong",   tags:["immunity","collagen","antioxidant","tendon","recovery"]},
  "Vitamin B Complex":               {category:"Vitamin",           timing:"Morning with food",        dose:"1 capsule",         evidence:"Good",     tags:["energy","stress","nerve","mood","busy"]},
  "Vitamin A":                       {category:"Vitamin",           timing:"With a fatty meal",        dose:"700–3000 IU",       evidence:"Good",     tags:["immunity","skin","eye","hormone"]},
  "Folate (Methylfolate)":           {category:"Vitamin",           timing:"Morning",                  dose:"400–800mcg",        evidence:"Strong",   tags:["pregnancy","heart","mood","women","MTHFR"]},
  "Omega-3 (EPA+DHA)":               {category:"Fatty Acid",        timing:"With a meal",              dose:"1000–2000mg",       evidence:"Strong",   tags:["heart","brain","inflammation","joint","mood","tendon"]},
  "Krill Oil":                       {category:"Fatty Acid",        timing:"With a meal",              dose:"500–1000mg",        evidence:"Good",     tags:["heart","inflammation","joint","women","PMS"]},
  "Ashwagandha KSM-66":              {category:"Adaptogen",         timing:"With dinner",              dose:"300–600mg",         evidence:"Strong",   tags:["stress","cortisol","sleep","testosterone","anxiety","busy"]},
  "Rhodiola Rosea":                  {category:"Adaptogen",         timing:"Morning (away from food)", dose:"200–400mg",         evidence:"Good",     tags:["fatigue","stress","focus","energy","busy","jet lag"]},
  "Panax Ginseng":                   {category:"Adaptogen",         timing:"Morning with food",        dose:"200–400mg",         evidence:"Good",     tags:["energy","cognition","libido","men","immunity"]},
  "Maca Root":                       {category:"Adaptogen",         timing:"Morning with food",        dose:"1500–3000mg",       evidence:"Good",     tags:["libido","hormone","energy","menopause","men over 40"]},
  "Holy Basil (Tulsi)":              {category:"Adaptogen",         timing:"With a meal",              dose:"300–600mg",         evidence:"Moderate", tags:["stress","cortisol","blood sugar","busy","anxiety"]},
  "L-Theanine":                      {category:"Amino Acid",        timing:"As needed",                dose:"100–200mg",         evidence:"Good",     tags:["calm","focus","sleep","anxiety","busy"]},
  "NAC":                             {category:"Amino Acid",        timing:"Away from meals",          dose:"600–1200mg",        evidence:"Good",     tags:["liver","antioxidant","lung","detox"]},
  "L-Glutamine":                     {category:"Amino Acid",        timing:"Post-workout or bedtime",  dose:"5–10g",             evidence:"Good",     tags:["gut","recovery","muscle","immunity","injury"]},
  "L-Citrulline":                    {category:"Amino Acid",        timing:"Pre-workout",              dose:"3–6g",              evidence:"Good",     tags:["performance","blood flow","men","pump","endurance"]},
  "Taurine":                         {category:"Amino Acid",        timing:"Any time",                 dose:"500–2000mg",        evidence:"Good",     tags:["heart","muscle","sleep","eye health","energy"]},
  "BCAA (Leucine/Isoleucine/Valine)":{category:"Amino Acid",        timing:"Around training",          dose:"5–10g",             evidence:"Good",     tags:["muscle","recovery","protein","performance"]},
  "Glycine":                         {category:"Amino Acid",        timing:"Before bed",               dose:"3–5g",              evidence:"Good",     tags:["sleep","collagen","tendon","joint","recovery"]},
  "Creatine Monohydrate":            {category:"Performance",       timing:"Any time",                 dose:"3–5g",              evidence:"Strong",   tags:["strength","muscle","cognition","men","aging"]},
  "Beta-Alanine":                    {category:"Performance",       timing:"Pre-workout",              dose:"2–5g",              evidence:"Good",     tags:["endurance","performance","lactic acid","training"]},
  "Electrolytes":                    {category:"Performance",       timing:"During or after training", dose:"Per product label",  evidence:"Strong",   tags:["hydration","cramps","recovery","jet lag","endurance"]},
  "CoQ10 Ubiquinol":                 {category:"Antioxidant",       timing:"With a fatty meal",        dose:"100–300mg",         evidence:"Good",     tags:["energy","heart","statins","men over 40","mitochondria"]},
  "Alpha Lipoic Acid":               {category:"Antioxidant",       timing:"Away from meals",          dose:"300–600mg",         evidence:"Good",     tags:["blood sugar","nerve","antioxidant","aging","energy"]},
  "Resveratrol":                     {category:"Antioxidant",       timing:"With a meal",              dose:"150–500mg",         evidence:"Good",     tags:["heart","aging","longevity","antioxidant","men over 40"]},
  "Quercetin":                       {category:"Antioxidant",       timing:"With a meal",              dose:"500–1000mg",        evidence:"Good",     tags:["immunity","allergy","inflammation","tendon","antioxidant"]},
  "Astaxanthin":                     {category:"Antioxidant",       timing:"With a fatty meal",        dose:"4–12mg",            evidence:"Good",     tags:["skin","eye","inflammation","endurance","recovery"]},
  "Collagen Peptides":               {category:"Connective Tissue", timing:"Morning (+ vitamin C)",    dose:"10–15g",            evidence:"Good",     tags:["skin","joint","bone","tendon","women","injury"]},
  "Glucosamine Sulphate":            {category:"Connective Tissue", timing:"With a meal",              dose:"1500mg",            evidence:"Good",     tags:["joint","cartilage","osteoarthritis","pain","knee"]},
  "Chondroitin Sulphate":            {category:"Connective Tissue", timing:"With a meal",              dose:"800–1200mg",        evidence:"Good",     tags:["joint","cartilage","osteoarthritis","knee","pain"]},
  "MSM":                             {category:"Connective Tissue", timing:"With a meal",              dose:"1000–3000mg",       evidence:"Good",     tags:["joint","inflammation","tendon","muscle","pain"]},
  "Boswellia Serrata":               {category:"Plant Extract",     timing:"With a meal",              dose:"300–500mg",         evidence:"Good",     tags:["joint","inflammation","tendon","pain","arthritis"]},
  "Cissus Quadrangularis":           {category:"Plant Extract",     timing:"With a meal",              dose:"500–1000mg",        evidence:"Good",     tags:["tendon","bone","injury","recovery","connective tissue"]},
  "Probiotics":                      {category:"Gut Health",        timing:"Before a meal",            dose:"10–50 billion CFU", evidence:"Good",     tags:["gut","immunity","mood","bloating","women"]},
  "Digestive Enzymes":               {category:"Gut Health",        timing:"Before a meal",            dose:"Per product label",  evidence:"Good",     tags:["digestion","bloating","gut","busy","IBS"]},
  "Psyllium Husk":                   {category:"Gut Health",        timing:"Before a meal (with water)",dose:"5–10g",            evidence:"Strong",   tags:["gut","cholesterol","blood sugar","fibre","digestion"]},
  "Berberine":                       {category:"Plant Extract",     timing:"With a meal",              dose:"500mg 2–3x daily",  evidence:"Good",     tags:["blood sugar","metabolism","gut","cholesterol","men over 40"]},
  "DHEA":                            {category:"Hormone Support",   timing:"Morning with food",        dose:"25–50mg",           evidence:"Good",     tags:["hormone","aging","men over 40","libido","energy"]},
  "DIM":                             {category:"Hormone Support",   timing:"With a meal",              dose:"100–200mg",         evidence:"Moderate", tags:["oestrogen","hormone","women over 40","PMS","menopause"]},
  "Black Cohosh":                    {category:"Plant Extract",     timing:"With a meal",              dose:"20–40mg",           evidence:"Good",     tags:["menopause","hot flashes","women","hormone"]},
  "Saw Palmetto":                    {category:"Plant Extract",     timing:"With a meal",              dose:"320mg",             evidence:"Good",     tags:["prostate","men over 40","hair","hormone"]},
  "Melatonin":                       {category:"Sleep",             timing:"30-60 min before bed",     dose:"0.5–3mg",           evidence:"Strong",   tags:["sleep","jet lag","circadian","shift work","insomnia"]},
  "5-HTP":                           {category:"Sleep",             timing:"Before bed",               dose:"50–100mg",          evidence:"Good",     tags:["sleep","mood","serotonin","anxiety","women"]},
  "Valerian Root":                   {category:"Plant Extract",     timing:"1 hour before bed",        dose:"300–600mg",         evidence:"Good",     tags:["sleep","anxiety","insomnia","relaxation"]},
  "Lemon Balm":                      {category:"Plant Extract",     timing:"Evening",                  dose:"300–600mg",         evidence:"Good",     tags:["sleep","anxiety","stress","calm"]},
  "Lion's Mane Mushroom":            {category:"Nootropic",         timing:"Morning with food",        dose:"500–1000mg",        evidence:"Good",     tags:["brain","cognition","nerve","memory","focus","aging"]},
  "Bacopa Monnieri":                 {category:"Nootropic",         timing:"With a meal",              dose:"300–450mg",         evidence:"Good",     tags:["memory","cognition","anxiety","focus","aging"]},
  "Phosphatidylserine":              {category:"Nootropic",         timing:"With a meal",              dose:"100–300mg",         evidence:"Good",     tags:["brain","memory","cortisol","aging","cognition"]},
  "NMN":                             {category:"Longevity",         timing:"Morning",                  dose:"250–500mg",         evidence:"Emerging", tags:["aging","energy","longevity","NAD+","mitochondria"]},
  "PQQ":                             {category:"Longevity",         timing:"Morning with food",        dose:"10–20mg",           evidence:"Good",     tags:["mitochondria","energy","brain","aging","cognition"]},
  "Spirulina":                       {category:"Superfood",         timing:"Morning",                  dose:"3–5g",              evidence:"Good",     tags:["energy","immunity","detox","protein","women","busy"]},
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /data/supplementDetails.js — Premium-tier deep-dive data for each supplement
// Structure per entry:
//   summary      — 2–3 sentence plain-English overview
//   mechanism    — how it works physiologically
//   research     — array of { finding, quality } where quality: "Strong"|"Good"|"Moderate"|"Emerging"
//   bestForm     — the specific form to buy and why
//   avoid        — contraindications, safety notes, upper limits
//   synergies    — array of { name, reason } — pairs that amplify effect
//   stackNotes   — practical tips for fitting into a stack
//   whoFor       — who benefits most
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const SUPPLEMENT_DETAIL_DB = {
  "Magnesium Glycinate": {
    summary: "Magnesium is involved in over 300 enzymatic reactions and is the most commonly depleted mineral in modern diets. The glycinate form is bound to glycine — an inhibitory amino acid — making it the most calming and bioavailable form available.",
    mechanism: "Magnesium acts as a natural calcium channel blocker at NMDA receptors, reducing excitatory nervous system activity. It is a required cofactor for ATP synthesis, DNA replication, and the activation of vitamin D. The glycine component independently activates glycine receptors in the brain, reinforcing its calming effect and promoting GABA activity.",
    research: [
      {finding:"A meta-analysis of 34 RCTs found magnesium supplementation significantly reduces subjective anxiety scores and is more pronounced in populations with confirmed deficiency.", quality:"Strong"},
      {finding:"Supplementation at 300–500mg/day consistently improves sleep onset latency and sleep efficiency in adults over 50 in multiple controlled trials.", quality:"Strong"},
      {finding:"Magnesium deficiency accelerates bone mineral density loss. Combined with vitamin D3 and K2, it significantly outperforms D3 alone in bone density outcomes.", quality:"Strong"},
      {finding:"Lowers systolic blood pressure by an average of 2–4 mmHg in hypertensive individuals, with effects seen within 3 months of consistent use.", quality:"Good"},
      {finding:"Magnesium glycinate specifically outperforms oxide and citrate in absorption studies, with bioavailability roughly 4× higher than magnesium oxide.", quality:"Good"},
    ],
    bestForm: "Magnesium glycinate (bisglycinate). Avoid oxide — only 4% elemental absorption. Citrate is a reasonable alternative but has a laxative effect at higher doses. Look for 'elemental magnesium' on the label — a '500mg magnesium glycinate' capsule typically contains only 50–80mg elemental magnesium.",
    avoid: "Upper tolerable intake is 350mg elemental magnesium from supplements. Excess causes loose stools. Use with caution if on antibiotics (fluoroquinolones/tetracyclines) or bisphosphonates — space 2+ hours apart. Renal impairment requires medical supervision.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"Magnesium is required to activate vitamin D. Without it, supplemental D3 cannot convert to its active form. This is the single most important pairing for people supplementing D3."},
      {name:"L-Theanine", reason:"Complementary calming pathways — magnesium works via NMDA and GABA modulation, L-theanine via alpha wave induction. Together they produce additive anxiolytic and sleep-onset benefits."},
      {name:"Glycine", reason:"Both act on inhibitory neurotransmitter systems. Glycine (3–5g) + magnesium glycinate before bed produces measurably better deep sleep scores than either alone."},
      {name:"Electrolytes", reason:"Magnesium, potassium, and sodium work as a team in muscle contraction and fluid balance. Supplementing magnesium without adequate potassium reduces its anti-cramp effect."},
    ],
    stackNotes: "Take 300–400mg 30–60 min before bed. Start at 200mg and build up over 1–2 weeks to minimise any initial digestive adjustment. Do not take with iron supplements — separate by 2+ hours. If you are also taking zinc at high doses long-term, monitor magnesium status.",
    whoFor: "Anyone experiencing poor sleep, anxiety, muscle cramps, or over 40. Particularly important for people taking vitamin D3 without magnesium — a very common and correctable oversight.",
  },

  "Vitamin D3 + K2": {
    summary: "Vitamin D3 is a fat-soluble pro-hormone that affects over 200 genes. Deficiency is epidemic in northern latitudes and strongly linked to immune dysfunction, low mood, and accelerated bone loss. K2 is essential to direct the calcium that D3 mobilises into bone, not arterial walls.",
    mechanism: "Vitamin D3 binds to the vitamin D receptor (VDR), present in nearly every cell. It upregulates antimicrobial peptides, modulates T-cell differentiation, enhances calcium absorption from the gut (increasing it up to 40%), and suppresses inflammatory cytokine production. K2 (MK-7) activates osteocalcin (which binds calcium into bone matrix) and matrix Gla protein (MGP), which prevents arterial calcification — the critical reason D3 and K2 must always be taken together.",
    research: [
      {finding:"Large-scale trials (VITAL study, n=25,871) show D3 supplementation reduces cancer mortality by 13% and autoimmune disease incidence by 22% in non-deficient adults over 5 years.", quality:"Strong"},
      {finding:"Vitamin D deficiency (below 50 nmol/L) is present in approximately 40–50% of adults in northern Europe and is independently associated with depression, fatigue, and reduced cognitive function.", quality:"Strong"},
      {finding:"Combined D3+K2 supplementation over 3 years produces significantly better bone density outcomes than D3 alone in postmenopausal women — K2 is not optional for bone health.", quality:"Strong"},
      {finding:"D3 at 2000–4000 IU/day reduces upper respiratory tract infection risk by 12% in adults with baseline deficiency, with stronger effects at higher baseline deficiency.", quality:"Good"},
      {finding:"MK-7 form of K2 has a plasma half-life of 72 hours vs. 1–2 hours for MK-4, making once-daily dosing effective. MK-7 is the only form validated in bone density RCTs.", quality:"Good"},
    ],
    bestForm: "D3 (cholecalciferol) — not D2 (ergocalciferol), which is significantly less potent. K2 as MK-7 (menaquinone-7) — not MK-4, which has far too short a half-life for once-daily dosing. Always in a combined supplement or taken together. A combined D3+K2 soft-gel with MCT oil has highest absorption as both are fat-soluble.",
    avoid: "Do not supplement D3 above 4000 IU/day without testing. Toxicity (hypercalcaemia) is possible above 10,000 IU/day sustained. K2 is safe at standard doses but use caution on warfarin — K2 can reduce anticoagulant effect (consult your prescriber). Always take with food containing fat.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Magnesium is a required cofactor for the conversion of D3 to its active form (calcitriol). Supplementing D3 without magnesium produces a fraction of the expected benefit."},
      {name:"Calcium Citrate", reason:"D3 dramatically increases gut calcium absorption. K2 then directs that calcium into bone. This trio is the clinical standard of care for osteoporosis prevention."},
      {name:"Omega-3 (EPA+DHA)", reason:"Both are fat-soluble. Taking together with a fatty meal increases absorption of both by up to 50%. Additionally, omega-3 reduces systemic inflammation that can impair VDR sensitivity."},
      {name:"Zinc Bisglycinate", reason:"Zinc is required for VDR expression. Low zinc status reduces the body's ability to respond to vitamin D, even at adequate serum levels."},
    ],
    stackNotes: "Take with the fattiest meal of the day — absorption increases by 50%+ with dietary fat. Get a 25(OH)D blood test before starting and again after 3 months to calibrate your dose. Optimal range is 80–125 nmol/L (32–50 ng/mL). Most deficient adults need 3000–4000 IU/day to reach optimal levels.",
    whoFor: "Almost everyone in northern latitudes, especially October to April. People who work indoors, use high-SPF sunscreen daily, or have darker skin require year-round supplementation. Critical for anyone over 40, post-menopausal women, and anyone with immune concerns.",
  },

  "Omega-3 (EPA+DHA)": {
    summary: "EPA and DHA are long-chain omega-3 fatty acids that cannot be synthesised in meaningful quantities by the body. EPA is the primary anti-inflammatory compound; DHA is a structural component of brain cell membranes. Most people consume far more omega-6 than omega-3, creating a pro-inflammatory imbalance that omega-3 supplementation directly corrects.",
    mechanism: "EPA competitively inhibits arachidonic acid conversion to pro-inflammatory prostaglandins and leukotrienes, reducing the inflammatory cascade at the source. DHA incorporates into phospholipid cell membranes — particularly neurons — improving membrane fluidity, receptor sensitivity, and synaptic signalling. Both EPA and DHA are precursors to specialised pro-resolving mediators (SPMs) — resolvins and protectins — that actively resolve inflammation rather than simply suppressing it.",
    research: [
      {finding:"REDUCE-IT trial (n=8,179) demonstrated that high-dose EPA (4g/day as icosapentaenoic acid) reduced major adverse cardiovascular events by 25% in high-risk patients on statins.", quality:"Strong"},
      {finding:"Meta-analysis of 26 RCTs found omega-3 supplementation at 1–2g/day EPA+DHA significantly reduces triglycerides by 15–30% and slightly reduces blood pressure in hypertensive subjects.", quality:"Strong"},
      {finding:"Omega-3 index below 4% is associated with a 2× increased risk of sudden cardiac death. Supplementation at 2g/day EPA+DHA raises omega-3 index to the protective range (8%+) within 4 months.", quality:"Strong"},
      {finding:"RCTs show EPA specifically reduces depression symptoms with effect sizes comparable to antidepressants in mild-moderate depression. DHA alone shows no antidepressant effect — EPA is the active component.", quality:"Good"},
      {finding:"Collagen synthesis combined with vitamin C and omega-3 post-exercise significantly accelerates tendon healing compared to collagen alone in a 2019 RCT.", quality:"Good"},
    ],
    bestForm: "Triglyceride form — not ethyl ester, which has 30% lower bioavailability. Check the supplement facts label for combined EPA + DHA content specifically, not total fish oil. Most 1000mg fish oil capsules contain only 300mg EPA+DHA. Target 1000–2000mg combined EPA+DHA daily. IFOS-certified products have been independently tested for purity and oxidation. Algae-based omega-3 (DHA+EPA from algae) is equally effective and suitable for vegetarians/vegans.",
    avoid: "High doses (above 3g/day EPA+DHA) may slightly increase bleeding time — use caution with blood thinners. Fishy burps are reduced by taking with food, freezing capsules, or using enteric-coated products. Store in refrigerator once opened to prevent oxidation. Rancid fish oil smells strongly — discard any product with a strong fishy/paint-like odour.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"Both fat-soluble and absorption increases taken together with a meal. Additionally, omega-3 upregulates VDR expression, improving the body's sensitivity to vitamin D."},
      {name:"Collagen Peptides", reason:"EPA reduces inflammatory prostaglandins that degrade collagen, while DHA supports membrane integrity in tendon fibroblasts. The combination accelerates connective tissue repair."},
      {name:"CoQ10 Ubiquinol", reason:"Both reduce cardiovascular oxidative stress via independent mechanisms. Together they produce additive cardioprotective effects, especially relevant for statin users."},
      {name:"Boswellia Serrata", reason:"Omega-3 (COX pathway) and Boswellia (5-LOX pathway) inhibit different arms of the inflammatory cascade. Combining them produces broader anti-inflammatory coverage for joint pain."},
    ],
    stackNotes: "Always take with a meal containing fat — absorption increases dramatically. The omega-3 index test (available as a home blood spot test) is the most useful biomarker to confirm you are in the protective range (target 8%+). If supplementing for depression, EPA content matters more than DHA — look for a product with a higher EPA:DHA ratio (e.g. 3:1 or 2:1).",
    whoFor: "Virtually everyone benefits. Highest priority for people with cardiovascular risk, joint pain, mood disorders, or a low-fish diet. Pregnant women require adequate DHA for foetal brain development. Critical for people with high omega-6 intake (processed food, seed oils).",
  },

  "Vitamin B12": {
    summary: "Vitamin B12 is essential for DNA synthesis, myelin sheath formation, and red blood cell production. Deficiency develops slowly over years due to liver storage, but produces profound and sometimes irreversible neurological damage when it occurs. It is the most common deficiency that is consistently missed due to its slow progression.",
    mechanism: "B12 functions as a cofactor in two enzymatic reactions: methionine synthase (converting homocysteine to methionine, essential for DNA methylation and SAMe production) and methylmalonyl-CoA mutase (involved in fatty acid metabolism and myelin synthesis). Deficiency impairs methylation across every system, raises homocysteine (a cardiovascular risk factor), and progressively demyelinates peripheral nerves and the spinal cord — damage that is only partially reversible.",
    research: [
      {finding:"Adults over 50 have reduced gastric acid, impairing B12 absorption from food by up to 60%. Sublingual methylcobalamin bypasses this entirely — it is absorbed directly through the oral mucosa.", quality:"Strong"},
      {finding:"Elevated homocysteine (driven by B12 deficiency) is an independent risk factor for cardiovascular disease, stroke, and dementia. B12 + folate supplementation reliably lowers homocysteine by 25–30%.", quality:"Strong"},
      {finding:"B12 deficiency causes subacute combined degeneration of the spinal cord — a progressive neurological condition that produces symptoms often misattributed to ageing: balance problems, paraesthesia, and cognitive decline.", quality:"Strong"},
      {finding:"Metformin (diabetes medication) depletes B12 by reducing intrinsic factor production. Up to 30% of long-term metformin users develop clinically significant B12 deficiency.", quality:"Good"},
      {finding:"Vegetarians have approximately 5× higher prevalence of B12 deficiency than omnivores. B12 is not found in any plant foods at biologically meaningful levels.", quality:"Strong"},
    ],
    bestForm: "Methylcobalamin — not cyanocobalamin, which is poorly retained in older adults and requires liver conversion. Sublingual tablets (dissolved under the tongue) bypass gastric acid dependency entirely and are the preferred form for anyone over 50 or with absorption concerns. At high doses (1000mcg), even cyanocobalamin produces some absorption via passive diffusion, but methylcobalamin remains superior.",
    avoid: "B12 has no established upper tolerable limit — it is water-soluble and excess is excreted. Very high doses (above 5000mcg/day) have been associated with acne in susceptible individuals. Get serum B12 tested — optimal range is 300–600 pmol/L (some labs flag 200 as 'normal' which is too low to prevent neurological effects).",
    synergies: [
      {name:"Folate (Methylfolate)", reason:"B12 and folate are metabolic partners in the methylation cycle. Neither functions optimally without the other — always supplement together. This pair is particularly important during pregnancy."},
      {name:"Vitamin B Complex", reason:"B12 works within the broader B-vitamin network. Isolated B12 without other B vitamins can mask folate deficiency and is generally less effective than a complete B complex."},
      {name:"Magnesium Glycinate", reason:"Magnesium is required for SAMe-dependent methylation reactions downstream of B12. Both support the same methylation pathway and are commonly deficient together."},
    ],
    stackNotes: "Take in the morning — B vitamins can be energising and may interfere with sleep if taken in the evening. Sublingual: hold under tongue for 30–60 seconds before swallowing. Get tested first if you have neurological symptoms, are vegan, or are over 60 — serum B12 below 200 pmol/L requires therapeutic not maintenance dosing.",
    whoFor: "Anyone over 50, vegetarians and vegans, people on metformin, people on proton pump inhibitors (PPIs), anyone with neurological symptoms or unexplained fatigue. The risk of under-supplementing dramatically outweighs any risk of over-supplementing.",
  },

  "Ashwagandha KSM-66": {
    summary: "Ashwagandha is the most clinically studied adaptogen for stress and cortisol reduction. KSM-66 is a root-only extract standardised to 5% withanolides — the highest concentration full-spectrum root extract available and the form used in the majority of human trials.",
    mechanism: "The primary active compounds — withanolides — modulate the hypothalamic-pituitary-adrenal (HPA) axis by reducing cortisol output at the hypothalamus level, rather than blocking cortisol peripherally. This produces genuine stress resilience rather than cortisol suppression. Ashwagandha also acts as a GABA-mimetic (binding GABA-A receptors), upregulates thyroid hormone (T3/T4) in subclinical hypothyroidism, and has demonstrated anabolic properties by increasing LH and testosterone in men with suboptimal levels.",
    research: [
      {finding:"KSM-66 at 600mg/day reduces serum cortisol by an average of 27.9% and significantly reduces perceived stress scores (PSS) versus placebo in a 60-day double-blind RCT.", quality:"Strong"},
      {finding:"In men with low-normal testosterone, KSM-66 at 600mg/day increases testosterone by 15–17% and DHEA-S by 18% after 8 weeks compared to placebo.", quality:"Good"},
      {finding:"KSM-66 improves VO2 max, muscular endurance, and recovery time in trained athletes, with effects seen at 8 weeks in a crossover RCT.", quality:"Good"},
      {finding:"Sleep quality (measured by PSQI) significantly improves at 300–600mg/day, with reductions in time to fall asleep and improvements in sleep efficiency versus placebo.", quality:"Good"},
      {finding:"In subclinical hypothyroidism, ashwagandha at 600mg/day for 8 weeks significantly increases T3 and T4 while reducing TSH — the only natural compound with consistent thyroid-stimulating evidence.", quality:"Moderate"},
    ],
    bestForm: "KSM-66 (root-only, 5% withanolides) or Sensoril (full-plant, 10% withanolides — calmer, more sedating). KSM-66 is the more energising and broadly studied option. Avoid unspecified 'ashwagandha extract' without standardisation — withanolide content varies enormously. Do not use leaf-based extracts — all clinical evidence is from root.",
    avoid: "Not recommended during pregnancy (uterotonic effects). May interact with thyroid medications — consult your prescriber if treated for thyroid conditions. Use caution with sedative medications. Rare cases of liver injury have been reported at very high doses — stick to 300–600mg. Cycle with a 1–2 week break every 2–3 months if using long-term.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Complementary nervous system calming — ashwagandha works on the HPA axis (cortisol), magnesium on the NMDA/GABA system. Together they address stress from two independent pathways."},
      {name:"L-Theanine", reason:"L-theanine acts within 30–60 minutes for acute stress. Ashwagandha builds over 4–8 weeks for chronic resilience. The combination provides both immediate and sustained stress management."},
      {name:"Rhodiola Rosea", reason:"Rhodiola is energising and stimulating; ashwagandha is calming. Split dosing (Rhodiola morning, ashwagandha evening) provides all-day adaptogen coverage without overstimulation."},
      {name:"Zinc Bisglycinate", reason:"Both support testosterone production via different pathways — zinc via 5-alpha reductase inhibition and LH support, ashwagandha via direct LH stimulation. Stack for men seeking hormonal optimisation."},
    ],
    stackNotes: "Take with dinner — the mild sedative effect is an asset in the evening. Allow 4–8 weeks minimum before assessing full effect. Benefits accumulate — do not stop after 2 weeks if you see no effect. Most people notice significant difference by week 6. Take consistently; sporadic use produces minimal benefit.",
    whoFor: "High-stress individuals, anyone with sleep difficulties driven by cortisol, men with suboptimal testosterone, athletes seeking recovery and performance, anyone with subclinical thyroid insufficiency. One of the highest-impact adaptogens for the 40+ age group.",
  },

  "Magnesium L-Threonate": {
    summary: "Magnesium L-threonate is uniquely able to cross the blood-brain barrier, raising brain magnesium levels where standard magnesium forms cannot. It is the form of choice specifically for cognitive and neurological applications.",
    mechanism: "Standard magnesium forms (glycinate, citrate, oxide) raise serum and tissue magnesium but do not meaningfully increase cerebrospinal fluid (CSF) magnesium. L-threonate was specifically developed to cross the blood-brain barrier via active transport. In the brain, magnesium regulates NMDA receptors — critical for synaptic plasticity, long-term potentiation (LTP), and memory formation. Raising brain magnesium density directly increases synaptic density and the density of functional NMDA receptors.",
    research: [
      {finding:"In animal models, magnesium L-threonate restores synaptic density and improves spatial memory in aged subjects — the first compound to demonstrate this effect via the brain-magnesium route.", quality:"Good"},
      {finding:"A human RCT (n=109, adults 50–70) showed significant improvements in executive function and working memory after 12 weeks of magnesium L-threonate at 2g/day (providing 144mg elemental Mg).", quality:"Good"},
      {finding:"Human pilot data suggests magnesium L-threonate reduces symptoms of anxiety and PTSD-associated fear memory reconsolidation, consistent with its NMDA modulation mechanism.", quality:"Moderate"},
    ],
    bestForm: "Magnesium L-threonate (Magtein is the patented form used in human trials). The standard dose is 2000mg L-threonate complex daily, providing ~144mg elemental magnesium. It is not a replacement for magnesium glycinate for sleep or muscle/bone benefits — best used alongside it for the cognitive-specific effect.",
    avoid: "May cause headaches in the first 1–2 weeks as brain magnesium adjusts — reduce dose temporarily if this occurs. Not a replacement for standard magnesium — elemental magnesium content is low. Not recommended as a sole magnesium source.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Complementary — glycinate handles systemic deficiency (sleep, muscle, bone) while L-threonate specifically addresses brain magnesium. Take both for complete coverage."},
      {name:"Lion's Mane Mushroom", reason:"Both support neuroplasticity via different mechanisms — L-threonate via NMDA/synaptic density, lion's mane via NGF stimulation. Strong nootropic combination."},
      {name:"Bacopa Monnieri", reason:"Bacopa improves memory consolidation; L-threonate improves the synaptic substrate for memory formation. Together they support different stages of the memory process."},
    ],
    stackNotes: "Take 1–2 hours before bed or split morning/evening. Takes 4–6 weeks for full cognitive effect. Stack with magnesium glycinate (not instead of it) — their elemental magnesium contributions are additive for systemic needs.",
    whoFor: "Anyone prioritising cognitive longevity, memory, or neuroProtection — particularly 45+. Relevant for people with anxiety, ADHD-type focus difficulties, or those who found standard magnesium improved sleep but not cognitive function.",
  },

  "Collagen Peptides": {
    summary: "Collagen is the most abundant structural protein in the body. Production declines at ~1% per year from the mid-20s, accelerating sharply at menopause. Hydrolysed collagen peptides are absorbed intact and have good clinical evidence for skin, joint, and tendon outcomes specifically — unlike standard dietary protein.",
    mechanism: "Hydrolysed collagen peptides are broken into dipeptides and tripeptides (notably hydroxyproline-proline) that are absorbed intact from the gut and accumulate specifically in skin, cartilage, and tendons. These peptides act as signalling molecules that stimulate local fibroblasts and chondrocytes to increase collagen synthesis — a direct feedback loop. Vitamin C is an essential cofactor for prolyl hydroxylase, the enzyme that cross-links collagen chains into stable triple-helix structures.",
    research: [
      {finding:"A meta-analysis of 19 RCTs found hydrolysed collagen (10g/day) significantly reduces skin wrinkle depth and improves hydration and elasticity, with effects visible at 8–12 weeks.", quality:"Good"},
      {finding:"A 2019 RCT found athletes taking 15g collagen + vitamin C 60 min before exercise for 24 weeks had significantly greater tendon cross-sectional area and force production compared to placebo.", quality:"Good"},
      {finding:"In knee osteoarthritis, 10g/day collagen peptides for 6 months significantly reduces WOMAC pain and function scores compared to placebo, with effects comparable to glucosamine.", quality:"Good"},
      {finding:"Collagen combined with resistance training and vitamin D preserves significantly more muscle mass than protein + D3 alone in sarcopenic elderly — possibly via its glycine content supporting muscle protein synthesis.", quality:"Moderate"},
    ],
    bestForm: "Hydrolysed collagen peptides — molecular weight 3,000–5,000 Da is the optimal range for gut absorption. Type I+III for skin, bone, and tendon. Type II (undenatured, UC-II) for joint cartilage specifically — lower dose (40mg/day) and a different mechanism. A dose of 10g+ hydrolysed peptides is the minimum therapeutic amount — many beauty products contain only 1–3g.",
    avoid: "Collagen is a protein — those with PKU or specific amino acid metabolism disorders should be cautious. Bovine collagen is the most clinically studied; marine collagen has weaker evidence but is suitable for those avoiding beef. Collagen is not a complete protein and should not replace general protein intake.",
    synergies: [
      {name:"Vitamin C", reason:"Vitamin C is not optional — it is the required cofactor for collagen cross-linking. Always take collagen within 30–60 minutes of vitamin C. Without it, the peptides cannot form stable collagen fibres."},
      {name:"Glycine", reason:"Glycine is the most abundant amino acid in collagen. Supplemental glycine (3–5g) alongside collagen peptides provides the raw material that limits collagen synthesis rate."},
      {name:"Omega-3 (EPA+DHA)", reason:"EPA reduces inflammatory prostaglandins that degrade collagen in joints and tendons, protecting the collagen that peptides help build."},
      {name:"Vitamin D3 + K2", reason:"Vitamin D is required for bone collagen mineralisation. The combination of collagen + D3 + K2 + calcium is the complete bone-building stack."},
    ],
    stackNotes: "Timing matters for tendons: take 45–60 minutes before exercise to maximise collagen synthesis in loaded tendons. For skin and general use, timing is less critical — morning with vitamin C works well. Dissolves easily in hot liquids. Unflavoured versions are versatile — add to coffee, smoothies, or soup.",
    whoFor: "Women 35+, anyone with joint pain or osteoarthritis, active individuals with tendon issues, people prioritising skin health and elasticity, post-menopausal women (oestrogen decline accelerates collagen loss dramatically).",
  },

  "Creatine Monohydrate": {
    summary: "Creatine is the most researched supplement in sports science, with over 1000 human clinical trials. It has a mechanism of action that extends well beyond muscle — emerging evidence shows significant cognitive, neurological, and metabolic benefits, particularly in older adults.",
    mechanism: "Creatine increases phosphocreatine stores in muscle and brain cells. Phosphocreatine is the immediate energy buffer that regenerates ATP during high-intensity effort — it is not an energy source itself but extends the window before fatigue during maximal effort. In the brain, creatine improves cellular energy resilience during cognitive load, sleep deprivation, and hypoxic stress. It also has direct effects on satellite cell activation (muscle repair) and reduces markers of muscle damage after intense exercise.",
    research: [
      {finding:"Meta-analysis of 22 RCTs: creatine supplementation consistently increases lean mass by 1–2kg and strength by 5–15% when combined with resistance training, with effects plateauing at 3–5g/day.", quality:"Strong"},
      {finding:"In older adults, creatine attenuates age-related muscle loss (sarcopenia) when combined with resistance training, with effect sizes not seen with exercise alone.", quality:"Strong"},
      {finding:"Cognitive benefits: creatine significantly improves working memory and reasoning in sleep-deprived subjects and vegetarians/vegans (who have lower baseline brain creatine stores).", quality:"Good"},
      {finding:"Creatine reduces homocysteine by providing an alternative methyl group donor, reducing the methylation burden on the folate/B12 cycle — a cardiovascular benefit independent of its performance effects.", quality:"Moderate"},
      {finding:"Post-concussion recovery: creatine supplementation reduces symptom duration and improves cognitive recovery in athletes after mild traumatic brain injury in two controlled trials.", quality:"Moderate"},
    ],
    bestForm: "Creatine monohydrate — specifically. No other form (HCl, ethyl ester, buffered) has demonstrated superior efficacy in a properly controlled trial. Creapure is the pharmaceutical-grade German-manufactured monohydrate — the industry gold standard. No loading phase is necessary for most people — 3–5g/day reaches saturation in 3–4 weeks. Loading (20g/day for 5–7 days) reaches saturation faster but offers no long-term advantage.",
    avoid: "Creatine increases water retention in muscle — scale weight may rise 1–2kg initially (this is intramuscular water, not fat). Ensure adequate hydration (2–3L/day). Not contraindicated in healthy individuals but avoid high doses with pre-existing kidney disease. Creatine does not cause kidney damage in healthy adults — this is a thoroughly debunked myth supported by over 20 years of safety data.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"Both support muscle and bone function with age. Vitamin D improves muscle protein synthesis efficiency and satellite cell function — synergistic with creatine's anabolic effects."},
      {name:"Omega-3 (EPA+DHA)", reason:"Omega-3 enhances muscle protein synthesis via mTOR activation. The combination of creatine + omega-3 + resistance training produces greater lean mass gains than creatine alone."},
      {name:"Magnesium Glycinate", reason:"Magnesium is required for creatine uptake into muscle cells via the creatine transporter. Sub-optimal magnesium may limit the full benefit of creatine supplementation."},
      {name:"Vitamin B12", reason:"Creatine synthesis is a major consumer of methyl groups — it competes with the B12/folate methylation cycle. Taking creatine reduces homocysteine and spares methyl groups for other processes."},
    ],
    stackNotes: "Timing is largely irrelevant — take it consistently at any time. Post-workout with a meal is marginally superior in some studies but the difference is small. No need to cycle — continuous use is supported by long-term safety data up to 5 years. Can be mixed in water, coffee, or protein shakes — dissolves best in warm liquid.",
    whoFor: "Everyone over 40 (sarcopenia prevention), strength athletes, vegetarians/vegans (typically have 50–70% lower baseline muscle creatine), anyone under high cognitive load, post-concussion recovery. One of the highest evidence-to-cost ratios of any supplement.",
  },

  "L-Theanine": {
    summary: "L-theanine is an amino acid found naturally in green tea that uniquely promotes alert relaxation — reducing anxiety and stress reactivity without sedation. It is one of very few compounds with fast-acting anxiolytic effects and no dependence or tolerance risk.",
    mechanism: "L-theanine increases alpha brain wave activity (8–12 Hz) — the brain state associated with calm focus, creativity, and meditative awareness — within 30–60 minutes of ingestion. It inhibits excitatory glutamate receptors (AMPA and NMDA) while promoting GABA and glycine receptor activity. It also moderates catecholamine release, reducing the cardiovascular and anxiogenic effects of acute stress without blunting the alertness that cortisol and adrenaline provide.",
    research: [
      {finding:"Single-dose 200mg L-theanine significantly increases alpha-band EEG power within 45 minutes in healthy adults, with strongest effects during active mental tasks — a measure of calm focus.", quality:"Strong"},
      {finding:"L-theanine + caffeine (200mg + 100mg) consistently improves sustained attention, reaction time, and working memory more than caffeine alone, while reducing caffeine-associated anxiety and jitteriness.", quality:"Strong"},
      {finding:"In high-trait-anxiety individuals, 200mg L-theanine reduces cortisol response to a psychosocial stressor and significantly lowers subjective anxiety versus placebo within 1 hour.", quality:"Good"},
      {finding:"Chronic supplementation (4 weeks, 200mg/day) improves sleep quality, sleep latency, and morning alertness in adults with high anxiety traits without the grogginess associated with sedatives.", quality:"Good"},
    ],
    bestForm: "Any reputable pure L-theanine — there is no proprietary form advantage. Suntheanine is the most clinically studied branded form but generic L-theanine from green tea extract is equivalent. 100–200mg per dose; effects plateau above 400mg.",
    avoid: "Extremely safe — no known toxicity at clinical doses. Very few interactions. Theoretical additive sedation with sedative medications at high doses. Not a treatment for clinical anxiety disorders — but a clinically useful daily tool for stress management.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"The most validated sleep combination: L-theanine reduces acute stress and racing thoughts; magnesium promotes physical relaxation and GABA activity. Together they address both cognitive and physical barriers to sleep."},
      {name:"Ashwagandha KSM-66", reason:"Fast-acting (L-theanine) and slow-building (ashwagandha) anxiolytics via completely different mechanisms. L-theanine for today; ashwagandha for next month. Ideal combined strategy."},
      {name:"Caffeine", reason:"The most evidence-backed combination in supplement science. 100mg caffeine + 200mg L-theanine is used in virtually every nootropic stack. L-theanine removes the jitteriness and crash while preserving alertness."},
    ],
    stackNotes: "Take 100–200mg as needed for acute stress, or 200mg nightly 30–60 minutes before bed alongside magnesium. The caffeine pairing (1:2 caffeine:theanine ratio) is ideal for focus — add 200mg L-theanine to your morning coffee. Non-habit-forming — can be taken daily indefinitely.",
    whoFor: "Anyone managing daily stress or anxiety, people who are caffeine-sensitive, anyone wanting to improve sleep quality without sedatives. Particularly effective as a cognitive focus tool combined with morning caffeine.",
  },

  "Zinc Bisglycinate": {
    summary: "Zinc is an essential trace mineral acting as a cofactor in over 300 enzymatic reactions. Absorption declines with age and is competitively inhibited by iron and calcium, making targeted bisglycinate supplementation particularly relevant for adults over 40.",
    mechanism: "Zinc is the structural component of zinc-finger proteins that regulate gene expression across virtually every biological system. It is directly required for testosterone synthesis (via 5-alpha reductase), thyroid hormone metabolism (T3 receptor binding), immune cell proliferation, wound healing via keratinocyte activation, and the antioxidant enzyme superoxide dismutase (SOD). The bisglycinate chelate bypasses the competitive absorption pathways that limit inorganic zinc forms.",
    research: [
      {finding:"In men with suboptimal zinc status, supplementation at 25–45mg/day for 6 months significantly raises both total and free testosterone levels — the effect is strongly dependent on baseline zinc status.", quality:"Good"},
      {finding:"Zinc supplementation reduces the duration of common cold symptoms by 33% when initiated within 24 hours of onset, across a meta-analysis of 17 RCTs.", quality:"Strong"},
      {finding:"Zinc deficiency is present in an estimated 17% of the global population and up to 40% of elderly individuals. Sub-clinical deficiency is not detected by standard serum zinc tests — erythrocyte zinc is more sensitive.", quality:"Strong"},
      {finding:"Daily supplementation reduces acne severity comparable to antibiotic treatment in moderate acne, with fewer side effects and no resistance concerns.", quality:"Good"},
    ],
    bestForm: "Zinc bisglycinate or zinc picolinate — both are superior to zinc sulphate, gluconate, or oxide in absorption studies. Zinc oxide has very poor bioavailability (~4%) and should be avoided as a supplement form (fine for sunscreen). At 15–25mg elemental zinc/day, bisglycinate achieves therapeutic levels without GI irritation.",
    avoid: "Do not exceed 40mg elemental zinc/day long-term — competitive inhibition of copper absorption leads to copper deficiency at sustained high doses. If taking 25mg+ zinc for more than 4–6 weeks, add 1–2mg copper bisglycinate. Space 2 hours from iron supplements. High zinc on an empty stomach causes nausea in some people.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"Zinc is required for VDR (vitamin D receptor) expression. Without adequate zinc, vitamin D cannot signal properly even if serum D levels appear adequate."},
      {name:"Copper Bisglycinate", reason:"Required pairing at higher zinc doses. Zinc and copper are metabolic partners and antagonists — zinc above 25mg/day requires 1–2mg copper to prevent induced copper deficiency."},
      {name:"Ashwagandha KSM-66", reason:"Both support testosterone production via different mechanisms. Zinc provides the raw mineral substrate; ashwagandha upregulates LH signalling. The combination is more effective than either alone for hormonal optimisation."},
    ],
    stackNotes: "Always take with food to minimise nausea. Separate from iron supplements by 2+ hours — they directly compete for absorption. If taking long-term above 25mg/day, add copper bisglycinate (1–2mg). The best dietary sources are oysters, red meat, and pumpkin seeds — supplement if these are limited in your diet.",
    whoFor: "Men with suboptimal testosterone, people with frequent infections, anyone with slow wound healing or persistent skin issues, vegetarians/vegans (phytates in plant foods significantly reduce zinc absorption), older adults.",
  },

  "CoQ10 Ubiquinol": {
    summary: "CoQ10 is a fat-soluble compound produced in every cell that plays a direct role in mitochondrial energy production. Levels decline significantly with age and are critically depleted by statins. After 40, the body's conversion of ubiquinone to ubiquinol (the active form) also declines.",
    mechanism: "CoQ10 functions as an electron carrier in the mitochondrial electron transport chain (complexes I–III), directly enabling ATP synthesis. It is also one of the most powerful endogenous lipophilic antioxidants, protecting cell membranes and LDL cholesterol from oxidative damage. Statins inhibit HMG-CoA reductase — the same pathway that produces cholesterol also produces CoQ10. Statin use reduces CoQ10 levels by up to 40%, which is the primary mechanism behind statin-associated myopathy (muscle pain and weakness).",
    research: [
      {finding:"In statin-treated patients, CoQ10 supplementation (200–300mg/day) significantly reduces myalgia (muscle pain) scores and allows continuation of statin therapy in patients who would otherwise discontinue.", quality:"Good"},
      {finding:"CoQ10 at 100–300mg/day reduces systolic blood pressure by approximately 11 mmHg and diastolic by 7 mmHg in hypertensive patients — a clinically meaningful reduction comparable to a low-dose antihypertensive.", quality:"Good"},
      {finding:"In heart failure patients, CoQ10 (300mg/day) reduces major adverse cardiovascular events by 43% and all-cause mortality by 42% versus placebo in the Q-SYMBIO trial.", quality:"Good"},
      {finding:"Migraine prevention: CoQ10 at 300mg/day reduces migraine frequency by approximately 50% in episodic migraine after 3 months — now included in some neurology guidelines.", quality:"Good"},
    ],
    bestForm: "Ubiquinol — the active, reduced form. After 40, the body's ability to convert ubiquinone (the oxidised, cheaper form) to active ubiquinol declines significantly. Ubiquinol is 2–8× more bioavailable in adults over 40 than ubiquinone. Takes fat for absorption — always take with the fattiest meal of the day. Kaneka QH is the most validated branded ubiquinol.",
    avoid: "May slightly reduce warfarin efficacy — monitor INR if on anticoagulants. Generally very well tolerated. GI upset at high doses can be reduced by splitting the dose. Insomnia reported by some at evening dosing — take with lunch or dinner if this occurs.",
    synergies: [
      {name:"Omega-3 (EPA+DHA)", reason:"Both fat-soluble and cardiovascular-protective via independent mechanisms — omega-3 reduces inflammation and triglycerides; CoQ10 improves mitochondrial energy and reduces oxidative stress. Take together with food."},
      {name:"Alpha Lipoic Acid", reason:"ALA regenerates CoQ10 from its oxidised form back to ubiquinol, extending its antioxidant activity. Both also support mitochondrial function — a strong longevity combination."},
      {name:"NMN", reason:"NMN raises NAD+ (the substrate for the electron transport chain); CoQ10 is the electron carrier within that chain. Addressing both components produces additive mitochondrial support."},
    ],
    stackNotes: "Absorption is strongly food-dependent — take with the largest, fattiest meal of the day. Essential for anyone on a statin. Takes 4–6 weeks for full tissue saturation. If you are on statins, start with 200mg and assess muscle symptoms at 6 weeks. Avoid evening dosing if you experience sleep disruption.",
    whoFor: "Anyone on statins (non-negotiable), people with heart disease or hypertension, migraine sufferers, adults over 50 with fatigue, anyone pursuing cardiovascular or longevity optimisation.",
  },

  "Iron Bisglycinate": {
    summary: "Iron bisglycinate is the most bioavailable and gut-friendly form of supplemental iron, critical for haemoglobin production and oxygen transport. Iron deficiency is the world's most common nutritional deficiency and frequently goes undiagnosed because standard tests miss early-stage depletion.",
    mechanism: "Iron is the central atom of haem in haemoglobin and myoglobin, directly enabling oxygen transport. It is also required for cytochrome enzymes in the mitochondrial electron transport chain. Deficiency impairs ATP production at the cellular level — explaining why iron deficiency fatigue is so profound and out of proportion to haemoglobin numbers. Ferritin (iron storage) depletes before haemoglobin falls, so the 'full anaemia' threshold is a late-stage marker. Bisglycinate chelation bypasses the competitive absorption pathways that limit ferrous sulphate and reduces GI irritation by approximately 50%.",
    research: [
      {finding:"Iron bisglycinate produces equivalent or superior haemoglobin response to ferrous sulphate at the same elemental dose with significantly fewer GI side effects in controlled comparisons.", quality:"Good"},
      {finding:"Sub-clinical iron deficiency (low ferritin, normal haemoglobin) measurably impairs VO2 max, cognitive performance, and cold tolerance even without anaemia — a clinically underappreciated state.", quality:"Strong"},
      {finding:"Women with heavy menstrual bleeding are iron deficient at a rate of approximately 20–35% — most of whom are undiagnosed because haemoglobin appears normal.", quality:"Strong"},
    ],
    bestForm: "Iron bisglycinate — gentler than ferrous sulphate and better absorbed than ferric forms. Ferrous bisglycinate at 25–30mg elemental iron every other day is as effective as daily dosing in some studies, with better tolerance. Always take with vitamin C (increases absorption up to 3×).",
    avoid: "ALWAYS test before supplementing — iron overload (haemochromatosis) causes organ damage and shares some symptoms with deficiency. Target ferritin 50–100 ng/mL, not just 'within range' (lab normals go as low as 12 ng/mL — clinically insufficient). Separate from zinc, magnesium, calcium, and coffee by 2+ hours — all reduce absorption.",
    synergies: [
      {name:"Vitamin C", reason:"Reduces ferric iron to ferrous iron for absorption and prevents re-oxidation in the gut. Take simultaneously — do not separate. 500mg vitamin C increases iron absorption up to 3×."},
      {name:"Vitamin B12", reason:"Iron deficiency anaemia and B12 deficiency anaemia frequently coexist, especially in women and vegetarians. Addressing both together resolves anaemia that doesn't respond to iron alone."},
    ],
    stackNotes: "Take on an empty stomach with vitamin C for maximum absorption (if tolerated). If GI distress occurs, take with a small amount of food — absorption reduces slightly but side effects greatly diminish. Never take with dairy, coffee, or tea — all significantly impair iron absorption. Every-other-day dosing is equally effective and better tolerated than daily for most people.",
    whoFor: "Menstruating women (especially with heavy periods), pregnant women, endurance athletes, vegetarians/vegans, older adults with reduced gastric acid. Must be confirmed by testing — do not supplement without a ferritin level.",
  },

  "Calcium Citrate": {
    summary: "Calcium is the most abundant mineral in the body and the primary structural component of bone. Citrate is the superior supplemental form for adults over 40 because it does not require stomach acid for absorption, unlike the cheaper carbonate form.",
    mechanism: "Calcium is absorbed in the small intestine via active transport dependent on vitamin D and magnesium, and passive diffusion. The citrate salt dissociates in neutral pH making it effective in people with reduced gastric acid, which is nearly universal after age 50. In bone, calcium integrates into hydroxyapatite crystals within the collagen matrix, requiring concurrent K2 to prevent arterial deposition.",
    research: [
      {finding:"Calcium plus vitamin D3 reduces hip fracture risk by 18% and non-vertebral fracture risk by 12% in post-menopausal women in a meta-analysis of 17 RCTs.", quality:"Strong"},
      {finding:"Calcium citrate produces 22-27% greater absorption than calcium carbonate when taken without food, particularly relevant for people on proton pump inhibitors.", quality:"Good"},
      {finding:"500mg calcium twice daily significantly improves absorption efficiency versus a single 1000mg dose as the gut can only absorb approximately 500mg at once.", quality:"Good"},
      {finding:"High supplemental calcium above 1000mg/day without adequate vitamin K2 is associated with increased arterial calcification risk.", quality:"Moderate"},
    ],
    bestForm: "Calcium citrate rather than carbonate. Citrate absorbs in neutral pH without stomach acid. At 500mg elemental calcium twice daily, citrate is significantly more reliable in adults over 50. Never exceed 500mg elemental calcium in a single dose.",
    avoid: "Do not supplement if dietary calcium is already adequate from dairy, fortified foods, or leafy greens. Excess calcium without K2 may increase cardiovascular risk. Separate from iron, zinc, and thyroid medications by 2+ hours. Associated with increased kidney stone risk in susceptible individuals.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"The essential bone trio: D3 increases gut calcium absorption by up to 40%, K2 directs calcium into bone not arteries via osteocalcin and MGP activation. Never supplement calcium long-term without both."},
      {name:"Magnesium Glycinate", reason:"Magnesium deficiency impairs PTH response and reduces efficacy of calcium supplementation. A 2:1 calcium-to-magnesium ratio is widely recommended."},
      {name:"Collagen Peptides", reason:"Calcium provides the mineral; collagen provides the protein scaffold. Bone is approximately 30% collagen matrix and 70% mineral so both are required for structural integrity."},
    ],
    stackNotes: "Split doses: 500mg elemental calcium maximum per sitting, taken with food and vitamin D3. Separate from iron, zinc, or thyroid medication by 2 hours minimum. Evening and morning split dosing is ideal. Prioritise dietary calcium first as one serving of dairy provides approximately 300mg.",
    whoFor: "Post-menopausal women, people with low dairy intake, older adults on PPIs, anyone with confirmed low bone density. Test bone density via DEXA scan if over 50 and female, or over 65 and male.",
  },

  "Selenium": {
    summary: "Selenium is a trace mineral that is the backbone of the selenoprotein family of enzymes critical for thyroid hormone conversion, antioxidant defence, and immune competence. It is uniquely concentrated in the thyroid gland where it plays a non-negotiable structural role.",
    mechanism: "Selenium is incorporated into selenoproteins as selenocysteine. The most critical are glutathione peroxidases (GPx1-4), thioredoxin reductases, and iodothyronine deiodinases (D1, D2, D3) which convert inactive T4 to active T3. Without adequate selenium, even optimal iodine intake produces impaired T3 conversion.",
    research: [
      {finding:"200mcg/day selenium in selenium-deficient populations with Hashimoto's thyroiditis significantly reduces thyroid peroxidase antibody titres by 40-50%.", quality:"Good"},
      {finding:"Selenium deficiency is strongly associated with Keshan disease (cardiomyopathy) and Kashin-Beck disease (musculoskeletal degeneration) in low-selenium geographic regions.", quality:"Strong"},
      {finding:"Low plasma selenium is an independent risk factor for all-cause and cancer mortality; supplementation in already-replete populations shows no additional benefit.", quality:"Moderate"},
      {finding:"Selenium and iodine co-supplementation produces significantly better thyroid hormone normalisation than iodine alone.", quality:"Good"},
    ],
    bestForm: "Selenomethionine (the organic form) is the most bioavailable and safest long-term choice. Sodium selenite is effective but less tolerated at higher doses. 100-200mcg/day is the appropriate supplemental range.",
    avoid: "Narrow therapeutic window with an upper tolerable limit of 400mcg/day. Chronic excess causes selenosis including garlic breath, hair loss, nail brittleness, and neuropathy. Do not exceed 200mcg/day without testing.",
    synergies: [
      {name:"Iodine", reason:"The most important selenium pairing. Selenium-dependent deiodinases are required to convert T4 to active T3. Supplementing iodine without selenium may accumulate T4 without enabling its conversion to the active form."},
      {name:"Vitamin C", reason:"Vitamin C regenerates selenium-dependent glutathione peroxidase activity and protects selenoproteins from oxidative inactivation."},
      {name:"Zinc Bisglycinate", reason:"Zinc and selenium co-regulate multiple immune pathways including NK cell function and T-cell maturation. Both decline with age and their combined deficiency significantly impairs immune resilience."},
    ],
    stackNotes: "100-200mcg/day with food. Test first if possible. Do not stack multiple selenium sources. Pair with iodine for thyroid support. If eating Brazil nuts regularly, eliminate supplemental selenium.",
    whoFor: "People with Hashimoto's thyroid disease, those in low-selenium geographic regions including most of Europe, people with low thyroid function, anyone with high oxidative stress. Not recommended for selenium-replete people without testing.",
  },

  "Iodine": {
    summary: "Iodine is an essential trace mineral and the sole structural component of thyroid hormones T3 and T4. Critical for metabolic rate, cognitive development, and every cell's energy utilisation, deficiency is the world's leading preventable cause of intellectual disability.",
    mechanism: "Iodide is actively transported into thyroid follicular cells via the sodium-iodide symporter, oxidised, and incorporated into thyroglobulin to form T3 and T4. These hormones regulate basal metabolic rate across virtually every tissue, are essential for foetal brain development during the first trimester, and modulate mitochondrial biogenesis. Deficiency triggers TSH elevation and progressive thyroid enlargement.",
    research: [
      {finding:"Residual mild-to-moderate iodine deficiency remains prevalent across Europe and in non-iodised-salt populations despite universal salt iodisation programs.", quality:"Strong"},
      {finding:"Mild iodine deficiency during pregnancy produces measurable cognitive deficits with an 8-point IQ reduction observed in a large UK cohort study.", quality:"Strong"},
      {finding:"Iodine supplementation in subclinically hypothyroid adults with confirmed deficiency improves cognitive function, reduces TSH, and restores thyroid volume in RCTs.", quality:"Good"},
    ],
    bestForm: "Potassium iodide is the most bioavailable supplemental form. Standard adult requirement is 150mcg/day and pregnancy requires 220-250mcg/day. Do not use high-dose protocols above 500mcg/day without medical supervision.",
    avoid: "Excess iodine can trigger or worsen autoimmune thyroid disease in susceptible individuals at doses above 500mcg/day. People with existing thyroid conditions should supplement only under medical supervision.",
    synergies: [
      {name:"Selenium", reason:"Selenium-dependent deiodinases convert T4 to active T3. Supplementing iodine without selenium in deficient individuals may accumulate T4 without enabling its conversion to the active form."},
      {name:"Vitamin D3 + K2", reason:"Vitamin D and thyroid hormone receptors share signalling pathways. Combined deficiency of iodine and vitamin D produces greater thyroid dysfunction than either deficiency alone."},
    ],
    stackNotes: "150-300mcg/day covers typical adult needs. Pregnant women should aim for 220-250mcg/day. Assess dietary intake from seafood, dairy, and iodised salt before supplementing. Always pair with selenium for thyroid hormone conversion.",
    whoFor: "People avoiding iodised salt, those on dairy-free diets, pregnant and breastfeeding women, people with fatigue and low thyroid symptoms. Consult a clinician if you have existing thyroid disease.",
  },

  "Copper Bisglycinate": {
    summary: "Copper is an essential trace mineral required for collagen cross-linking, iron metabolism, antioxidant defence, and myelin formation. It is the most commonly induced mineral deficiency, caused by long-term zinc supplementation above 25mg/day competing for absorption.",
    mechanism: "Copper is a cofactor for lysyl oxidase (essential for collagen and elastin cross-linking), ceruloplasmin (the ferroxidase enzyme that loads iron onto transferrin), cytochrome c oxidase (complex IV of the mitochondrial electron transport chain), and superoxide dismutase SOD1 (the primary intracellular antioxidant enzyme). Copper deficiency produces a syndrome overlapping with vitamin B12 deficiency including myelopathy, anaemia, and neurological dysfunction.",
    research: [
      {finding:"Chronic zinc supplementation above 25mg/day induces copper deficiency by upregulating intestinal metallothionein, which binds copper preferentially and prevents absorption.", quality:"Strong"},
      {finding:"Copper deficiency causes a neurological syndrome (myeloneuropathy) clinically indistinguishable from subacute combined degeneration of the cord, which resolves with copper repletion.", quality:"Good"},
      {finding:"Lysyl oxidase activity (copper-dependent collagen cross-linking) is significantly impaired in copper-deficient states, producing weak and poorly cross-linked connective tissue.", quality:"Good"},
    ],
    bestForm: "Copper bisglycinate or copper gluconate, both well-absorbed organic chelates. Avoid copper oxide which has poor bioavailability. The standard prophylactic dose when taking zinc above 25mg/day is 1-2mg elemental copper.",
    avoid: "Wilson's disease (genetic copper toxicity) is an absolute contraindication. The upper tolerable limit is 10mg/day in healthy individuals. Do not supplement copper unless also taking high-dose zinc or confirmed deficient.",
    synergies: [
      {name:"Zinc Bisglycinate", reason:"Zinc and copper are metabolic antagonists competing for the same intestinal transporter. Anyone taking 25mg+ zinc for more than 4-6 weeks should add 1-2mg copper."},
      {name:"Vitamin C", reason:"Vitamin C enhances copper absorption and is required for ceruloplasmin synthesis. Both support collagen cross-linking via different mechanisms."},
      {name:"Collagen Peptides", reason:"Copper-dependent lysyl oxidase is the enzyme that cross-links collagen peptides into stable functional fibres. Copper deficiency renders collagen supplementation significantly less effective."},
    ],
    stackNotes: "1-2mg/day copper alongside zinc supplementation. Take with food and separate from zinc by 2+ hours if possible. Most multivitamins include 1-2mg copper so check before adding additional copper.",
    whoFor: "Anyone taking zinc supplements above 25mg/day (non-negotiable), people with connective tissue disorders, athletes with high collagen turnover, vegans where plant copper is less bioavailable.",
  },

  "Potassium Citrate": {
    summary: "Potassium is the primary intracellular cation essential for fluid balance, blood pressure regulation, and muscle contraction. The citrate form additionally alkalinises urine, reducing kidney stone risk and buffering metabolic acidosis from high-protein diets.",
    mechanism: "Potassium maintains the resting membrane potential via the Na+/K+ ATPase pump, essential for nerve impulse transmission and muscle relaxation after contraction. In skeletal muscle, potassium efflux during exercise causes local fatigue and adequate stores extend time to fatigue. The citrate anion is metabolised to bicarbonate, raising urinary pH and reducing precipitation of uric acid and calcium oxalate crystals.",
    research: [
      {finding:"A meta-analysis of 25 RCTs found potassium supplementation reduces systolic blood pressure by 4.5 mmHg and diastolic by 3.5 mmHg in hypertensive individuals.", quality:"Strong"},
      {finding:"Potassium citrate reduces recurrent kidney stone formation by 50% in calcium oxalate stone formers in a controlled trial over 3 years.", quality:"Good"},
      {finding:"Low dietary potassium is independently associated with a 20-30% increased risk of stroke and cardiovascular mortality.", quality:"Strong"},
    ],
    bestForm: "Potassium citrate provides both the mineral and the alkalinising citrate effect. Most capsules contain only 99mg to avoid GI and cardiac risk from high bolus doses. Dietary sources such as banana, potato, and avocado are safer and preferable for most people.",
    avoid: "Genuinely dangerous in excess as hyperkalemia causes life-threatening cardiac arrhythmias. Never exceed 3,500mg/day total from supplements. Contraindicated with ACE inhibitors, ARBs, potassium-sparing diuretics, and in kidney disease.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Magnesium deficiency causes intracellular potassium wasting, meaning potassium status cannot be corrected without adequate magnesium. They are co-regulators of cellular membrane potential."},
      {name:"Electrolytes", reason:"Potassium works as part of the electrolyte system alongside sodium, magnesium, and chloride. Isolated potassium supplementation without electrolyte context can cause relative imbalances."},
    ],
    stackNotes: "200-400mg elemental potassium/day from supplements is a conservative safe range. Prioritise dietary sources as one banana provides approximately 420mg. Always take with food.",
    whoFor: "People with high blood pressure without kidney disease, endurance athletes with high sweat loss, those on high-protein diets, recurrent kidney stone formers.",
  },

  "Boron": {
    summary: "Boron is a trace mineral that potentiates the biological activity of both vitamin D and sex hormones by slowing their metabolic clearance. Often overlooked, it has a disproportionate impact on bone health and hormonal vitality in adults over 40.",
    mechanism: "Boron inhibits 24-hydroxylase, the enzyme that degrades active vitamin D, extending its half-life and amplifying bone-protective effects without increasing vitamin D intake. Boron also reduces sex hormone binding globulin (SHBG) activity, increasing the proportion of free bioavailable oestrogen and testosterone. Additionally, it is a cofactor for enzymes involved in calcium and magnesium homeostasis.",
    research: [
      {finding:"Boron supplementation at 3mg/day in post-menopausal women significantly reduced urinary calcium and magnesium excretion and elevated serum oestradiol and testosterone within 8 days.", quality:"Good"},
      {finding:"3-6mg/day boron significantly elevates free testosterone in men with suboptimal levels by reducing SHBG in a placebo-controlled trial.", quality:"Good"},
      {finding:"Boron supplementation significantly extends the half-life of vitamin D metabolites, effectively amplifying the impact of vitamin D supplementation.", quality:"Moderate"},
      {finding:"Epidemiological data consistently shows lower arthritis rates in regions with higher soil boron content and supplementation reduces inflammatory markers in osteoarthritis.", quality:"Moderate"},
    ],
    bestForm: "Boron glycinate, boron citrate, or boron fructoborate, all significantly better absorbed than boric acid. 3-6mg elemental boron/day is the established therapeutic range. Most diets provide only 1-3mg/day from fruits, vegetables, and legumes.",
    avoid: "The upper tolerable limit is 20mg/day and toxicity is unlikely at recommended doses. Boron affects oestrogen metabolism so use with caution in oestrogen-sensitive conditions without medical guidance.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"Boron extends the half-life of active vitamin D, making it the most cost-effective way to amplify your vitamin D supplementation for bone and immune health."},
      {name:"Magnesium Glycinate", reason:"Boron enhances magnesium retention by reducing urinary excretion. Both support bone mineralisation and are commonly co-deficient in adults over 40."},
      {name:"Zinc Bisglycinate", reason:"Boron's SHBG-lowering effect frees bioavailable testosterone while zinc provides the raw substrate for testosterone synthesis."},
    ],
    stackNotes: "3mg/day with food is a well-tolerated starting dose. Effects on hormones and vitamin D half-life are cumulative so allow 4-8 weeks. Most useful when already taking vitamin D3 as it amplifies rather than replaces.",
    whoFor: "Post-menopausal women (bone density and oestrogen support), men over 40 (free testosterone and prostate health), anyone wanting to amplify their vitamin D supplementation's effects, people with osteoarthritis.",
  },

  "Vitamin C": {
    summary: "Vitamin C is a water-soluble antioxidant and essential cofactor in at least 8 enzymatic reactions, most critically the hydroxylation steps required to form stable collagen. Humans cannot synthesise their own vitamin C so dietary and supplemental intake is essential.",
    mechanism: "Ascorbic acid is required by prolyl and lysyl hydroxylases, the enzymes that cross-link collagen chains into stable triple-helix structures. Without it, collagen is structurally defective. It also regenerates vitamin E from its oxidised form, maintains glutathione in its active reduced state, and accumulates to concentrations 50-100x higher than plasma within leukocytes where it is consumed rapidly during infection.",
    research: [
      {finding:"Vitamin C at 200mg or more per day reduces cold duration by 8% in adults and 14% in children, with a 50% reduction in incidence in people under high physical stress such as athletes and soldiers.", quality:"Strong"},
      {finding:"IV vitamin C at pharmacological doses of 10g or more significantly reduces ICU length of stay and mortality in sepsis patients in multiple meta-analyses.", quality:"Good"},
      {finding:"Collagen synthesis is significantly enhanced by vitamin C consumption 60 minutes before exercise, now used to optimise tendon and ligament repair protocols.", quality:"Good"},
      {finding:"500mg/day significantly reduces systolic blood pressure by 4.8 mmHg and diastolic by 1.7 mmHg in hypertensive subjects in meta-analysis.", quality:"Good"},
    ],
    bestForm: "Plain ascorbic acid is effective and cheap. Sodium ascorbate (buffered) is gentler on tooth enamel at higher doses. Liposomal vitamin C has higher bioavailability at doses above 1g, approaching IV absorption levels. 500-1000mg twice daily with meals is a practical approach.",
    avoid: "Above 2g/day can cause osmotic diarrhoea. High doses may increase oxalate excretion, raising kidney stone risk in susceptible individuals. Chewable tablets damage tooth enamel so rinse after use.",
    synergies: [
      {name:"Collagen Peptides", reason:"Non-negotiable pairing. Vitamin C is the required cofactor for collagen cross-linking. Always take within 30-60 minutes of each other as without it, supplemented collagen cannot form stable fibres."},
      {name:"Iron Bisglycinate", reason:"Vitamin C reduces ferric iron to ferrous iron and prevents re-oxidation in the gut, increasing iron absorption by up to 3x. The most evidence-backed absorption enhancer for iron."},
      {name:"Quercetin", reason:"Vitamin C regenerates quercetin from its oxidised form back to active quercetin, allowing it to quench multiple free radicals catalytically. Together they have greater antioxidant and antiviral potency."},
    ],
    stackNotes: "500mg twice daily with food. Take one dose 45-60 minutes before exercise if using for collagen or tendon support. Buffered or liposomal forms at higher doses if GI sensitivity is an issue.",
    whoFor: "Everyone, with highest priority for smokers who deplete vitamin C twice as fast, athletes, people under high stress, those with active infections or poor wound healing, and anyone supplementing collagen or iron.",
  },

  "Vitamin B Complex": {
    summary: "The B vitamins are a family of 8 water-soluble vitamins that collectively govern energy metabolism, neurological function, and methylation. They work as a system where deficiency in one often impairs the function of others, and a complete B complex is frequently more effective than isolated B vitamins.",
    mechanism: "Each B vitamin has distinct roles: B1 (thiamine) is a cofactor in pyruvate dehydrogenase (the gateway from glycolysis into the citric acid cycle). B2 (riboflavin) is the precursor to FAD/FMN electron carriers. B3 (niacin) is the precursor to NAD+, the central coenzyme in energy production. B5 (pantothenate) synthesises coenzyme A. B6 (pyridoxine) is required for neurotransmitter synthesis of serotonin, dopamine, and GABA. B7 (biotin) supports fatty acid synthesis. B12 and folate coordinate the methylation cycle.",
    research: [
      {finding:"B vitamin supplementation consistently reduces fatigue and improves mood in people with sub-optimal status, particularly relevant for stressed adults and those consuming high-carbohydrate diets depleting B1.", quality:"Good"},
      {finding:"B vitamin complex significantly reduces work-related stress, personal strain, and depressive mood versus placebo in a 90-day RCT of healthy working adults.", quality:"Good"},
      {finding:"High-dose B6+B12+folate reduces age-related cognitive decline and brain atrophy by 53% in subjects with elevated homocysteine in the VITACOG trial.", quality:"Strong"},
    ],
    bestForm: "Look for active methylated forms: methylcobalamin (B12), methylfolate (B9), pyridoxal-5-phosphate (P5P for active B6), and riboflavin-5-phosphate. These bypass conversion steps compromised in MTHFR variants present in approximately 40% of people.",
    avoid: "B6 toxicity causing peripheral neuropathy occurs at sustained doses above 100mg/day so avoid high-potency formulations with more than 50mg B6 without medical indication. Niacin at high doses above 1g causes flushing and the non-flush nicotinamide form should be used instead.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Magnesium is required as a cofactor in many of the same enzymatic reactions as B vitamins, particularly B6 and B1 metabolism. Both are commonly depleted together in high-stress individuals."},
      {name:"Vitamin B12", reason:"Isolated B12 without the full B complex may unmask folate deficiency. A complete B complex with methylated B12 is more effective than isolated B12 for the full methylation cycle."},
      {name:"CoQ10 Ubiquinol", reason:"B vitamins (particularly B3 as NAD+) and CoQ10 address different stages of mitochondrial energy production. Together they support the full electron transport chain from substrate to ATP output."},
    ],
    stackNotes: "Take with breakfast as B vitamins can be energising and may interfere with sleep if taken late. The neon yellow urine from riboflavin is harmless. If you have confirmed MTHFR variants, specifically seek methylfolate and methylcobalamin rather than folic acid and cyanocobalamin.",
    whoFor: "People under chronic stress, shift workers, vegetarians and vegans, heavy alcohol drinkers who deplete B1 B6 B9 and B12, people over 50 with reduced absorption, people with MTHFR genetic variants, anyone with fatigue or mood changes.",
  },

  "Vitamin A": {
    summary: "Vitamin A is a fat-soluble vitamin essential for vision, immune function, skin cell turnover, and embryonic development. It exists as preformed retinol from animal foods and provitamin A carotenoids (beta-carotene) from plants, with highly variable conversion efficiency between the two.",
    mechanism: "Retinol is converted to retinoic acid, which binds retinoic acid receptors (RARs), nuclear transcription factors regulating gene expression in virtually every tissue. In vision, 11-cis-retinal is the chromophore of rhodopsin essential for phototransduction in low light. In the immune system, retinoic acid is required for differentiation of regulatory T-cells and mucosal IgA production, critical for gut and respiratory defence. In skin, retinoids regulate epidermal cell differentiation and sebum production.",
    research: [
      {finding:"Vitamin A deficiency is the leading cause of preventable childhood blindness globally, affecting approximately 250,000-500,000 children annually.", quality:"Strong"},
      {finding:"Vitamin A supplementation significantly reduces measles mortality and all-cause infectious disease mortality in deficient children, representing one of the largest effect sizes in nutritional intervention research.", quality:"Strong"},
      {finding:"Topical retinoic acid (tretinoin) significantly reduces wrinkle depth and skin roughness versus placebo and is the most evidence-backed topical anti-ageing compound.", quality:"Strong"},
    ],
    bestForm: "Retinyl palmitate (preformed vitamin A) for reliable delivery, or mixed carotenoids providing beta-carotene. Beta-carotene conversion is highly variable and those with BCMO1 gene variants (approximately 45% of people) convert poorly and require preformed retinol. 700-3000 IU/day is appropriate for most adults.",
    avoid: "Preformed retinol above 10,000 IU/day chronically is hepatotoxic. Pregnant women must avoid above 3,000 IU/day preformed as it is teratogenic at supraphysiological levels. High-dose beta-carotene supplements increase lung cancer risk in smokers.",
    synergies: [
      {name:"Zinc Bisglycinate", reason:"Zinc is required for retinol-binding protein synthesis, the carrier transporting vitamin A in the blood. Zinc deficiency impairs vitamin A mobilisation from liver stores even when vitamin A status is adequate."},
      {name:"Vitamin D3 + K2", reason:"Vitamin A, D, and K2 interact on shared nuclear receptor pathways. Adequate vitamin D is required for full vitamin A receptor signalling and both regulate immune function via overlapping mechanisms."},
      {name:"Omega-3 (EPA+DHA)", reason:"Both fat-soluble and absorption enhanced together with a fatty meal. Omega-3 supports the membrane function of retinal cells, complementing vitamin A's role in visual phototransduction."},
    ],
    stackNotes: "Always take with a fatty meal as vitamin A is fat-soluble and absorption increases dramatically with dietary fat. Do not exceed 3,000 IU/day preformed retinol during pregnancy. Check your multivitamin first as most already include vitamin A.",
    whoFor: "People with low dietary fat intake, night vision difficulties, poor immune function, skin conditions such as acne and dry skin, anyone with confirmed deficiency. Most people in developed countries with varied diets have adequate vitamin A from food.",
  },

  "Folate (Methylfolate)": {
    summary: "Folate is a B vitamin essential for DNA synthesis, methylation, and neural tube development. Methylfolate (5-MTHF) is the active form that bypasses the metabolic conversion step impaired in people with MTHFR gene variants, which affect approximately 40% of the general population.",
    mechanism: "Folate donates one-carbon units in the methylation cycle, the central biochemical hub connecting B12, homocysteine, methionine, and SAMe (the universal methyl donor). SAMe methylates DNA, neurotransmitters, hormones, and phospholipids. MTHFR converts folic acid to active 5-MTHF. Common MTHFR variants (C677T, A1298C) impair this conversion by 30-70%, meaning supplemental folic acid provides limited benefit in affected individuals.",
    research: [
      {finding:"Periconceptional folate supplementation at 400mcg/day reduces neural tube defect risk by 50-70%, one of the most robust nutritional intervention findings in medicine.", quality:"Strong"},
      {finding:"Folate plus B12 supplementation reduces plasma homocysteine by 25-30%, an independent risk factor for cardiovascular disease and dementia, in a meta-analysis of 25 RCTs.", quality:"Strong"},
      {finding:"Methylfolate at 15mg/day significantly augments antidepressant response in treatment-resistant depression with MTHFR variants in an RCT.", quality:"Good"},
      {finding:"Women with MTHFR C677T homozygosity have significantly higher risk of neural tube defect pregnancies when supplemented with standard folic acid versus methylfolate specifically.", quality:"Good"},
    ],
    bestForm: "Methylfolate (5-MTHF) rather than folic acid. Folic acid requires MTHFR enzyme conversion; methylfolate is already in the active form. Quatrefolic and Metafolin are premium validated forms. 400-800mcg/day for general use.",
    avoid: "High-dose folate can mask B12 deficiency as the neurological symptoms of B12 deficiency continue while haematological markers are corrected by folate. Always supplement B12 alongside folate. Avoid folic acid in people with MTHFR variants.",
    synergies: [
      {name:"Vitamin B12", reason:"The essential metabolic partnership. Folate and B12 are co-factors in the same enzymatic step (methionine synthase). Supplementing one without the other is incomplete for the methylation cycle."},
      {name:"Vitamin B Complex", reason:"Riboflavin (B2) is required for MTHFR enzyme function itself. B2 deficiency impairs folate metabolism even when folate intake is adequate."},
      {name:"Magnesium Glycinate", reason:"Magnesium is required downstream in methylation-dependent reactions. Both support the SAMe cycle and are commonly co-deficient in people with methylation difficulties."},
    ],
    stackNotes: "Take in the morning as folate is involved in energy metabolism. If you have confirmed MTHFR variants, use only methylfolate and not folic acid. Pregnant or planning pregnancy: start 400-800mcg methylfolate at least 1 month before conception. Always pair with B12.",
    whoFor: "Pregnant women or those planning pregnancy (non-negotiable), people with MTHFR gene variants, anyone with elevated homocysteine, people with depression not responding to standard treatment, vegetarians and vegans where plant folate has variable bioavailability.",
  },

  "Krill Oil": {
    summary: "Krill oil provides EPA and DHA omega-3 fatty acids in phospholipid form, a structural difference from fish oil that may improve brain delivery and removes the need for fat co-ingestion for absorption. It also naturally contains astaxanthin, a potent antioxidant that protects the omega-3s from oxidation.",
    mechanism: "Unlike fish oil triglycerides, krill oil binds EPA and DHA to phosphatidylcholine, the dominant form in human cell membranes. This phospholipid packaging allows absorption without requiring dietary fat and may improve incorporation into brain cell membranes specifically. The naturally occurring astaxanthin (1-2mg per standard dose) prevents in vivo oxidation of the omega-3 fatty acids.",
    research: [
      {finding:"Krill oil at equivalent EPA+DHA doses produces 25-30% higher omega-3 index elevation compared to fish oil triglycerides in some comparative studies, though other trials show equivalent absorption.", quality:"Moderate"},
      {finding:"Krill oil significantly reduces PMS symptoms including emotional symptoms, breast tenderness, and joint pain versus placebo in a 3-month RCT.", quality:"Good"},
      {finding:"Krill oil reduces triglycerides and LDL cholesterol comparably to fish oil at matched EPA+DHA doses, with additional HDL-raising effects in some trials.", quality:"Good"},
    ],
    bestForm: "Standard krill oil softgels as the phospholipid form is inherently the best form for this supplement. Check for EPA+DHA content specifically on the label. Ensure astaxanthin is naturally present and not synthetic. Cold extraction methods preserve phospholipid integrity.",
    avoid: "Shellfish allergy is a contraindication as krill are crustaceans. Blood thinning effects similar to fish oil mean caution is needed with anticoagulants. More expensive per gram of EPA+DHA than fish oil.",
    synergies: [
      {name:"Vitamin D3 + K2", reason:"Both fat-soluble and cardiovascular-protective via independent mechanisms. The phospholipid form of krill oil may enhance D3 absorption and both reduce cardiovascular inflammation and calcification risk."},
      {name:"Astaxanthin", reason:"Krill oil naturally contains astaxanthin. Taking additional astaxanthin alongside amplifies antioxidant skin, eye, and anti-inflammatory protection beyond what the krill dose provides alone."},
    ],
    stackNotes: "Unlike fish oil, krill oil does not require fat co-ingestion for absorption. Standard doses of 1-2g krill oil/day provide modest EPA+DHA so supplement with additional fish oil if targeting 1000mg+ EPA+DHA for cardiovascular or anti-inflammatory benefits.",
    whoFor: "People who prefer a compact oxidation-stable omega-3 source, women with PMS symptoms, those without shellfish allergy wanting the bonus astaxanthin. Not the most cost-effective source at high EPA+DHA therapeutic doses.",
  },

  "Rhodiola Rosea": {
    summary: "Rhodiola is a stimulating adaptogen from high-altitude cold climates that primarily targets fatigue and mental performance under stress. Unlike most adaptogens, effects are fast-acting and noticeable within days rather than weeks, making it useful for acute performance demands as well as long-term resilience.",
    mechanism: "Rosavins and salidroside inhibit catechol-O-methyltransferase (COMT), which breaks down dopamine and noradrenaline, reduce cortisol via HPA axis modulation, activate AMPK (the cellular energy sensor), and promote mitochondrial biogenesis. The net effect is increased mental and physical energy and improved stress tolerance without the cardiovascular stimulation of caffeine.",
    research: [
      {finding:"Rhodiola extract (SHR-5, 576mg/day) significantly reduces mental fatigue, improves complex cognitive function, and reduces burnout in students and physicians in RCTs.", quality:"Good"},
      {finding:"A 28-day RCT in burnout patients found Rhodiola (400mg/day) significantly reduced exhaustion, impaired concentration, and physical performance deficits versus placebo.", quality:"Good"},
      {finding:"Rhodiola reduces perceived exertion during endurance exercise and extends time to exhaustion in trained athletes in multiple RCTs.", quality:"Moderate"},
      {finding:"In one head-to-head RCT, Rhodiola (340mg/day) produced significant improvement in depression scores not significantly different from sertraline, with fewer side effects.", quality:"Moderate"},
    ],
    bestForm: "Standardised extract with minimum 3% rosavins and 1% salidroside, the ratio used in clinical trials. Avoid non-standardised products. 200-400mg/day of standardised extract is the evidenced range.",
    avoid: "Stimulating so do not take in the afternoon or evening as it can cause insomnia. Can cause agitation at high doses. Caution with stimulant medications. Cycle use (5 days on/2 off, or 6 weeks on/2 weeks off) is recommended to prevent tolerance.",
    synergies: [
      {name:"Ashwagandha KSM-66", reason:"Ideal adaptogen pairing: Rhodiola (energising, fast-acting) in the morning and Ashwagandha (calming, builds over weeks) in the evening. Together they provide all-day stress resilience and energy support."},
      {name:"L-Theanine", reason:"L-theanine moderates any overstimulation from Rhodiola while preserving the focus benefits, useful if you find Rhodiola alone produces slight anxiety."},
      {name:"Vitamin B Complex", reason:"B vitamins support the mitochondrial energy pathways that Rhodiola activates via AMPK. Together they address fatigue from complementary nutritional and botanical directions."},
    ],
    stackNotes: "Take on an empty stomach in the morning as absorption is better without food and the stimulating effect is appropriate then. Start at 200mg and assess tolerability. Do not take after 2pm. Cycle use to prevent tolerance. Effects noticeable within 1-5 days.",
    whoFor: "People with mental or physical fatigue, those under high cognitive demand, endurance athletes, anyone experiencing burnout, shift workers, jet lag recovery. Less suited to anxious individuals as ashwagandha is better for anxiety-driven fatigue.",
  },

  "Panax Ginseng": {
    summary: "Panax ginseng (Korean or Asian ginseng) is one of the most extensively researched herbal adaptogens with centuries of traditional use and modern clinical evidence for energy, cognition, immunity, and libido. It is distinct from Siberian ginseng (Eleutherococcus) which has weaker evidence.",
    mechanism: "Ginsenosides interact with steroid hormone receptors, modulate nitric oxide synthesis (improving blood flow via eNOS activation), and exert immunomodulatory effects on NK cells and macrophages. Different ginsenoside fractions have opposing effects contributing to its adaptogenic bidirectional normalising profile. It also upregulates neurotrophic factors including BDNF in the hippocampus, supporting cognitive function.",
    research: [
      {finding:"Panax ginseng (200-400mg/day) significantly improves working memory, sustained attention, and reaction time versus placebo in a series of well-designed crossover RCTs in healthy adults.", quality:"Good"},
      {finding:"Ginseng extract significantly improves erectile function scores in men with mild-moderate erectile dysfunction in a meta-analysis of 7 RCTs, the most evidence-backed natural compound for this indication.", quality:"Good"},
      {finding:"400mg/day Panax ginseng significantly reduces fasting blood glucose and post-meal glucose response in type 2 diabetes patients.", quality:"Good"},
      {finding:"Standardised ginseng extract reduces the incidence of acute respiratory illness and shortens duration by approximately 50% versus placebo during cold and flu season.", quality:"Good"},
    ],
    bestForm: "Standardised extract with minimum 5-7% total ginsenosides. G115 (Ginsana) is the most clinically validated commercial extract. 200-400mg/day standardised extract. Red ginseng (steamed and dried) has a more warming stimulating profile while white ginseng (dried root) is milder.",
    avoid: "Stimulating at higher doses so avoid in the evening. Can cause insomnia, headache, and agitation above 600mg/day. Mild estrogenic activity means caution in oestrogen-sensitive conditions. Interacts with warfarin. Cycle use: 2-3 months on, 1-2 weeks off.",
    synergies: [
      {name:"Zinc Bisglycinate", reason:"Both support testosterone and libido via different mechanisms. Zinc provides the mineral substrate for testosterone synthesis while ginseng modulates LH signalling and improves blood flow."},
      {name:"Rhodiola Rosea", reason:"Complementary adaptogens with different primary targets: Rhodiola primarily for fatigue and resilience, Panax for cognition and immunity. Together they provide broader adaptogenic coverage."},
      {name:"L-Theanine", reason:"L-theanine moderates the stimulating effects of ginseng while preserving its cognitive and immune benefits, useful if you find ginseng slightly overstimulating."},
    ],
    stackNotes: "Take in the morning with food as ginseng is moderately stimulating and food reduces GI irritation. Start at 200mg. Cycle use: 8-12 weeks on, then a 2-week break. Do not combine with other stimulants without monitoring your response.",
    whoFor: "Men over 40 (testosterone, libido, erectile function), people with cognitive fatigue, those seeking immune resilience, people with type 2 diabetes or blood sugar concerns, anyone with chronic fatigue unresponsive to other adaptogens.",
  },

  "Maca Root": {
    summary: "Maca (Lepidium meyenii) is a Peruvian root used medicinally for energy, libido, and fertility. Unlike hormone-containing supplements, maca does not contain phytoestrogens or sex hormones and appears to work by supporting the hypothalamic-pituitary axis and adrenal function.",
    mechanism: "Macamides and macaenes are unique to maca and appear to act on the endocannabinoid system and monoamine oxidase enzymes, supporting dopaminergic and serotonergic tone. Maca does not contain sex hormones but influences their production by modulating hypothalamic-pituitary signalling. Yellow maca suits energy and fertility best; black maca for memory and male fertility; red maca for prostate and bone health.",
    research: [
      {finding:"Maca at 1500-3000mg/day significantly improves self-reported sexual dysfunction in post-menopausal women and men in multiple RCTs.", quality:"Good"},
      {finding:"Black maca specifically improves sperm motility and count in men in a 4-month RCT, with effects greater than yellow or red maca.", quality:"Good"},
      {finding:"Maca at 3500mg/day significantly reduces menopausal symptoms (hot flushes, night sweats, depression, anxiety) without changing oestrogen levels.", quality:"Good"},
    ],
    bestForm: "Gelatinised maca (pre-cooked/extruded) is better tolerated and more bioavailable than raw maca powder as the process breaks down starch and concentrates active compounds. 1500-3000mg/day is the evidenced dose.",
    avoid: "Raw maca contains goitrogens so always use the gelatinised form. Caution in people with thyroid conditions. Not recommended during pregnancy or breastfeeding.",
    synergies: [
      {name:"Ashwagandha KSM-66", reason:"Both modulate the HPA axis via different mechanisms. Ashwagandha lowers cortisol while maca supports hypothalamic-pituitary signalling for sex hormones. Together they provide comprehensive hormonal support for men and women over 40."},
      {name:"Zinc Bisglycinate", reason:"Zinc provides the mineral substrate for sex hormone synthesis while maca supports the hormonal signalling that drives production, a well-rounded hormonal support stack."},
      {name:"Vitamin B Complex", reason:"B vitamins support adrenal function and the dopaminergic signalling that maca depends on for its energising and libido effects."},
    ],
    stackNotes: "Take with breakfast as maca is stimulating enough that evening use may affect sleep. Gelatinised powder mixes into smoothies or coffee. Allow 4-6 weeks for full libido and hormonal effects. Cycle use (3 months on, 1 month off) for long-term use.",
    whoFor: "Women approaching or in menopause, men with low libido or subfertility, men and women experiencing stress-related hormonal disruption, anyone wanting non-hormonal support for sexual health and energy.",
  },

  "Holy Basil (Tulsi)": {
    summary: "Holy basil is an adaptogenic Ayurvedic herb with cortisol-lowering, blood sugar-stabilising, and anti-inflammatory properties. It occupies a lower-evidence position than ashwagandha but has unique advantages including blood glucose modulation and anti-infective properties.",
    mechanism: "Eugenol inhibits COX-1 and COX-2 (anti-inflammatory), rosmarinic acid inhibits cortisol synthesis at the adrenal level, and ursolic acid activates AMPK signalling, mimicking the cellular effects of caloric restriction and exercise. Holy basil also has demonstrated antimicrobial activity against H. pylori, Staphylococcus aureus, and oral bacteria.",
    research: [
      {finding:"Holy basil extract (300mg/day) significantly reduces fasting blood glucose, post-meal glucose, and HbA1c in type 2 diabetics in a 4-week placebo-controlled trial.", quality:"Moderate"},
      {finding:"Tulsi supplementation significantly reduces perceived stress, anxiety, and cognitive impairment scores versus placebo in a 6-week RCT in healthy working adults.", quality:"Moderate"},
      {finding:"Fixed oil of holy basil demonstrates anti-inflammatory activity comparable to ibuprofen in carrageenan-induced models, mediated by COX inhibition.", quality:"Moderate"},
    ],
    bestForm: "Standardised extract standardised to ursolic acid and eugenol content. 300-600mg/day. Whole-plant extracts including leaves, stems, and seeds are more clinically relevant than single isolated compounds. Tulsi tea provides milder but cumulative effects.",
    avoid: "May reduce blood sugar so monitor closely if on antidiabetic medications. Anticoagulant properties at high doses mean caution with blood thinners. Avoid during pregnancy due to uterotonic potential in animal models.",
    synergies: [
      {name:"Ashwagandha KSM-66", reason:"Complementary cortisol reduction: ashwagandha works on the HPA axis centrally while holy basil acts peripherally at the adrenal level. Together they provide broader stress pathway coverage."},
      {name:"Berberine", reason:"Both independently reduce post-meal blood glucose via different mechanisms, making this a useful combined blood sugar management strategy."},
    ],
    stackNotes: "Take with food to reduce GI irritation and improve absorption. Can be taken twice daily (morning and evening). Tulsi tea is a pleasant effective gentler delivery method. Monitor blood sugar if diabetic as combination with antidiabetic medications may require dose adjustment.",
    whoFor: "People with mild blood sugar dysregulation, those seeking immune support, high-stress individuals seeking a gentler adaptogen than ashwagandha, people interested in Ayurvedic tradition with clinical evidence.",
  },

  "NAC": {
    summary: "N-acetylcysteine is the rate-limiting precursor to glutathione, the body's primary intracellular antioxidant. It is one of the most clinically validated natural compounds in medicine, used as an FDA-approved treatment for acetaminophen overdose and increasingly for liver protection, lung health, and psychiatric conditions.",
    mechanism: "NAC is deacetylated to cysteine in the gut and liver, then incorporated into glutathione via glutamate-cysteine ligase. Cysteine is the rate-limiting substrate so providing it is the most efficient way to raise intracellular glutathione. NAC also directly reduces disulphide bonds in mucus glycoproteins (mucolytic action) and modulates glutamate neurotransmission by restoring the cystine-glutamate antiporter, which is relevant to addiction and psychiatric applications.",
    research: [
      {finding:"NAC is the standard-of-care treatment for acetaminophen overdose in emergency medicine, with greater than 90% effectiveness in preventing liver failure when given within 8 hours.", quality:"Strong"},
      {finding:"1200-1800mg/day NAC significantly reduces COPD exacerbations and improves lung function in a meta-analysis of 13 RCTs and is now included in some COPD management guidelines.", quality:"Strong"},
      {finding:"NAC augments antidepressant response in treatment-resistant depression and significantly reduces compulsive behaviours in OCD and trichotillomania in multiple RCTs.", quality:"Good"},
      {finding:"NAC supplementation significantly reduces liver injury markers in non-alcoholic fatty liver disease in controlled studies.", quality:"Good"},
    ],
    bestForm: "Standard N-acetylcysteine oral form. 600-1200mg/day in divided doses is the evidence-based range. Take on an empty stomach for maximum cysteine availability. Liposomal NAC may improve systemic bioavailability.",
    avoid: "Can cause nausea and GI upset so take with food if needed. May reduce platelet aggregation at high doses requiring caution with blood thinners. The sulphurous smell is distinctive but harmless. High doses above 3g/day may paradoxically act as a pro-oxidant in some contexts.",
    synergies: [
      {name:"Vitamin C", reason:"Vitamin C regenerates glutathione from its oxidised form and works synergistically with NAC-raised glutathione in the cellular antioxidant network."},
      {name:"Alpha Lipoic Acid", reason:"ALA regenerates glutathione directly and is itself regenerated by glutathione, so they mutually reinforce each other's antioxidant activity. A potent combination for liver protection and mitochondrial defence."},
      {name:"Selenium", reason:"Selenium-dependent glutathione peroxidase (GPx) uses glutathione to neutralise hydrogen peroxide and lipid peroxides. Selenium activates the system while NAC provides the glutathione fuel."},
    ],
    stackNotes: "Take away from food for best absorption, 30-60 minutes before meals or 2 hours after. If GI upset occurs, split into 600mg twice daily with a small amount of food. Benefits accumulate over 4-8 weeks.",
    whoFor: "People with high alcohol intake or liver stress, anyone with respiratory conditions such as COPD or chronic bronchitis, those under high oxidative stress, people with mood disorders or compulsive behaviours, general antioxidant support in adults over 50.",
  },

  "L-Glutamine": {
    summary: "L-glutamine is the most abundant amino acid in the body and the primary fuel source for enterocytes (intestinal lining cells) and immune cells. During stress, illness, or intense exercise, demand can exceed production capacity making supplementation therapeutically relevant.",
    mechanism: "Glutamine is the preferential oxidative fuel for rapidly dividing cells including intestinal epithelium and lymphocytes. In the gut, it maintains tight junction integrity (preventing intestinal permeability) and supports proliferation of intestinal villi. In the immune system, it is required for lymphocyte proliferation and macrophage phagocytic activity. In muscle, it contributes to protein synthesis and acts as a volumising agent signalling an anabolic cellular environment.",
    research: [
      {finding:"Perioperative glutamine supplementation significantly reduces infection rates, length of hospital stay, and mortality in critically ill and surgical patients and is now included in ESPEN clinical nutrition guidelines.", quality:"Strong"},
      {finding:"Glutamine supplementation significantly reduces intestinal permeability markers in patients with inflammatory bowel disease and following chemotherapy.", quality:"Good"},
      {finding:"Athletes supplementing with glutamine (0.1g/kg/day) during heavy training significantly reduce infection incidence and upper respiratory illness days compared to placebo.", quality:"Good"},
    ],
    bestForm: "L-glutamine powder is the most practical and cost-effective form. 5-10g/day for gut and immune support. Glutamine dipeptides (alanyl-glutamine) have better stability in solution and may be slightly more bioavailable.",
    avoid: "Theoretical concern in people with glutamate sensitivity (MSG sensitivity, seizure disorders) as high-dose glutamine converts to glutamate in the gut. Contraindicated in liver disease with hepatic encephalopathy.",
    synergies: [
      {name:"Probiotics", reason:"The most clinically validated gut repair combination: glutamine rebuilds the physical epithelial barrier that probiotics colonise and protect. Used together in clinical gut rehabilitation protocols."},
      {name:"Collagen Peptides", reason:"Both support connective tissue repair where glutamine acts as an amino acid building block and collagen acts as the structural scaffold."},
      {name:"Zinc Bisglycinate", reason:"Zinc is required for intestinal cell proliferation and tight junction protein expression. Glutamine provides the fuel while zinc provides the regulatory signal for intestinal repair."},
    ],
    stackNotes: "Take 5g before bed or post-workout for muscle recovery. For gut health, take 5g on an empty stomach twice daily. Mix into water as it is essentially tasteless. Allow 4-8 weeks of consistent use to assess gut permeability improvements.",
    whoFor: "Athletes in heavy training blocks, people with IBS or inflammatory bowel disease, those recovering from surgery or illness, people with frequent upper respiratory infections, anyone seeking to support intestinal lining integrity.",
  },

  "L-Citrulline": {
    summary: "L-citrulline is the most effective precursor to L-arginine in the body, overcoming the metabolic limitations that make direct arginine supplementation less effective. It is the primary evidence-based nitric oxide booster for athletic performance and cardiovascular blood flow.",
    mechanism: "Citrulline is transported to the kidneys where it is converted to L-arginine via argininosuccinate synthase and lyase. This renal conversion bypasses the first-pass extraction and arginase catabolism that limits oral arginine. Arginine is then the substrate for nitric oxide synthase, producing nitric oxide which relaxes vascular smooth muscle, reduces blood pressure, improves oxygen delivery to muscles, and is required for endothelial function.",
    research: [
      {finding:"L-citrulline (6-8g) before exercise significantly reduces muscle fatigue, increases repetitions to failure by 52%, and reduces post-exercise soreness by 40% versus placebo in a randomised crossover trial.", quality:"Good"},
      {finding:"Citrulline malate (8g) improves 4km cycling time trial performance and reduces perceived exertion in trained cyclists in a double-blind RCT.", quality:"Good"},
      {finding:"3-6g/day L-citrulline significantly reduces systolic blood pressure by 4-7 mmHg in adults with hypertension or pre-hypertension.", quality:"Good"},
    ],
    bestForm: "L-citrulline (pure) or citrulline malate (2:1 ratio). 3-6g/day for blood pressure support; 6-8g, 60 minutes pre-workout for performance. Avoid plain L-arginine as it has inferior bioavailability compared to citrulline.",
    avoid: "Hypotension risk if combined with antihypertensive medications or PDE5 inhibitors such as sildenafil or tadalafil as the combined vasodilatory effect can be excessive.",
    synergies: [
      {name:"Creatine Monohydrate", reason:"Complementary performance enhancement: creatine extends immediate phosphocreatine energy while citrulline improves oxygen delivery and reduces fatigue during sustained effort."},
      {name:"Omega-3 (EPA+DHA)", reason:"Omega-3 reduces vascular inflammation that impairs nitric oxide synthesis while citrulline provides the substrate. Together they support optimal endothelial function."},
      {name:"CoQ10 Ubiquinol", reason:"CoQ10 supports mitochondrial ATP production while citrulline improves oxygen delivery to those mitochondria. A synergistic combination for cardiovascular performance and energy."},
    ],
    stackNotes: "Take 6-8g, 45-60 minutes before training for performance. For blood pressure support, 3g twice daily with meals. No loading phase required. Mix easily in water with a slightly tart taste. Effects on blood pressure are cumulative over 4-6 weeks.",
    whoFor: "Strength and endurance athletes, people with hypertension or pre-hypertension, men with erectile dysfunction (adjunct to medical treatment), anyone seeking to improve vascular health and exercise performance.",
  },

  "Taurine": {
    summary: "Taurine is a conditionally essential amino acid that acts as an osmolyte, antioxidant, and membrane stabiliser. It is the most abundant free amino acid in heart, skeletal muscle, retina, and brain, and recent longevity research suggests taurine deficiency may be a driver of mammalian ageing.",
    mechanism: "Taurine regulates cell volume and membrane excitability by modulating intracellular chloride concentration, directly affecting action potential threshold in cardiac and skeletal muscle. It conjugates bile acids for fat digestion, activates mitochondrial protein synthesis independently of mTOR, and protects photoreceptor cells from oxidative damage. Plasma taurine declines approximately 80% from youth to middle age across mammalian species.",
    research: [
      {finding:"Taurine (1-3g/day) significantly improves exercise performance, time to exhaustion, and reduces muscle damage markers in meta-analyses of athletic RCTs.", quality:"Good"},
      {finding:"Plasma taurine levels decline approximately 80% between youth and middle age; taurine supplementation extends lifespan and healthspan in animal models with human observational correlates.", quality:"Emerging"},
      {finding:"Taurine supplementation significantly reduces blood pressure and arterial stiffness in hypertensive patients, likely via its osmolytic and antioxidant vascular effects.", quality:"Good"},
    ],
    bestForm: "Plain taurine powder or capsules with no proprietary form required. 500-2000mg/day for general wellness; 1-3g pre-workout for performance. Note that taurine in energy drinks is typically at sub-therapeutic doses of 100-200mg versus the 1g or more evidenced dose.",
    avoid: "No established toxicity at up to 6g/day in studies. Very safe profile. Caution with lithium as taurine may increase lithium excretion.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Both stabilise cell membrane excitability and support cardiovascular and muscle function. Magnesium regulates calcium channels while taurine regulates chloride conductance. Complementary for heart rhythm support and muscle relaxation."},
      {name:"CoQ10 Ubiquinol", reason:"Taurine protects mitochondrial membranes from oxidative damage while CoQ10 provides the electron transport function within those membranes. Both support mitochondrial integrity via different mechanisms."},
      {name:"Electrolytes", reason:"Taurine is an osmolyte that regulates cellular hydration alongside sodium, potassium, and magnesium, forming a complete intracellular hydration system."},
    ],
    stackNotes: "1-2g/day is a practical safe daily dose. Take pre-workout for performance (1-3g, 30-60 minutes before). For general wellness and longevity, 500-1000mg with dinner is convenient.",
    whoFor: "Athletes (performance, recovery, muscle protection), people with heart arrhythmias or hypertension, anyone interested in longevity protocols, people with eye conditions, vegans and vegetarians where taurine is found primarily in meat and fish.",
  },

  "BCAA (Leucine/Isoleucine/Valine)": {
    summary: "Branched-chain amino acids are essential amino acids metabolised directly in skeletal muscle. Leucine is the primary anabolic trigger and the combination stimulates muscle protein synthesis and reduces catabolism during training or caloric restriction.",
    mechanism: "Leucine directly activates mTORC1, the central regulator of muscle protein synthesis, independently of growth hormone or insulin. Isoleucine enhances glucose uptake in muscle via GLUT4 translocation. Valine may reduce central nervous system fatigue by competing with tryptophan for the blood-brain barrier transporter, reducing serotonin production during exercise. The 2:1:1 leucine:isoleucine:valine ratio is the clinically validated standard.",
    research: [
      {finding:"BCAA supplementation reduces muscle soreness (DOMS) and markers of muscle damage following eccentric exercise by approximately 33% in multiple RCTs.", quality:"Good"},
      {finding:"Leucine supplementation stimulates muscle protein synthesis in older adults even in the absence of additional amino acids, providing evidence for anabolic resistance correction in sarcopenia.", quality:"Good"},
      {finding:"BCAA supplementation during caloric restriction significantly preserves lean mass compared to caloric restriction alone, relevant for weight loss phases and physique athletes.", quality:"Good"},
    ],
    bestForm: "2:1:1 leucine:isoleucine:valine ratio, the standard clinically validated form. 5-10g/dose around training. If dietary protein intake is already high (1.6g+/kg bodyweight), BCAAs provide marginal additional benefit as whole protein sources containing BCAAs are often a better investment.",
    avoid: "May be unnecessary with high overall protein intake. High leucine chronically without balanced essential amino acids may impair amino acid balance over time. The insulin-stimulating effect is relevant for people managing blood sugar closely.",
    synergies: [
      {name:"Creatine Monohydrate", reason:"Creatine extends high-intensity energy availability while BCAAs reduce muscle breakdown and stimulate protein synthesis. Together they support the complete cycle of performance and recovery."},
      {name:"L-Glutamine", reason:"Glutamine supports immune function during heavy training while BCAAs stimulate muscle protein synthesis. Both address the catabolic challenge of high training loads from different angles."},
      {name:"Vitamin D3 + K2", reason:"Vitamin D improves muscle protein synthesis efficiency and satellite cell responsiveness, amplifying the anabolic signal that leucine triggers via mTOR. A critical combination for older adults with sarcopenia."},
    ],
    stackNotes: "Take 5-10g around training, either 30 minutes before or immediately after. For anti-catabolic use during fasted training, take before the session. Ensure at least 2-3g leucine per dose. If already consuming whey protein, additional BCAAs are likely redundant.",
    whoFor: "Strength athletes, older adults managing muscle mass (sarcopenia), people in caloric deficits, vegans and vegetarians with limited animal protein, endurance athletes seeking to reduce central fatigue.",
  },

  "Glycine": {
    summary: "Glycine is the simplest amino acid and one of the most abundant in the body, constituting approximately 33% of collagen by amino acid content. It is increasingly recognised for roles in sleep quality, metabolic health, and as the rate-limiting substrate for both collagen synthesis and glutathione production.",
    mechanism: "Glycine activates inhibitory glycine receptors in the spinal cord and brainstem, reducing motor neuron excitability and producing muscle relaxation during sleep. It also enhances slow-wave sleep by reducing core body temperature via peripheral vasodilation. In the liver, glycine conjugates bile acids and detoxification intermediates. Glycine is one of the three amino acids that form glutathione and is frequently the limiting substrate in elderly individuals with impaired glycine synthesis.",
    research: [
      {finding:"3g glycine taken 30-60 minutes before bed significantly improves subjective sleep quality, reduces sleep onset latency, and reduces daytime fatigue in RCTs in people with sleep complaints.", quality:"Good"},
      {finding:"Glycine supplementation (3g/day) significantly raises plasma glutathione in elderly subjects and reduces oxidative stress markers that were elevated despite adequate dietary protein.", quality:"Good"},
      {finding:"Collagen plus glycine supplementation produces greater collagen synthesis stimulation than collagen alone, supporting the rate-limiting role of glycine in collagen amino acid assembly.", quality:"Moderate"},
    ],
    bestForm: "Pure glycine powder, simple, cheap, and highly effective. The slightly sweet taste makes it palatable dissolved in warm water. 3g/night for sleep; 3-5g/day for collagen and glutathione support.",
    avoid: "Extremely safe at doses up to 60g/day in clinical studies. No known drug interactions at standard doses. Caution in schizophrenia as high-dose glycine can worsen psychosis by modulating NMDA receptor co-agonist sites.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Both activate inhibitory neurotransmitter pathways before bed. Glycine works via glycine receptors while magnesium works via NMDA modulation. The combination produces additive sleep onset and quality improvements."},
      {name:"Collagen Peptides", reason:"Glycine is the most abundant amino acid in collagen and often the limiting substrate. Glycine plus collagen peptides plus vitamin C is the complete collagen synthesis substrate stack."},
      {name:"NAC", reason:"Glycine and NAC are two of the three glutathione precursor amino acids. The GlyNAC protocol raises glutathione dramatically more effectively than either alone and is a validated mitochondrial and antioxidant protocol for ageing."},
    ],
    stackNotes: "Take 3g in warm water 30-60 minutes before bed. For glutathione support, stack with NAC (GlyNAC protocol): 1.5-3g glycine plus 600-900mg NAC twice daily. For collagen support, take alongside collagen peptides and vitamin C.",
    whoFor: "Anyone with poor sleep quality, people over 50 where glutathione and collagen decline accelerate, athletes supplementing collagen, anyone supporting liver health, vegetarians and vegans where glycine is abundant in animal skin and bones but low in plant foods.",
  },

  "Beta-Alanine": {
    summary: "Beta-alanine is the rate-limiting precursor to carnosine, a dipeptide that buffers lactic acid in skeletal muscle, extending the time to muscular failure during high-intensity exercise. It is particularly effective for exercise lasting 1-4 minutes where hydrogen ion accumulation is the primary fatigue mechanism.",
    mechanism: "Beta-alanine combines with histidine in skeletal muscle via carnosine synthase to form carnosine. Carnosine acts as a pH buffer in the cytoplasm, accepting hydrogen ions released from ATP hydrolysis and lactic acid accumulation, delaying the acidosis that causes the burning sensation and muscle failure in high-intensity exercise. Muscle carnosine increases 40-80% after 4-10 weeks of supplementation. Carnosine also has antioxidant and anti-glycation properties relevant to longevity.",
    research: [
      {finding:"A meta-analysis of 40 studies found beta-alanine significantly improves exercise performance in activities lasting 1-4 minutes, the window where carnosine buffering is most relevant.", quality:"Strong"},
      {finding:"4-6g/day beta-alanine for 8 weeks increases muscle carnosine content by 40-80% in trained athletes, with the increase proportional to baseline.", quality:"Strong"},
      {finding:"Beta-alanine provides minimal benefit for activities shorter than 60 seconds (phosphocreatine-dominant) or longer than 10 minutes (aerobic capacity-dominant).", quality:"Good"},
    ],
    bestForm: "Plain beta-alanine or slow-release formulations (CarnoSyn SR) as slow-release significantly reduces the characteristic tingling side effect. 3.2-6.4g/day in divided doses. Loading over 4+ weeks is required to achieve meaningful carnosine elevation.",
    avoid: "The tingling/flushing sensation (paraesthesia) is harmless but sometimes uncomfortable at doses above 800mg. Divide doses into 400-800mg portions to minimise. Not effective for power sports (sprints under 30 seconds) or long endurance (over 10 minutes).",
    synergies: [
      {name:"Creatine Monohydrate", reason:"The classic pre-workout pairing: creatine extends phosphocreatine energy for the first 10 seconds while beta-alanine/carnosine buffers acid for 1-4 minutes. Together they cover the full anaerobic exercise spectrum."},
      {name:"L-Citrulline", reason:"Citrulline improves oxygen delivery via nitric oxide while beta-alanine buffers the resulting lactic acid accumulation. A well-rounded performance combination addressing both oxygen delivery and metabolic buffering."},
    ],
    stackNotes: "3.2g/day minimum (in 800mg doses 4x daily) to reach the threshold for meaningful carnosine elevation. Slow-release formulations allow a single daily dose. Requires 4-8 weeks of consistent use to maximise muscle carnosine. Take with food to reduce GI irritation.",
    whoFor: "Strength and power athletes with high-intensity training blocks, combat sports athletes, HIIT participants, cyclists and rowers in 1-4 minute effort events, older adults wanting to improve fatigue threshold.",
  },

  "Electrolytes": {
    summary: "Electrolytes are ionically charged minerals, primarily sodium, potassium, magnesium, and chloride, that govern fluid balance, muscle contraction, and nerve function. Sweating causes significant loss and plain water rehydration without replacement can worsen cellular hydration status.",
    mechanism: "Sodium is the primary extracellular cation determining plasma osmolality and driving thirst. Drinking plain water without sodium during prolonged exercise dilutes plasma sodium (hyponatraemia), causing fluid to move into cells, and in severe cases causing life-threatening cerebral oedema. Sodium also co-transports glucose and amino acids in the intestine. Potassium is the primary intracellular cation essential for membrane potential. Magnesium is required for muscle relaxation via calcium release and re-uptake.",
    research: [
      {finding:"Sodium supplementation during ultra-endurance events (above 6 hours) prevents exercise-associated hyponatraemia, a condition responsible for multiple marathon deaths, with high certainty.", quality:"Strong"},
      {finding:"Electrolyte replacement during exercise lasting more than 60-90 minutes significantly maintains exercise capacity, cognitive function, and body weight versus water alone.", quality:"Strong"},
      {finding:"500-1000mg sodium/hour during endurance exercise in the heat is the current evidence-based recommendation for sweat-rate-adjusted replacement.", quality:"Good"},
    ],
    bestForm: "Products containing sodium (at least 500mg per litre of water), potassium (200-400mg), and magnesium (50-100mg). Avoid products that are primarily sugar. LMNT and Precision Hydration are well-formulated for athletes.",
    avoid: "High sodium supplementally (above 3g/day) is inappropriate for people with hypertension or kidney disease without medical guidance. Hyponatraemia from too much plain water is more acutely dangerous than sodium excess in endurance athletes.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Electrolyte formulas often contain insufficient magnesium for muscle recovery and sleep. Supplementing magnesium glycinate separately ensures therapeutic levels beyond what electrolyte drinks provide."},
      {name:"Taurine", reason:"Taurine is an osmolyte that works alongside sodium, potassium, and magnesium to regulate cellular hydration. Its supplementation complements electrolyte replacement."},
      {name:"Creatine Monohydrate", reason:"Creatine draws water into muscle cells (intramuscular hydration) while electrolytes maintain extracellular fluid balance. Together they represent complete hydration management for athletes."},
    ],
    stackNotes: "For exercise under 60 minutes at moderate intensity, water is sufficient. For over 60-90 minutes, add 500mg sodium per 500ml fluid. For ultra-endurance, target 500-1000mg sodium/hour. In daily life, prioritise dietary electrolytes from whole foods and supplement around exercise only.",
    whoFor: "Endurance athletes, anyone exercising in heat, people on low-sodium diets doing intense exercise, jet lag recovery, people with frequent muscle cramps, shift workers with high sweat exposure.",
  },

  "Alpha Lipoic Acid": {
    summary: "Alpha-lipoic acid is a unique antioxidant that is both water- and fat-soluble, allowing it to work in all cellular compartments. It is the only antioxidant that can regenerate vitamins C, E, and glutathione from their oxidised forms and is a cofactor for critical mitochondrial dehydrogenase enzymes.",
    mechanism: "ALA is a cofactor for pyruvate dehydrogenase and alpha-ketoglutarate dehydrogenase, key enzymes in the citric acid cycle. It chelates transition metals (iron, copper) that drive free radical production, acts as an antioxidant in both the cytoplasm (water-soluble) and cell membranes (fat-soluble), and its reduced form DHLA regenerates oxidised glutathione, vitamin C, and vitamin E. ALA also activates AMPK, mimicking the cellular response to caloric restriction.",
    research: [
      {finding:"600mg/day ALA significantly reduces HbA1c and fasting blood glucose in type 2 diabetes over 12 weeks in a meta-analysis of RCTs, with effects comparable to low-dose oral antidiabetics.", quality:"Good"},
      {finding:"600mg/day ALA significantly reduces symptoms of diabetic peripheral neuropathy in a meta-analysis of 15 RCTs and is now included in some European neuropathy guidelines.", quality:"Strong"},
      {finding:"ALA supplementation significantly reduces liver steatosis scores and liver enzyme elevations in non-alcoholic fatty liver disease in controlled trials.", quality:"Good"},
    ],
    bestForm: "R-ALA (the natural isomer) is more bioavailable and biologically active than S-ALA or racemic mixtures. R-ALA stabilised as a sodium salt (Na-RALA) has improved room-temperature stability. Standard ALA (racemic) at 600mg is the dose used in most clinical trials.",
    avoid: "Can lower blood sugar so monitor carefully if on antidiabetic medications. May chelate minerals (iron, zinc, copper) so take 2 hours away from these supplements. High doses above 1200mg/day may cause GI upset or paradoxical pro-oxidant effects in some individuals.",
    synergies: [
      {name:"CoQ10 Ubiquinol", reason:"ALA and CoQ10 both support mitochondrial function and each regenerates the other's antioxidant capacity. Together they represent comprehensive mitochondrial antioxidant protection."},
      {name:"NAC", reason:"ALA regenerates glutathione at the enzymatic level while NAC raises glutathione by providing cysteine substrate. Together they raise glutathione more effectively than either alone, a key combination for liver protection."},
      {name:"Berberine", reason:"Both activate AMPK, the cellular energy sensor, via independent mechanisms. Together they produce additive effects on insulin sensitivity, blood glucose, and metabolic rate."},
    ],
    stackNotes: "Take away from meals (1 hour before or 2 hours after) for best absorption. Separate from mineral supplements by 2 hours. R-ALA is preferable to racemic at half the dose. Start at 300mg/day and titrate up as GI tolerance improves with gradual introduction.",
    whoFor: "People with type 2 diabetes or pre-diabetes, anyone with peripheral neuropathy, people with non-alcoholic fatty liver disease, adults over 50 pursuing longevity and antioxidant strategies, people with high cardiovascular oxidative stress.",
  },

  "Resveratrol": {
    summary: "Resveratrol is a polyphenol found in red grape skin and Japanese knotweed that activates sirtuins, the family of NAD+-dependent enzymes central to longevity pathways. It is one of the foundational compounds in longevity research with robust mechanistic evidence and emerging human data.",
    mechanism: "Resveratrol activates SIRT1, a deacetylase enzyme that regulates stress resistance, mitochondrial biogenesis, fatty acid oxidation, and inflammation through PGC-1alpha activation. SIRT1 activation mimics the molecular response to caloric restriction. Resveratrol also directly inhibits COX-1 and COX-2 (anti-inflammatory), activates AMPK (improving insulin sensitivity), and modulates oestrogen receptor activity.",
    research: [
      {finding:"Resveratrol dramatically extends lifespan and healthspan in yeast, worms, flies, and short-lived fish via SIRT1 activation, among the most consistently replicated longevity findings in model organisms.", quality:"Strong"},
      {finding:"150mg/day resveratrol improves memory performance in post-menopausal women and is associated with increased hippocampal functional connectivity on fMRI after 6 months.", quality:"Good"},
      {finding:"Resveratrol supplementation significantly improves flow-mediated dilation, reduces LDL oxidation, and lowers inflammatory markers in multiple RCTs in adults with cardiovascular risk.", quality:"Good"},
    ],
    bestForm: "Micronized resveratrol, liposomal resveratrol, or trans-resveratrol in cyclodextrin complex, all significantly improving the notably poor bioavailability of standard resveratrol. Pterostilbene (a methylated resveratrol analogue) has 4x better bioavailability and crosses the blood-brain barrier more effectively. 150-500mg/day trans-resveratrol or 50-150mg pterostilbene.",
    avoid: "Resveratrol has oestrogenic activity so caution is needed in oestrogen-sensitive conditions. May inhibit CYP enzymes with potential interactions with medications metabolised by CYP2C9, CYP2D6, and CYP3A4. Avoid above 1g/day.",
    synergies: [
      {name:"NMN", reason:"The most validated longevity combination: NMN raises NAD+ (the substrate) while resveratrol activates sirtuins (the NAD+-dependent enzymes). NMN provides the fuel and resveratrol activates the engine."},
      {name:"Quercetin", reason:"Both polyphenols activate AMPK and have complementary anti-inflammatory profiles. Quercetin also improves resveratrol's bioavailability by inhibiting phase II enzymes that rapidly metabolise it."},
      {name:"CoQ10 Ubiquinol", reason:"Resveratrol activates mitochondrial biogenesis via SIRT1/PGC-1alpha while CoQ10 is required for those new mitochondria to function efficiently."},
    ],
    stackNotes: "Take with a fatty meal as fat dramatically improves resveratrol absorption. Take with quercetin for improved bioavailability. Stack with NMN for a comprehensive sirtuin activation protocol. Morning is preferred to align with circadian SIRT1 activity patterns.",
    whoFor: "Adults pursuing longevity optimisation, anyone with cardiovascular risk, post-menopausal women (cognitive and vascular benefits), people with metabolic syndrome, those with a family history of age-related cognitive decline.",
  },

  "Quercetin": {
    summary: "Quercetin is one of the most abundant dietary flavonoids found in onions, apples, and capers with broad anti-inflammatory, antioxidant, antiviral, and senolytic properties. It is increasingly studied for its ability to selectively eliminate senescent (zombie) cells that accumulate with ageing.",
    mechanism: "Quercetin inhibits multiple pro-inflammatory enzymes including COX-1/2, lipoxygenase, and phosphodiesterase, and directly inhibits NF-kB (the master transcription factor for inflammatory gene expression). It is a potent inhibitor of mast cell degranulation, explaining its anti-allergic effects. As a senolytic, quercetin inhibits survival pathways (Bcl-2 and Bcl-xL) that allow senescent cells to resist apoptosis. It also acts as a zinc ionophore that transports zinc into cells where it inhibits viral replication.",
    research: [
      {finding:"Quercetin plus Dasatinib used intermittently as a senolytic protocol significantly reduces senescent cell burden and improves physical function in idiopathic pulmonary fibrosis, the first human senolytic trial.", quality:"Good"},
      {finding:"500-1000mg/day quercetin significantly reduces upper respiratory infection frequency and symptom duration in randomised trials, particularly in highly stressed populations.", quality:"Good"},
      {finding:"Quercetin phytosome reduces osteoarthritis symptoms and CRP significantly versus placebo in a 12-week RCT.", quality:"Moderate"},
    ],
    bestForm: "Quercetin phytosome (bound to phosphatidylcholine, with 20x better bioavailability than standard quercetin). For senolytic use, the clinical protocol uses 1000mg quercetin phytosome in intermittent 3-day cycles rather than daily dosing.",
    avoid: "May inhibit thyroid peroxidase at high chronic doses above 1g/day, so caution in thyroid conditions. Inhibits CYP3A4 with potential interaction with many medications. Anticoagulant properties require caution with warfarin.",
    synergies: [
      {name:"Vitamin C", reason:"Vitamin C regenerates quercetin from its oxidised form back to active quercetin, allowing it to quench multiple free radicals catalytically. Together they have greater antioxidant and antiviral potency than either alone."},
      {name:"Zinc Bisglycinate", reason:"Quercetin acts as a zinc ionophore, transporting zinc across cell membranes into the cytoplasm where zinc inhibits viral RNA polymerase. The mechanistic basis for the quercetin plus zinc antiviral protocol."},
      {name:"Resveratrol", reason:"Complementary senolytic and longevity activators: resveratrol activates sirtuins (prolonging healthy cell function) while quercetin eliminates senescent cells that impair those functions."},
    ],
    stackNotes: "For immune and anti-inflammatory support: 500mg quercetin phytosome daily with food. For senolytic use: 1000mg for 3 consecutive days, repeated monthly or quarterly (not daily). For antiviral: stack with zinc bisglycinate and vitamin C. Always take with a fatty meal.",
    whoFor: "People with allergies (seasonal rhinitis, skin reactions), anyone wanting immune resilience, people with inflammatory joint conditions, adults pursuing longevity protocols (senolytic benefits), people with frequent viral infections.",
  },

  "Astaxanthin": {
    summary: "Astaxanthin is a marine carotenoid produced by microalgae (Haematococcus pluvialis) that gives flamingos and salmon their pink colour. It is the most potent antioxidant among carotenoids and uniquely spans the entire cell membrane, protecting both the outer and inner lipid layers simultaneously.",
    mechanism: "Unlike beta-carotene (which sits in one membrane layer) or vitamin C (water-soluble), astaxanthin's molecular geometry allows it to span the full width of the phospholipid bilayer, anchoring at both surfaces. This provides comprehensive membrane protection from lipid peroxidation. It also crosses the blood-brain barrier and blood-retinal barrier, rare for a dietary carotenoid, providing neuroprotective and retinal protective effects. Critically, it does not have pro-oxidant activity at high concentrations, unlike many other antioxidants.",
    research: [
      {finding:"4-12mg/day astaxanthin significantly improves skin elasticity, moisture, and reduces wrinkle depth and age spot size in a series of double-blind RCTs.", quality:"Good"},
      {finding:"Astaxanthin supplementation significantly reduces macular degeneration progression risk and improves visual acuity in age-related macular degeneration in observational and small controlled studies.", quality:"Moderate"},
      {finding:"12mg/day astaxanthin significantly reduces muscle damage markers (CK, LDH) and oxidative stress during endurance exercise and accelerates post-exercise recovery in trained athletes.", quality:"Good"},
      {finding:"Astaxanthin (6mg/day) reduces LDL oxidation and increases HDL cholesterol significantly more than placebo over 12 weeks.", quality:"Good"},
    ],
    bestForm: "Natural astaxanthin from H. pluvialis microalgae, not synthetic astaxanthin which has different isomer profiles and potentially inferior bioactivity. 4-12mg/day with a fatty meal. AstaREAL and Zanthin are premium validated brands.",
    avoid: "Very safe profile with no established toxicity at supplemental doses. May cause slight skin orange tint at very high doses (harmless). Strongly lipophilic so always take with food containing fat for meaningful absorption. Verify 'natural' source on the label.",
    synergies: [
      {name:"Omega-3 (EPA+DHA)", reason:"Astaxanthin's primary location is within phospholipid membranes, the same location where EPA and DHA are incorporated. Together they protect and structurally maintain the cell membranes that omega-3 builds."},
      {name:"Vitamin D3 + K2", reason:"Both fat-soluble and best absorbed together with a fatty meal. Astaxanthin's antioxidant activity protects vitamin D from oxidative degradation in the body."},
      {name:"CoQ10 Ubiquinol", reason:"CoQ10 protects mitochondrial membranes from the inside via electron transport; astaxanthin protects the outer membrane from lipid peroxidation. Comprehensive mitochondrial membrane protection."},
    ],
    stackNotes: "Take with your fattiest meal as absorption increases 4-12x with dietary fat. 6-12mg/day is the sweet spot for most applications. Effects accumulate over 4-8 weeks, particularly for skin and endurance benefits. Protect from light and heat during storage.",
    whoFor: "People prioritising skin health and anti-ageing, endurance athletes (muscle protection, reduced oxidative damage), anyone with age-related macular degeneration risk, people with high cardiovascular oxidative stress, anyone already taking omega-3 where astaxanthin extends its benefit.",
  },

  "Glucosamine Sulphate": {
    summary: "Glucosamine sulphate is a naturally occurring compound in cartilage that serves as the primary building block for glycosaminoglycans, the structural molecules that give cartilage its shock-absorbing properties. It is one of the most studied natural interventions for osteoarthritis.",
    mechanism: "Glucosamine is a hexosamine sugar incorporated into proteoglycans and hyaluronic acid, the large structural molecules in cartilage extracellular matrix. Supplemental glucosamine increases synovial fluid viscosity, may stimulate chondrocyte biosynthesis of proteoglycans, and has anti-inflammatory effects via NF-kB inhibition. The sulphate form provides inorganic sulphate, an additional required substrate for glycosaminoglycan synthesis.",
    research: [
      {finding:"The GAIT trial (n=1,583) found glucosamine sulphate significantly reduced knee pain in the moderate-to-severe osteoarthritis subgroup (37% reduction) versus placebo and comparable to celecoxib.", quality:"Strong"},
      {finding:"Long-term glucosamine sulphate (1500mg/day for 3 years) significantly reduces knee joint space narrowing, a structural measure of cartilage preservation, versus placebo in two large European RCTs.", quality:"Good"},
      {finding:"Combined glucosamine plus chondroitin sulphate reduces OA pain comparably to celecoxib in the GAIT trial moderate-severe subgroup. The combination consistently outperforms either alone.", quality:"Strong"},
    ],
    bestForm: "Glucosamine sulphate, not hydrochloride. The sulphate form has significantly more clinical evidence and the sulphate ion provides additional benefit for glycosaminoglycan synthesis. 1500mg/day elemental glucosamine sulphate in divided doses. Allow at least 8-12 weeks for full symptom assessment.",
    avoid: "Shellfish-derived glucosamine should be avoided in severe shellfish allergy. Synthetic glucosamine is available as an alternative. Mild GI upset with large single doses so divide into 500mg three times daily or take with food.",
    synergies: [
      {name:"Chondroitin Sulphate", reason:"The most clinically validated pairing. The GAIT trial specifically tested the combination and found superior outcomes to either alone. Both are structural components of articular cartilage working via complementary mechanisms."},
      {name:"MSM", reason:"MSM provides sulphur, reduces inflammation, and may enhance joint connective tissue synthesis independently. The three-way combination (glucosamine plus chondroitin plus MSM) is widely used clinically."},
      {name:"Collagen Peptides", reason:"Collagen provides the protein scaffold; glucosamine provides the glycosaminoglycan structural molecules. Both are essential components of cartilage, together addressing the complete composition of joint tissue."},
    ],
    stackNotes: "1500mg/day (500mg three times daily with meals). Minimum 8-12 weeks before assessing response as this is a slow-acting structural intervention, not a painkiller. Most effective for moderate-to-severe OA symptoms rather than mild or purely preventive use.",
    whoFor: "People with moderate-to-severe osteoarthritis (particularly knee and hip), older adults wanting to preserve joint cartilage, people with chronic joint pain unresponsive to standard analgesics, active individuals with high joint load.",
  },

  "Chondroitin Sulphate": {
    summary: "Chondroitin sulphate is a glycosaminoglycan, a long chain of alternating sugars that forms the structural backbone of articular cartilage alongside collagen. It is almost always most effective when combined with glucosamine sulphate, as the two compounds address complementary aspects of cartilage structure and function.",
    mechanism: "Chondroitin sulphate chains in cartilage create a highly hydrophilic gel network that gives cartilage its compressive strength. Supplemental chondroitin may inhibit matrix metalloproteinases (enzymes that degrade cartilage), reduce nitric oxide production in chondrocytes (a driver of cartilage cell death), and stimulate proteoglycan and hyaluronic acid synthesis. It also has anti-inflammatory effects via NF-kB pathway inhibition.",
    research: [
      {finding:"In the GAIT trial, chondroitin plus glucosamine sulphate showed the strongest pain reduction in moderate-severe OA (54.3% response rate vs. 19.2% for placebo).", quality:"Strong"},
      {finding:"Chondroitin sulphate significantly reduces joint space narrowing versus placebo over 2 years in multiple European RCTs.", quality:"Good"},
      {finding:"800mg/day chondroitin sulphate reduces OA hand pain and improves functional scores versus placebo in a 6-month Swiss RCT, evidence extending beyond knee OA.", quality:"Good"},
    ],
    bestForm: "Pharmaceutical-grade chondroitin sulphate with greater than 95% purity. Lower-grade products can contain as little as 10-20% actual chondroitin. Products from bovine tracheal cartilage or shark cartilage are both well-studied. 800-1200mg/day in divided doses. Allow 8-12 weeks minimum.",
    avoid: "Generally very safe. Mild anticoagulant properties so use caution with warfarin. Theoretical concern about cartilage-degrading enzymes in very low-purity products so check purity certification on label.",
    synergies: [
      {name:"Glucosamine Sulphate", reason:"The most evidence-backed joint supplement pairing. Glucosamine provides the hexosamine building block; chondroitin provides the sulphated polysaccharide backbone of articular cartilage."},
      {name:"Boswellia Serrata", reason:"Chondroitin reduces cartilage breakdown; Boswellia inhibits the 5-LOX inflammatory pathway that drives chondrocyte death. Complementary structural and anti-inflammatory approaches to joint protection."},
      {name:"MSM", reason:"MSM provides organic sulphur for chondroitin sulphate synthesis, reduces joint inflammation, and improves pain independently. A complete structural joint support combination."},
    ],
    stackNotes: "800-1200mg/day with meals. Most clinical trials used 800mg once daily or 400mg twice daily. Onset is slow so allow 8-12 weeks minimum. Combine with glucosamine sulphate from the outset as the combination is the clinically validated approach.",
    whoFor: "People with osteoarthritis (knee, hip, hand), active individuals with high joint stress, older adults wanting to preserve cartilage, people who have found other pain interventions inadequate.",
  },

  "MSM": {
    summary: "Methylsulfonylmethane (MSM) is an organic sulphur compound found naturally in plants, animals, and humans. Sulphur is the third most abundant mineral in the body and the backbone of cysteine, methionine, collagen cross-links, and glutathione, making MSM a substrate for multiple critical structural and antioxidant systems.",
    mechanism: "MSM provides bioavailable organic sulphur required for disulphide bonds that stabilise collagen and keratin structure, cysteine synthesis (and therefore glutathione), cross-linking of collagen and elastin by lysyl oxidase, and sulphation of glycosaminoglycans (chondroitin sulphate formation). It also directly inhibits NF-kB and reduces release of pro-inflammatory cytokines (TNF-alpha, IL-6, IL-1beta), independent of its sulphur substrate role.",
    research: [
      {finding:"3g/day MSM significantly reduces OA pain and physical function impairment scores versus placebo in a 12-week RCT.", quality:"Good"},
      {finding:"MSM reduces delayed onset muscle soreness (DOMS) and blood markers of oxidative stress following exercise in a placebo-controlled crossover study in trained athletes.", quality:"Good"},
      {finding:"MSM oral plus topical combination reduces facial skin roughness, sallowness, and wrinkle depth in a 16-week RCT in women aged 45-65.", quality:"Moderate"},
    ],
    bestForm: "OptiMSM is the most purified and clinically studied branded MSM, distilled rather than crystallised which removes impurities. Standard MSM powder is effective but quality varies. 1000-3000mg/day is the established dose range.",
    avoid: "Very safe at evidence-based doses. Mild GI upset at higher doses so divide doses if needed. The 'detox reaction' (headache, fatigue in first 1-2 weeks) is likely sulphur adaptation, so reduce dose temporarily if it occurs.",
    synergies: [
      {name:"Glucosamine Sulphate", reason:"MSM provides the organic sulphur that glucosamine sulphate needs for glycosaminoglycan synthesis, and adds its own anti-inflammatory effect. The combination is more effective than either alone for joint outcomes."},
      {name:"Collagen Peptides", reason:"Collagen provides the protein scaffold; MSM provides the sulphur for collagen cross-linking via lysyl oxidase. Both address essential aspects of connective tissue synthesis."},
      {name:"NAC", reason:"Both are sulphur-containing compounds that support glutathione synthesis. MSM via systemic sulphur provision; NAC via direct cysteine delivery. Together they optimise both glutathione pathways."},
    ],
    stackNotes: "1000mg three times daily with meals is the standard approach. Can be taken as powder in water as it is essentially tasteless. Allow 6-8 weeks for full anti-inflammatory and structural effects. Combine with glucosamine plus chondroitin for joint OA, with collagen plus vitamin C for connective tissue repair.",
    whoFor: "People with osteoarthritis or chronic joint pain, athletes with high training loads, anyone with connective tissue issues, people with seasonal allergies, adults over 50 wanting to support collagen and glutathione synthesis.",
  },

  "Boswellia Serrata": {
    summary: "Boswellia serrata (Indian frankincense) has the most specific anti-inflammatory action of any botanical supplement, selectively inhibiting 5-lipoxygenase (5-LOX), the enzyme producing leukotrienes, while not affecting COX enzymes. This makes it complementary to NSAIDs and omega-3 rather than redundant.",
    mechanism: "The primary active compound AKBA (acetyl-keto-beta-boswellic acid) specifically inhibits 5-LOX, blocking production of pro-inflammatory leukotrienes, particularly LTB4, a potent neutrophil chemoattractant. Boswellia also inhibits human leukocyte elastase (HLE), an enzyme that degrades connective tissue at inflammation sites. Unlike NSAIDs, Boswellia does not inhibit COX-2, meaning it does not reduce the protective prostaglandins of the gastric mucosa and has no GI erosion risk.",
    research: [
      {finding:"Standardised Boswellia extract (5-LOXIN, 100mg/day AKBA) significantly reduces knee OA pain by 90mm VAS versus placebo at 90 days, with effects visible from day 7.", quality:"Good"},
      {finding:"Boswellia significantly reduces morning stiffness and improves walking distance in rheumatoid arthritis in a 3-month RCT.", quality:"Good"},
      {finding:"Boswellia serrata extract (H15) significantly reduces asthma symptom frequency and improves lung function in a 6-week double-blind trial.", quality:"Good"},
    ],
    bestForm: "Standardised extract with minimum 10% AKBA. 5-LOXIN and Aflapin are the most clinically validated forms. Standard Boswellia resin extract has much lower AKBA content and requires higher doses (300-500mg). Phytosome forms improve bioavailability significantly.",
    avoid: "Very safe with no established serious drug interactions. Mild GI upset in some people so take with food. Rare: skin rash in sensitive individuals. Not a replacement for medical treatment in active IBD or RA.",
    synergies: [
      {name:"Omega-3 (EPA+DHA)", reason:"Omega-3 inhibits COX-2 (prostaglandin pathway); Boswellia inhibits 5-LOX (leukotriene pathway). Together they block both major inflammatory cascades, the most mechanistically comprehensive combination for joint pain."},
      {name:"Glucosamine Sulphate", reason:"Glucosamine provides structural rebuilding; Boswellia reduces the inflammation that drives cartilage destruction. Addressing both simultaneously is more effective than either alone for OA management."},
      {name:"Quercetin", reason:"Quercetin inhibits NF-kB and mast cell degranulation; Boswellia inhibits 5-LOX. Different but complementary inflammatory targets providing broader anti-inflammatory coverage."},
    ],
    stackNotes: "AKBA-standardised extract (100-200mg AKBA) with food as fat improves absorption. Response is often faster than glucosamine/chondroitin and many people notice reduced pain within 1-2 weeks. Take with omega-3 for the most evidence-backed anti-inflammatory combination for joint pain.",
    whoFor: "People with osteoarthritis or chronic joint pain, athletes with tendinopathy, people with inflammatory bowel disease as adjunctive use, anyone with asthma, people seeking anti-inflammatory support without the GI side effects of NSAIDs.",
  },

  "Cissus Quadrangularis": {
    summary: "Cissus quadrangularis (veldt grape) is a succulent vine with modern evidence supporting its use in fracture repair, joint pain, and connective tissue recovery. It is one of the few natural compounds with clinical evidence specifically for tendon and ligament healing.",
    mechanism: "Cissus contains ketosteroids (oxa-sterols) that promote fibroblast and osteoblast proliferation, the cells responsible for connective tissue and bone formation. It increases expression of bone morphogenetic proteins (BMPs) and collagen synthesis genes. Phytochemicals in Cissus (quercetin, kaempferol, resveratrol) inhibit COX-2 and reduce inflammation at the injury site. The combination of anabolic (fibroblast activation) and anti-inflammatory actions makes it uniquely suited for connective tissue repair.",
    research: [
      {finding:"Cissus extract significantly reduces fracture healing time by approximately 55% versus placebo in a controlled trial, one of the most dramatic effects seen for a natural compound on bone repair rate.", quality:"Moderate"},
      {finding:"1g/day Cissus supplementation significantly reduces joint pain, stiffness, and improves function in active adults with joint discomfort in a placebo-controlled trial.", quality:"Good"},
      {finding:"Cissus extract combined with resistance training significantly reduces body fat and improves lipid profile in overweight adults versus placebo in an 8-week trial.", quality:"Moderate"},
    ],
    bestForm: "Standardised Cissus quadrangularis extract standardised to ketosteroid content at 500-1000mg/day. Non-standardised root powder has highly variable active compound content.",
    avoid: "Limited long-term safety data so cycles of 8-12 weeks with breaks are prudent. Mild GI upset in some people. Not recommended during pregnancy due to anabolic hormonal activity.",
    synergies: [
      {name:"Collagen Peptides", reason:"Cissus activates fibroblasts (the cells that make collagen); collagen peptides provide the amino acid substrate for fibroblasts to use. Together they address both the signal and the raw material for connective tissue repair."},
      {name:"Vitamin C", reason:"Vitamin C is required for collagen cross-linking downstream of fibroblast activation. Cissus plus collagen peptides plus vitamin C work synergistically in the same connective tissue repair pathway."},
      {name:"Boswellia Serrata", reason:"Boswellia reduces inflammation at the injury site; Cissus promotes active repair. Anti-inflammation plus active regeneration is the optimal two-phase approach to tendon and ligament recovery."},
    ],
    stackNotes: "500-1000mg/day with food. Most effective during active injury recovery phases. Combine with collagen peptides (taken 45 min before exercise) and vitamin C for maximum tendon/ligament repair benefit. Allow 8-12 weeks of consistent use.",
    whoFor: "People with tendinopathy (Achilles, patellar, rotator cuff), ligament injuries, stress fractures or slow-healing fractures, active individuals with high joint load, anyone in a connective tissue rehabilitation protocol.",
  },

  "Probiotics": {
    summary: "Probiotics are live microorganisms that confer a health benefit on the host when administered in adequate amounts. The clinical evidence is highly strain-specific and different strains produce different effects, so genus/species/strain designation matters enormously for selecting the right product.",
    mechanism: "Probiotic bacteria colonise and compete with pathogenic bacteria for adhesion sites in the gut epithelium (competitive exclusion). They produce short-chain fatty acids (SCFAs), particularly butyrate, from dietary fibre fermentation, which fuels colonocytes, strengthens tight junctions, and has systemic anti-inflammatory effects via GPR receptor signalling. Specific strains modulate immune maturation by interacting with Peyer's patches and dendritic cells in gut-associated lymphoid tissue (GALT). The gut-brain axis via the vagus nerve and tryptophan metabolism links gut microbiome composition to mood and cognitive function.",
    research: [
      {finding:"Lactobacillus rhamnosus GG significantly reduces the incidence of antibiotic-associated diarrhoea by 60% and C. difficile-associated diarrhoea by 71% in a Cochrane meta-analysis.", quality:"Strong"},
      {finding:"Multi-strain probiotics significantly reduce IBS symptom severity scores in a meta-analysis of 24 RCTs.", quality:"Good"},
      {finding:"Bifidobacterium longum plus Lactobacillus helveticus combination significantly reduces anxiety and depression symptoms in otherwise healthy adults with psychological stress in multiple RCTs.", quality:"Good"},
      {finding:"Lactobacillus acidophilus plus Bifidobacterium bifidum significantly reduce eczema severity in infants born to atopic parents when given from birth.", quality:"Good"},
    ],
    bestForm: "Strain-specific product selection is essential. For antibiotic-associated diarrhoea: Lactobacillus rhamnosus GG (Culturelle) or Saccharomyces boulardii. For IBS: Bifidobacterium infantis 35624 (Align). For mood/brain: L. helveticus R0052 plus B. longum R0175. For general gut health: diverse multi-strain products (10+ strains, 10-50 billion CFU).",
    avoid: "Immunocompromised individuals (HIV, organ transplant recipients, active cancer treatment) should use caution as rare cases of bacteraemia have been reported. Saccharomyces boulardii should not be used with a central venous catheter. Start slowly if gut-sensitive as initial bloating usually resolves within 1-2 weeks.",
    synergies: [
      {name:"L-Glutamine", reason:"The most clinically validated gut repair combination: glutamine rebuilds the physical epithelial barrier that probiotics colonise and protect. Used together in clinical gut rehabilitation protocols."},
      {name:"Psyllium Husk", reason:"Psyllium provides prebiotic fermentable fibre that feeds beneficial bacteria and supports their colonisation. Probiotics provide the organisms; psyllium provides their food source."},
      {name:"Digestive Enzymes", reason:"Digestive enzymes improve food breakdown, reducing undigested substrate available for pathogenic fermentation while probiotics re-establish beneficial populations."},
    ],
    stackNotes: "Take 30 minutes before a meal for best gut survivability. Keep refrigerated unless the product is specifically formulated for room-temperature stability. Take 2+ hours apart from antibiotics when using concurrently. Allow 4-8 weeks of consistent use to assess gut symptom improvement.",
    whoFor: "Anyone who has recently taken antibiotics, people with IBS or IBD, those with frequent travel diarrhoea, people with anxiety or depression (gut-brain axis), infants with atopic risk, anyone who has undergone gut-disrupting medical treatments.",
  },

  "Digestive Enzymes": {
    summary: "Digestive enzyme supplements replace or supplement the enzymes produced by the pancreas and intestinal brush border that break down food macromolecules. They are most relevant when endogenous enzyme production is inadequate due to ageing, stress, gut inflammation, or pancreatic insufficiency.",
    mechanism: "The primary digestive enzymes are amylase (carbohydrates/starch), lipase (dietary fats into fatty acids and glycerol), protease/peptidase (proteins into amino acids), lactase (lactose), alpha-galactosidase (oligosaccharides in beans and legumes), and bromelain/papain (plant-derived proteases with anti-inflammatory activity). Pancreatic enzyme production declines with age, low-grade chronic inflammation, and under chronic stress. Adequate enzyme activity is required for nutrient extraction and deficiency produces bloating, malabsorption, and nutritional deficiencies even with adequate dietary intake.",
    research: [
      {finding:"Supplemental digestive enzymes significantly reduce symptoms of exocrine pancreatic insufficiency (EPI) and this is the most robust indication for enzyme supplementation.", quality:"Strong"},
      {finding:"Lactase enzyme supplements taken immediately before dairy consumption effectively prevent lactose intolerance symptoms in lactase-deficient individuals in multiple RCTs.", quality:"Strong"},
      {finding:"Multi-enzyme preparations significantly reduce post-meal bloating, gas, and digestive discomfort in adults with functional digestive disorders in controlled trials.", quality:"Good"},
    ],
    bestForm: "Broad-spectrum plant-derived enzyme complex including amylase, protease, lipase, and ideally lactase and alpha-galactosidase for general use. Enteric-coated lipase for fat malabsorption. Bromelain and papain are effective plant-based proteases. Take immediately before meals as timing is critical.",
    avoid: "Contraindicated in active acute pancreatitis. Proteolytic enzymes at high doses may increase gastric irritation so take immediately before meals. Porcine-sourced pancreatin is the gold standard for EPI but not appropriate for those avoiding animal products.",
    synergies: [
      {name:"Probiotics", reason:"Digestive enzymes break down food more completely, reducing undigested substrate available for pathogenic fermentation; probiotics restore the healthy microbial ecosystem. Together they address dysbiosis and impaired digestion simultaneously."},
      {name:"Psyllium Husk", reason:"Digestive enzymes improve food breakdown; psyllium supports orderly transit and provides prebiotic fibre. Together they support complete and efficient digestion from enzyme activity through elimination."},
      {name:"L-Glutamine", reason:"Glutamine maintains the integrity of the intestinal brush border where many digestive enzymes (lactase, sucrase, maltase) are located. Supporting gut lining integrity supports endogenous enzyme expression."},
    ],
    stackNotes: "Take immediately before or with the first bite of food as enzymes work in the first 60-90 minutes after eating. For fat malabsorption, ensure enteric-coated lipase is included. Do not take on an empty stomach for extended periods as proteolytic enzymes may cause mild irritation.",
    whoFor: "People over 50 (pancreatic enzyme output declines with age), anyone with IBS-type symptoms or bloating after meals, confirmed enzyme deficiency, people with a history of pancreatitis, anyone recovering from gut inflammation, people who experience GI distress after high-fat or high-protein meals.",
  },

  "Psyllium Husk": {
    summary: "Psyllium husk is a soluble dietary fibre from Plantago ovata seeds with evidence for cholesterol reduction, blood sugar management, and bowel regularity that rivals low-dose pharmaceutical interventions. It is one of the most validated natural compounds for cardiovascular and metabolic health.",
    mechanism: "Psyllium forms a viscous gel in the intestinal lumen when hydrated, which slows gastric emptying and carbohydrate absorption (reducing post-meal glucose spikes), binds bile acids in the colon and promotes their excretion (forcing the liver to convert more LDL cholesterol into new bile acids), provides fermentable substrate for short-chain fatty acid production (particularly butyrate), and adds bulk and water to stool while normalising transit time.",
    research: [
      {finding:"A meta-analysis of 21 RCTs found psyllium reduces LDL cholesterol by approximately 6-18% and total cholesterol by 4-11% at 10-20g/day alongside a healthy diet.", quality:"Strong"},
      {finding:"5-10g psyllium before meals significantly reduces post-meal blood glucose response and HbA1c over 8-12 weeks in type 2 diabetes in a meta-analysis of 35 RCTs.", quality:"Strong"},
      {finding:"Psyllium is effective in treating both constipation and diarrhoea-predominant IBS, normalising bowel function rather than pushing in one direction only.", quality:"Good"},
    ],
    bestForm: "Pure psyllium husk powder (not psyllium seed as the husk contains 70% soluble fibre vs. 10% in the seed). 5-10g per dose, taken 30 minutes before meals with at least 250ml water. Organic versions avoid pesticide residue concerns.",
    avoid: "Must be taken with adequate water as expanding psyllium without sufficient fluid can cause oesophageal or intestinal obstruction. Start with 5g/day and increase slowly to allow gut adaptation. Psyllium significantly reduces absorption of many medications so take all medications at least 2 hours before or after psyllium.",
    synergies: [
      {name:"Berberine", reason:"Both independently reduce post-meal blood glucose via different mechanisms. Psyllium slows absorption; berberine inhibits hepatic glucose production. A clinically additive blood sugar management pairing."},
      {name:"Probiotics", reason:"Psyllium provides fermentable prebiotic fibre that feeds and sustains probiotic bacteria. Taking both ensures the bacteria have the substrate to colonise and produce beneficial SCFAs."},
      {name:"Omega-3 (EPA+DHA)", reason:"Omega-3 reduces cardiovascular inflammation; psyllium reduces LDL cholesterol. Together they address the two major modifiable cardiovascular risk factors from different mechanistic directions."},
    ],
    stackNotes: "Take 30 minutes before your two largest meals. Always take with at least 250ml water. Separate from all medications by 2 hours minimum. Start at 5g once daily and increase over 2-3 weeks.",
    whoFor: "Anyone with elevated LDL cholesterol, type 2 diabetes or pre-diabetes, IBS (both constipation and diarrhoea predominant), people with irregular bowel habits, those at cardiovascular risk wanting a drug-free intervention.",
  },

  "Berberine": {
    summary: "Berberine is a plant alkaloid found in goldenseal and barberry that has emerged as one of the most evidence-backed natural compounds for blood sugar management, cholesterol reduction, and metabolic health, with mechanisms broadly comparable to metformin.",
    mechanism: "Berberine's primary mechanism is AMPK activation, the cellular energy sensor that acts as a master metabolic switch. AMPK activation produces inhibition of hepatic glucose production (gluconeogenesis, the same primary mechanism as metformin), increased cellular glucose uptake via GLUT4 translocation, upregulation of insulin receptors, inhibition of complex I of the mitochondrial electron transport chain (producing mild energetic stress that triggers AMPK), and modulation of the gut microbiome toward bacteria that produce butyrate and reduce inflammation.",
    research: [
      {finding:"Berberine 500mg three times daily reduces HbA1c, fasting glucose, and post-meal glucose comparably to metformin 500mg three times daily in a head-to-head RCT in type 2 diabetics.", quality:"Strong"},
      {finding:"Berberine significantly reduces total cholesterol (-0.61 mmol/L), LDL (-0.65 mmol/L), and triglycerides (-0.50 mmol/L) in a meta-analysis of 14 RCTs.", quality:"Strong"},
      {finding:"Berberine supplementation alongside lifestyle modification significantly improves outcomes in PCOS, reducing testosterone, fasting insulin, and LH:FSH ratio in RCTs.", quality:"Good"},
    ],
    bestForm: "Berberine HCl (hydrochloride salt) is the most common and well-studied form. Dihydroberberine (DHB) is a more bioavailable reduced form absorbed 5x more efficiently with less GI irritation. Standard berberine HCl: 500mg three times daily with meals is the evidence-based dosing protocol.",
    avoid: "Significant drug interactions as berberine inhibits CYP2D6, CYP3A4, and P-glycoprotein. Can interact with antidiabetic medications (combined hypoglycaemia risk), cyclosporine, anticoagulants, and many cardiac medications. Not recommended during pregnancy. GI side effects are the primary dose-limiting issue so take with food.",
    synergies: [
      {name:"Alpha Lipoic Acid", reason:"Both activate AMPK via different mechanisms (berberine via complex I inhibition, ALA via ROS sensing). Together they produce additive insulin sensitisation and blood glucose reduction."},
      {name:"Psyllium Husk", reason:"Psyllium slows carbohydrate absorption; berberine reduces hepatic glucose production. Together they address pre- and post-absorptive glucose management."},
      {name:"Probiotics", reason:"Berberine's beneficial metabolic effects are significantly mediated through gut microbiome modulation. Probiotics may amplify berberine's prebiotic effects and reduce GI side effects."},
    ],
    stackNotes: "500mg three times daily with meals. Start with 500mg once daily for 1-2 weeks before titrating to full dose. Check for drug interactions carefully as this is one of the most interaction-prone natural supplements. Cycle use (8-12 weeks on, 4 weeks off) is a common prudent approach.",
    whoFor: "People with type 2 diabetes or pre-diabetes, those with elevated cholesterol not wanting or tolerating statins, women with PCOS, people with metabolic syndrome, anyone wanting metformin-comparable metabolic support.",
  },

  "DHEA": {
    summary: "DHEA (dehydroepiandrosterone) is the most abundant steroid hormone in the human body, produced primarily by the adrenal cortex. It is a precursor to both testosterone and oestrogen and declines approximately 80% between ages 25 and 75, one of the most dramatic age-related hormonal declines.",
    mechanism: "DHEA is converted peripherally (in skin, brain, adipose tissue, and gonads) to androstenedione, testosterone, and oestradiol via tissue-specific enzymes. This peripheral conversion delivers sex hormone effects in specific tissues without the systemic suppression that occurs with direct hormone administration. DHEA also has direct neurosteroid receptor activity in the brain, modulating GABA-A, NMDA, and sigma-1 receptors, producing mood, memory, and stress resilience effects independent of conversion.",
    research: [
      {finding:"In women with adrenal insufficiency, DHEA replacement at 25-50mg/day significantly improves wellbeing, energy, mood, and sexual function. This is the clearest evidence base for DHEA supplementation.", quality:"Strong"},
      {finding:"DHEA supplementation in elderly adults with low DHEA-S levels improves bone density, lean mass, and sexual function in multiple RCTs, with more consistent effects in women than men.", quality:"Good"},
      {finding:"Vaginal DHEA (prasterone) is FDA-approved for treating vulvovaginal atrophy in post-menopausal women, converting locally to oestradiol and testosterone without significant systemic absorption.", quality:"Strong"},
    ],
    bestForm: "Micronised DHEA for improved bioavailability. 25-50mg/day is the standard range for women; some men use 50-100mg. Blood testing (DHEA-S) before and during supplementation is strongly recommended.",
    avoid: "Stimulates conversion to sex hormones so it is contraindicated in hormone-sensitive cancers (ER+ breast cancer, prostate cancer) without oncology guidance. May cause acne, oily skin, and hair loss in androgen-sensitive individuals. Testosterone and oestrogen levels should be monitored. Not for use under age 40 without confirmed deficiency.",
    synergies: [
      {name:"Ashwagandha KSM-66", reason:"Ashwagandha supports adrenal function (the DHEA source) and LH production. DHEA provides the hormone substrate. Together they support the full HPA-gonadal axis from signalling to substrate."},
      {name:"Vitamin D3 + K2", reason:"Vitamin D is a prohormone produced by the same steroidogenesis pathway as DHEA. Both decline with age and supplementing both supports the broader steroidal hormone environment."},
    ],
    stackNotes: "Test DHEA-S before starting and do not supplement if already in the high-normal range. 25mg/day is conservative and appropriate for most healthy adults over 50. Take in the morning as DHEA has a natural morning peak. Reassess with blood testing after 8-12 weeks.",
    whoFor: "Adults over 50 with confirmed low DHEA-S levels, people with adrenal fatigue or insufficiency, post-menopausal women on medical advice, anyone experiencing age-related decline in energy, mood, and sexual function not explained by other factors. Not appropriate without testing.",
  },

  "DIM": {
    summary: "DIM (3,3-diindolylmethane) is a compound formed from indole-3-carbinol (found in cruciferous vegetables) during digestion. It modulates oestrogen metabolism by promoting conversion of potent oestrogens to weaker forms, particularly relevant for women during perimenopause and oestrogen metabolism disorders.",
    mechanism: "DIM activates the aryl hydrocarbon receptor (AhR), upregulating hepatic cytochrome P450 enzymes, particularly CYP1A1 and CYP1A2, which convert oestradiol to 2-hydroxyoestrone (a 'good' oestrogen) rather than 16-alpha-hydroxyoestrone (a more proliferative form associated with higher breast cancer risk). DIM shifts the 2:16 hydroxyoestrone ratio in a favourable direction. It also inhibits aromatase (converting androgens to oestrogens in fat tissue), modulates SHBG, and has anti-inflammatory effects via NF-kB inhibition.",
    research: [
      {finding:"DIM supplementation significantly increases urinary 2-hydroxyoestrone:16-hydroxyoestrone ratio in premenopausal women, a shift considered protective against oestrogen-related cancers.", quality:"Good"},
      {finding:"DIM demonstrates anti-proliferative effects on oestrogen receptor-positive breast cancer cell lines via multiple mechanisms in extensive in vitro research. Clinical evidence in humans is still emerging.", quality:"Moderate"},
      {finding:"DIM at 100mg/day significantly reduces PMS symptom scores (mood, bloating, breast tenderness) in a double-blind RCT.", quality:"Moderate"},
    ],
    bestForm: "Bioavailability-enhanced DIM as standard crystalline DIM has poor and variable absorption. BioResponse DIM (with phosphatidylcholine) is the form used in most human trials. 100-200mg/day of the enhanced form.",
    avoid: "Should not be taken alongside oestrogen-based medications without medical oversight as DIM may reduce their efficacy. Not for use in pregnancy. May cause green-tinted urine (harmless metabolite). Caution in people on CYP1A2-metabolised medications (caffeine, clozapine, olanzapine).",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Magnesium supports Phase II liver detoxification pathways that conjugate and excrete the oestrogen metabolites DIM generates. Without adequate magnesium, DIM-shifted oestrogens can accumulate."},
      {name:"Folate (Methylfolate)", reason:"Adequate methylation (B12 and folate) is required for COMT enzyme activity, the enzyme that inactivates catechol oestrogens downstream of DIM's action. Methylation support is the critical complement."},
      {name:"Black Cohosh", reason:"DIM modulates oestrogen metabolism; Black Cohosh reduces central oestrogen-withdrawal symptoms. Together they address both the metabolic and symptomatic aspects of perimenopause."},
    ],
    stackNotes: "100-200mg BioResponse DIM with food as fat improves absorption. Effects on oestrogen metabolite ratio are measurable at 30 days. Most useful as part of a broader hormonal support protocol including B vitamins and magnesium.",
    whoFor: "Women with oestrogen dominance symptoms (heavy periods, PMS, fibrocystic breasts), women in perimenopause, women with a family history of oestrogen-sensitive cancers wanting metabolic support, anyone eating a low-cruciferous-vegetable diet.",
  },

  "Black Cohosh": {
    summary: "Black cohosh (Actaea racemosa) is the most clinically studied herbal remedy for menopausal symptoms, specifically hot flushes, night sweats, and mood disturbance, with a safety profile that distinguishes it from conventional hormone therapy.",
    mechanism: "Current evidence suggests black cohosh acts via serotonergic pathways (modulating 5-HT1A and 5-HT7 receptors in the hypothalamic thermoregulatory centre), dopaminergic activity, and possibly opioid receptor modulation, explaining its effects on hot flushes (via thermoregulation), mood, and sleep. Critically, it does not appear to stimulate oestrogen receptors in the breast or uterus, making its safety profile in oestrogen-sensitive conditions significantly different from HRT.",
    research: [
      {finding:"A Cochrane review of 16 RCTs found black cohosh significantly reduces menopausal vasomotor symptoms versus placebo, with effect size modest to moderate, comparable to low-dose HRT in some trials.", quality:"Good"},
      {finding:"The HALT trial (n=351) found black cohosh equivalent to placebo for hot flush frequency, one of the largest and most rigorous negative trials, highlighting heterogeneous evidence.", quality:"Strong"},
      {finding:"Black cohosh significantly improves sleep quality scores and reduces irritability and anxiety in peri- and post-menopausal women in multiple RCTs.", quality:"Good"},
    ],
    bestForm: "Standardised isopropanolic extract (Remifemin is the most clinically validated product, used in the majority of positive RCTs). 20-40mg standardised extract twice daily. The isopropanolic extract has the strongest clinical evidence.",
    avoid: "Rare but serious liver toxicity has been reported. Avoid in people with liver disease and discontinue if jaundice occurs. Not recommended for more than 6 months without medical review. Despite likely absence of oestrogenic activity, manufacturers recommend against use in ER+ breast cancer without oncology guidance.",
    synergies: [
      {name:"DIM", reason:"DIM modulates oestrogen metabolism; Black Cohosh reduces central hot flush and mood symptoms. Together they address both the hormonal and symptomatic aspects of perimenopause from different angles."},
      {name:"Magnesium Glycinate", reason:"Magnesium reduces anxiety and improves sleep quality, the two most common non-vasomotor menopausal complaints. Combined with Black Cohosh's vasomotor and mood benefits, this is a comprehensive menopausal support combination."},
    ],
    stackNotes: "20mg standardised extract (Remifemin or equivalent) twice daily with food. Allow 4-8 weeks minimum for full symptom effect. Do not use for more than 6 months without medical review. Discontinue if you develop any unusual fatigue, jaundice, or abdominal discomfort.",
    whoFor: "Peri- and post-menopausal women with hot flushes, night sweats, mood disturbance, or sleep disruption, particularly those who cannot use or prefer to avoid conventional hormone therapy.",
  },

  "Saw Palmetto": {
    summary: "Saw palmetto (Serenoa repens) berry extract is the most extensively studied natural compound for benign prostatic hyperplasia (BPH), the age-related prostate enlargement causing urinary symptoms in the majority of men over 60. It is also the most used natural DHT-inhibitor for androgenic hair loss.",
    mechanism: "Saw palmetto inhibits 5-alpha reductase, the enzyme that converts testosterone to dihydrotestosterone (DHT), the primary driver of prostate cell proliferation and androgenic hair follicle miniaturisation. Unlike finasteride, saw palmetto inhibits both type 1 and type 2 isoforms less potently, is not as systemically suppressive of DHT, and does not appear to significantly reduce serum DHT at therapeutic doses. It also has anti-inflammatory and antiproliferative effects directly on prostate cells.",
    research: [
      {finding:"A Cochrane review of 32 RCTs found saw palmetto significantly improves urinary peak flow rate and reduces International Prostate Symptom Score compared to placebo.", quality:"Good"},
      {finding:"320mg/day saw palmetto extract was comparable to 5mg finasteride for improving urinary flow and symptom scores in BPH in a 6-month head-to-head trial, with significantly fewer sexual side effects.", quality:"Good"},
      {finding:"Saw palmetto combined with lycopene, selenium, and tamsulosin significantly reduces PSA levels and improves urinary symptoms versus placebo in a 3-year trial.", quality:"Good"},
    ],
    bestForm: "Supercritical CO2 extracted saw palmetto berry extract, standardised to 85-95% fatty acids and sterols. 320mg/day (as a single dose or two 160mg doses). Non-standardised powders and low-quality extracts are ineffective.",
    avoid: "May increase bleeding risk so caution is needed 2 weeks before surgery. Saw palmetto may slightly reduce PSA levels by approximately 50%, so inform your doctor if undergoing PSA screening as it may mask PSA elevation that warrants medical attention.",
    synergies: [
      {name:"Zinc Bisglycinate", reason:"Zinc is a direct inhibitor of 5-alpha reductase activity and prolactin secretion, both relevant to prostate health. Zinc plus saw palmetto addresses prostate health from complementary hormonal directions."},
      {name:"Ashwagandha KSM-66", reason:"Ashwagandha reduces cortisol (which increases DHT sensitivity in prostate tissue when elevated) and supports testosterone balance, providing broader hormonal support for prostate health."},
    ],
    stackNotes: "320mg/day with food as fat significantly improves absorption of the fatty acid active compounds. Allow 3-6 months for full urinary symptom improvement. Have PSA baseline tested before starting and inform your doctor you are taking saw palmetto.",
    whoFor: "Men over 50 with BPH symptoms (frequent urination, poor flow, nocturia), men with androgenic hair loss as an adjunct to finasteride or minoxidil, men wanting prostate health maintenance.",
  },

  "Melatonin": {
    summary: "Melatonin is the primary chronobiotic hormone that signals to the brain that it is nighttime, enabling the physiological cascade of sleep initiation. It does not cause sedation directly. Low doses are more effective than high doses for most applications, and its primary evidence base is for circadian rhythm disorders rather than primary insomnia.",
    mechanism: "Melatonin is synthesised from serotonin in the pineal gland in response to darkness. It binds MT1 and MT2 receptors in the suprachiasmatic nucleus (the brain's master clock), suppressing wakefulness-promoting signals and phase-shifting the circadian cycle. It also lowers core body temperature, which is essential for sleep onset. Exogenous melatonin works best at doses of 0.5-1mg, mimicking physiological peak levels. Higher doses (5-10mg) flood receptors and may paradoxically impair circadian entrainment with chronic use.",
    research: [
      {finding:"A meta-analysis of 19 RCTs found melatonin significantly improves sleep onset latency, total sleep time, and sleep quality in people with primary sleep disorders, with modest but consistent effects.", quality:"Strong"},
      {finding:"Melatonin 0.5-3mg is highly effective for jet lag, phase-advancing the circadian clock when travelling east and reducing jet lag symptoms versus placebo in a Cochrane review of 10 RCTs.", quality:"Strong"},
      {finding:"Low-dose melatonin (0.5mg) is as effective as high-dose (5mg) for sleep onset latency in healthy volunteers, with better circadian entrainment and fewer next-day sedation effects.", quality:"Good"},
    ],
    bestForm: "Standard immediate-release melatonin at 0.5-3mg, not the 5mg or 10mg doses commonly sold. Extended-release melatonin is appropriate for sleep maintenance issues (frequent waking). Start at 0.5mg and increase only if ineffective. Liquid or sublingual preparations allow precise low-dose titration.",
    avoid: "Avoid high doses (above 3mg) for regular use as they may suppress endogenous production over time and impair circadian rhythm quality. Not a treatment for primary insomnia without sleep hygiene optimisation. Do not drive after taking. May interact with warfarin, immunosuppressants, and antidiabetics.",
    synergies: [
      {name:"Magnesium Glycinate", reason:"Magnesium addresses the physical relaxation and GABA components of sleep while melatonin addresses the circadian timing signal. They target different aspects of sleep physiology and are the most complementary sleep combination available."},
      {name:"L-Theanine", reason:"L-theanine reduces racing thoughts and anxiety that delay sleep onset while melatonin provides the circadian sleep signal. Particularly effective when sleep issues are partly cognitive or stress-driven."},
      {name:"Ashwagandha KSM-66", reason:"Ashwagandha lowers cortisol (which suppresses melatonin production) while melatonin provides the direct sleep signal. A logical combination when cortisol-driven sleep difficulty is the underlying issue."},
    ],
    stackNotes: "Take 30-60 minutes before your target bedtime as timing is more important than dose. 0.5-1mg is the optimal starting dose. For jet lag: take at the destination bedtime for 3-5 nights. Use consistently as melatonin works by training the circadian rhythm. Darkness after taking maximises efficacy.",
    whoFor: "People with jet lag or shift work, older adults (endogenous melatonin declines significantly after 50), people with delayed sleep phase disorder, anyone with difficulty falling asleep as part of a broader sleep hygiene strategy.",
  },
  "5-HTP": {
    summary: "5-Hydroxytryptophan is the direct precursor to serotonin, bypassing the rate-limiting tryptophan hydroxylase step. It crosses the blood-brain barrier and converts to serotonin in the brain, making it the most direct approach to supporting serotonin levels from nutritional supplementation.",
    mechanism: "5-HTP is converted to serotonin by aromatic amino acid decarboxylase in the brain. Serotonin is both a direct neurotransmitter and the precursor to melatonin (via N-acetyltransferase and ASMT). Raising serotonin supports mood regulation, reduces appetite (particularly carbohydrate craving), promotes sleep onset (via melatonin conversion), and modulates pain perception via descending pain inhibitory pathways.",
    research: [
      {finding:"5-HTP at 50-300mg/day significantly reduces depression symptoms comparable to a modest dose of SSRIs in multiple controlled trials, with faster onset in some studies.", quality:"Good"},
      {finding:"5-HTP significantly reduces fibromyalgia symptoms (pain, stiffness, anxiety, fatigue) in a double-blind RCT at 100mg three times daily.", quality:"Good"},
      {finding:"5-HTP significantly reduces carbohydrate intake and appetite in overweight women, producing greater weight loss compared to placebo in a 12-week RCT.", quality:"Moderate"},
    ],
    bestForm: "5-HTP from Griffonia simplicifolia seed extract with purity certification important. 50-100mg/day for general wellness; 100-200mg for clinical applications. Enteric-coated capsules prevent peripheral serotonin conversion, reducing serotonin syndrome risk from gut-level conversion.",
    avoid: "ABSOLUTE CONTRAINDICATION: Do not combine with MAOIs, SSRIs, SNRIs, tramadol, or any serotonergic agents as there is a life-threatening serotonin syndrome risk. Use with caution in bipolar disorder. Do not take more than 300mg/day without medical supervision.",
    synergies: [
      {name:"Vitamin B Complex", reason:"Pyridoxal-5-phosphate (active B6) is the essential cofactor for aromatic amino acid decarboxylase, the enzyme that converts 5-HTP to serotonin. Without adequate B6, 5-HTP conversion is significantly impaired."},
      {name:"Magnesium Glycinate", reason:"Both support GABA-ergic calming and sleep. 5-HTP raises serotonin (and therefore melatonin) while magnesium promotes physical relaxation. Together they address mood and sleep comprehensively."},
      {name:"Melatonin", reason:"5-HTP raises serotonin which is the precursor to melatonin. Evening 5-HTP may raise melatonin via natural conversion. Adding melatonin directly provides the sleep signal more reliably if 5-HTP alone is insufficient."},
    ],
    stackNotes: "Start at 50mg before bed and assess tolerance as GI nausea is the main side effect, reduced by starting low and taking with food. Take in the evening as the melatonin conversion pathway makes evening timing most relevant for sleep support. NEVER combine with prescription serotonergic medications.",
    whoFor: "People with mild-moderate depression or anxiety (NOT on SSRIs), anyone with sleep difficulties related to poor serotonin-melatonin conversion, women with PMS mood symptoms, people with fibromyalgia. NOT for anyone taking serotonergic medications as this is an absolute restriction.",
  },
  "Valerian Root": {
    summary: "Valerian (Valeriana officinalis) root is one of the most widely used herbal sleep aids with the longest clinical history of any botanical for insomnia. Its mechanism is GABA modulation, the same pathway as prescription benzodiazepines and Z-drugs, but with significantly less potency and without dependence risk at standard doses.",
    mechanism: "Valerianic acids and valerenic acid inhibit GABA-A transaminase, the enzyme that breaks down GABA, increasing synaptic GABA levels. Valerenic acid also acts as a partial agonist at GABA-A receptors (the same receptor targeted by benzodiazepines) and inhibits synaptic reuptake of GABA. Additionally, valerian extract inhibits the breakdown of adenosine (a sleep-promoting purine nucleoside) and may partially agonise 5-HT5a receptors involved in circadian regulation.",
    research: [
      {finding:"A meta-analysis of 16 RCTs found valerian significantly improves subjective sleep quality without producing side effects at 300-600mg, though objective sleep architecture measures show more modest effects.", quality:"Good"},
      {finding:"Valerian significantly reduces sleep onset latency in post-menopausal women in a double-blind RCT at 530mg twice daily.", quality:"Good"},
      {finding:"Valerian + lemon balm combination significantly reduces restlessness and sleep difficulties in children in a prospective observational study, one of the few natural sleep interventions with paediatric data.", quality:"Moderate"},
    ],
    bestForm: "Standardised valerian root extract standardised to 0.8% valerenic acid content at 300-600mg 30-60 minutes before bed. Aqueous extracts are used in most clinical trials. LiDelta (hydroalcoholic extract) is among the better-validated commercial forms.",
    avoid: "Mild dependence may occur with prolonged high-dose use so taper if stopping after extended use. May produce vivid dreams in some people. Avoid driving after taking. Additive CNS depression with alcohol and benzodiazepines. Valerian inhibits CYP3A4 so possible interactions with medications metabolised by this enzyme.",
    synergies: [
      {name:"Lemon Balm", reason:"The most clinically validated valerian pairing with multiple published RCTs testing this combination specifically. Valerian provides the stronger GABA-ergic sedative anchor while lemon balm contributes anxiolytic and mild cognitive benefits."},
      {name:"Magnesium Glycinate", reason:"Valerian's GABA mechanism and magnesium's NMDA modulation produce additive physical relaxation. Together they address both neurotransmitter and mineral deficiency components of poor sleep."},
      {name:"L-Theanine", reason:"L-theanine's alpha-wave induction and valerian's GABA-ergic effect reduce both cognitive and physical barriers to sleep onset, complementary mechanisms for comprehensive sleep support."},
    ],
    stackNotes: "300-600mg 30-60 minutes before bed. Effects accumulate over 2-4 weeks of consistent use as valerian is more effective with regular nightly use than as an acute sleep aid. The characteristic strong smell is harmless. Take consistently rather than reactively.",
    whoFor: "People with sleep onset difficulties, adults with chronic insomnia as part of a multi-modal strategy, anyone seeking natural GABA support without pharmaceutical sedatives, post-menopausal women with sleep disruption, people with anxiety-related sleep difficulty.",
  },
  "Lemon Balm": {
    summary: "Lemon balm (Melissa officinalis) is a mild anxiolytic and GABA-supportive herb from the mint family with unique applications for situational anxiety, children's sleep, and cognitive calmness. Its evidence profile is modest but it is well-tolerated, pleasant, and has a specific advantage for daytime calm without sedation.",
    mechanism: "Rosmarinic acid in lemon balm is a potent inhibitor of GABA-transaminase, increasing synaptic GABA availability in the anxiolytic pathways of the amygdala and prefrontal cortex. Lemon balm also has mild acetylcholinesterase inhibitory activity (increasing acetylcholine, beneficial for memory) and anxiolytic effects mediated through 5-HT receptor modulation. The terpene eugenol provides mild COX-2 inhibition.",
    research: [
      {finding:"Lemon balm (600mg) significantly reduces stress and anxiety and improves mood in a randomised controlled trial with laboratory-induced psychological stress, with effects visible within 3 hours.", quality:"Good"},
      {finding:"Lemon balm + valerian combination significantly improves sleep quality and reduces sleep onset time versus placebo in a double-blind RCT in adults with mild insomnia.", quality:"Good"},
      {finding:"Cyracos (standardised lemon balm extract) at 300mg twice daily significantly reduces anxiety symptoms and insomnia over 15 days in a pilot RCT.", quality:"Moderate"},
    ],
    bestForm: "Standardised lemon balm extract standardised to rosmarinic acid content (minimum 5%) at 300-600mg. Cyracos is a validated commercial extract. Lemon balm tea provides meaningful phytochemicals at milder and more variable doses than standardised extracts.",
    avoid: "Very safe with no known serious drug interactions at standard doses. May have mild additive sedation with CNS depressants. Theoretical thyroid peroxidase inhibition at very high long-term doses means caution in hypothyroidism.",
    synergies: [
      {name:"Valerian Root", reason:"The most clinically validated combination in herbal sleep medicine with multiple published RCTs testing this pairing specifically. Valerian provides the stronger GABA-ergic sedative anchor while lemon balm contributes anxiolytic and mild cognitive benefits."},
      {name:"Magnesium Glycinate", reason:"Magnesium provides the mineral GABA-supportive component while lemon balm provides the botanical GABA-enzymatic component. Together they address GABA deficiency from both nutritional and phytochemical directions."},
      {name:"L-Theanine", reason:"Both promote a calm alert mental state via related but distinct mechanisms. Taken together they suit daytime stress management without sedation, while combined with valerian at bedtime for sleep support."},
    ],
    stackNotes: "300mg for daytime stress management (non-sedating at this dose); 600mg for sleep support 30-60 minutes before bed. Well-tolerated in children at appropriate doses under parental supervision, one of the few herbal options with paediatric data.",
    whoFor: "People with mild anxiety and situational stress, anyone with sleep onset difficulties (particularly combined with valerian), people wanting a plant-based daytime anxiolytic without sedation, parents seeking a gentle option for children's restlessness.",
  },
  "Lion's Mane Mushroom": {
    summary: "Lion's mane (Hericium erinaceus) is a medicinal mushroom with unique evidence for promoting nerve growth factor (NGF) synthesis, the signalling protein that promotes the growth, maintenance, and survival of neurons. It is the only natural compound with this specific mechanism, making it genuinely distinctive as a nootropic.",
    mechanism: "Hericenones (from the fruiting body) and erinacines (from the mycelium) are NGF-inducing agents that cross the blood-brain barrier and stimulate endogenous NGF production. NGF is required for the maintenance of cholinergic neurons in the basal forebrain, the neurons most affected in Alzheimer's disease. Lion's mane also promotes remyelination of peripheral nerves, reduces neuroinflammation via NF-kB inhibition, and has prebiotic effects in the gut relevant to the gut-brain axis.",
    research: [
      {finding:"Lion's mane (3g/day) significantly improved mild cognitive impairment scores versus placebo in a 16-week RCT in adults aged 50-80, with scores returning to baseline 4 weeks after stopping, suggesting reversible cognitive enhancement.", quality:"Good"},
      {finding:"Lion's mane extract significantly reduces depression and anxiety scores in a 4-week study in overweight adults, possibly via the gut-brain axis or direct anti-inflammatory effects.", quality:"Moderate"},
      {finding:"In animal models of peripheral nerve damage, erinacine-rich lion's mane extract significantly accelerates nerve regeneration and functional recovery versus controls.", quality:"Good"},
    ],
    bestForm: "Dual-extract lion's mane (alcohol and hot water extraction) from the fruiting body, not mycelium-on-grain products which have high starch content and low beta-glucan and hericenone concentration. Look for >25% beta-glucans and confirmed hericenone/erinacine content. 500-1000mg/day of a high-quality fruiting body extract.",
    avoid: "Very safe with no established toxicity or serious drug interactions. Rare GI discomfort at high doses. Theoretical concern about stimulating NGF in existing cancers, not established in humans but worth noting during active cancer treatment.",
    synergies: [
      {name:"Magnesium L-Threonate", reason:"Both specifically support brain function via different mechanisms: L-threonate raises synaptic magnesium density (improving NMDA/LTP function) while lion's mane stimulates NGF (supporting neuronal maintenance). A comprehensive cognitive longevity combination."},
      {name:"Bacopa Monnieri", reason:"Lion's mane supports NGF-dependent neuronal growth while Bacopa improves the speed of memory consolidation. Complementary nootropics addressing different aspects of cognitive function."},
      {name:"Omega-3 (EPA+DHA)", reason:"DHA is the structural building block of brain cell membranes while lion's mane promotes NGF-driven neuronal maintenance. Both support long-term brain structural and functional integrity."},
    ],
    stackNotes: "500-1000mg with breakfast. Fruiting body extracts are strongly preferred over mycelium products so verify the source. Benefits are cumulative so allow 8-12 weeks of consistent use. Not a stimulant and no acute cognitive effects are expected.",
    whoFor: "Adults over 50 prioritising cognitive longevity, anyone with mild cognitive impairment, people recovering from peripheral nerve injuries, those with anxiety or depression seeking adjunctive natural support, anyone in a neurological protection protocol.",
  },
  "Bacopa Monnieri": {
    summary: "Bacopa monnieri is an Ayurvedic herb (Brahmi) with the most consistent clinical evidence of any botanical for improving memory consolidation, specifically the rate at which new information is encoded into long-term memory. Its effects develop slowly and persist beyond supplementation.",
    mechanism: "Bacosides A and B enhance the activity of protein kinase in the hippocampus and increase hippocampal synaptogenesis. Bacopa also modulates the serotonergic system (increasing 5-HT2A receptor expression), reduces beta-amyloid aggregation in hippocampal neurons (relevant to Alzheimer's risk), inhibits acetylcholinesterase (increasing acetylcholine availability), and has significant antioxidant activity in neuronal tissue via superoxide dismutase and catalase upregulation.",
    research: [
      {finding:"A meta-analysis of 6 RCTs found Bacopa significantly improves the speed of information processing and memory consolidation versus placebo.", quality:"Good"},
      {finding:"Bacopa at 300-450mg/day for 12 weeks significantly reduces the rate of forgetting newly learned information, the most distinctive finding as most nootropics improve encoding but not consolidation.", quality:"Good"},
      {finding:"Bacopa significantly reduces anxiety scores and cortisol levels in a 12-week RCT, providing secondary anxiolytic benefits alongside cognitive enhancement.", quality:"Good"},
    ],
    bestForm: "Bacopa monnieri standardised to 50% bacosides at 300-450mg/day with food. Synapsa and BacoMind are clinically validated branded forms. Allow 12 weeks minimum as Bacopa is the slowest-onset nootropic of all the well-evidenced options.",
    avoid: "GI side effects (nausea, cramping) are the primary issue so always take with a fatty meal. Effects may initially include very slight cognitive slowing as synaptic consolidation occurs. This is transient. Avoid in active hypothyroidism without supervision.",
    synergies: [
      {name:"Lion's Mane Mushroom", reason:"Lion's mane supports NGF-dependent neuronal growth while Bacopa improves the speed of memory consolidation. Complementary nootropics addressing different aspects of cognitive function."},
      {name:"Phosphatidylserine", reason:"Phosphatidylserine supports cell membrane fluidity and acetylcholine release while Bacopa inhibits acetylcholinesterase (preserving that acetylcholine). Together they maximise cholinergic cognitive signalling."},
      {name:"L-Theanine", reason:"L-theanine reduces the mild cognitive slowing that can accompany initial Bacopa supplementation. Together they support calm focus: L-theanine for the present, Bacopa for long-term memory improvement."},
    ],
    stackNotes: "Always take with the fattiest meal of the day as absorption is dramatically food-dependent. Morning with breakfast is ideal. 12 weeks minimum before evaluating as cognitive effects in RCTs are measured at 12 weeks. The delayed onset is the mechanism (slow structural synaptic change), not a failure of the supplement.",
    whoFor: "Students and professionals with high memory demands, adults over 50 concerned about memory decline, anyone wanting to improve learning speed and retention, people with mild anxiety who also want cognitive support, those pursuing long-term neuroprotection.",
  },
  "Phosphatidylserine": {
    summary: "Phosphatidylserine is a phospholipid making up approximately 15% of the brain's neuronal cell membranes and is the only supplement with an FDA-qualified health claim for reducing cognitive decline risk. It supports membrane-dependent signalling processes critical for memory, learning, and stress response.",
    mechanism: "Phosphatidylserine is a structural component of neuronal plasma membranes, concentrated in the inner leaflet where it participates in signal transduction cascades (protein kinase C activation, Akt/PI3K signalling). It is required for acetylcholine release from presynaptic terminals, modulates cortisol release from the adrenal gland (reducing HPA axis overactivation), and supports dopamine and noradrenaline metabolism. Endogenous PS synthesis declines with age and DHA availability.",
    research: [
      {finding:"Phosphatidylserine (300-400mg/day) significantly improves memory, learning, and cognitive performance in adults with age-associated memory impairment in multiple double-blind RCTs, leading to the FDA qualified health claim.", quality:"Good"},
      {finding:"PS significantly reduces cortisol response to exercise-induced and psychological stress tests in healthy men, demonstrating a clear neuroendocrine effect on the HPA axis.", quality:"Good"},
      {finding:"PS + DHA combination significantly improves ADHD symptom scores in children in a 30-week double-blind RCT.", quality:"Good"},
    ],
    bestForm: "Phosphatidylserine from soy or sunflower lecithin as sunflower-derived PS is preferred for those avoiding soy or GMO sources. 100-300mg/day with food. Most clinical trials used 300mg/day in divided doses. Sharp PS and SerinAid are validated commercial forms.",
    avoid: "Blood-thinning potential at high doses means caution with anticoagulants. GI upset at higher doses so divide into two doses with food. Soy-derived PS should be avoided in confirmed soy allergy.",
    synergies: [
      {name:"Omega-3 (EPA+DHA)", reason:"DHA is the preferred fatty acid incorporated alongside phosphatidylserine in neuronal membranes. PS requires DHA for optimal incorporation and function. Supplementing both is more effective than PS alone. PS+DHA is the validated combination in ADHD and cognitive ageing trials."},
      {name:"Bacopa Monnieri", reason:"PS supports acetylcholine release while Bacopa inhibits its breakdown. Together they maximise cholinergic neurotransmission, the system most critical for memory encoding and retrieval."},
      {name:"Lion's Mane Mushroom", reason:"PS provides the membrane substrate quality for neuronal function while lion's mane provides the NGF signal that maintains those neurons. A structural plus trophic support combination for neuronal health."},
    ],
    stackNotes: "100-300mg with food, ideally with the meal containing the most fat. Morning or early afternoon as it supports alertness. Divide into 100-200mg twice daily if taking 300mg total. Most effective as a long-term supplement so allow 8-12 weeks for full cognitive assessment.",
    whoFor: "Adults over 50 with age-associated memory impairment, students and professionals with high cognitive demands, people with ADHD (particularly children, consult paediatrician), athletes needing to manage cortisol and cognitive recovery.",
  },
  "NMN": {
    summary: "Nicotinamide mononucleotide is a direct precursor to NAD+, the coenzyme central to cellular energy production, DNA repair, and sirtuin-mediated longevity pathways. NAD+ declines approximately 50% between ages 40 and 60, and NMN supplementation is one of the most studied approaches to restoring it.",
    mechanism: "NAD+ is required by: complex I of the mitochondrial electron transport chain (energy production), PARP enzymes (DNA repair, critical for cancer prevention), sirtuin deacetylases SIRT1-7 (which regulate cellular stress responses, mitochondrial biogenesis, inflammation, and circadian rhythms), and CD38 (the major NAD+ consumer that increases with inflammation and ageing). NMN enters NAD+ synthesis via the Slc12a8 transporter, a more efficient route for some tissues than nicotinamide riboside (NR).",
    research: [
      {finding:"NMN supplementation (250mg/day) significantly increases whole blood NAD+ levels in healthy middle-aged adults, with increases detectable within 2 weeks in the first human pharmacokinetic RCT.", quality:"Good"},
      {finding:"NMN (250mg/day) significantly improves muscle insulin sensitivity in post-menopausal women with prediabetes in a 10-week RCT, the strongest human metabolic evidence for NMN.", quality:"Good"},
      {finding:"NMN supplementation (300mg/day) significantly improves sleep quality and physical performance scores in healthy older adults versus placebo in a Japanese RCT.", quality:"Moderate"},
    ],
    bestForm: "Crystalline beta-NMN (the biologically active isomer). Sublingual NMN has demonstrated superior plasma elevation to oral capsules in pharmacokinetic comparisons, bypassing first-pass intestinal degradation. 250-500mg/day is the clinically studied range. Uthever is the most validated branded NMN with human RCT data.",
    avoid: "Generally very safe with no established serious adverse effects in human trials. Theoretical concern: NAD+ increases CD38 activity in some contexts. Not for use in active cancer treatment without oncology guidance. Long-term human safety data beyond 2-3 years is still accumulating.",
    synergies: [
      {name:"Resveratrol", reason:"The most established longevity combination: NMN raises NAD+ (the substrate) while resveratrol activates sirtuins (the NAD+-dependent enzymes). NMN provides the fuel and resveratrol activates the engine."},
      {name:"CoQ10 Ubiquinol", reason:"NAD+ (raised by NMN) drives complex I of the mitochondrial electron transport chain while CoQ10 carries electrons through complex I to III. Addressing both components maximises mitochondrial function."},
      {name:"Alpha Lipoic Acid", reason:"ALA is a cofactor for the NAD+-dependent pyruvate dehydrogenase and alpha-ketoglutarate dehydrogenase complexes. Raising NAD+ via NMN amplifies ALA's benefit in these critical energy pathway enzymes."},
    ],
    stackNotes: "Take in the morning as NMN has mild energising effects consistent with circadian NAD+ physiology. Sublingual: hold under tongue for 60-90 seconds. Stack with resveratrol for sirtuin activation synergy. Allow 8-12 weeks to assess benefits. Energy, sleep, and muscle function are the most reported early outcomes.",
    whoFor: "Adults over 40 pursuing longevity and metabolic optimisation, people with insulin resistance or pre-diabetes, anyone experiencing age-related energy decline, athletes wanting mitochondrial support, those with family history of metabolic or neurodegenerative disease.",
  },
  "PQQ": {
    summary: "Pyrroloquinoline quinone is a micronutrient that serves as a redox cofactor and is the only known compound that promotes mitochondrial biogenesis, the creation of new mitochondria, in humans. It works synergistically with CoQ10 and NMN on mitochondrial quantity and quality.",
    mechanism: "PQQ activates CREB (cAMP response element-binding protein) and PGC-1alpha, the master regulator of mitochondrial biogenesis, resulting in measurable increases in mitochondrial number per cell. It is also a potent antioxidant within the mitochondrial matrix, protects neurons from oxidative damage and excitotoxicity (NMDA receptor-mediated neurotoxicity), and reduces NF-kB-driven inflammation. Unlike most antioxidants, PQQ can cycle through thousands of redox reactions before being consumed.",
    research: [
      {finding:"PQQ supplementation (20mg/day) significantly increased indicators of mitochondrial biogenesis in healthy adults in a placebo-controlled crossover study.", quality:"Good"},
      {finding:"PQQ (20mg) + CoQ10 (300mg) significantly improved memory and attention in older adults versus placebo in a 12-week RCT, the most validated combination for PQQ's cognitive effects.", quality:"Good"},
      {finding:"PQQ significantly reduces markers of systemic inflammation (IL-6, CRP) and oxidative stress in a healthy adult RCT.", quality:"Moderate"},
    ],
    bestForm: "BioPQQ (disodium salt of PQQ) is the most bioavailable and clinically studied form, used in all major human RCTs. 10-20mg/day is the evidenced dose range. No benefit has been demonstrated above 20mg/day. Typically sold in combination with CoQ10, the validated pairing.",
    avoid: "Very safe at studied doses (up to 20mg/day). Limited long-term human safety data, though decades of dietary exposure via fermented foods (natto particularly) suggests no significant concerns.",
    synergies: [
      {name:"CoQ10 Ubiquinol", reason:"PQQ promotes mitochondrial biogenesis (more mitochondria) while CoQ10 ensures those mitochondria function efficiently (electron transport). This is the canonical PQQ combination, used together in the RCT showing cognitive improvement."},
      {name:"NMN", reason:"NMN raises NAD+ for existing mitochondria while PQQ creates new mitochondria. Together they address both quantity and substrate availability, comprehensive mitochondrial energy optimisation."},
      {name:"Lion's Mane Mushroom", reason:"PQQ promotes mitochondrial health in neurons while lion's mane promotes NGF-driven neuronal maintenance. Both support long-term brain energy and structural integrity via different cellular mechanisms."},
    ],
    stackNotes: "10-20mg PQQ with a meal alongside CoQ10 as both are fat-soluble and absorbed together with food. Morning with the fattiest meal. Most valuable as part of a mitochondrial health stack rather than standalone. Effects are subtle and most measurable with objective testing.",
    whoFor: "Adults over 45 pursuing mitochondrial and cognitive longevity, people with fatigue or low energy resistant to other interventions, anyone already taking CoQ10 wanting to add the biogenesis component, endurance athletes, those with family history of mitochondrial or neurodegenerative disease.",
  },
  "Spirulina": {
    summary: "Spirulina is a blue-green algae (cyanobacterium) that is one of the most nutrient-dense foods on earth by weight, with approximately 60-70% complete protein, rich in B vitamins, iron, gamma-linolenic acid (GLA), and the unique phytochemical phycocyanin.",
    mechanism: "Phycocyanin, the blue pigment unique to spirulina, is a potent antioxidant and anti-inflammatory compound that inhibits COX-2, leukotriene production, and NF-kB. It also chelates iron in a haeme-like complex that may be more bioavailable than non-haeme plant iron. Spirulina's protein (60-70% by dry weight, complete amino acid profile) provides all essential amino acids. Its GLA content supports skin health and prostaglandin balance.",
    research: [
      {finding:"Spirulina supplementation (4.5g/day) significantly reduces total cholesterol, LDL, and triglycerides while raising HDL in a meta-analysis of 7 RCTs.", quality:"Good"},
      {finding:"Spirulina at 3g/day significantly reduces muscle damage (CK, LDH) and fatigue during exercise, extending time to exhaustion in trained men in a double-blind RCT.", quality:"Good"},
      {finding:"Spirulina supplementation significantly reduces fasting blood glucose and HbA1c in type 2 diabetes patients over 12 weeks in a controlled trial.", quality:"Good"},
      {finding:"Spirulina significantly reduces allergic rhinitis symptoms (nasal discharge, sneezing, congestion) compared to placebo in a double-blind RCT, consistent with phycocyanin's anti-allergic effects.", quality:"Good"},
    ],
    bestForm: "Certified spirulina powder or tablets as organic certification reduces heavy metal contamination risk (spirulina accumulates environmental toxins). USDA organic or Informed Sport certified products are preferred. 3-10g/day is the therapeutic range.",
    avoid: "Contamination risk from unverified sources as spirulina can contain microcystin toxins, heavy metals, and bacterial contaminants. Phenylketonuria (PKU) due to high phenylalanine content. Autoimmune conditions as spirulina may stimulate immune function and worsen autoimmune flares. The B12 in spirulina is pseudocobalamin and not biologically active. Do not rely on spirulina for B12.",
    synergies: [
      {name:"Vitamin C", reason:"Vitamin C enhances absorption of spirulina's non-haeme iron, and its antioxidant activity complements phycocyanin's anti-inflammatory effects."},
      {name:"Iron Bisglycinate", reason:"Spirulina provides dietary iron that may partially reduce supplemental iron requirements. For confirmed deficiency, both together maximise iron repletion."},
      {name:"Magnesium Glycinate", reason:"Spirulina's broad micronutrient profile is complemented by targeted magnesium supplementation as magnesium is frequently insufficient in spirulina despite its mineral density, given the high ATP production demands it supports."},
    ],
    stackNotes: "3-5g/day mixed into smoothies, water, or yoghurt. Organic certification is non-negotiable for spirulina safety. Do not rely on spirulina's B12 content and supplement B12 separately. Start at 1g/day and build up to minimise initial GI adjustment.",
    whoFor: "Vegans and vegetarians (complete protein, iron, GLA from a plant source), people with high exercise demands, anyone wanting broad micronutrient support in a single food, people with seasonal allergies, those with elevated cholesterol wanting plant-based support.",
  },
};

const INTERACTIONS = [
  {a:"Iron Bisglycinate",    b:"Zinc Bisglycinate",    severity:"moderate", note:"Iron and zinc compete for absorption. Take at least 2 hours apart."},
  {a:"Iron Bisglycinate",    b:"Magnesium Glycinate",  severity:"moderate", note:"Magnesium inhibits iron absorption. Separate by 2+ hours."},
  {a:"Iron Bisglycinate",    b:"Calcium Citrate",      severity:"moderate", note:"Calcium significantly reduces iron absorption. Take at least 2 hours apart."},
  {a:"Iron Bisglycinate",    b:"Vitamin D3 + K2",      severity:"mild",     note:"Calcium in D3/K2 formulas can reduce iron absorption. Separate if possible."},
  {a:"Iron Bisglycinate",    b:"Berberine",            severity:"moderate", note:"Berberine may reduce iron absorption. Separate by 2+ hours."},
  {a:"Iron Bisglycinate",    b:"Psyllium Husk",        severity:"moderate", note:"Fibre binds to iron and reduces absorption. Take iron away from psyllium."},
  {a:"Zinc Bisglycinate",    b:"Magnesium Glycinate",  severity:"mild",     note:"High-dose zinc can deplete magnesium over time. Monitor with long-term use."},
  {a:"Zinc Bisglycinate",    b:"Copper Bisglycinate",  severity:"moderate", note:"Zinc and copper compete directly. Long-term zinc use requires small copper supplementation."},
  {a:"Zinc Bisglycinate",    b:"Calcium Citrate",      severity:"mild",     note:"Calcium can reduce zinc absorption. Space apart where possible."},
  {a:"Zinc Bisglycinate",    b:"NAC",                  severity:"mild",     note:"NAC can chelate zinc. Take a few hours apart if using both."},
  {a:"Calcium Citrate",      b:"Magnesium Glycinate",  severity:"mild",     note:"Calcium and magnesium compete at high doses. Separate if taking large amounts."},
  {a:"Vitamin B12",          b:"Psyllium Husk",        severity:"mild",     note:"Fibre may slightly reduce B12 absorption. Take separately."},
  {a:"Ashwagandha KSM-66",   b:"Vitamin B12",          severity:"mild",     note:"Both support thyroid function. Generally fine — monitor if on thyroid medication."},
  {a:"Black Cohosh",         b:"DIM",                  severity:"mild",     note:"Both influence oestrogen. Fine for most — avoid in oestrogen-sensitive conditions without medical guidance."},
  {a:"5-HTP",                b:"Ashwagandha KSM-66",   severity:"mild",     note:"Both affect serotonin/cortisol. Generally safe — monitor mood closely."},
  {a:"DHEA",                 b:"Maca Root",            severity:"mild",     note:"Both influence hormone levels. Introduce one at a time to avoid excess stimulation."},
  {a:"Melatonin",            b:"5-HTP",                severity:"mild",     note:"Both affect sleep neurotransmitters. Fine at low doses — avoid high doses simultaneously."},
  {a:"Melatonin",            b:"Valerian Root",        severity:"mild",     note:"Both are sedating. Combining at standard doses is fine but may cause grogginess."},
  {a:"Rhodiola Rosea",       b:"Panax Ginseng",        severity:"mild",     note:"Both are stimulating adaptogens. Combining may cause overstimulation in sensitive individuals."},
  {a:"Selenium",             b:"Iodine",               severity:"none",     note:"Ideal thyroid pair — selenium is essential for converting T4 to active T3."},
  {a:"Berberine",            b:"Alpha Lipoic Acid",    severity:"none",     note:"Excellent blood sugar combination. Both reduce insulin resistance via different mechanisms."},
  {a:"Rhodiola Rosea",       b:"Ashwagandha KSM-66",   severity:"none",     note:"Complementary adaptogens — rhodiola energises, ashwagandha calms. Ideal daytime/evening split."},
  {a:"Melatonin",            b:"Ashwagandha KSM-66",   severity:"none",     note:"Complementary sleep support — ashwagandha lowers cortisol, melatonin signals sleep onset."},
  {a:"Melatonin",            b:"L-Theanine",           severity:"none",     note:"Excellent jet lag and insomnia pair. L-theanine relaxes without sedation; melatonin resets the clock."},
  {a:"Rhodiola Rosea",       b:"Melatonin",            severity:"none",     note:"Good jet lag combination — rhodiola fights daytime fatigue, melatonin resets sleep timing."},
  {a:"CoQ10 Ubiquinol",      b:"Omega-3 (EPA+DHA)",    severity:"none",     note:"Excellent cardiovascular combination. Both fat-soluble — take together with a fatty meal."},
  {a:"CoQ10 Ubiquinol",      b:"NMN",                  severity:"none",     note:"Both support mitochondrial energy production via different pathways. Strong longevity stack."},
  {a:"CoQ10 Ubiquinol",      b:"Alpha Lipoic Acid",    severity:"none",     note:"Both support mitochondrial function and regenerate each other's antioxidant capacity."},
  {a:"Magnesium Glycinate",  b:"L-Theanine",           severity:"none",     note:"Outstanding sleep and anxiety combination — synergistic calming effect before bed."},
  {a:"Magnesium Glycinate",  b:"Glycine",              severity:"none",     note:"Potent sleep combination. Glycine and magnesium work through complementary pathways."},
  {a:"Magnesium Glycinate",  b:"Electrolytes",         severity:"none",     note:"Comprehensive hydration and muscle support. Especially useful for athletes and jet lag."},
  {a:"Vitamin D3 + K2",      b:"Omega-3 (EPA+DHA)",    severity:"none",     note:"Both fat-soluble — take together with a meal for enhanced absorption of both."},
  {a:"Vitamin D3 + K2",      b:"Calcium Citrate",      severity:"none",     note:"Classic bone health trio. K2 directs calcium to bones, not arteries."},
  {a:"Vitamin D3 + K2",      b:"Magnesium Glycinate",  severity:"none",     note:"Magnesium is required to activate vitamin D. Essential pairing for full benefit."},
  {a:"Vitamin D3 + K2",      b:"Creatine Monohydrate", severity:"none",     note:"Both support muscle and bone function. Strong combination for people over 40."},
  {a:"Collagen Peptides",    b:"Vitamin C",            severity:"none",     note:"Vitamin C is essential for collagen synthesis. Always take together."},
  {a:"Collagen Peptides",    b:"Glycine",              severity:"none",     note:"Glycine is the most abundant collagen amino acid. Together they maximise connective tissue support."},
  {a:"Collagen Peptides",    b:"Cissus Quadrangularis",severity:"none",     note:"Excellent tendon recovery pair — Cissus accelerates repair, collagen provides building blocks."},
  {a:"Cissus Quadrangularis",b:"Vitamin C",            severity:"none",     note:"Vitamin C enhances Cissus's connective tissue repair benefits. Ideal injury recovery combination."},
  {a:"L-Theanine",           b:"Ashwagandha KSM-66",   severity:"none",     note:"Complementary stress reducers — L-theanine acts fast, ashwagandha builds resilience over weeks."},
  {a:"Lion's Mane Mushroom", b:"Bacopa Monnieri",      severity:"none",     note:"Complementary nootropics — lion's mane supports nerve growth factor, bacopa improves memory."},
  {a:"Lion's Mane Mushroom", b:"Omega-3 (EPA+DHA)",    severity:"none",     note:"Both support brain structure and neuroplasticity. Strong cognitive combination."},
  {a:"Phosphatidylserine",   b:"Omega-3 (EPA+DHA)",    severity:"none",     note:"Both are structural brain cell membrane components. Strong combination for cognitive ageing."},
  {a:"Glucosamine Sulphate", b:"Chondroitin Sulphate", severity:"none",     note:"Classic joint combination with the strongest clinical evidence. Most trials studied these together."},
  {a:"Glucosamine Sulphate", b:"MSM",                  severity:"none",     note:"Complementary joint support — MSM adds anti-inflammatory action to glucosamine's structural benefits."},
  {a:"Boswellia Serrata",    b:"Omega-3 (EPA+DHA)",    severity:"none",     note:"Powerful anti-inflammatory duo — different mechanisms, additive effect for joint and tendon pain."},
  {a:"Probiotics",           b:"L-Glutamine",           severity:"none",     note:"Excellent gut health combination — L-glutamine repairs the lining, probiotics restore bacteria."},
  {a:"Resveratrol",          b:"NMN",                  severity:"none",     note:"Resveratrol activates sirtuins that NMN fuels. Classic longevity stack."},
  {a:"Quercetin",            b:"Vitamin C",            severity:"none",     note:"Vitamin C regenerates quercetin's antioxidant activity. Both enhance immunity synergistically."},
  {a:"Spirulina",            b:"Vitamin C",            severity:"none",     note:"Vitamin C enhances iron absorption from spirulina. Good combination for plant-based iron support."},
  {a:"Folate (Methylfolate)",b:"Vitamin B12",          severity:"none",     note:"Folate and B12 work together in methylation. Always supplement together."},
  {a:"5-HTP",                b:"Vitamin B12",          severity:"none",     note:"B12 supports serotonin synthesis alongside 5-HTP. Useful mood support combination."},
  {a:"Maca Root",            b:"Zinc Bisglycinate",    severity:"none",     note:"Both support hormone production and libido. Useful for testosterone support in men and women."},
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /data/protocols.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const PROTOCOL_CATEGORIES = {
  "Life Stage":  ["Foundation Stack","Women Over 40","Post-Menopause","Hormone Balance (Women)","Men Over 40","Busy Mum","Busy Dad"],
  "Performance": ["Strength & Muscle","Endurance & Cardio","Recovery & Repair"],
  "Condition":   ["Muscle Tightness","Tendon & Injury","Joint Pain","Gut Health Reset"],
  "Lifestyle":   ["Sleep & Stress","Jet Lag Recovery","Energy & Focus","Longevity & Ageing"],
};
const PROTOCOLS = {
  "Foundation Stack":        {icon:"🏛",color:C.teal,    description:"The evidence-based starting point. Addresses the four most common deficiencies across all demographics.",supplements:["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Vitamin B12"]},
  "Women Over 40":           {icon:"✨",color:C.purple,   description:"Targeted support for perimenopause, hormonal fluctuation, bone density, and energy decline.",supplements:["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Vitamin B12","Iron Bisglycinate","Collagen Peptides","DIM","Calcium Citrate"]},
  "Post-Menopause":          {icon:"🌸",color:C.pink,     description:"Bone protection, cardiovascular support, and hormonal balance post-menopause.",supplements:["Vitamin D3 + K2","Calcium Citrate","Magnesium Glycinate","Omega-3 (EPA+DHA)","Collagen Peptides","Black Cohosh","CoQ10 Ubiquinol","Boron"]},
  "Hormone Balance (Women)": {icon:"⚖",color:"#F472B6",  description:"Supports healthy oestrogen metabolism, reduces PMS, addresses perimenopause symptoms.",supplements:["DIM","Magnesium Glycinate","Vitamin B Complex","Omega-3 (EPA+DHA)","Selenium","Iodine","Maca Root","Folate (Methylfolate)"]},
  "Men Over 40":             {icon:"🦁",color:C.blue,     description:"Testosterone support, cardiovascular health, prostate protection, and maintaining muscle mass.",supplements:["Vitamin D3 + K2","Zinc Bisglycinate","Omega-3 (EPA+DHA)","Creatine Monohydrate","Ashwagandha KSM-66","Saw Palmetto","CoQ10 Ubiquinol","Maca Root"]},
  "Busy Mum":                {icon:"👩",color:"#F472B6",  description:"Targets energy restoration, stress resilience, immunity, and mood for the time-poor parent.",supplements:["Magnesium Glycinate","Vitamin B Complex","Iron Bisglycinate","Ashwagandha KSM-66","Vitamin D3 + K2","Omega-3 (EPA+DHA)","L-Theanine","Probiotics"]},
  "Busy Dad":                {icon:"👨",color:"#6366F1",  description:"Sustained energy, mental sharpness, and stress management for the time-poor father.",supplements:["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Vitamin B Complex","Ashwagandha KSM-66","Creatine Monohydrate","CoQ10 Ubiquinol"]},
  "Strength & Muscle":       {icon:"💪",color:C.orange,   description:"Evidence-based performance stack for building strength, increasing muscle mass, and supporting recovery.",supplements:["Creatine Monohydrate","Omega-3 (EPA+DHA)","Magnesium Glycinate","Zinc Bisglycinate","Vitamin D3 + K2","BCAA (Leucine/Isoleucine/Valine)","L-Citrulline"]},
  "Endurance & Cardio":      {icon:"🏃",color:C.green,    description:"Cardiovascular performance, reduced oxidative stress from training, and electrolyte balance.",supplements:["Omega-3 (EPA+DHA)","CoQ10 Ubiquinol","Electrolytes","Beta-Alanine","Iron Bisglycinate","Vitamin C","Rhodiola Rosea","Taurine"]},
  "Recovery & Repair":       {icon:"🔄",color:"#14B8A6",  description:"Accelerate muscle recovery, reduce post-exercise inflammation, and support tissue repair.",supplements:["Omega-3 (EPA+DHA)","Magnesium Glycinate","L-Glutamine","Collagen Peptides","Vitamin C","Taurine","BCAA (Leucine/Isoleucine/Valine)","Electrolytes"]},
  "Muscle Tightness":        {icon:"🧘",color:C.purple,   description:"Targets chronic muscle tension, cramps, and spasm.",supplements:["Magnesium Glycinate","Potassium Citrate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Taurine","Electrolytes","L-Theanine"]},
  "Tendon & Injury":         {icon:"🩹",color:C.amber,    description:"Accelerates tendon healing and connective tissue repair for tendinopathy, sprains, and overuse injuries.",supplements:["Collagen Peptides","Vitamin C","Glycine","Cissus Quadrangularis","Omega-3 (EPA+DHA)","Boswellia Serrata","MSM","Copper Bisglycinate"]},
  "Joint Pain":              {icon:"🦴",color:C.orange,   description:"Combines structural building blocks with anti-inflammatory compounds for osteoarthritis.",supplements:["Glucosamine Sulphate","Chondroitin Sulphate","MSM","Boswellia Serrata","Omega-3 (EPA+DHA)","Collagen Peptides","Vitamin D3 + K2","Quercetin"]},
  "Gut Health Reset":        {icon:"🌱",color:"#22C55E",  description:"Rebuilds the gut microbiome, heals the gut lining, reduces bloating and IBS symptoms.",supplements:["Probiotics","L-Glutamine","Digestive Enzymes","Psyllium Husk","Omega-3 (EPA+DHA)","Vitamin D3 + K2","Zinc Bisglycinate"]},
  "Sleep & Stress":          {icon:"🌙",color:"#6366F1",  description:"Comprehensive stack to reduce cortisol, improve sleep onset and quality.",supplements:["Magnesium Glycinate","Ashwagandha KSM-66","L-Theanine","Vitamin D3 + K2","5-HTP","Lemon Balm","Valerian Root"]},
  "Jet Lag Recovery":        {icon:"✈",color:"#0EA5E9",   description:"Resets circadian rhythm, combats travel fatigue, and accelerates adjustment to new time zones.",supplements:["Melatonin","Rhodiola Rosea","L-Theanine","Vitamin D3 + K2","Magnesium Glycinate","Electrolytes","Vitamin C"]},
  "Energy & Focus":          {icon:"⚡",color:C.amber,    description:"Supports mitochondrial function, cognitive performance, and sustained mental energy.",supplements:["Vitamin B12","CoQ10 Ubiquinol","Creatine Monohydrate","Omega-3 (EPA+DHA)","L-Theanine","Lion's Mane Mushroom","Rhodiola Rosea"]},
  "Longevity & Ageing":      {icon:"⏳",color:"#A78BFA",  description:"Targets biological ageing mechanisms — mitochondrial decline, inflammation, and cognitive longevity.",supplements:["NMN","Resveratrol","CoQ10 Ubiquinol","Omega-3 (EPA+DHA)","Vitamin D3 + K2","Alpha Lipoic Acid","Lion's Mane Mushroom","Quercetin"]},
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /data/affiliates.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const TAG = "peakhealthsta-20";
const amzSearch = (q) => "https://www.amazon.com/s?k=" + encodeURIComponent(q) + "&tag=" + TAG;
const AFFILIATE_LINKS = {
  "Magnesium Glycinate":     {amazon:"https://amzn.to/4bP1c4h"},
  "Vitamin D3 + K2":         {amazon:"https://amzn.to/4bpf97W"},
  "Omega-3 (EPA+DHA)":       {amazon:"https://amzn.to/4bpwq0L", alt:{label:"Myprotein",url:"https://tidd.ly/4dBxgdy"}},
  "Ashwagandha KSM-66":      {amazon:"https://amzn.to/4bvXviR"},
  "Collagen Peptides":       {amazon:"https://amzn.to/4rRi4MU", alt:{label:"Myprotein",url:"https://tidd.ly/48hrXfG"}},
  "Iron Bisglycinate":       {amazon:"https://amzn.to/3NrMJ5a"},
  "Zinc Bisglycinate":       {amazon:"https://amzn.to/4cUKPV2"},
  "Vitamin C":               {amazon:"https://amzn.to/4uxVoUt"},
  "Creatine Monohydrate":    {amazon:amzSearch("creatine monohydrate"), alt:{label:"Myprotein",url:"https://tidd.ly/3O5zEPk"}},
  "Black Cohosh":            {amazon:"https://amzn.to/4vm8pRa"},
  "Magnesium L-Threonate":   {amazon:amzSearch("magnesium l-threonate")},
  "Magnesium Citrate":       {amazon:amzSearch("magnesium citrate supplement")},
  "Magnesium Malate":        {amazon:amzSearch("magnesium malate supplement")},
  "Magnesium Taurate":       {amazon:amzSearch("magnesium taurate supplement")},
  "Magnesium Oxide":         {amazon:amzSearch("magnesium oxide supplement")},
  "Calcium Citrate":         {amazon:amzSearch("calcium citrate supplement")},
  "Selenium":                {amazon:amzSearch("selenium supplement")},
  "Iodine":                  {amazon:amzSearch("iodine supplement")},
  "Copper Bisglycinate":     {amazon:amzSearch("copper bisglycinate supplement")},
  "Potassium Citrate":       {amazon:amzSearch("potassium citrate supplement")},
  "Boron":                   {amazon:amzSearch("boron supplement")},
  "Vitamin B12":             {amazon:amzSearch("vitamin b12 methylcobalamin")},
  "Vitamin B Complex":       {amazon:amzSearch("vitamin b complex supplement")},
  "Vitamin A":               {amazon:amzSearch("vitamin a supplement")},
  "Folate (Methylfolate)":   {amazon:amzSearch("methylfolate supplement")},
  "Krill Oil":               {amazon:amzSearch("krill oil supplement")},
  "Rhodiola Rosea":          {amazon:amzSearch("rhodiola rosea supplement")},
  "Panax Ginseng":           {amazon:amzSearch("panax ginseng supplement")},
  "Maca Root":               {amazon:amzSearch("maca root supplement")},
  "Holy Basil (Tulsi)":      {amazon:amzSearch("holy basil tulsi supplement")},
  "L-Theanine":              {amazon:amzSearch("l-theanine supplement")},
  "NAC":                     {amazon:amzSearch("NAC n-acetyl cysteine supplement")},
  "L-Glutamine":             {amazon:amzSearch("l-glutamine supplement")},
  "L-Citrulline":            {amazon:amzSearch("l-citrulline supplement")},
  "Taurine":                 {amazon:amzSearch("taurine supplement")},
  "BCAA (Leucine/Isoleucine/Valine)":{amazon:amzSearch("BCAA supplement")},
  "Glycine":                 {amazon:amzSearch("glycine supplement")},
  "Beta-Alanine":            {amazon:amzSearch("beta-alanine supplement")},
  "Electrolytes":            {amazon:amzSearch("electrolyte supplement")},
  "CoQ10 Ubiquinol":         {amazon:amzSearch("CoQ10 ubiquinol supplement")},
  "Alpha Lipoic Acid":       {amazon:amzSearch("alpha lipoic acid supplement")},
  "Resveratrol":             {amazon:amzSearch("resveratrol supplement")},
  "Quercetin":               {amazon:amzSearch("quercetin supplement")},
  "Astaxanthin":             {amazon:amzSearch("astaxanthin supplement")},
  "Glucosamine Sulphate":    {amazon:amzSearch("glucosamine sulphate supplement")},
  "Chondroitin Sulphate":    {amazon:amzSearch("chondroitin sulphate supplement")},
  "MSM":                     {amazon:amzSearch("MSM supplement joint")},
  "Boswellia Serrata":       {amazon:amzSearch("boswellia serrata supplement")},
  "Cissus Quadrangularis":   {amazon:amzSearch("cissus quadrangularis supplement")},
  "Probiotics":              {amazon:amzSearch("probiotic supplement")},
  "Digestive Enzymes":       {amazon:amzSearch("digestive enzymes supplement")},
  "Psyllium Husk":           {amazon:amzSearch("psyllium husk supplement")},
  "Berberine":               {amazon:amzSearch("berberine supplement")},
  // DHEA intentionally excluded — prescription-only in UK; Amazon UK does not stock it
  // and US listings could create regulatory concerns for UK-targeted users.
  "DIM":                     {amazon:amzSearch("DIM supplement diindolylmethane")},
  "Saw Palmetto":            {amazon:amzSearch("saw palmetto supplement")},
  "Melatonin":               {amazon:amzSearch("melatonin supplement")},
  "5-HTP":                   {amazon:amzSearch("5-HTP supplement")},
  "Valerian Root":           {amazon:amzSearch("valerian root supplement")},
  "Lemon Balm":              {amazon:amzSearch("lemon balm supplement")},
  "Lion's Mane Mushroom":    {amazon:amzSearch("lion mane mushroom supplement")},
  "Bacopa Monnieri":         {amazon:amzSearch("bacopa monnieri supplement")},
  "Phosphatidylserine":      {amazon:amzSearch("phosphatidylserine supplement")},
  "NMN":                     {amazon:amzSearch("NMN supplement NAD")},
  "PQQ":                     {amazon:amzSearch("PQQ supplement")},
  "Spirulina":               {amazon:amzSearch("spirulina supplement")},
};
function isSpecificLink(name) {
  const l = AFFILIATE_LINKS[name];
  return l && l.amazon && !l.amazon.includes("/s?k=");
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /data/timing.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const TIMING_ORDER = [
  "Morning (away from food)","Morning","Morning with food","Morning (+ vitamin C)",
  "Before a meal","Before a meal (with water)",
  "Pre-workout","Around training","During or after training",
  "With a meal","With a fatty meal","With a meal (+ vitamin C)",
  "Any time","Away from meals","Post-workout or bedtime",
  "With dinner","Evening","As needed",
  "30-60 min before bed","1 hour before bed","Before bed",
];
const EFFECTS      = ["Energy","Sleep Quality","Mood","Focus","Digestion","Stress Levels","Physical Performance","Skin & Hair"];
const EFFECT_LABELS = ["","Poor","Below avg","Average","Good","Excellent"];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /logic/sorting.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function timingIndex(s) {
  const t = SUPPLEMENT_DB[s]?.timing ?? "";
  const i = TIMING_ORDER.indexOf(t);
  return i === -1 ? 999 : i;
}
function sortByTiming(stack) {
  return [...stack].sort((a, b) => timingIndex(a) - timingIndex(b));
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /logic/interactions.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function getInteractions(stack) {
  const found = [];
  for (let i = 0; i < stack.length; i++)
    for (let j = i+1; j < stack.length; j++) {
      const hit = INTERACTIONS.find(x =>
        (x.a === stack[i] && x.b === stack[j]) ||
        (x.a === stack[j] && x.b === stack[i])
      );
      if (hit) found.push({...hit, pair:[stack[i],stack[j]]});
    }
  return found;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /logic/display.js — pure colour helpers, no UI imports
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function evidenceColor(ev) {
  return ev==="Strong"?"#10B981":ev==="Good"?"#E5B83F":ev==="Moderate"?"#F59E0B":ev==="Emerging"?"#8B5CF6":"#9CA3AF";
}
function severityColor(s) {
  return s==="none"?"#10B981":s==="mild"?"#F59E0B":s==="moderate"?"#F97316":"#EF4444";
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /services/storage.js
// Abstraction over localStorage — swap body of each fn for Supabase calls later
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const storage = {
  get:    (key, fallback = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; } },
  set:    (key, value)           => { try { localStorage.setItem(key, JSON.stringify(value)); } catch(e) { if (e.name === "QuotaExceededError") { try { localStorage.removeItem("phs_old_logs_cache"); localStorage.setItem(key, JSON.stringify(value)); } catch { console.error("[Storage] Quota exceeded, could not save:", key); } } } },
  remove: (key)                  => { try { localStorage.removeItem(key); } catch {} },
};

// Bug 6: Cross-user data leak protection
function validateUserIdConsistency(expectedUserId, resourceType, data) {
  if (data?.user_id && data.user_id !== expectedUserId) {
    console.error('[SECURITY] User ID mismatch in ' + resourceType + ': expected ' + expectedUserId + ', got ' + data.user_id);
    return false;
  }
  return true;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /services/api.js — Real Supabase + Stripe integration
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const EDGE = SUPABASE_URL + "/functions/v1";

const api = {

  // ── AUTH ───────────────────────────────────────────────────────────────────
  // Passwords are handled entirely by Supabase Auth — they are bcrypt-hashed
  // server-side before storage. Your app never sees or stores raw passwords.
  signUp: async (email, password) => {
    const data = await sbAuth("signup", email, password);
    if (data.session) {
      sbSession = data.session;
      if (data.user?.id) sbSession.user_id = data.user.id;
      if (!sbSession.expires_at && sbSession.access_token) {
        sbSession.expires_at = getTokenExpiry(sbSession.access_token);
      }
    }
    return { id: data.user?.id ?? null, email: data.user?.email ?? null, plan: "free" };
  },

signIn: async (email, password) => {
  try {
    const data = await sbAuth("token?grant_type=password", email, password);
    sbSession = data;
    if (data.user?.id) sbSession.user_id = data.user.id;
    if (!sbSession.expires_at && sbSession.access_token) {
      sbSession.expires_at = getTokenExpiry(sbSession.access_token);
    }
    storage.set("phs_token", sbSession);
    let plan = "free";
    try {
      const subs = await sbFetch(
        "subscriptions?user_id=eq." + data.user.id +
        "&app_id=eq.phs_supplements&limit=1&order=created_at.desc"
      );
      if (subs?.length) {
        const sub = subs[0];
        const isActive = sub.status === "active" ||
          (["cancelled", "canceled"].includes(sub.status) && sub.current_period_end &&
           new Date(sub.current_period_end) > new Date());
        if (isActive) {
          plan = sub.plan || "free";
          if (!["free", "premium", "annual"].includes(plan)) plan = "free";
        }
      }
    } catch (e) {
      console.warn("[signIn] Could not load subscription:", e);
    }
    return { id: data.user.id, email: data.user.email, plan };
  } catch (e) {
    console.error("[signIn error]", e);
    sbSession = null;
    Sentry.captureException(e);
    throw e;
  }
},

  signOut: async () => {
    if (sbSession?.access_token) await sbSignOut(sbSession.access_token);
    sbSession = null;
  },

  // Refresh the Supabase access token using the stored refresh_token
  refreshSession: async () => {
    const token = storage.get("phs_token", null);
    if (!token?.refresh_token) throw new Error("No refresh token available");
    const res = await fetch(SUPABASE_URL + "/auth/v1/token?grant_type=refresh_token", {
      method: "POST",
      headers: { "apikey": SUPABASE_ANON, "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: token.refresh_token }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error_description ?? data.msg ?? "Token refresh failed");
    sbSession = data;
    if (!sbSession.expires_at && sbSession.access_token) {
      sbSession.expires_at = getTokenExpiry(sbSession.access_token);
    }
    storage.set("phs_token", sbSession);
    return sbSession;
  },

  // Refresh plan from Supabase (call after returning from Stripe)
loadPlan: async (userId) => {
  try {
    const subs = await sbFetch(
      "subscriptions?user_id=eq." + userId +
      "&app_id=eq.phs_supplements&limit=1&order=created_at.desc"
    );
    if (!subs?.length) return "free";
    const sub = subs[0];
    // Treat active OR cancelled-but-within-paid-period as having a valid plan
    const isActive = sub.status === "active" ||
      (["cancelled", "canceled"].includes(sub.status) && sub.current_period_end &&
       new Date(sub.current_period_end) > new Date());
    if (!isActive) return "free";
    const plan = sub.plan || "free";
    if (!["free", "premium", "annual"].includes(plan)) {
      console.warn("[loadPlan] Unrecognised plan:", plan);
      return "free";
    }
    return plan;
  } catch (e) {
    console.error("[loadPlan error]", e);
    return "free";
  }
},

  // Load full user profile row (created_at, goal, issue, onboarding_assessment, etc.)
  loadProfile: async (userId) => {
    try {
      const rows = await sbFetch("profiles?id=eq." + userId + "&select=*&limit=1");
      return rows?.[0] ?? null;
    } catch (e) {
      console.warn("[loadProfile] Failed:", e);
      return null;
    }
  },

  // Load full subscription details — syncs live data from Stripe before returning
  loadSubscription: async (userId) => {
    try {
      const res = await fetch(EDGE + "/get-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": getAuthHeader(), "apikey": SUPABASE_ANON },
        body: JSON.stringify({ userId, appId: "phs_supplements" }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  },

  // ── STRIPE CHECKOUT ────────────────────────────────────────────────────────
  // Redirects user to Stripe checkout page; plan is activated via webhook
  createCheckout: async (plan, userId, email) => {
    const res = await fetch("/api/create-checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": getAuthHeader(), "apikey": SUPABASE_ANON },
      body: JSON.stringify({ plan, userId, email, appId: "phs_supplements" }),
    });
    if (res.status === 404) throw new Error("Checkout service unavailable — contact support@peakhealthstack.com");
    const data = await res.json();
    if (data.url) window.location.href = data.url;
    else throw new Error(data.error ?? "Checkout failed");
  },

  // Cancel subscription (sets cancel_at_period_end in Stripe via Edge Function)
  cancelSubscription: async (userId) => {
    const res = await fetch(EDGE + "/cancel-subscription", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": getAuthHeader(), "apikey": SUPABASE_ANON },
      body: JSON.stringify({ userId, appId: "phs_supplements" }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? "Cancel failed: " + res.status);
    }
    return res.json();
  },

  // Resume subscription (clears cancel_at_period_end in Stripe via Edge Function)
  resumeSubscription: async (userId) => {
    const res = await fetch(EDGE + "/resume-subscription", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": getAuthHeader(), "apikey": SUPABASE_ANON },
      body: JSON.stringify({ userId, appId: "phs_supplements" }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? "Resume failed: " + res.status);
    }
    return res.json();
  },

  // ── STACK ──────────────────────────────────────────────────────────────────
  saveStack: async (userId, stack, customTimings, customProtocols, activeProtocol) => {
    // Try PATCH first (update existing row), fall back to POST (insert)
    const body = JSON.stringify({
      user_id: userId,
      stack: stack,
      custom_timings: customTimings ?? {},
      custom_protocols: customProtocols ?? [],
      active_protocol: activeProtocol ?? null,
      updated_at: new Date().toISOString(),
    });
    const existing = await sbFetch("supplement_stacks?user_id=eq." + userId + "&limit=1");
    if (existing && existing.length > 0) {
      await sbFetch("supplement_stacks?user_id=eq." + userId, {
        method: "PATCH",
        headers: { "Prefer": "return=minimal" },
        body,
      });
    } else {
      await sbFetch("supplement_stacks", {
        method: "POST",
        headers: { "Prefer": "return=minimal" },
        body,
      });
    }
  },

  loadStack: async (userId) => {
    if (!userId || typeof userId !== 'string') return { stack: [], custom_timings: {}, custom_protocols: [] };
    const rows = await sbFetch("supplement_stacks?user_id=eq." + userId + "&limit=1");
    if (rows?.[0] && !validateUserIdConsistency(userId, 'supplement_stacks', rows[0])) {
      return { stack: [], custom_timings: {}, custom_protocols: [] };
    }
    return rows?.[0] ?? { stack: [], custom_timings: {}, custom_protocols: [] };
  },

  // ── LOGS ───────────────────────────────────────────────────────────────────
  // Upsert a single log entry by (user_id, date) — requires UNIQUE constraint on those columns
  saveLog: async (userId, entry) => {
    await sbFetch("supplement_logs?on_conflict=user_id,date", {
      method: "POST",
      headers: { "Prefer": "resolution=merge-duplicates,return=minimal" },
      body: JSON.stringify({
        user_id: userId,
        date: entry.date,
        taken: entry.taken,
        effects: entry.effects,
        notes: entry.notes ?? "",
        // stack_snapshot records the stack size at time of entry so historical
        // taken/total ratios remain accurate after the stack is edited
        ...(entry.stack_snapshot != null ? { stack_snapshot: entry.stack_snapshot } : {}),
        updated_at: new Date().toISOString(),
      }),
    });
  },

  deleteLog: async (userId, date) => {
    await sbFetch(
      "supplement_logs?user_id=eq." + userId + "&date=eq." + encodeURIComponent(date),
      { method: "DELETE", headers: { "Prefer": "return=minimal" } }
    );
  },

  loadLogs: async (userId) => {
    if (!userId || typeof userId !== 'string') return [];
    const rows = await sbFetch(
      "supplement_logs?user_id=eq." + userId + "&order=date.desc&limit=90"
    );
    if (rows?.length && !rows.every(r => validateUserIdConsistency(userId, 'supplement_logs', r))) return [];
    return rows ?? [];
  },

  // ── AI USAGE ───────────────────────────────────────────────────────────────
  // Returns { used, period } for the current billing period
  getAiUsage: async (userId, plan) => {
    const period = aiUsagePeriod(plan);
    const rows = await sbFetch(
      "ai_usage?user_id=eq." + userId +
      "&app_id=eq.phs_supplements&period=eq." + period + "&limit=1"
    );
    return { used: rows?.[0]?.call_count ?? 0, period };
  },

  // Increment usage count — only called after a successful AI response
  incrementAiUsage: async (userId, plan) => {
    const period = aiUsagePeriod(plan);
    // Use the SQL function for atomic upsert+increment
    await fetch(SUPABASE_URL + "/rest/v1/rpc/increment_ai_usage", {
      method: "POST",
      headers: {
        "apikey": SUPABASE_ANON,
        "Authorization": getAuthHeader(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ p_user_id: userId, p_app_id: "phs_supplements", p_period: period }),
    });
  },
};

function aiUsagePeriod(/* plan param kept for compat */) {
  const now = new Date();
  return now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /services/aiService.js
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// All AI calls route through the Supabase ai-proxy Edge Function.
// Usage enforcement (quota checks) happens server-side in that function.
const AI_ENDPOINT = `${SUPABASE_URL}/functions/v1/ai-proxy`;

async function callAI({ system, prompt, max_tokens = 800 }) {
  // Refresh proactively so an expired session doesn't silently fail mid-call
  if (isSessionExpired()) {
    try { await api.refreshSession(); } catch (_) { /* getAuthHeader(true) will throw below */ }
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000);
  try {
    const res = await fetch(AI_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": getAuthHeader(true),
        "apikey": SUPABASE_ANON,
      },
      body: JSON.stringify({ system, prompt, max_tokens }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!res.ok) {
      if (res.status === 429) throw new Error("usage_limit");
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error ?? "API " + res.status);
    }
    const d = await res.json();
    return d.text ?? "";
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") throw new Error("Request timed out — try a shorter question.");
    if (error.message !== "usage_limit") Sentry.captureException(error);
    throw error;
  }
}

// Helpers for building compact prompts
function stackSummary(stack, dosages = {}) {
  if (!stack.length) return "no supplements yet";
  return stack.map(s => {
    const d = SUPPLEMENT_DB[s];
    const dos = dosages[s];
    const dosStr = dos?.dosage_amount ? ` ${dos.dosage_amount}${dos.dosage_unit}` : '';
    return `${s}${dosStr} (${d?.dose??"std"}, ev:${d?.evidence??"Good"})`;
  }).join("; ");
}

function logSummary(logs, limit = 7) {
  if (!logs.length) return "no logs yet";
  const last7 = logs.slice(0, limit);
  const notes = last7.filter(l=>l.notes).map(l=>`${l.date}: "${l.notes}"`).join("; ");
  // Only average over logs that actually recorded each metric — avoid diluting with 0-defaults
  const safeAvg = key => {
    const vals = last7.map(l => l.effects?.[key]).filter(v => v != null && !isNaN(Number(v)));
    return vals.length ? (vals.reduce((a,b) => a+Number(b), 0) / vals.length).toFixed(1) : null;
  };
  const avgE = safeAvg("Energy");
  const avgS = safeAvg("Sleep Quality");
  const avgM = safeAvg("Mood");
  const adh  = Math.round(last7.filter(l=>l.taken?.length>0).length/last7.length*100);
  const intensityLabels = ['','Rest','Light','Moderate','Hard','Intense'];
  const exLines = last7.filter(l=>l.effects?.exercise_intensity!=null).map(l=>`${l.date}:${intensityLabels[l.effects.exercise_intensity]??l.effects.exercise_intensity}`).join(", ");
  const metricParts = [
    avgE != null && `Energy avg ${avgE}/5`,
    avgS != null && `Sleep avg ${avgS}/5`,
    avgM != null && `Mood avg ${avgM}/5`,
  ].filter(Boolean);
  const metricsStr = metricParts.length ? metricParts.join(", ") : "no metric ratings yet";
  return `${logs.length} log entries. Last 7 days: ${metricsStr}, adherence ${adh}%.${exLines ? " Exercise intensity: "+exLines+"." : ""}${notes ? " Recent notes: "+notes : ""}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Usage tracking (client-side localStorage; swap for Supabase on backend)
//
// ─────────────────────────────────────────────────────────────────────────────
// APP-WIDE CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const APP_NAME = "Your Peak Stack";

const DISCLAIMER_SHORT = "For informational purposes only. Not medical advice.";
const DISCLAIMER_FULL = "This app is for informational and educational purposes only. It does not constitute medical advice, diagnosis, or treatment. Always consult a qualified healthcare professional before starting, changing, or stopping any supplement regimen, particularly if you have an existing health condition, are pregnant or breastfeeding, or are taking prescription medications. Supplement science evolves — information here reflects current evidence but may not be exhaustive.";
const DISCLAIMER_AI = "AI-generated content is informational only and is not a substitute for professional medical advice. Results are based on the data you provide and general supplement research — they are not personalised medical recommendations.";
const DISCLAIMER_AFFILIATE = "Some links on this page are affiliate links. Purchasing via these links supports Your Peak Stack at no extra cost to you. Affiliate links are not endorsements and do not influence the supplement information or recommendations shown in the app.";
const DISCLAIMER_SHARE_CARD = "Stack score is informational only · Not medical advice · peakhealthstack.com";
const DISCLAIMER_FTC = "For users in the United States: This app uses artificial intelligence to generate supplement analysis and recommendations. AI outputs are informational only and have not been evaluated by the Food and Drug Administration (FDA). These statements and any products mentioned are not intended to diagnose, treat, cure, or prevent any disease. The Stack Score and AI assessments are educational tools, not clinical evaluations. This site contains affiliate links — we may earn a commission at no cost to you.";

const PRIVACY_POLICY_TEXT = `Your Peak Stack — Privacy Policy
Last updated: April 2026

1. Who we are
Your Peak Stack is operated by Peak Health Stack (peakhealthstack.com), registered in Ireland.

2. Data we collect
• Email address and password (for account creation, stored securely via Supabase Auth)
• Supplement stack selections and dosages
• Daily check-in logs (supplements taken, energy/mood/sleep ratings, notes)
• AI usage counts (number of AI insight calls per month)
• Subscription and billing status (managed by Stripe — we never see your card details)
• Device type and basic usage data (for app performance)

3. How we use your data
• To provide the app's core functionality (stack tracking, logging, AI analysis)
• To manage your subscription via Stripe
• To generate AI insights using your anonymous stack and log data
• We do not sell your data to any third parties

4. AI and your data
When you use AI analysis, your stack and anonymised log summaries are sent to our AI endpoint. No personally identifiable information (name, email) is included in AI prompts.

5. Data storage
All data is stored securely via Supabase (EU region). Data is encrypted at rest and in transit.

6. Your rights
You may request deletion of your account and all associated data at any time by emailing contact@peakhealthstack.com. We will process all requests within 30 days.

7. Cookies and local storage
We use browser localStorage to store session tokens and preferences. No advertising cookies are used.

8. Third-party services
• Supabase (database and auth): supabase.com/privacy
• Stripe (payments): stripe.com/privacy
• Anthropic (AI): anthropic.com/privacy

9. Contact
For any privacy questions: contact@peakhealthstack.com`;

const TERMS_OF_SERVICE_TEXT = `Your Peak Stack — Terms of Service
Last updated: April 2026

1. Acceptance of terms
By using Your Peak Stack you agree to these Terms. If you do not agree, please do not use the app.

2. Description of service
Your Peak Stack is an educational supplement tracking tool. It provides AI-generated supplement analysis for informational purposes only. It is not a medical device and does not constitute medical advice.

3. Medical disclaimer
Nothing in this app constitutes medical advice, diagnosis, or treatment. Always consult a qualified healthcare professional before starting, changing, or stopping any supplement regimen. The Stack Score and AI analysis are informational tools only.

4. Eligibility
You must be 18 or older to use this app. By creating an account you confirm you meet this requirement.

5. Your account
You are responsible for maintaining the confidentiality of your account credentials. Notify us immediately of any unauthorised access.

6. Subscription and billing
• Free plan: 3 AI insights per month at no charge
• Premium: £9.99/month, billed monthly via Stripe
• Annual: £59.99/year, billed annually via Stripe
• Subscriptions renew automatically. Cancel anytime via your Account page.
• Refunds are handled at our discretion in line with consumer protection law.

7. Acceptable use
You may not use this app to: distribute malware, scrape data, attempt to access other users' data, or use the AI system to generate harmful or misleading health information.

8. Intellectual property
All content, design, and AI outputs generated by Your Peak Stack are the intellectual property of Peak Health Stack. You may not reproduce or redistribute them without permission.

9. Limitation of liability
To the maximum extent permitted by law, Peak Health Stack shall not be liable for any indirect, incidental, or consequential damages arising from your use of this app.

10. Changes to terms
We may update these Terms at any time. Continued use of the app following notification of changes constitutes acceptance.

11. Governing law
These Terms are governed by the laws of Ireland.

12. Contact
contact@peakhealthstack.com`;

const AI_LIMITS = {
  guest:   { total: 0  },
  free:    { total: 3  },
  premium: { total: 40 },
};
const MIN_LOGS_FOR_AI = 7;

const PLAN_PRICING = {
  premium: { monthly: 9.99, annual: 59.99 },
};

const PLAN_DETAILS = {
  free:    { price: '£0',     calls: 3,  period: 'month', label: 'Free'    },
  premium: { price: '£9.99',  calls: 40, period: 'month', label: 'Premium' },
  annual:  { price: '£59.99', calls: 40, period: 'year',  label: 'Annual',  badge: 'Save ~50%' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Default dosages per supplement (for inline dosage editing, Feature 2)
// ─────────────────────────────────────────────────────────────────────────────
const SUPPLEMENT_DEFAULTS = {
  'magnesium glycinate':   { amount: 300,  unit: 'mg'       },
  'magnesium':             { amount: 300,  unit: 'mg'       },
  'vitamin d3':            { amount: 2000, unit: 'IU'       },
  'vitamin d3 + k2':       { amount: 2000, unit: 'IU'       },
  'vitamin d':             { amount: 2000, unit: 'IU'       },
  'omega-3 (epa+dha)':     { amount: 1000, unit: 'mg'       },
  'omega-3':               { amount: 1000, unit: 'mg'       },
  'fish oil':              { amount: 1000, unit: 'mg'       },
  'vitamin b12':           { amount: 500,  unit: 'mcg'      },
  'b12':                   { amount: 500,  unit: 'mcg'      },
  'ashwagandha ksm-66':    { amount: 300,  unit: 'mg'       },
  'ashwagandha':           { amount: 300,  unit: 'mg'       },
  'iron bisglycinate':     { amount: 18,   unit: 'mg'       },
  'iron':                  { amount: 18,   unit: 'mg'       },
  'collagen peptides':     { amount: 10,   unit: 'g'        },
  'collagen':              { amount: 10,   unit: 'g'        },
  'coq10 ubiquinol':       { amount: 100,  unit: 'mg'       },
  'coq10':                 { amount: 100,  unit: 'mg'       },
  'zinc bisglycinate':     { amount: 15,   unit: 'mg'       },
  'zinc':                  { amount: 15,   unit: 'mg'       },
  'l-theanine':            { amount: 100,  unit: 'mg'       },
  'creatine monohydrate':  { amount: 5,    unit: 'g'        },
  'creatine':              { amount: 5,    unit: 'g'        },
  'vitamin c':             { amount: 500,  unit: 'mg'       },
  'vitamin k2':            { amount: 100,  unit: 'mcg'      },
  'probiotics':            { amount: 10,   unit: 'billion CFU' },
  'melatonin':             { amount: 0.5,  unit: 'mg'       },
  'rhodiola rosea':        { amount: 200,  unit: 'mg'       },
  'turmeric':              { amount: 500,  unit: 'mg'       },
  'curcumin':              { amount: 500,  unit: 'mg'       },
  'berberine':             { amount: 500,  unit: 'mg'       },
  'nac':                   { amount: 600,  unit: 'mg'       },
  'alpha lipoic acid':     { amount: 300,  unit: 'mg'       },
  'resveratrol':           { amount: 250,  unit: 'mg'       },
  'nmn':                   { amount: 250,  unit: 'mg'       },
  'quercetin':             { amount: 500,  unit: 'mg'       },
  "lion's mane mushroom":  { amount: 500,  unit: 'mg'       },
  'reishi mushroom':       { amount: 500,  unit: 'mg'       },
  'cordyceps mushroom':    { amount: 500,  unit: 'mg'       },
};

function getDefaultDosage(name) {
  if (!name) return { amount: null, unit: 'mg' };
  const key = name.toLowerCase().trim();
  if (SUPPLEMENT_DEFAULTS[key]) return { ...SUPPLEMENT_DEFAULTS[key] };
  const partial = Object.keys(SUPPLEMENT_DEFAULTS).find(k => key.includes(k) || k.includes(key));
  if (partial) return { ...SUPPLEMENT_DEFAULTS[partial] };
  return { amount: null, unit: 'mg' };
}

const ADVANCED_METRICS_KEYS = [
  'hrv_overnight','resting_hr','sleep_duration_mins','sleep_score',
  'deep_sleep_mins','rem_sleep_mins','spo2','respiration_rate',
  'skin_temp_deviation','body_battery_start','body_battery_end',
  'stress_avg','steps','active_calories',
];

function extractAdvancedMetrics(effects) {
  const out = {};
  ADVANCED_METRICS_KEYS.forEach(k => { out[k] = effects[k] ?? null; });
  return out;
}

function parseSleepDuration(raw) {
  if (raw === null || raw === '') return null;
  if (typeof raw === 'number') return raw;
  const str = String(raw).trim();
  const hm = str.match(/^(\d+)h\s*(\d+)m$/i);
  if (hm) return parseInt(hm[1]) * 60 + parseInt(hm[2]);
  const h = str.match(/^(\d+)h$/i);
  if (h) return parseInt(h[1]) * 60;
  const n = parseFloat(str);
  return isNaN(n) ? null : Math.round(n);
}

function buildAdvancedMetricsContext(log) {
  const e = log?.effects || {};
  const parts = [];
  if (e.hrv_overnight != null)       parts.push(`Overnight HRV: ${e.hrv_overnight}ms`);
  if (e.resting_hr != null)          parts.push(`Resting HR: ${e.resting_hr}bpm`);
  if (e.sleep_duration_mins != null) parts.push(`Sleep: ${Math.floor(e.sleep_duration_mins/60)}h ${e.sleep_duration_mins%60}m`);
  if (e.sleep_score != null)         parts.push(`Sleep score: ${e.sleep_score}/100`);
  if (e.deep_sleep_mins != null)     parts.push(`Deep sleep: ${e.deep_sleep_mins}mins`);
  if (e.rem_sleep_mins != null)      parts.push(`REM: ${e.rem_sleep_mins}mins`);
  if (e.spo2 != null)                parts.push(`SpO2: ${e.spo2}%`);
  if (e.respiration_rate != null)    parts.push(`Respiration: ${e.respiration_rate}br/min`);
  if (e.skin_temp_deviation != null) parts.push(`Skin temp: ${e.skin_temp_deviation > 0 ? '+' : ''}${e.skin_temp_deviation}°C`);
  if (e.body_battery_start != null)  parts.push(`Body battery wake: ${e.body_battery_start}/100`);
  if (e.body_battery_end != null)    parts.push(`Body battery end: ${e.body_battery_end}/100`);
  if (e.stress_avg != null)          parts.push(`Avg stress: ${e.stress_avg}/100`);
  if (e.steps != null)               parts.push(`Steps: ${e.steps.toLocaleString()}`);
  if (e.active_calories != null)     parts.push(`Active calories: ${e.active_calories}kcal`);
  if (!parts.length) return '';
  return `\nBiometric data:\n${parts.join(' · ')}`;
}

function getUsageKey(userId) {
  const now = new Date();
  // Month is 1-indexed and zero-padded to match the DB period format (YYYY-MM)
  return `ai_usage_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${userId}`;
}

// In-memory AI usage store — seeded from server on login, never touches localStorage.
// Clearing browser storage cannot bypass the client-side gate; the server RPC is the hard limit.
const _aiUsageMemory = new Map();

function getAiUsage(userId, plan) {
  if (!userId) return { used: 0, limit: 0, canUse: false };
  const key   = getUsageKey(userId);
  const used  = _aiUsageMemory.get(key) ?? 0;
  const limit = AI_LIMITS[planNormalise(plan)]?.total ?? 0;
  const pct   = limit > 0 ? Math.round(used / limit * 100) : 100;
  return { used, limit, canUse: used < limit, pct };
}

function incrementAiUsage(userId) {
  if (!userId) return;
  const key = getUsageKey(userId);
  _aiUsageMemory.set(key, (_aiUsageMemory.get(key) ?? 0) + 1);
}

// Hook for components to read + consume AI usage
function useAiUsage() {
  const { user } = useUser();
  const [usage, setUsage] = useState(() => getAiUsage(user?.id, user?.plan));

  const refresh = () => setUsage(getAiUsage(user?.id, user?.plan));

  // consume() now returns a commit function — call commit() only after a
  // successful AI response. This prevents counting failed calls.
  const consume = () => {
    if (!user?.id) return null;
    const u = getAiUsage(user.id, user.plan);
    if (!u.canUse) return null;
    // Return a commit fn — caller must call this after successful AI response.
    // Supabase increment is handled server-side by the ai-proxy Edge Function.
    const commit = () => {
      incrementAiUsage(user.id);
      refresh();
      // Broadcast so other useAiUsage instances (e.g. AiUsageBanner) also refresh
      window.dispatchEvent(new CustomEvent("phs:ai-usage-updated"));
    };
    return commit;
  };

  useEffect(() => { refresh(); }, [user?.id, user?.plan]);
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("phs:ai-usage-updated", handler);
    return () => window.removeEventListener("phs:ai-usage-updated", handler);
  }, [user?.id, user?.plan]);
  return { ...usage, consume, refresh };
}

// Banner shown when user is at 80%+ of their AI limit
function AiUsageBanner({ onUpgrade }) {
  const { user } = useUser();
  const { used, limit, pct, canUse } = useAiUsage();
  const plan = user?.plan ?? "guest";
  if (!user?.isLoggedIn || pct < 80 || limit === 0 || isWithin3DaysOfSignup(user)) return null;

  const isAtLimit = !canUse;
  const canUpgrade = plan === "free";
  const nextLabel  = "Premium";

  return (
    <div style={{background:isAtLimit?"rgba(239,68,68,0.1)":"rgba(245,158,11,0.08)",border:"1px solid "+(isAtLimit?"rgba(239,68,68,0.3)":"rgba(245,158,11,0.25)"),borderRadius:10,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:700,color:isAtLimit?C.red:C.amber,marginBottom:2}}>
          {isAtLimit ? "That was your last free insight this month." : `⚠️ You've used ${pct}% of your AI insights`}
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>
          {isAtLimit
            ? "Upgrade to continue — weekly AI reports, full analysis, 40 insights/month."
            : `${used} of ${limit} free insights this month`}
        </div>
        <div style={{height:4,background:"rgba(255,255,255,0.08)",borderRadius:2,marginTop:6,overflow:"hidden"}}>
          <div style={{height:"100%",width:Math.min(pct,100)+"%",background:isAtLimit?C.red:pct>=80?C.amber:C.teal,borderRadius:2,transition:"width .4s"}} />
        </div>
      </div>
      {canUpgrade && (
        <button style={{...S.btnAmber,fontSize:11,padding:"5px 12px",flexShrink:0}} onClick={onUpgrade}>
          Upgrade for more insights
        </button>
      )}
    </div>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /logic/features.js — Feature gating
//
// PLAN TIERS:
//   guest   → no account. Session data only (cleared on reload). Read-only protocols.
//   free    → registered account. Stack + log history persisted. Built-in protocols only.
//   premium → paid. All features. Custom protocols. Advanced insights. AI advisor.
//
// TO WIRE STRIPE:
//   1. On checkout success, set user.plan = "premium" via your auth/DB
//   2. canAccess() reads plan from UserContext — no other changes needed
//
// FEATURE KEYS:
//   viewProtocols      — browse built-in protocols              (all tiers)
//   loadProtocol       — load a protocol into stack             (all tiers)
//   dailyLog           — use daily log today                    (all tiers)
//   persistData        — stack + log saved after reload         (free + premium)
//   customProtocols    — create/edit own protocols              (premium only)
//   advancedInsights   — trends, correlation, AI advisor        (premium only)
//   exportData         — CSV export                             (premium only)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ─────────────────────────────────────────────────────────────────────────────
// TIER MATRIX — 3 tiers
//
//   guest   → no account. Session only. No AI, no saving.
//   free    → registered. Stack + log saved. 3 AI insights/month.
//   premium → paid (monthly £9.99 or annual £59.99). All features. 40 AI calls/month.
// ─────────────────────────────────────────────────────────────────────────────
const TIERS = ["guest","free","premium","annual"];

// Maps "annual" → "premium" so all feature/limit checks work without enumerating "annual" everywhere
function planNormalise(plan) {
  return plan === "annual" ? "premium" : (plan ?? "guest");
}

const FEATURE_MAP = {
  // Available to all
  viewProtocols:      ["guest","free","premium"],
  loadProtocol:       ["guest","free","premium"],
  dailyLog:           ["guest","free","premium"],
  // Registered users only
  persistData:        ["free","premium"],
  // Free users get basic AI (3/month)
  aiBasic:            ["free","premium"],
  // Premium only
  aiAdvisor:          ["premium"],
  basicTrends:        ["premium"],
  customProtocols:    ["premium"],
  advancedInsights:   ["premium"],
  exportData:         ["premium"],
  aiContextChat:      ["premium"],   // remembers conversation history
  buildMyStack:       ["premium"],   // build optimal protocol from logs
  weeklyReport:       ["premium"],
  monthlyReport:      ["premium"],
  whyNotImproving:    ["premium"],
  predictiveInsights: ["premium"],
};

function canAccess(feature, plan) {
  return (FEATURE_MAP[feature] ?? []).includes(planNormalise(plan));
}

function planRank(plan) {
  return TIERS.indexOf(plan ?? "guest");
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /context/UserContext.jsx
//
// TO CONNECT REAL AUTH (Supabase example):
//   useEffect(() => {
//     const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
//       setUser(session?.user ? { isLoggedIn:true, plan: session.user.user_metadata.plan ?? "free", ...session.user } : DEFAULT_USER);
//     });
//     return () => subscription.unsubscribe();
//   }, []);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// plan: "guest" | "free" | "premium"
const DEFAULT_USER = { isLoggedIn:false, plan:"guest", id:null, email:null };
const UserCtx = createContext({ user:DEFAULT_USER, setUser:()=>{}, signIn:()=>{}, signOut:()=>{}, showAuth:false, setShowAuth:()=>{} });

function startPlanPolling(userId, onPlanChange, initialPlan = "free") {
  if (planPollInterval) clearTimeout(planPollInterval);
  let lastKnownPlan = initialPlan; // always from Supabase, never localStorage
  let stablePolls = 0;
  const MAX_DELAY = 300_000; // 5 min ceiling
  // Free users can't change plan mid-session without Stripe redirecting them away and back
  // (?upgraded=true triggers its own aggressive poll). Poll infrequently until plan changes.
  let delay = initialPlan === "free" ? MAX_DELAY : 30_000;

  const poll = async () => {
    try {
      const currentPlan = await api.loadPlan(userId);
      if (currentPlan !== lastKnownPlan) {
        lastKnownPlan = currentPlan;
        stablePolls = 0;
        delay = 30_000; // reset backoff on change
        onPlanChange(currentPlan);
      } else {
        stablePolls++;
        if (stablePolls >= 10) {
          delay = Math.min(delay * 2, MAX_DELAY);
          stablePolls = 0;
        }
      }
    } catch (e) {
      console.warn("[Plan polling] Error:", e);
    }
    planPollInterval = setTimeout(poll, delay);
  };

  planPollInterval = setTimeout(poll, delay);
}

function stopPlanPolling() {
  if (planPollInterval) {
    clearTimeout(planPollInterval);
    planPollInterval = null;
  }
}

function UserProvider({ children }) {
  const [user, setUserRaw] = useState(() => {
    const saved = storage.get("phs_user", DEFAULT_USER);
    if (!saved?.isLoggedIn) return DEFAULT_USER;
    const token = storage.get("phs_token", null);
    if (!token) return DEFAULT_USER;
    // Check token expiry — clear session if expired
    const expiresAt = token.expires_at;
    if (expiresAt && Math.floor(Date.now() / 1000) > expiresAt) {
      storage.remove("phs_token");
      storage.remove("phs_user");
      storage.remove("phs_current_page");
      return DEFAULT_USER;
    }
    sbSession = token;
    return saved;
  });
  const [showAuth, setShowAuth] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState(null);
  const [confirmCheckLoading, setConfirmCheckLoading] = useState(false);
  const [confirmCheckError, setConfirmCheckError]     = useState("");
  // isAuthLoading: true while we verify the stored token on mount
  const [isAuthLoading, setIsAuthLoading] = useState(() => {
    const saved = storage.get("phs_user", DEFAULT_USER);
    return !!(saved?.isLoggedIn);
  });

  // setUser must be defined before the useEffect below that uses it
  const setUser = (uOrFn) => {
    if (typeof uOrFn === 'function') {
      setUserRaw(prev => {
        const next = uOrFn(prev);
        storage.set("phs_user", next);
        return next;
      });
    } else {
      setUserRaw(uOrFn);
      storage.set("phs_user", uOrFn);
    }
  };

  const sessionRefreshAttempted = useRef(false);

  // On mount: refresh session token, then load plan from Supabase.
  // Fallback chain: refreshSession → loadPlan → logout (only if both fail).
  // Also handles returning from Stripe checkout (?upgraded=true)
  useEffect(() => {
    if (sessionRefreshAttempted.current) return; // Guard: StrictMode/multi-mount protection
    sessionRefreshAttempted.current = true;

    // Capture hash before any URL manipulation — replaceState clears window.location.hash
    const hash = window.location.hash;

    // Strip Supabase auth fragments from the URL on mount (defensive cleanup)
    if (hash && (hash.includes("access_token=") || hash.includes("error=") || hash.includes("type=signup"))) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    // Read user from storage directly — avoids stale closure with empty dep array
    const savedUser = storage.get("phs_user", DEFAULT_USER);

    // ── Handle Supabase email-confirmation redirect ───────────────────────────
    // When /verify redirects here, Supabase puts the new session in the URL hash:
    // #access_token=X&refresh_token=Y&token_type=bearer&type=signup
    if (hash && hash.includes("access_token=")) {
      (async () => {
        const params = new URLSearchParams(hash.slice(1));
        const accessToken  = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const expiresIn    = parseInt(params.get("expires_in") ?? "3600", 10);
        const type         = params.get("type");


        if (!accessToken || !refreshToken) return;

        // ── PASSWORD RESET — show modal, do NOT auto sign-in ─────────────────
        if (type === "recovery") {
          let userId = null;
          let userEmail = null;
          try {
            const payload = JSON.parse(atob(accessToken.split(".")[1]));
            userId    = payload.sub ?? null;
            userEmail = payload.email ?? null;
          } catch { /* ignore decode errors */ }

          recoverySession = {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: Math.floor(Date.now() / 1000) + expiresIn,
            user_id: userId,
            user_email: userEmail,
          };
          setIsAuthLoading(false); // unblock spinner for already-logged-in users
          setShowPasswordReset(true);
          return; // stop — do not fall through to auto sign-in
        }

        // ── EMAIL CONFIRMATION / EMAIL CHANGE auto sign-in ───────────────────
        console.log("[Auth] Email confirmation token found in URL fragment — auto sign-in");

        const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;
        const session = { access_token: accessToken, refresh_token: refreshToken, expires_at: expiresAt };

        let userId = null;
        let userEmail = null;
        try {
          const payload = JSON.parse(atob(accessToken.split(".")[1]));
          userId    = payload.sub ?? null;
          userEmail = payload.email ?? null;
        } catch { /* ignore decode errors */ }

        if (userId) {
          sbSession = session;
          sbSession.user_id = userId;
          storage.set("phs_token", sbSession);

          const authLoadingFallback = setTimeout(() => setIsAuthLoading(false), 8000);
          try {
            const plan = await api.loadPlan(userId);
            const profile = { isLoggedIn: true, plan: plan ?? "free", id: userId, email: userEmail };
            setUser(profile);
            api.loadProfile(userId).then(profileData => {
              if (!profileData) return;
              const assessment = profileData.onboarding_assessment
                ? (() => { try { return JSON.parse(profileData.onboarding_assessment); } catch { return null; } })()
                : null;
              setUser(prev => ({
                ...prev,
                created_at: profileData.created_at ?? null,
                onboarding_assessment: assessment ?? null,
              }));
            }).catch(() => {});
            startPlanPolling(userId, (newPlan) => {
              setUser(prev => prev?.plan !== newPlan ? { ...prev, plan: newPlan } : prev);
            }, plan ?? "free");
          } catch (e) {
            console.warn("[Auth] Auto sign-in from fragment failed:", e);
            sbSession = null;
            storage.remove("phs_token");
          } finally {
            clearTimeout(authLoadingFallback);
            setTimeout(() => setIsAuthLoading(false), 300);
          }
        }
      })();
    }
    // ── End fragment handler ──────────────────────────────────────────────────

    // Only run the refresh-session flow if we didn't just handle a fragment
    if (!hash?.includes("access_token=") && savedUser.isLoggedIn && savedUser.id) {
      // Hard fallback: never leave the user on the loading screen longer than 8 seconds
      const authLoadingFallback = setTimeout(() => setIsAuthLoading(false), 8000);

      console.log("[Auth] Attempting session refresh for user:", savedUser.id);
      api.refreshSession()
        .then(() => {
          console.log("[Auth] Session refreshed OK — loading plan");
          return api.loadPlan(savedUser.id);
        })
        .then(plan => {
          console.log("[Auth] Plan loaded:", plan);
          setUser(prev => plan !== prev.plan ? { ...prev, plan } : prev);
          return api.loadProfile(savedUser.id);
        })
        .then(profileData => {
          if (!profileData) return;
          const assessment = profileData.onboarding_assessment
            ? (() => { try { return JSON.parse(profileData.onboarding_assessment); } catch { return null; } })()
            : null;
          setUser(prev => ({
            ...prev,
            created_at: profileData.created_at ?? prev.created_at ?? null,
            onboarding_assessment: assessment ?? prev.onboarding_assessment ?? null,
          }));
        })
        .catch(async (refreshErr) => {
          console.warn("[Auth] refreshSession failed:", refreshErr, "— trying loadPlan fallback");
          try {
            const plan = await api.loadPlan(savedUser.id);
            console.log("[Auth] loadPlan fallback OK, plan:", plan);
            setUser(prev => plan !== prev.plan ? { ...prev, plan } : prev);
          } catch (planErr) {
            console.error("[Auth] loadPlan also failed — logging out:", planErr);
            setUserRaw(DEFAULT_USER);
            storage.remove("phs_user");
            storage.remove("phs_token");
            storage.remove("phs_current_page");
          }
        })
        .finally(() => {
          clearTimeout(authLoadingFallback); // Cancel fallback — normal resolution happened
          setTimeout(() => setIsAuthLoading(false), 500);
        });
    } else {
      setIsAuthLoading(false);
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "true" && savedUser.isLoggedIn) {
      window.history.replaceState({}, "", "/");
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const plan = await api.loadPlan(savedUser.id).catch(() => null);
        if (plan === "premium" || plan === "annual") {
          clearInterval(poll);
          setUser(prev => ({ ...prev, plan }));
          // Reset usage counter so upgraded users start from 0/40
          _aiUsageMemory.set(getUsageKey(savedUser.id), 0);
          const period = aiUsagePeriod();
          sbFetch("ai_usage?user_id=eq." + savedUser.id + "&app_id=eq.phs_supplements&period=eq." + period, { method: "PATCH", body: JSON.stringify({ call_count: 0, updated_at: new Date().toISOString() }) }).catch(() => {});
          window.dispatchEvent(new CustomEvent("phs:plan-upgraded"));
        } else if (attempts >= 20) {
          clearInterval(poll);
        }
      }, 500);
    }
    if (params.get("checkout") === "cancelled") {
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new CustomEvent("phs:checkout-cancelled"));
    }

    const handleSessionExpired = () => {
      console.warn("[Auth] Session expired mid-use — clearing auth state");
      stopPlanPolling();
      setUserRaw(DEFAULT_USER);
      storage.remove("phs_user");
      storage.remove("phs_token");
      storage.remove("phs_current_page");
      setIsAuthLoading(false);
      setShowAuth(true); // Re-open sign-in modal so user can log in without page reload
    };

    window.addEventListener("phs:session-expired", handleSessionExpired);

    return () => {
      stopPlanPolling();
      window.removeEventListener("phs:session-expired", handleSessionExpired);
    };
  }, []);

  const signIn = async (email, pw) => {
    // isSigningIn intentionally NOT set here — setting it now would unmount AuthModal,
    // which means if credentials fail, the error can't be shown (remounted component = fresh state).
    // We flip it only after api.signIn confirms valid credentials.
    try {
      const u = await api.signIn(email, pw);
      // Credentials confirmed — now safe to show the full-screen loading transition
      setIsSigningIn(true);
      setIsAuthLoading(true);
      const profile = { isLoggedIn:true, plan:u.plan ?? "free", id:u.id, email:u.email };
      setUser(profile);
      // setTimeout breaks React 18 automatic batching: without this, setUser +
      // setIsAuthLoading(false) batch into one render and the loading screen never shows.
      setTimeout(() => setIsAuthLoading(false), 300);
      if (sbSession) storage.set("phs_token", sbSession);
      api.loadProfile(u.id).then(profileData => {
        if (!profileData) return;
        const assessment = profileData.onboarding_assessment
          ? (() => { try { return JSON.parse(profileData.onboarding_assessment); } catch { return null; } })()
          : null;
        setUser(prev => ({
          ...prev,
          created_at: profileData.created_at ?? null,
          onboarding_assessment: assessment,
        }));
      }).catch(() => {});
      try {
        const usage = await api.getAiUsage(u.id, u.plan ?? "free");
        const key = getUsageKey(u.id);
        _aiUsageMemory.set(key, usage.used);
      } catch(e) { /* non-fatal */ }
      startPlanPolling(u.id, (newPlan) => {
        setUser(prev => {
          if (prev?.plan === newPlan) return prev;
          // Plan changed mid-session — reset usage counter
          _aiUsageMemory.set(getUsageKey(u.id), 0);
          const period = aiUsagePeriod();
          sbFetch("ai_usage?user_id=eq." + u.id + "&app_id=eq.phs_supplements&period=eq." + period, { method: "PATCH", body: JSON.stringify({ call_count: 0, updated_at: new Date().toISOString() }) }).catch(() => {});
          return { ...prev, plan: newPlan };
        });
      }, u.plan ?? "free");
      // If returning from Stripe checkout, poll aggressively for plan update
      const params = new URLSearchParams(window.location.search);
      if (params.get("upgraded") === "true" && (u.plan ?? "free") === "free") {
        window.history.replaceState({}, "", "/");
        let attempts = 0;
        const upgradePoll = setInterval(async () => {
          attempts++;
          const plan = await api.loadPlan(u.id).catch(() => null);
          if (plan === "premium" || plan === "annual") {
            clearInterval(upgradePoll);
            setUser(prev => ({ ...prev, plan }));
            // Reset usage counter so upgraded users start from 0/40
            _aiUsageMemory.set(getUsageKey(u.id), 0);
            const period = aiUsagePeriod();
            sbFetch("ai_usage?user_id=eq." + u.id + "&app_id=eq.phs_supplements&period=eq." + period, { method: "PATCH", body: JSON.stringify({ call_count: 0, updated_at: new Date().toISOString() }) }).catch(() => {});
            window.dispatchEvent(new CustomEvent("phs:plan-upgraded"));
          } else if (attempts >= 20) {
            clearInterval(upgradePoll);
          }
        }, 500);
      }
      return profile;
    } catch (err) {
      // Clear any loading state that may have been set (e.g. credentials were valid
      // but a subsequent call failed), then propagate so AuthModal can show the error.
      setIsAuthLoading(false);
      setIsSigningIn(false);
      throw err;
    } finally {
      setIsSigningIn(false);
    }
  };

  const signUp = async (email, pw) => {
    // Do NOT set isSigningIn here — that flag unmounts AuthModal, which loses any
    // error state if the API call fails. AuthModal manages its own loading button state.
    try {
      const data = await sbAuth("signup", email, pw, {
        options: { emailRedirectTo: "https://stack.peakhealthstack.com/" }
      });

      // Case 1: No access token returned — email confirmation required
      // (Raw Supabase REST returns session fields at root, not nested under data.session)
      if (!data.access_token) {
        setPendingConfirmEmail(email);
        return;
      }

      // Case 2: Token returned immediately (email confirmation disabled in Supabase)
      const u = data.user;
      sbSession = data; // data IS the session (same structure as signIn)
      if (sbSession) sbSession.user_id = u.id;
      const profile = { isLoggedIn: true, plan: "free", id: u.id, email: u.email };
      setUser(profile);
      setTimeout(() => setIsAuthLoading(false), 300);
      if (sbSession) storage.set("phs_token", sbSession);
      startPlanPolling(u.id, (newPlan) => {
        setUser(prev => prev?.plan !== newPlan ? { ...prev, plan: newPlan } : prev);
      });
      return profile;
    } catch (err) {
      const msg = (err.message ?? "").toLowerCase();
      if (
        msg.includes("already registered") ||
        msg.includes("user already exists") ||
        msg.includes("email address is already") ||
        msg.includes("already been registered") ||
        msg.includes("error sending confirmation email") ||
        msg.includes("sending confirmation") ||
        msg.includes("email rate limit")
      ) {
        // Account exists (confirmed or unconfirmed) — direct user to check inbox
        setPendingConfirmEmail(email);
        return;
      }
      throw err;
    }
  };

  const checkEmailConfirmed = async () => {
    if (!pendingConfirmEmail) return;
    // Don't probe Supabase with a bogus password — just open sign-in.
    // If email isn't confirmed yet, signIn() will surface the correct error message.
    storage.set("phs_prefill_email", pendingConfirmEmail);
    setPendingConfirmEmail(null);
    setConfirmCheckError("");
    setShowAuth(true);
  };

  const signOut = async () => {
    stopPlanPolling();
    const userId = storage.get("phs_user")?.id;
    await api.signOut();
    setUserRaw(DEFAULT_USER);
    storage.remove("phs_user");
    storage.remove("phs_token");
    storage.remove("phs_current_page");
    if (userId) storage.remove("phs_active_protocol_" + userId);
  };

  // upgradeToPremium redirects to Stripe — plan update happens via webhook + poll
  const upgradeToPremium = async (plan) => {
    if (!user.id || !user.email) throw new Error("You must be signed in to upgrade.");
    await api.createCheckout(plan, user.id, user.email);
    // If we get here without redirect, createCheckout threw — that's caught by caller
  };

  // Refresh plan on demand (e.g. after returning from account management)
  const refreshPlan = async () => {
    if (!user.id) return;
    const plan = await api.loadPlan(user.id);
    setUser(prev => {
      if (plan === prev.plan) return prev;
      // Plan changed — reset in-memory usage so the new limit is reflected immediately
      _aiUsageMemory.set(getUsageKey(user.id), 0);
      const period = aiUsagePeriod();
      sbFetch("ai_usage?user_id=eq." + user.id + "&app_id=eq.phs_supplements&period=eq." + period, { method: "PATCH", body: JSON.stringify({ call_count: 0, updated_at: new Date().toISOString() }) }).catch(() => {});
      return { ...prev, plan };
    });
  };

  return (
    <UserCtx.Provider value={{ user, setUser, signIn, signUp, signOut, upgradeToPremium, refreshPlan, showAuth, setShowAuth, showPasswordReset, setShowPasswordReset, isSigningIn, setIsAuthLoading, isAuthLoading, pendingConfirmEmail, setPendingConfirmEmail, confirmCheckLoading, confirmCheckError, checkEmailConfirmed }}>
      {children}
    </UserCtx.Provider>
  );
}
const useUser = () => useContext(UserCtx);
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /context/AppContext.jsx — central app state (stack, logs, custom protocols)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const AppCtx = createContext(null);

function AppProvider({ children }) {
  const { user } = useUser();
  // Captured at render time (before any effects) so child effects can't clear it first
  const pendingOnboardingAtMount = useRef(!!storage.get('phs_pending_onboarding', null));
  // Set by App's post-auth effect when it applies onboarding supplements — prevents
  // the async Supabase load from overwriting with STARTER_STACK after the fact
  const onboardingAppliedRef = useRef(false);

  // Stack — persisted for logged-in users, session-only for guests
  const [stack, setStack]         = useState(["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)"]);
  const [stackDosages, setStackDosages] = useState(() => {
    // Hydrate defaults for initial demo stack
    const d = {};
    ["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)"].forEach(name => {
      const def = getDefaultDosage(name);
      if (def.amount) d[name] = { dosage_amount: def.amount, dosage_unit: def.unit };
    });
    return d;
  });
  const [logs, setLogs]     = useState([]);
  const [customProtocols, setCustomProtocols] = useState([]);
  const [activeProtocol, setActiveProtocol] = useState(() => storage.get("phs_active_protocol_" + (storage.get("phs_user")?.id ?? "guest"), null));
  const [toast, setToast]   = useState("");
  const [customTimings, setCustomTimings] = useState({});
  
  // hasLoaded prevents auto-save firing before persisted data is loaded on login
  const hasLoaded = useRef(false);

  // Load persisted data from Supabase when user logs in
  useEffect(() => {
    if (!user.isLoggedIn) {
      onboardingAppliedRef.current = false;
      // Clear all user-specific state on logout to prevent cross-user data leak
      setStack(["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)"]);
      setStackDosages({});
      setCustomProtocols([]);
      setLogs([]);
      setCustomTimings({});
      setActiveProtocol(null);
      hasLoaded.current = false;
      return;
    }
    // Consume the mount-time snapshot (set before any child effects ran)
    const hasPendingOnboarding = pendingOnboardingAtMount.current;
    pendingOnboardingAtMount.current = false;
    // Reset all state before loading new user's data — prevents previous user's data persisting
    // Skip resetting stack when onboarding data is pending (mount snapshot) OR when App's
    // post-auth effect already applied the onboarding stack (post-mount flow — child runs first)
    if (!hasPendingOnboarding && !onboardingAppliedRef.current) setStack([]);
    setStackDosages({});
    setCustomProtocols([]);
    setLogs([]);
    setCustomTimings({});
    setActiveProtocol(null);
    hasLoaded.current = false;
    Promise.all([
      api.loadStack(user.id),
      api.loadLogs(user.id),
    ]).then(([stackData, logsData]) => {
      if (stackData?.stack?.length) {
        const raw = stackData.stack;
        // Stack items may be strings (legacy) or objects with dosage keys
        if (typeof raw[0] === 'object' && raw[0] !== null) {
          const names = raw.map(s => s.name);
          const dosages = {};
          raw.forEach(s => {
            if (s.dosage_amount != null) dosages[s.name] = { dosage_amount: s.dosage_amount, dosage_unit: s.dosage_unit || 'mg' };
          });
          // Hydrate missing dosages from defaults
          names.forEach(name => {
            if (!dosages[name]) {
              const def = getDefaultDosage(name);
              if (def.amount) dosages[name] = { dosage_amount: def.amount, dosage_unit: def.unit };
            }
          });
          setStack(names);
          setStackDosages(dosages);
        } else {
          // Legacy string array — hydrate defaults
          const dosages = {};
          raw.forEach(name => {
            const def = getDefaultDosage(name);
            if (def.amount) dosages[name] = { dosage_amount: def.amount, dosage_unit: def.unit };
          });
          setStack(raw);
          setStackDosages(dosages);
        }
      } else {
        // No stack in DB. If the user also has no logs they are genuinely new and
        // skipped (or haven't reached) onboarding — seed a sensible starter stack
        // so they land on a useful screen rather than an empty state.
        // Users who have logs but cleared their stack intentionally keep an empty stack.
        if (hasPendingOnboarding || onboardingAppliedRef.current) {
          // Onboarding useEffect already set (or will set) the correct stack — don't touch it
        } else if (!logsData?.length) {
          const STARTER_STACK = ['Magnesium Glycinate', 'Omega-3 (EPA+DHA)', 'Vitamin D3 + K2'];
          const starterDosages = {};
          STARTER_STACK.forEach(name => {
            const def = getDefaultDosage(name);
            if (def.amount) starterDosages[name] = { dosage_amount: def.amount, dosage_unit: def.unit };
          });
          setStack(STARTER_STACK);
          setStackDosages(starterDosages);
        } else {
          setStack([]);
          setStackDosages({});
        }
      }
      setCustomTimings(stackData?.custom_timings ?? {});
      setCustomProtocols(stackData?.custom_protocols ?? []);
      setActiveProtocol(stackData?.active_protocol ?? null);
      setLogs(logsData ?? []);
      hasLoaded.current = true;
    }).catch(e => { hasLoaded.current = true; Sentry.captureException(e); });
  }, [user.id, user.isLoggedIn]);

  // Auto-save stack to Supabase — only after initial load
  useEffect(() => {
    if (!user.isLoggedIn || !hasLoaded.current) return;
    const t = setTimeout(() => {
      const stackForSave = stack.map(name => ({ name, ...(stackDosages[name] || {}) }));
      api.saveStack(user.id, stackForSave, customTimings, customProtocols, activeProtocol);
    }, 1500);
    return () => clearTimeout(t);
  }, [stack, stackDosages, customTimings, customProtocols, activeProtocol, user.id, user.isLoggedIn]);

  const toggleStack = useCallback(name => {
    setStack(p => {
      const adding = !p.includes(name);
      if (adding) {
        const def = getDefaultDosage(name);
        if (def.amount) setStackDosages(prev => ({ ...prev, [name]: { dosage_amount: def.amount, dosage_unit: def.unit } }));
        return [...p, name];
      } else {
        setStackDosages(prev => { const n = { ...prev }; delete n[name]; return n; });
        return p.filter(s => s !== name);
      }
    });
  }, []);

  const loadProtocol = useCallback((name, custom = false) => {
    const p = custom ? customProtocols.find(x => x.name === name) : PROTOCOLS[name];
    if (!p) return;
    setStack(p.supplements);
    // Hydrate dosages for protocol supplements
    const newDosages = {};
    p.supplements.forEach(sName => {
      const def = getDefaultDosage(sName);
      if (def.amount) newDosages[sName] = { dosage_amount: def.amount, dosage_unit: def.unit };
    });
    setStackDosages(newDosages);
    setActiveProtocol(name);
    const userId = storage.get("phs_user")?.id ?? "guest";
    storage.set("phs_active_protocol_" + userId, name);
    setToast((p.icon ?? "📋") + " " + name + " loaded");
  }, [customProtocols]);

  // saveLog — upserts by date (prevents duplicate saves, updates existing)
  const saveLog = useCallback((entry) => {
    setLogs(prev => {
      const idx = prev.findIndex(l => l.date === entry.date);
      const updated = idx >= 0
        ? prev.map((l, i) => i === idx ? entry : l)
        : [entry, ...prev];
      if (user.isLoggedIn) api.saveLog(user.id, entry).catch(e => console.warn("saveLog failed:", e));
      return updated;
    });
  }, [user]);

  const deleteLog = useCallback((date) => {
    setLogs(prev => {
      const updated = prev.filter(l => l.date !== date);
      if (user.isLoggedIn) api.deleteLog(user.id, date).catch(e => console.warn("deleteLog failed:", e));
      return updated;
    });
  }, [user]);

  const saveCustomProtocol = useCallback((protocol) => {
    setCustomProtocols(prev => {
      const idx = prev.findIndex(p => p.name === protocol.name);
      return idx >= 0 ? prev.map((p, i) => i === idx ? protocol : p) : [...prev, protocol];
    });
  }, []);

  const deleteCustomProtocol = useCallback((name) => {
    setCustomProtocols(prev => prev.filter(p => p.name !== name));
    setActiveProtocol(prev => prev === name ? null : prev);
    setToast('"' + name + '" deleted');
  }, []);

  const saveStack = useCallback((supplements) => {
    if (!user.isLoggedIn || !user.id) return Promise.resolve();
    const newStack = supplements ?? stack;
    setStack(newStack);
    return api.saveStack(user.id, newStack, customTimings, customProtocols, activeProtocol)
      .catch(e => console.warn("[saveStack]", e));
  }, [user.id, user.isLoggedIn, stack, customTimings, customProtocols, activeProtocol]);

  return (
    <AppCtx.Provider value={{
      stack, setStack, toggleStack, loadProtocol, saveStack,
      stackDosages, setStackDosages,
      logs, saveLog, deleteLog,
      customProtocols, saveCustomProtocol, deleteCustomProtocol,
      customTimings, setCustomTimings,
      activeProtocol, setActiveProtocol,
      toast, setToast,
      onboardingAppliedRef,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
const useApp = () => useContext(AppCtx);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/ui.jsx — purely presentational micro-components
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, [onDone]);
  return (
    <div style={{position:"fixed",bottom:92,left:"50%",transform:"translateX(-50%)",background:"linear-gradient(135deg,#F5D67A,#E5B83F)",color:C.navyDeep,padding:"11px 26px",borderRadius:30,fontWeight:700,fontSize:13,zIndex:9999,boxShadow:"0 10px 34px rgba(229,184,63,0.45), 0 0 0 1px rgba(229,184,63,0.35)",whiteSpace:"nowrap",fontFamily:"'DM Sans',sans-serif",animation:"tsUp 0.3s cubic-bezier(0.34,1.56,0.64,1) both",backdropFilter:"blur(8px)",maxWidth:"calc(100vw - 32px)",overflow:"hidden",textOverflow:"ellipsis"}}>
      ✓ {message}
    </div>
  );
}

function Loader() {
  return (
    <div style={{display:"flex",gap:5,justifyContent:"center",padding:"10px 0"}}>
      {[0,1,2].map(i => (
        <div key={i} style={{width:8,height:8,borderRadius:"50%",background:i===1?C.teal:"rgba(229,184,63,0.5)",animation:"ldWave 1.0s ease-in-out " + (i*0.15) + "s infinite"}} />
      ))}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BrandLockup — the "Your Peak Stack" logo lockup: gold mountain mark
// (/logo-mark.svg, transparent) + gold/cream serif wordmark. Reproduces the
// brand logo so it renders crisp at any size and themes with the palette.
//   variant="hero"   → large, centred (landing / share-style surfaces)
//   variant="header" → compact (app top bar)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ShareBrand — the logo lockup drawn with an inline SVG mark + solid gold/cream
// wordmark, so it rasterises cleanly through html2canvas on the share cards
// (CSS background-clip:text gradients do NOT render in html2canvas).
function ShareBrand({ size = 42 }) {
  const w = size, h = Math.round(size * 0.84);
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:size*0.3}}>
      <svg width={w} height={h} viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="psShareGold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#FCEFA8"/><stop offset="0.46" stopColor="#EAC24E"/><stop offset="1" stopColor="#C28A22"/>
          </linearGradient>
        </defs>
        <g stroke="url(#psShareGold)" strokeWidth="6.5" strokeLinejoin="round" strokeLinecap="round" fill="none">
          <path d="M8 74 L46 14 L70 52"/>
          <path d="M50 74 L74 34 L104 74"/>
          <path d="M12 75 Q42 57 62 70 Q82 84 100 66"/>
        </g>
      </svg>
      <div style={{textAlign:"left",fontFamily:'Georgia, "Times New Roman", serif'}}>
        <div style={{fontSize:size*0.26,color:"#ECE3CD",fontWeight:600,lineHeight:1,letterSpacing:"0.4px"}}>Your</div>
        <div style={{fontSize:size*0.42,fontWeight:700,lineHeight:1.05,letterSpacing:"1.5px"}}>
          <span style={{color:"#EAC24E"}}>PEAK</span> <span style={{color:"#F0E8D2"}}>STACK</span>
        </div>
        <div style={{fontSize:size*0.165,color:"#E5B83F",fontWeight:600,letterSpacing:"2.4px",marginTop:3}}>BY PEAK HEALTH STACK</div>
      </div>
    </div>
  );
}

function BrandLockup({ variant = "header", style = {} }) {
  const hero = variant === "hero";
  const clip = (image) => ({ backgroundImage:image, WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent", WebkitTextFillColor:"transparent" });
  const gold  = clip("linear-gradient(180deg,#FBE38C 0%,#EAC451 44%,#C8922A 100%)");
  const cream = clip("linear-gradient(180deg,#FFFFFF 0%,#ECE3CD 100%)");
  const z = hero
    ? { mark:"clamp(58px,15vw,94px)", your:"clamp(19px,4.6vw,28px)", main:"clamp(33px,8.8vw,56px)", tag:"clamp(8px,2.3vw,11px)", gap:"clamp(13px,3.6vw,22px)", tagGap:"clamp(6px,1.6vw,9px)" }
    : { mark:"34px", your:"13px", main:"19px", tag:"7.5px", gap:"11px", tagGap:"6px" };
  return (
    <div style={{display:"inline-flex",alignItems:"center",gap:z.gap,...style}}>
      <img src="/logo-mark.svg" alt="Your Peak Stack" style={{width:z.mark,height:z.mark,display:"block",flexShrink:0,filter:"drop-shadow(0 4px 14px rgba(229,184,63,0.26))"}} />
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start",fontFamily:"'DM Serif Display',serif",lineHeight:1}}>
        <span style={{...cream,fontSize:z.your,lineHeight:1,letterSpacing:"0.01em",opacity:0.95}}>Your</span>
        <span style={{fontSize:z.main,lineHeight:1.02,letterSpacing:"0.015em",whiteSpace:"nowrap",marginTop:hero?"2px":"1px"}}>
          <span style={gold}>PEAK</span><span style={{...cream,marginLeft:"0.22em"}}>STACK</span>
        </span>
        {hero && (
          <span style={{display:"flex",alignItems:"center",width:"100%",gap:z.tagGap,marginTop:z.tagGap}}>
            <span style={{height:"1px",flex:1,background:"linear-gradient(90deg,transparent,rgba(229,184,63,0.55))"}} />
            <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:z.tag,letterSpacing:"0.34em",color:"#E5B83F",fontWeight:600,whiteSpace:"nowrap"}}>BY PEAK HEALTH STACK</span>
            <span style={{height:"1px",flex:1,background:"linear-gradient(90deg,rgba(229,184,63,0.55),transparent)"}} />
          </span>
        )}
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/GateBanner.jsx
// Shown when guest tries to use a feature that needs login/premium
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function GateBanner({ type = "login", featureName = "this feature", onAction }) {
  const isLogin = type === "login";
  return (
    <div style={{...S.card, borderColor: isLogin ? C.teal + "40" : C.amber + "40", textAlign:"center", padding:32}}>
      <div style={{fontSize:32, marginBottom:12}}>{isLogin ? "🔒" : "⭐"}</div>
      <div style={{fontSize:15, fontWeight:700, marginBottom:8, color: isLogin ? C.teal : C.amber}}>
        {isLogin ? "Sign in to save your progress" : "Premium feature"}
      </div>
      <div style={{fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:20, lineHeight:1.6}}>
        {isLogin
          ? "Create a free account to save your stack, log history, and access " + featureName + " across devices."
          : featureName + ` is available on ${APP_NAME} Premium. Upgrade to unlock advanced insights, custom protocols, AI advisor, and more.`
        }
      </div>
      <button
        style={{...S.btn, background: isLogin ? "linear-gradient(135deg," + C.teal + "," + C.tealDark + ")" : "linear-gradient(135deg,#F59E0B,#F97316)", padding:"10px 28px"}}
        onClick={onAction}
      >
        {isLogin ? "Create Free Account" : "Upgrade to Premium"}
      </button>
    </div>
  );
}

// Inline gate lock — shows a blurred preview with an overlay instead of hiding content
function GateOverlay({ children, feature, featureName }) {
  const { user, setShowAuth } = useUser();
  const { setToast } = useApp();
  const allowed = canAccess(feature, user.plan);
  if (allowed) return children;
  const needsLogin = !user.isLoggedIn;
  return (
    <div style={{position:"relative"}}>
      <div style={{filter:"blur(3px)", pointerEvents:"none", userSelect:"none", opacity:0.4}}>{children}</div>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10}}>
        <div style={{fontSize:24}}>{needsLogin ? "🔒" : "⭐"}</div>
        <div style={{fontSize:13,fontWeight:700,color:needsLogin?C.teal:C.amber}}>{needsLogin?"Sign in to use":"Premium only"}</div>
        <button
          style={{background:needsLogin?"linear-gradient(135deg,"+C.teal+","+C.tealDark+")":"linear-gradient(135deg,#F59E0B,#F97316)",color:needsLogin?C.navy:"#fff",border:"none",padding:"8px 18px",borderRadius:9,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
          onClick={() => needsLogin ? setShowAuth(true) : setToast("Upgrade to Premium to use " + (featureName ?? feature))}
        >
          {needsLogin ? "Create Free Account" : "Upgrade to Unlock"}
        </button>
      </div>
    </div>
  );
}

// Persistent save-reminder banner for guests
function GuestBanner() {
  const { user, setShowAuth } = useUser();
  const [dismissed, setDismissed] = useState(false);
  if (user.isLoggedIn || dismissed) return null;
  return (
    <div style={{background:"rgba(229,184,63,0.08)",borderBottom:"1px solid rgba(229,184,63,0.2)",padding:"10px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
      <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.5}}>
        <span style={{color:C.teal,fontWeight:700}}>Your data won’t be saved</span> — create a free account to keep your stack and log history.
      </div>
      <div style={{display:"flex",gap:8,flexShrink:0}}>
        <button onClick={() => setShowAuth(true)} style={{background:"linear-gradient(135deg,"+C.teal+","+C.tealDark+")",color:C.navy,border:"none",padding:"6px 14px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          Sign up free
        </button>
        <button onClick={() => setDismissed(true)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:16,padding:"0 4px"}}>×</button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/PasswordResetModal.jsx
// Shown when a password-reset link (type=recovery) is clicked. Submits the new
// password via Supabase REST, then signs the user in using the recovery tokens.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function PasswordResetModal() {
  const { setShowPasswordReset, setUser, setIsAuthLoading } = useUser();
  const { setToast } = useApp();
  const [pw, setPw]           = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    if (pw.length < 5) {
      setPwError("Password must be at least 5 characters."); return;
    }
    if (pw !== confirm) {
      setPwError("Passwords do not match."); return;
    }
    if (!recoverySession?.access_token) {
      setPwError("Reset session expired. Please request a new password reset email."); return;
    }

    setLoading(true);
    setPwError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON,
          "Authorization": `Bearer ${recoverySession.access_token}`,
        },
        body: JSON.stringify({ password: pw }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const rawMsg = err.message ?? err.msg ?? err.error_description ?? err.error ?? "";
        if (err.error_code === "same_password" || rawMsg.toLowerCase().includes("same")) {
          throw new Error("Please choose a password different from your current one.");
        }
        throw new Error(rawMsg || "Password update failed. Please try again.");
      }

      // Use recovery tokens directly — no re-authentication needed
      sbSession = {
        access_token: recoverySession.access_token,
        refresh_token: recoverySession.refresh_token,
        expires_at: recoverySession.expires_at,
        user_id: recoverySession.user_id,
      };
      storage.set("phs_token", sbSession);

      setIsAuthLoading(true);
      const authFallback = setTimeout(() => setIsAuthLoading(false), 8000);
      try {
        const plan = await api.loadPlan(recoverySession.user_id);
        const profile = {
          isLoggedIn: true,
          plan: plan ?? "free",
          id: recoverySession.user_id,
          email: recoverySession.user_email,
        };
        setUser(profile);
        api.loadProfile(recoverySession.user_id).then(profileData => {
          if (!profileData) return;
          const assessment = profileData.onboarding_assessment
            ? (() => { try { return JSON.parse(profileData.onboarding_assessment); } catch { return null; } })()
            : null;
          setUser(prev => ({ ...prev, created_at: profileData.created_at ?? null, onboarding_assessment: assessment ?? null }));
        }).catch(() => {});
        startPlanPolling(recoverySession.user_id, (newPlan) => {
          setUser(prev => prev?.plan !== newPlan ? { ...prev, plan: newPlan } : prev);
        }, plan ?? "free");
        recoverySession = null; // one-time use — clear immediately
        setShowPasswordReset(false);
        setToast("Password updated — you're now signed in.");
      } catch {
        sbSession = null;
        storage.remove("phs_token");
        setPwError("Sign-in failed after password update. Please sign in manually.");
      } finally {
        clearTimeout(authFallback);
        setTimeout(() => setIsAuthLoading(false), 300);
      }
    } catch (e) {
      setPwError(e.message ?? "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:C.navyDeep, display:"flex", alignItems:"center", justifyContent:"center", padding:24, zIndex:1000 }}>
      <div style={{ width:"100%", maxWidth:400 }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🔑</div>
          <p style={{ color:"#fff", fontSize:20, fontWeight:700, margin:"0 0 8px" }}>Set a New Password</p>
          <p style={{ color:"rgba(255,255,255,0.5)", fontSize:14, margin:0 }}>Choose a password for your account.</p>
        </div>
        <input
          type="password"
          placeholder="New password"
          value={pw}
          onChange={e => { setPw(e.target.value); setPwError(""); }}
          onKeyDown={e => e.key === "Enter" && handle()}
          style={{ ...S.input, marginBottom: 10 }}
        />
        <input
          type="password"
          placeholder="Confirm new password"
          value={confirm}
          onChange={e => { setConfirm(e.target.value); setPwError(""); }}
          onKeyDown={e => e.key === "Enter" && handle()}
          style={{ ...S.input, marginBottom: pwError ? 8 : 16 }}
        />
        {!pwError && (
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginBottom:16 }}>
            Min. 5 characters.
          </div>
        )}
        {pwError && (
          <div style={{ fontSize:12, color:C.red, marginBottom:16, padding:"8px 12px", background:"rgba(239,68,68,0.08)", borderRadius:8 }}>
            {pwError}
          </div>
        )}
        <button
          onClick={handle}
          disabled={loading}
          style={{ ...S.btn, width:"100%", padding:14, fontSize:15, opacity: loading ? 0.7 : 1 }}
        >
          {loading ? "Updating…" : "Set Password & Sign In"}
        </button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/AuthModal.jsx
// Simple sign-in / sign-up modal. Replace body with Supabase Auth UI or Auth0.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function AuthModal({ onClose, initialMode }) {
  const { signIn, signUp } = useUser();
  const { setToast } = useApp();
  const isUpgradeReturn = new URLSearchParams(window.location.search).get('upgraded') === 'true';
  const [mode, setMode]           = useState(initialMode ?? "signup");
  const [authMethod, setAuthMethod] = useState("password"); // "password" | "magic"
  const [email, setEmail]         = useState(() => {
    const prefill = storage.get("phs_prefill_email", "");
    if (prefill) { storage.remove("phs_prefill_email"); return prefill; }
    return "";
  });
  const [pw, setPw]               = useState("");
  const [loading, setLoading]     = useState(false);
  const [emailError, setEmailError] = useState("");
  const [pwError, setPwError]       = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStatus, setForgotStatus] = useState("idle"); // "idle"|"sending"|"sent"
  const [magicStatus, setMagicStatus] = useState("idle"); // "idle"|"sending"|"sent"

  const handle = async () => {
    if (!email.trim()) { setEmailError("Please enter your email."); return; }
    if (!pw.trim()) { setPwError("Please enter your password."); return; }
    // Client-side email format check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError("Please enter a valid email address (e.g. name@example.com)."); return;
    }
    // Client-side password length check for signup
    if (mode === "signup" && pw.length < 5) {
      setPwError("Password must include: at least 5 characters."); return;
    }
    setLoading(true); setEmailError(""); setPwError("");
    try {
      if (mode === "signup") {
        const result = await signUp(email.trim(), pw);
        // signUp returns undefined when email confirmation is pending (pendingConfirmEmail set)
        // Only close + toast when an immediate session was created
        if (result) { setToast("Welcome! Your account is ready."); onClose(); }
      } else {
        await signIn(email.trim(), pw);
        setToast("Welcome back!");
        // Existing users land on Check-In — their main day-to-day screen
        window.dispatchEvent(new CustomEvent("phs:signed-in"));
        onClose();
      }
    } catch(e) {
      const msg = (e.message ?? "").toLowerCase();
      if (mode === "signup") {
        if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("already been registered") ||
            msg.includes("error sending confirmation email") || msg.includes("sending confirmation") || msg.includes("email rate limit")) {
          setEmailError("This email is already registered. If you haven't confirmed it yet, check your inbox. Otherwise sign in instead.");
        } else if (msg.includes("password should be") || msg.includes("password must be")) {
          setPwError("Password must include: at least 5 characters.");
        } else if (msg.includes("unable to validate email") || msg.includes("invalid format") || msg.includes("invalid email")) {
          setEmailError("Please enter a valid email address (e.g. name@example.com).");
        } else {
          setEmailError("Something went wrong. Please try again.");
        }
      } else {
        if (msg.includes("user not found") || msg.includes("no user found")) {
          setEmailError("No account found with that email. Check the address or create a free account.");
        } else if (msg.includes("invalid login credentials") || msg.includes("invalid_credentials")) {
          setEmailError("Email or password incorrect.");
        } else if (msg.includes("email not confirmed")) {
          setEmailError("Please confirm your email before signing in. Check your inbox.");
        } else if (msg.includes("unable to validate email") || msg.includes("invalid format")) {
          setEmailError("Please enter a valid email address.");
        } else {
          setEmailError("Something went wrong. Please try again.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email || !email.includes("@")) { setEmailError("Please enter a valid email address."); return; }
    setMagicStatus("sending"); setEmailError("");
    try {
      const resp = await fetch(`${SUPABASE_URL}/auth/v1/otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON },
        body: JSON.stringify({ email, create_user: false }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error_description ?? err.msg ?? "Failed to send magic link");
      }
      setMagicStatus("sent");
    } catch(err) {
      const msg = (err.message ?? "").toLowerCase();
      if (msg.includes("user not found") || msg.includes("no user") || msg.includes("not registered")) {
        setEmailError("No account found with this email. Please sign up first.");
      } else if (msg.includes("rate limit") || msg.includes("too many")) {
        setEmailError("Too many attempts. Please wait a moment and try again.");
      } else {
        setEmailError("Something went wrong sending the link. Please try again.");
      }
      setMagicStatus("idle");
    }
  };

  const handleForgotPassword = async () => {
    if (!email || !email.includes("@")) { setEmailError("Enter your email address above first."); return; }
    setForgotStatus("sending");
    try {
      const resp = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "apikey": SUPABASE_ANON },
        body: JSON.stringify({ email }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.msg ?? err.error_description ?? "Failed to send");
      }
      setForgotStatus("sent");
    } catch {
      setForgotStatus("idle");
      setEmailError("Couldn't send reset email. Please try again.");
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,backdropFilter:"blur(5px)"}} onClick={onClose}>
      <div style={{background:C.navyDeep,border:"1px solid rgba(229,184,63,0.25)",borderRadius:20,padding:36,maxWidth:400,width:"90%",fontFamily:"'DM Sans',sans-serif"}} onClick={e => e.stopPropagation()}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:32,marginBottom:8}}>🌿</div>
          <div style={{fontSize:18,fontWeight:700,marginBottom:4}}>
            {mode === "signup" ? "Start Your Peak Stack" : "Welcome Back"}
          </div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>
            {mode === "signup" ? "Save your stack, track your progress, sync across devices — all free." : "Sign in to access your saved stack and history."}
          </div>
        </div>

        {/* Payment confirmation banner */}
        {isUpgradeReturn && mode === 'signin' && (
          <div style={{background:'rgba(229,184,63,0.1)',border:'1px solid rgba(229,184,63,0.3)',borderRadius:10,padding:'10px 14px',marginBottom:16,textAlign:'center'}}>
            <p style={{color:'#F5C84A',fontSize:13,fontWeight:600,margin:0}}>🎉 Payment received! Sign in to activate Premium.</p>
          </div>
        )}
        {/* Sign-in only: password vs magic link tabs */}
        {mode === "signin" && (
          <div style={{display:"flex",background:"rgba(255,255,255,0.06)",borderRadius:8,padding:3,marginBottom:20}}>
            {[["password","Password"],["magic","Magic Link"]].map(([val,label]) => (
              <button key={val} onClick={() => { setAuthMethod(val); setEmailError(""); setPwError(""); setMagicStatus("idle"); }}
                style={{flex:1,padding:"8px 0",borderRadius:6,border:"none",
                  background:authMethod===val?"rgba(255,255,255,0.12)":"transparent",
                  color:authMethod===val?"#fff":"rgba(255,255,255,0.45)",
                  fontSize:13,fontWeight:authMethod===val?700:400,cursor:"pointer",fontFamily:"inherit"
                }}>{label}</button>
            ))}
          </div>
        )}

        {/* Magic link sent state */}
        {mode === "signin" && authMethod === "magic" && magicStatus === "sent" ? (
          <div style={{textAlign:"center",padding:"16px 0"}}>
            <p style={{fontSize:32,marginBottom:12}}>📬</p>
            <p style={{color:"#fff",fontSize:16,fontWeight:600,marginBottom:8}}>Check your email</p>
            <p style={{color:"rgba(255,255,255,0.55)",fontSize:14}}>We sent a sign-in link to <strong>{email}</strong>. Click it to sign in instantly.</p>
          </div>
        ) : mode === "signin" && authMethod === "magic" ? (
          <>
            <input style={{...S.input,marginBottom:emailError?8:16}} type="email" placeholder="your@email.com"
              value={email} onChange={e => { setEmail(e.target.value); setEmailError(""); }} />
            {emailError && <div style={{fontSize:12,color:C.red,marginBottom:12,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>{emailError}</div>}
            <button style={{...S.btn,width:"100%",padding:12,fontSize:14,opacity:magicStatus==="sending"?0.7:1}} onClick={handleMagicLink} disabled={magicStatus==="sending"}>
              {magicStatus==="sending" ? "Sending…" : "Send Magic Link"}
            </button>
          </>
        ) : (
          <>
            <input style={{...S.input,marginBottom:emailError?6:10}} type="email" placeholder="Email address" value={email} onChange={e => { setEmail(e.target.value); setEmailError(""); }} />
            {emailError && <div style={{fontSize:12,color:C.red,marginBottom:10,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>{emailError}</div>}
            {forgotMode ? (
              <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:14,marginBottom:16}}>
                <p style={{color:"rgba(255,255,255,0.7)",fontSize:13,margin:"0 0 10px"}}>
                  We'll send a reset link to <strong>{email || "your email"}</strong>.
                </p>
                {forgotStatus === "sent" ? (
                  <p style={{color:C.teal,fontSize:13,margin:0}}>✓ Reset email sent — check your inbox.</p>
                ) : (
                  <div style={{display:"flex",gap:8}}>
                    <button style={{...S.btn,flex:1,padding:"8px 12px",fontSize:13,opacity:forgotStatus==="sending"?0.6:1}} onClick={handleForgotPassword} disabled={forgotStatus==="sending"}>
                      {forgotStatus==="sending" ? "Sending…" : "Send Reset Email"}
                    </button>
                    <button style={{background:"none",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.5)",borderRadius:8,padding:"8px 12px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}} onClick={() => { setForgotMode(false); setForgotStatus("idle"); }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <input style={{...S.input,marginBottom:(pwError||mode==="signup")?6:mode==="signin"?4:16}} type="password" placeholder="Password" value={pw} onChange={e => { setPw(e.target.value); setPwError(""); }} onKeyDown={e => e.key === "Enter" && handle()} />
                {pwError
                  ? <div style={{fontSize:12,color:C.red,marginBottom:mode==="signup"?10:6,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>{pwError}</div>
                  : mode === "signup" && <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginBottom:10,paddingLeft:2}}>Min. 5 characters.</div>
                }
                {mode === "signin" && (
                  <div style={{textAlign:"right",marginBottom:12}}>
                    <button style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:12,cursor:"pointer",fontFamily:"inherit",padding:0,textDecoration:"underline"}}
                      onClick={() => { setForgotMode(true); setForgotStatus("idle"); setEmailError(""); setPwError(""); }}>
                      Forgot password?
                    </button>
                  </div>
                )}
              </>
            )}
            <button style={{...S.btn,width:"100%",padding:12,fontSize:14,marginBottom:12,opacity:loading?0.6:1}} onClick={handle} disabled={loading || forgotMode}>
              {loading ? "..." : mode === "signup" ? "Create Account" : "Sign In"}
            </button>
          </>
        )}

        <div style={{textAlign:"center",fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:12}}>
          {mode === "signup" ? "Already have an account? " : "No account yet? "}
          <button onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setEmailError(""); setPwError(""); setForgotMode(false); setForgotStatus("idle"); setMagicStatus("idle"); }} style={{background:"none",border:"none",color:C.teal,cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:700}}>
            {mode === "signup" ? "Sign in" : "Create one free"}
          </button>
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:16}}>
          Free forever. No credit card required.
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/BuyButtons.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function resolveAffiliateName(name) {
  if (AFFILIATE_LINKS[name]) return name;
  const lower = name.toLowerCase();
  return Object.keys(AFFILIATE_LINKS).find(k =>
    k.toLowerCase().startsWith(lower) || lower.startsWith(k.toLowerCase().split(" ")[0])
  ) ?? name;
}

function BuyButtons({ name, compact = false }) {
  const links = AFFILIATE_LINKS[resolveAffiliateName(name)];
  if (!links) return null;
  const specific = isSpecificLink(name);
  const amzLabel = specific ? (compact ? "Buy" : "Buy on Amazon") : (compact ? "Search" : "Search Amazon");
  const amzStyle = compact
    ? {display:"flex",alignItems:"center",gap:4,background:"rgba(255,153,0,0.15)",color:"#FF9900",border:"1px solid rgba(255,153,0,0.3)",padding:"4px 9px",borderRadius:7,fontSize:10,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap",fontFamily:"inherit"}
    : {display:"flex",alignItems:"center",gap:6,background:"linear-gradient(135deg,#FF9900,#e88800)",color:"#1a1a1a",border:"none",padding:"8px 14px",borderRadius:9,fontSize:12,fontWeight:700,textDecoration:"none",fontFamily:"inherit"};
  const altStyle = compact
    ? {display:"flex",alignItems:"center",gap:4,background:"rgba(229,184,63,0.12)",color:C.teal,border:"1px solid rgba(229,184,63,0.25)",padding:"4px 9px",borderRadius:7,fontSize:10,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap",fontFamily:"inherit"}
    : {display:"flex",alignItems:"center",gap:6,background:C.teal+"18",color:C.teal,border:"1px solid "+C.teal+"40",padding:"8px 14px",borderRadius:9,fontSize:12,fontWeight:700,textDecoration:"none",fontFamily:"inherit"};
  return (
    <div style={{display:"flex",gap:compact?5:8,flexWrap:"wrap"}}>
      <a href={links.amazon} target="_blank" rel="noopener noreferrer nofollow" style={amzStyle}>{amzLabel}</a>
      {links.alt && <a href={links.alt.url} target="_blank" rel="noopener noreferrer nofollow" style={altStyle}>{compact ? links.alt.label : "Buy on " + links.alt.label}</a>}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/SupplementDrawer.jsx
// Free: dose, timing, benefits, buy links
// Premium: full clinical deep-dive — mechanism, research, synergies, stack notes
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const QUALITY_COLOR = { Strong:"#10B981", Good:"#E5B83F", Moderate:"#F59E0B", Emerging:"#8B5CF6" };

function EvidencePill({ quality }) {
  const col = QUALITY_COLOR[quality] ?? "#9CA3AF";
  return (
    <span style={{background:col+"18",color:col,border:"1px solid "+col+"35",padding:"1px 7px",borderRadius:5,fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.6px",flexShrink:0}}>
      {quality}
    </span>
  );
}

function DetailSection({ title, children }) {
  return (
    <div style={{marginBottom:20}}>
      <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:"1.2px",marginBottom:10,paddingBottom:6,borderBottom:"1px solid rgba(255,255,255,0.06)"}}>{title}</div>
      {children}
    </div>
  );
}

function SupplementDrawer({ name, onAdd, onClose, inStack }) {
  const info   = SUPPLEMENT_DB[name];
  const detail = SUPPLEMENT_DETAIL_DB[name];
  const { user, setShowAuth } = useUser();
  const isPremium = canAccess("advancedInsights", user.plan);
  const [activeTab, setActiveTab] = useState("overview");

  if (!info) return null;

  const TABS = [
    { id:"overview", label:"Overview" },
    ...(isPremium && detail ? [
      { id:"science",   label:"Science" },
      { id:"synergies", label:"Synergies" },
      { id:"stack",     label:"Stack Tips" },
    ] : [
      { id:"premium",   label:"⭐ Deep Dive" },
    ]),
  ];

  // Interactions from the global list relevant to this supplement
  const relatedInteractions = INTERACTIONS.filter(i => i.a === name || i.b === name);
  const cautions  = relatedInteractions.filter(i => i.severity !== "none");
  const synergies = relatedInteractions.filter(i => i.severity === "none");

  return (
    <div style={{position:"fixed",inset:0,zIndex:500,display:"flex",alignItems:"flex-end",background:"rgba(0,0,0,0.65)",backdropFilter:"blur(6px)"}} onClick={onClose}>
      <div
        style={{width:"100%",maxWidth:560,margin:"0 auto",background:C.navyDeep,borderRadius:"22px 22px 0 0",border:"1px solid rgba(229,184,63,0.2)",borderBottom:"none",display:"flex",flexDirection:"column",maxHeight:"88vh",fontFamily:"'DM Sans',sans-serif",animation:"drawerSlideUp 0.28s cubic-bezier(0.32,0.72,0,1) both"}}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={{width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"14px auto 0",flexShrink:0}} />

        {/* Header — always visible */}
        <div style={{padding:"14px 20px 0",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10}}>
            <div style={{flex:1,minWidth:0,paddingRight:12}}>
              <div style={{fontSize:18,fontWeight:700,marginBottom:6,lineHeight:1.25}}>{name}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <span style={{background:evidenceColor(info.evidence)+"20",color:evidenceColor(info.evidence),border:"1px solid "+evidenceColor(info.evidence)+"40",padding:"2px 8px",borderRadius:7,fontSize:11}}>{info.evidence} evidence</span>
                <span style={{background:"rgba(255,255,255,0.07)",color:"rgba(255,255,255,0.45)",border:"1px solid rgba(255,255,255,0.1)",padding:"2px 8px",borderRadius:7,fontSize:11}}>{info.category}</span>
              </div>
            </div>
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.08)",border:"none",color:"rgba(255,255,255,0.5)",width:30,height:30,borderRadius:"50%",cursor:"pointer",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
          </div>

          {/* Tab bar */}
          <div style={{display:"flex",gap:2,borderBottom:"1px solid rgba(255,255,255,0.07)",marginLeft:-20,marginRight:-20,paddingLeft:20}}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                style={{padding:"7px 14px",border:"none",background:"transparent",color:activeTab===t.id?C.teal:"rgba(255,255,255,0.38)",fontSize:12,fontWeight:activeTab===t.id?700:400,cursor:"pointer",fontFamily:"inherit",borderBottom:activeTab===t.id?"2px solid "+C.teal:"2px solid transparent",whiteSpace:"nowrap",transition:"color .15s"}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{overflowY:"auto",padding:"16px 20px",flex:1}}>

          {/* ── OVERVIEW TAB (all users) ── */}
          {activeTab === "overview" && (
            <>
              {/* Dose + Timing cards */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                {[{label:"Recommended Dose",val:info.dose,icon:"💊"},{label:"Best Timing",val:info.timing,icon:"🕐"}].map(r => (
                  <div key={r.label} style={{background:"rgba(255,255,255,0.04)",borderRadius:11,padding:"11px 13px"}}>
                    <div style={{fontSize:9,color:"rgba(255,255,255,0.32)",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.8px"}}>{r.label}</div>
                    <div style={{fontSize:13,fontWeight:600,lineHeight:1.35}}>{r.val}</div>
                  </div>
                ))}
              </div>

              {/* Summary (if detail exists) */}
              {detail?.summary && (
                <div style={{background:"rgba(229,184,63,0.05)",border:"1px solid rgba(229,184,63,0.15)",borderRadius:11,padding:"13px 15px",marginBottom:16,fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.7}}>
                  {detail.summary}
                </div>
              )}

              {/* Benefits tags */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.32)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.8px"}}>Key Benefits</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {info.tags.map(t => (<span key={t} style={{background:"rgba(229,184,63,0.09)",color:C.teal,border:"1px solid rgba(229,184,63,0.2)",padding:"3px 10px",borderRadius:20,fontSize:11,textTransform:"capitalize"}}>{t}</span>))}
                </div>
              </div>

              {/* Quick interactions summary */}
              {relatedInteractions.length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.32)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.8px"}}>Quick Interaction Notes</div>
                  {cautions.slice(0,2).map((i,idx) => (
                    <div key={idx} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <span style={{color:"#F97316",fontSize:11,flexShrink:0,marginTop:1}}>⚠</span>
                      <span style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.5}}><b style={{color:"rgba(255,255,255,0.75)"}}>{i.a===name?i.b:i.a}:</b> {i.note}</span>
                    </div>
                  ))}
                  {synergies.slice(0,2).map((i,idx) => (
                    <div key={idx} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"7px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <span style={{color:"#10B981",fontSize:11,flexShrink:0,marginTop:1}}>✦</span>
                      <span style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.5}}><b style={{color:"rgba(255,255,255,0.75)"}}>{i.a===name?i.b:i.a}:</b> {i.note}</span>
                    </div>
                  ))}
                  {(cautions.length + synergies.length) > 4 && !isPremium && (
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",paddingTop:6,fontStyle:"italic"}}>+ {(cautions.length+synergies.length)-4} more in the deep dive</div>
                  )}
                </div>
              )}

              {/* Where to buy */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.32)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.8px"}}>Where to Buy</div>
                <BuyButtons name={name} />
              </div>
            </>
          )}

          {/* ── SCIENCE TAB (premium only) ── */}
          {activeTab === "science" && detail && (
            <>
              <DetailSection title="Mechanism of Action">
                <p style={{fontSize:13,color:"rgba(255,255,255,0.72)",lineHeight:1.75,margin:0}}>{detail.mechanism}</p>
              </DetailSection>

              <DetailSection title="Research Evidence">
                {detail.research.map((r,i) => (
                  <div key={i} style={{display:"flex",gap:10,padding:"10px 0",borderBottom:i<detail.research.length-1?"1px solid rgba(255,255,255,0.05)":"none",alignItems:"flex-start"}}>
                    <EvidencePill quality={r.quality} />
                    <p style={{fontSize:13,color:"rgba(255,255,255,0.68)",lineHeight:1.65,margin:0,flex:1}}>{r.finding}</p>
                  </div>
                ))}
              </DetailSection>

              <DetailSection title="Best Form to Buy">
                <div style={{background:"rgba(229,184,63,0.06)",border:"1px solid rgba(229,184,63,0.15)",borderRadius:10,padding:"12px 14px"}}>
                  <p style={{fontSize:13,color:"rgba(255,255,255,0.72)",lineHeight:1.7,margin:0}}>{detail.bestForm}</p>
                </div>
              </DetailSection>

              <DetailSection title="Safety & Contraindications">
                <div style={{background:"rgba(239,68,68,0.05)",border:"1px solid rgba(239,68,68,0.15)",borderRadius:10,padding:"12px 14px"}}>
                  <p style={{fontSize:13,color:"rgba(255,255,255,0.68)",lineHeight:1.7,margin:0}}>{detail.avoid}</p>
                </div>
              </DetailSection>

              <DetailSection title="Best For">
                <p style={{fontSize:13,color:"rgba(255,255,255,0.72)",lineHeight:1.7,margin:0}}>{detail.whoFor}</p>
              </DetailSection>
            </>
          )}

          {/* ── SYNERGIES TAB (premium only) ── */}
          {activeTab === "synergies" && detail && (
            <>
              {detail.synergies.length > 0 && (
                <DetailSection title={"Synergistic Combinations (" + detail.synergies.length + ")"}>
                  {detail.synergies.map((syn, i) => (
                    <div key={i} style={{padding:"12px 0",borderBottom:i<detail.synergies.length-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <span style={{fontSize:18}}>✦</span>
                        <div style={{fontSize:13,fontWeight:700,color:C.teal}}>{syn.name}</div>
                      </div>
                      <p style={{fontSize:13,color:"rgba(255,255,255,0.65)",lineHeight:1.65,margin:0,paddingLeft:26}}>{syn.reason}</p>
                    </div>
                  ))}
                </DetailSection>
              )}

              {cautions.length > 0 && (
                <DetailSection title="Timing Cautions with Other Supplements">
                  {cautions.map((c, i) => (
                    <div key={i} style={{padding:"10px 0",borderBottom:i<cautions.length-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                        <span style={{background:"rgba(249,115,22,0.15)",color:"#F97316",border:"1px solid rgba(249,115,22,0.3)",padding:"1px 7px",borderRadius:5,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>{c.severity}</span>
                        <div style={{fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.85)"}}>{c.a===name?c.b:c.a}</div>
                      </div>
                      <p style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.65,margin:0}}>{c.note}</p>
                    </div>
                  ))}
                </DetailSection>
              )}

              {synergies.length > 0 && (
                <DetailSection title="Synergies in Your Interactions DB">
                  {synergies.map((s, i) => (
                    <div key={i} style={{padding:"10px 0",borderBottom:i<synergies.length-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.green,marginBottom:4}}>{s.a===name?s.b:s.a}</div>
                      <p style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.65,margin:0}}>{s.note}</p>
                    </div>
                  ))}
                </DetailSection>
              )}
            </>
          )}

          {/* ── STACK TIPS TAB (premium only) ── */}
          {activeTab === "stack" && detail && (
            <>
              <DetailSection title="Stack Notes & Practical Tips">
                <div style={{background:"rgba(229,184,63,0.05)",border:"1px solid rgba(229,184,63,0.12)",borderRadius:10,padding:"14px 16px"}}>
                  <p style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.8,margin:0}}>{detail.stackNotes}</p>
                </div>
              </DetailSection>

              <DetailSection title="Who Benefits Most">
                <p style={{fontSize:13,color:"rgba(255,255,255,0.72)",lineHeight:1.7,margin:0}}>{detail.whoFor}</p>
              </DetailSection>

              <DetailSection title="Quick Reference">
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[
                    {label:"Dose",val:info.dose},
                    {label:"Timing",val:info.timing},
                    {label:"Category",val:info.category},
                    {label:"Evidence",val:info.evidence},
                  ].map(r => (
                    <div key={r.label} style={{background:"rgba(255,255,255,0.04)",borderRadius:9,padding:"10px 12px"}}>
                      <div style={{fontSize:9,color:"rgba(255,255,255,0.3)",marginBottom:3,textTransform:"uppercase",letterSpacing:"0.8px"}}>{r.label}</div>
                      <div style={{fontSize:12,fontWeight:600}}>{r.val}</div>
                    </div>
                  ))}
                </div>
              </DetailSection>

              <div style={{marginBottom:16}}>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.32)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.8px"}}>Where to Buy</div>
                <BuyButtons name={name} />
              </div>
            </>
          )}

          {/* ── PREMIUM GATE TAB (non-premium users) ── */}
          {activeTab === "premium" && (
            <div style={{textAlign:"center",padding:"32px 16px"}}>
              <div style={{fontSize:40,marginBottom:14}}>🔬</div>
              <div style={{fontSize:16,fontWeight:700,color:C.amber,marginBottom:10}}>
                {detail ? "Deep Dive Analysis" : "Advanced Analysis"}
              </div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.75,marginBottom:24}}>
                {APP_NAME} Premium unlocks full clinical analysis for every supplement — including mechanism of action, research findings with evidence ratings, optimal forms, contraindications, synergy science, and personalised stack notes.
              </div>
              {detail && (
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24,textAlign:"left"}}>
                  {["Mechanism of action — how it works at the cellular level","Research findings — key studies with evidence quality ratings","Best form to buy and why the form matters","Safety, contraindications and upper limits","Synergy science — why specific combinations work","Practical stack notes and timing guidance"].map(f => (
                    <div key={f} style={{display:"flex",alignItems:"flex-start",gap:9,fontSize:12,color:"rgba(255,255,255,0.55)"}}>
                      <span style={{color:C.amber,flexShrink:0,marginTop:1}}>⭐</span>{f}
                    </div>
                  ))}
                </div>
              )}
              <button
                style={{background:"linear-gradient(135deg,#F59E0B,#F97316)",color:"#fff",border:"none",padding:"11px 28px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",width:"100%",marginBottom:10}}
                onClick={() => { onClose(); }}
              >
                Upgrade to Premium
              </button>
              {!user.isLoggedIn && (
                <button
                  style={{background:"transparent",color:C.teal,border:"1px solid rgba(229,184,63,0.3)",padding:"9px 28px",borderRadius:10,fontSize:12,cursor:"pointer",fontFamily:"inherit",width:"100%"}}
                  onClick={() => { setShowAuth(true); onClose(); }}
                >
                  Create Free Account First
                </button>
              )}
            </div>
          )}

        </div>

        {/* Footer — CTA + affiliate note */}
        <div style={{padding:"10px 20px 20px",flexShrink:0,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
          <button
            style={{width:"100%",background:inStack?"rgba(239,68,68,0.1)":"linear-gradient(135deg,"+C.teal+","+C.tealDark+")",color:inStack?C.red:C.navy,border:inStack?"1px solid rgba(239,68,68,0.22)":"none",padding:12,borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
            onClick={() => { onAdd(name); onClose(); }}
          >
            {inStack ? "Remove from Stack" : "Add to My Stack"}
          </button>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.18)",textAlign:"center",marginTop:8,fontStyle:"italic"}}>Affiliate links support this site at no extra cost to you</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.18)",textAlign:"center",marginTop:4,fontStyle:"italic"}}>{DISCLAIMER_SHORT} Individual results vary.</div>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/ProtocolShopList.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ProtocolShopList({ protocolName, color }) {
  const [open, setOpen] = useState(false);
  const supplements = PROTOCOLS[protocolName]?.supplements ?? [];
  return (
    <div style={{marginTop:10}}>
      <button style={{width:"100%",background:color+"10",color,border:"1px solid "+color+"30",padding:"8px 12px",borderRadius:9,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:6}} onClick={() => setOpen(o => !o)}>
        🛒 {open ? "Hide" : "Shop This Protocol"} {open?"▲":"▼"}
      </button>
      {open && (
        <div style={{marginTop:8,background:"rgba(0,0,0,0.2)",borderRadius:10,padding:12}}>
          {supplements.map(s => {
            const links = AFFILIATE_LINKS[s];
            const specific = isSpecificLink(s);
            return (
              <div key={s} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.05)",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>{SUPPLEMENT_DB[s]?.dose}</div>
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  {links?.amazon && <a href={links.amazon} target="_blank" rel="noopener noreferrer nofollow" style={{background:"rgba(255,153,0,0.15)",color:"#FF9900",border:"1px solid rgba(255,153,0,0.3)",padding:"3px 9px",borderRadius:6,fontSize:10,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>{specific?"Buy":"Search"} ↗</a>}
                  {links?.alt && <a href={links.alt.url} target="_blank" rel="noopener noreferrer nofollow" style={{background:"rgba(229,184,63,0.1)",color:C.teal,border:"1px solid rgba(229,184,63,0.25)",padding:"3px 9px",borderRadius:6,fontSize:10,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>{links.alt.label} ↗</a>}
                </div>
              </div>
            );
          })}
          <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:10,fontStyle:"italic"}}>Affiliate links support this site at no extra cost to you</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.18)",textAlign:"center",marginTop:4,fontStyle:"italic"}}>{DISCLAIMER_SHORT} Individual results vary.</div>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/ProtocolBuilder.jsx
// Gated to premium — GateOverlay wraps the trigger, not the full builder
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const PROTOCOL_EMOJIS = ["📋","⭐","💊","🧬","🌿","🔥","❤","🧠","💪","🦴","🌙","⚡","🎯","🌱","🏃","🧘","🛡","🔬","🌊","✨"];
const PROTOCOL_COLORS = ["#E5B83F","#8B5CF6","#3B82F6","#F59E0B","#F97316","#EC4899","#10B981","#14B8A6","#6366F1","#A78BFA","#0EA5E9","#22C55E","#EF4444","#F472B6","#FF9900"];

function ProtocolBuilder({ existing, onSave, onClose }) {
  const [name, setName]       = useState(existing?.name ?? "");
  const [icon, setIcon]       = useState(existing?.icon ?? "📋");
  const [color, setColor]     = useState(existing?.color ?? C.teal);
  const [desc, setDesc]       = useState(existing?.description ?? "");
  const [selected, setSelected] = useState(existing?.supplements ?? []);
  const [search, setSearch]   = useState("");
  const [error, setError]     = useState("");

  const filtered = Object.keys(SUPPLEMENT_DB).filter(s =>
    s.toLowerCase().includes(search.toLowerCase()) ||
    SUPPLEMENT_DB[s].tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );
  const toggle = s => setSelected(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
  const sortedSel = sortByTiming(selected);

  const handleSave = () => {
    if (!name.trim()) { setError("Please give your protocol a name"); return; }
    if (selected.length < 2) { setError("Add at least 2 supplements"); return; }
    onSave({ name:name.trim(), icon, color, description:desc.trim(), supplements:selected, custom:true });
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:600,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(5px)",display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={onClose}>
      <div style={{width:"100%",maxWidth:560,background:C.navyDeep,borderRadius:"20px 20px 0 0",border:"1px solid rgba(229,184,63,0.22)",borderBottom:"none",display:"flex",flexDirection:"column",maxHeight:"90vh",fontFamily:"'DM Sans',sans-serif"}} onClick={e => e.stopPropagation()}>
        <div style={{width:36,height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,margin:"14px auto 0",flexShrink:0}} />
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px 0",flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:700}}>{existing ? "Edit Protocol" : "Create Custom Protocol"}</div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.08)",border:"none",color:"rgba(255,255,255,0.5)",width:28,height:28,borderRadius:"50%",cursor:"pointer",fontSize:14}}>✕</button>
        </div>
        <div style={{overflowY:"auto",padding:"16px 20px 24px",flex:1}}>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:6,textTransform:"uppercase",letterSpacing:"1px"}}>Protocol Name</div>
            <input style={{...S.input,border:"1px solid " + (error&&!name.trim()?"rgba(239,68,68,0.5)":"rgba(255,255,255,0.12)")}} placeholder="e.g. My Morning Stack, Post-Surgery Recovery..." value={name} onChange={e => {setName(e.target.value);setError("");}} maxLength={40} />
            <div style={{fontSize:10,color:"rgba(255,255,255,0.22)",marginTop:4,textAlign:"right"}}>{name.length}/40</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:6,textTransform:"uppercase",letterSpacing:"1px"}}>Icon</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {PROTOCOL_EMOJIS.map(e => (<button key={e} onClick={() => setIcon(e)} style={{width:32,height:32,fontSize:16,borderRadius:8,border:icon===e?"2px solid "+color:"1px solid rgba(255,255,255,0.12)",background:icon===e?"rgba(255,255,255,0.1)":"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>{e}</button>))}
              </div>
            </div>
            <div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:6,textTransform:"uppercase",letterSpacing:"1px"}}>Colour</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {PROTOCOL_COLORS.map(c => (<button key={c} onClick={() => setColor(c)} style={{width:24,height:24,borderRadius:"50%",background:c,border:color===c?"3px solid #fff":"2px solid transparent",cursor:"pointer",boxShadow:color===c?"0 0 0 2px "+c:"none"}} />))}
              </div>
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:6,textTransform:"uppercase",letterSpacing:"1px"}}>Description <span style={{opacity:.5}}>(optional)</span></div>
            <textarea style={{...S.input,height:60,resize:"vertical"}} placeholder="What is this protocol for?" value={desc} onChange={e => setDesc(e.target.value)} maxLength={200} />
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:8,textTransform:"uppercase",letterSpacing:"1px",display:"flex",alignItems:"center",gap:8}}>
              Supplements
              <span style={{background:color+"20",color,border:"1px solid "+color+"40",padding:"1px 7px",borderRadius:10,fontSize:10,fontWeight:700}}>{selected.length}</span>
            </div>
            {selected.length === 0
              ? <div style={{fontSize:12,color:"rgba(255,255,255,0.25)",fontStyle:"italic",padding:"8px 0"}}>None selected yet — use the search below</div>
              : <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>{sortedSel.map(s => (<button key={s} onClick={() => toggle(s)} style={{background:color+"18",color,border:"1px solid "+color+"35",padding:"3px 9px",borderRadius:20,fontSize:11,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:4}}>{s} <span style={{opacity:.6,fontSize:10}}>✕</span></button>))}</div>
            }
          </div>
          <div style={{marginBottom:4}}>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:6,textTransform:"uppercase",letterSpacing:"1px"}}>Add Supplements</div>
            <input style={{...S.input,marginBottom:9}} placeholder="Search by name or effect..." value={search} onChange={e => setSearch(e.target.value)} />
            <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:160,overflowY:"auto"}}>
              {filtered.map(s => (<button key={s} onClick={() => toggle(s)} style={{background:selected.includes(s)?color:"rgba(255,255,255,0.06)",color:selected.includes(s)?"#0f2540":"rgba(255,255,255,0.6)",border:selected.includes(s)?"none":"1px solid rgba(255,255,255,0.1)",padding:"4px 11px",borderRadius:20,fontSize:11,cursor:"pointer",fontWeight:selected.includes(s)?700:400,fontFamily:"inherit"}}>{selected.includes(s)?"✓ ":""}{s}</button>))}
            </div>
          </div>
          {error && <div style={{fontSize:12,color:C.red,marginTop:10,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>{error}</div>}
          {name && selected.length > 0 && (
            <div style={{marginTop:16,background:color+"0e",border:"1px solid "+color+"25",borderRadius:12,padding:14}}>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:8,textTransform:"uppercase",letterSpacing:"1px"}}>Preview</div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}><span style={{fontSize:20}}>{icon}</span><span style={{fontSize:13,fontWeight:700,color}}>{name}</span></div>
              {desc && <div style={{fontSize:11,color:"rgba(255,255,255,0.42)",marginBottom:8,lineHeight:1.5}}>{desc}</div>}
              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{sortedSel.map(s => (<span key={s} style={{background:color+"15",color,border:"1px solid "+color+"28",padding:"2px 6px",borderRadius:7,fontSize:9}}>{s}</span>))}</div>
            </div>
          )}
        </div>
        <div style={{padding:"12px 20px 20px",flexShrink:0,borderTop:"1px solid rgba(255,255,255,0.06)",display:"flex",gap:10}}>
          <button onClick={onClose} style={{flex:1,background:"transparent",color:"rgba(255,255,255,0.4)",border:"1px solid rgba(255,255,255,0.12)",padding:11,borderRadius:10,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button onClick={handleSave} style={{flex:2,background:"linear-gradient(135deg,"+color+","+color+"cc)",color:"#fff",border:"none",padding:11,borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {existing ? "Save Changes" : "Save Protocol"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AccountModal — plan details, billing info, cancel subscription
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function AccountModal({ onClose, onUpgrade }) {
  const { user, signOut, refreshPlan } = useUser();
  const { setToast } = useApp();
  const [sub, setSub]           = useState(null);
  const [loading, setLoading]   = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reportEnabled, setReportEnabled] = useState(user?.weekly_report_enabled ?? true);
  const [reportSaving, setReportSaving]   = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    if (!user?.id) return;
    api.loadSubscription(user.id).then(s => {
      setSub(s);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user?.id]);

  const PLAN_LABELS = { free:"Free", premium:"Premium", annual:"Annual", guest:"Guest" };
  const PLAN_COLORS = { free:"rgba(255,255,255,0.4)", premium:C.amber, annual:"#A78BFA" };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.cancelSubscription(user.id);
      await refreshPlan();
      setToast("Cancellation scheduled — you keep access until the billing period ends.");
      setConfirmCancel(false);
      // Reload subscription data
      const updated = await api.loadSubscription(user.id);
      setSub(updated);
    } catch (e) {
      setToast("Could not cancel — contact support@peakhealthstack.com");
    }
    setCancelling(false);
  };

  const handleResume = async () => {
    if (!sub?.cancel_at_period_end) return; // already active — nothing to resume
    setResuming(true);
    try {
      await api.resumeSubscription(user.id);
      await refreshPlan();
      const updated = await api.loadSubscription(user.id);
      setSub(updated);
      setToast("Subscription resumed — billing will continue as normal.");
    } catch (e) {
      setToast("Could not resume — contact support@peakhealthstack.com");
    }
    setResuming(false);
  };

  const handleToggleWeeklyReport = async (enabled) => {
    setReportEnabled(enabled);
    setReportSaving(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON,
          "Authorization": getAuthHeader(),
          "Prefer": "return=minimal",
        },
        body: JSON.stringify({ weekly_report_enabled: enabled }),
      });
    } catch(err) {
      console.error("[AccountModal] weekly_report_enabled update failed:", err);
      setReportEnabled(!enabled); // revert on error
      setToast("Could not update preference — please try again.");
    } finally {
      setReportSaving(false);
    }
  };

  const planColor = PLAN_COLORS[user.plan] ?? "rgba(255,255,255,0.4)";
  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
  const isCancelling = sub?.cancel_at_period_end;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,padding:16}} onClick={onClose}>
      <div style={{background:C.navyDeep,border:"1px solid rgba(229,184,63,0.2)",borderRadius:18,padding:28,maxWidth:360,width:"100%",fontFamily:"'DM Sans',sans-serif"}} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <div style={{width:40,height:40,borderRadius:12,background:"rgba(229,184,63,0.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>👤</div>
          <div>
            <div style={{fontSize:13,fontWeight:700}}>{user.email}</div>
            <div style={{fontSize:11,color:planColor,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.8px"}}>{PLAN_LABELS[user.plan] ?? user.plan} Plan</div>
          </div>
        </div>

        {loading ? (
          <div style={{textAlign:"center",padding:"20px 0",color:"rgba(255,255,255,0.4)",fontSize:13}}>Loading account details...</div>
        ) : (
          <>
            {/* Plan details */}
            <div style={{background:"rgba(255,255,255,0.04)",borderRadius:12,padding:"14px 16px",marginBottom:16}}>
              {periodEnd && (
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                  <span style={{fontSize:12,color:"rgba(255,255,255,0.5)"}}>{isCancelling ? "Access until" : "Next billing"}</span>
                  <span style={{fontSize:12,fontWeight:600,color:isCancelling?C.amber:C.white}}>{periodEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                </div>
              )}
              {isCancelling && (
                <div style={{fontSize:11,color:C.amber,padding:"6px 10px",background:"rgba(245,158,11,0.08)",borderRadius:8,marginTop:4}}>
                  Cancellation scheduled. You keep full access until {periodEnd ? periodEnd.toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"}) : "your next billing date"}.
                  <button
                    onClick={handleResume}
                    disabled={resuming}
                    style={{display:"block",marginTop:8,width:"100%",padding:"7px 0",background:"rgba(229,184,63,0.12)",border:"1px solid rgba(229,184,63,0.35)",color:C.teal,borderRadius:7,fontSize:11,fontWeight:700,cursor:resuming?"default":"pointer",fontFamily:"inherit",opacity:resuming?0.6:1}}
                  >
                    {resuming ? "Resuming..." : "Resume Subscription"}
                  </button>
                </div>
              )}
              {user.plan === "free" && (
                <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.5}}>Free plan — {AI_LIMITS.free.total} AI insights per month. Upgrade to Premium for {AI_LIMITS.premium.total} AI insights/month and weekly reports.</div>
              )}
            </div>

            {/* Weekly report toggle — premium only */}
            {(user.plan === "premium" || user.plan === "annual") && (
              <div style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"14px 16px",marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <p style={{color:"rgba(255,255,255,0.85)",fontSize:14,fontWeight:600,margin:0}}>Weekly AI report</p>
                    <p style={{color:"rgba(255,255,255,0.45)",fontSize:12,margin:"3px 0 0"}}>Delivered every Sunday — requires 3+ logs/week</p>
                  </div>
                  <button
                    onClick={() => !reportSaving && handleToggleWeeklyReport(!reportEnabled)}
                    style={{width:44,height:24,borderRadius:12,border:"none",cursor:reportSaving?"default":"pointer",
                      background:reportEnabled?"#F5C84A":"rgba(255,255,255,0.15)",position:"relative",
                      transition:"background 0.2s",padding:0,opacity:reportSaving?0.5:1,flexShrink:0}}
                  >
                    <span style={{position:"absolute",top:3,left:reportEnabled?23:3,width:18,height:18,
                      borderRadius:"50%",background:"#fff",transition:"left 0.2s"}} />
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            {user.plan !== "free" && user.plan !== "guest" && !isCancelling && (
              <>
                {!confirmCancel ? (
                  <button style={{...S.btnGhost(),width:"100%",marginBottom:10,fontSize:12}} onClick={() => setConfirmCancel(true)}>
                    Cancel subscription
                  </button>
                ) : (
                  <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:10,lineHeight:1.5}}>
                      Are you sure? You keep access until {periodEnd?.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}.
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button style={{flex:1,padding:"8px",background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",color:"#EF4444",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:700}} onClick={handleCancel} disabled={cancelling}>
                        {cancelling ? "Cancelling..." : "Yes, cancel"}
                      </button>
                      <button style={{flex:1,padding:"8px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.6)",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"inherit"}} onClick={() => setConfirmCancel(false)}>
                        Keep plan
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {user.plan === "free" && (
              <button style={{...S.btnAmber,width:"100%",marginBottom:10,fontSize:12}} onClick={onUpgrade}>
                Upgrade to Premium
              </button>
            )}

            {!isStandalone && (
              <button style={{...S.btnGhost(),width:"100%",marginBottom:10,fontSize:12}} onClick={() => setShowInstallGuide(true)}>
                📲 Install App
              </button>
            )}

            <button style={{...S.btn,width:"100%",marginBottom:10,fontSize:12,background:"rgba(239,68,68,0.12)",borderColor:"rgba(239,68,68,0.3)"}} onClick={() => { signOut(); onClose(); }}>
              Sign out
            </button>
          </>
        )}

        <button style={{background:"transparent",color:"rgba(255,255,255,0.3)",border:"none",width:"100%",padding:"8px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}} onClick={onClose}>Close</button>
      </div>

      {/* Install guide modal */}
      {showInstallGuide && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:1100,padding:"0 0 20px"}} onClick={() => setShowInstallGuide(false)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(229,184,63,0.3)",borderRadius:"20px 20px 16px 16px",padding:"28px 24px 24px",maxWidth:420,width:"100%",margin:"0 16px"}} onClick={e => e.stopPropagation()}>
            <div style={{color:C.teal,fontSize:18,fontWeight:700,marginBottom:20,textAlign:"center"}}>
              {isIOS ? "Install on iPhone / iPad" : "Install on your device"}
            </div>
            {isIOS ? (
              <ol style={{listStyle:"none",padding:0,margin:"0 0 24px",display:"flex",flexDirection:"column",gap:16}}>
                <li style={{display:"flex",alignItems:"flex-start",gap:12,color:C.white,fontSize:15,lineHeight:1.5}}>
                  <span style={{background:C.teal,color:C.navyDeep,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:1}}>1</span>
                  <span>Tap the <strong>Share</strong> button <span style={{fontSize:18,verticalAlign:"middle"}}>⎙</span> at the bottom of Safari</span>
                </li>
                <li style={{display:"flex",alignItems:"flex-start",gap:12,color:C.white,fontSize:15,lineHeight:1.5}}>
                  <span style={{background:C.teal,color:C.navyDeep,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:1}}>2</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </li>
                <li style={{display:"flex",alignItems:"flex-start",gap:12,color:C.white,fontSize:15,lineHeight:1.5}}>
                  <span style={{background:C.teal,color:C.navyDeep,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:1}}>3</span>
                  <span>Tap <strong>Add</strong> — the app icon appears on your home screen</span>
                </li>
              </ol>
            ) : (
              <ol style={{listStyle:"none",padding:0,margin:"0 0 24px",display:"flex",flexDirection:"column",gap:16}}>
                <li style={{display:"flex",alignItems:"flex-start",gap:12,color:C.white,fontSize:15,lineHeight:1.5}}>
                  <span style={{background:C.teal,color:C.navyDeep,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:1}}>1</span>
                  <span>Tap the <strong>⋮ menu</strong> (three dots) in the top-right of Chrome</span>
                </li>
                <li style={{display:"flex",alignItems:"flex-start",gap:12,color:C.white,fontSize:15,lineHeight:1.5}}>
                  <span style={{background:C.teal,color:C.navyDeep,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:1}}>2</span>
                  <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></span>
                </li>
                <li style={{display:"flex",alignItems:"flex-start",gap:12,color:C.white,fontSize:15,lineHeight:1.5}}>
                  <span style={{background:C.teal,color:C.navyDeep,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:13,flexShrink:0,marginTop:1}}>3</span>
                  <span>Tap <strong>Install</strong> — the app icon appears on your home screen</span>
                </li>
              </ol>
            )}
            <button style={{width:"100%",background:C.teal,color:C.navyDeep,border:"none",borderRadius:10,padding:"13px",fontSize:15,fontWeight:700,cursor:"pointer"}} onClick={() => setShowInstallGuide(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/PremiumModal.jsx — 3 tiers: Free / Premium / Annual
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const PRICING_TIERS = [
  {
    id:       'free',
    label:    'Free',
    price:    '£0',
    sub:      'forever',
    badge:    null,
    aiCalls:  `${AI_LIMITS.free.total} insights/month`,
    color:    'rgba(255,255,255,0.5)',
    features: [
      '✓ Build your supplement stack',
      '✓ Daily log & streak tracking',
      '✓ Browse all protocols',
      '✓ Supplement interaction checker',
      '✓ Supplement deep-dive info',
      `✓ ${AI_LIMITS.free.total} AI insights per month`,
      '✗ No AI Advisor or reports',
      '✗ No advanced trends',
    ],
  },
  {
    id:       'premium',
    label:    'Premium',
    price:    `£${PLAN_PRICING.premium.monthly.toFixed(2)}`,
    sub:      '/month',
    note:     'Billed monthly · Cancel anytime',
    badge:    'MOST POPULAR',
    aiCalls:  `${AI_LIMITS.premium.total} insights/month`,
    color:    '#F5C84A',
    features: [
      '✓ Everything in Free',
      '✓ AI Stack Advisor (ask anything)',
      '✓ Context AI chat (remembers history)',
      '✓ Advanced trends & correlations',
      '✓ Build My Optimal Stack',
      '✓ Weekly AI report to your inbox',
      '✓ Monthly deep-dive report',
      '✓ "Why am I not improving?" analysis',
      '✓ Custom protocol builder',
      '✓ CSV export',
      `✓ ${AI_LIMITS.premium.total} AI insights/month`,
    ],
  },
  {
    id:       'annual',
    label:    'Annual',
    price:    `£${PLAN_PRICING.premium.annual.toFixed(2)}`,
    sub:      '/year',
    badge:    'Save ~50%',
    aiCalls:  `${AI_LIMITS.premium.total} insights/month`,
    color:    '#A78BFA',
    features: [
      '✓ Everything in Premium',
      '✓ ~50% cheaper than monthly',
      '✓ Priority support',
    ],
  },
];

function PremiumModal({ onClose, targetPlan, logs = [] }) {
  const { upgradeToPremium, user } = useUser();
  const [loading, setLoading] = useState(null); // plan id being checked out
  const [checkoutError, setCheckoutError] = useState("");

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || planId === user?.plan) return;
    if (loading) return; // Bug 5: prevent double-click
    setLoading(planId);
    setCheckoutError("");
    try {
      await upgradeToPremium(planId);
      // If no redirect happens something went wrong — error thrown by upgradeToPremium
    } catch(err) {
      setLoading(null);
      setCheckoutError(err?.message ?? "Couldn't start checkout — please try again.");
    }
  };

  const s = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(10,14,23,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: 16, overflowY: 'auto', backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
    },
    modal: {
      background: 'linear-gradient(135deg, rgba(27,58,92,0.95) 0%, rgba(15,37,64,0.95) 100%)',
      border: '1px solid rgba(229,184,63,0.2)',
      borderRadius: 20,
      width: '100%', maxWidth: 680, position: 'relative',
      maxHeight: '90vh', overflowY: 'auto',
      boxShadow: SHADOWS.xl,
      animation: 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    stickyHeader: {
      position: 'sticky', top: 0,
      background: 'linear-gradient(135deg, rgba(27,58,92,0.98) 0%, rgba(15,37,64,0.98) 100%)',
      padding: '32px 28px 0', zIndex: 1, backdropFilter: 'blur(10px)',
    },
    closeBtn: {
      position: 'absolute', top: 16, right: 16,
      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
      color: 'rgba(255,255,255,0.6)', width: 36, height: 36,
      borderRadius: '50%', cursor: 'pointer', fontSize: 18, fontFamily: 'inherit',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.2s ease',
    },
    contentArea: { padding: '0 28px 32px' },
    heading: { color: '#fff', fontSize: 22, fontWeight: 800, marginBottom: 6 },
    sub:     { color: 'rgba(255,255,255,0.55)', fontSize: 14, marginBottom: 28 },
    tiersRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: 14,
    },
    tierCard: (color, isCurrent) => ({
      background: isCurrent ? `${color}12` : 'rgba(255,255,255,0.04)',
      border: `${isCurrent ? '2px' : '1px'} solid ${isCurrent ? color : 'rgba(255,255,255,0.1)'}`,
      borderRadius: 14, padding: 20, position: 'relative',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.2s ease',
      boxShadow: isCurrent ? `0 4px 20px ${color}20` : 'none',
    }),
    badge: (color) => ({
      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
      background: color, color: '#1B3A5C', fontSize: 10, fontWeight: 800,
      letterSpacing: '0.8px', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
    }),
    upgradeBtn: (color, disabled) => ({
      width: '100%', padding: '10px 0', borderRadius: 8,
      background: disabled ? 'rgba(255,255,255,0.08)' : color,
      color: disabled ? 'rgba(255,255,255,0.3)' : '#1B3A5C',
      fontSize: 13, fontWeight: 700, border: 'none',
      cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
    }),
    currentBadge: {
      width: '100%', padding: '10px 0', textAlign: 'center',
      color: 'rgba(255,255,255,0.4)', fontSize: 13,
    },
  };

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        <div style={s.stickyHeader}>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
          <p style={s.heading}>Your 30-Day Stack Optimisation Protocol</p>
          <p style={s.sub}>Here's what the next 30 days looks like with AI guidance:</p>
        </div>
        <div style={s.contentArea}>
          {/* Animated preview chart */}
          {logs.length >= 2 && (() => {
            const last6 = logs.slice(0, 6).reverse();
            const vals = last6.map(l => Number(l.effects?.Energy || 0));
            const maxVal = Math.max(...vals, 1);
            return (
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(229,184,63,0.2)", borderRadius: 14, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 10 }}>Your energy trend ({last6.length} days)</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 60 }}>
                  {vals.map((v, i) => (
                    <div key={i} style={{ flex: 1, background: "linear-gradient(180deg, rgba(229,184,63,0.8) 0%, rgba(229,184,63,0.3) 100%)", borderRadius: "4px 4px 0 0", height: `${Math.max((v / maxVal) * 100, 8)}%`, minHeight: 4, animation: `phs-growUp 0.6s ease ${i * 0.08}s both` }} />
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#FBBF24", fontWeight: 700, marginTop: 8 }}>🔒 Premium shows what's driving your score</div>
              </div>
            );
          })()}

          <div style={{marginBottom:24}}>
            {[
              ["Week 1", "Baseline — log daily, AI identifies your patterns"],
              ["Week 2", "First insight — AI flags what's working and what to adjust"],
              ["Week 3", "Optimisation — timing changes based on your data"],
              ["Week 4", "Protocol review — is your stack still right for where you are now?"],
            ].map(([wk,desc]) => (
              <div key={wk} style={{display:"flex",gap:12,padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.06)",alignItems:"flex-start"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#F5C84A",width:52,flexShrink:0,paddingTop:1}}>{wk}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.5}}>{desc}</div>
              </div>
            ))}
          </div>
          {logs.length >= 3 && (
            <div style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"14px 16px",marginBottom:20,border:"1px solid rgba(255,255,255,0.08)"}}>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:10}}>Based on your {logs.length} days of data, your Premium report would include:</div>
              {[
                "Your Magnesium Glycinate pattern shows consistent correlation with...",
                "On days you logged exercise, your sleep score was...",
                "One supplement in your stack appears redundant based on...",
              ].map((line,i) => (
                <div key={i} style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:6,filter:"blur(4px)",userSelect:"none",lineHeight:1.5}}>{line}</div>
              ))}
              <div style={{fontSize:12,fontWeight:700,color:"#FBBF24",marginTop:8}}>🔒 Upgrade to read your full report</div>
            </div>
          )}
          <div style={s.tiersRow}>
            {PRICING_TIERS.map(tier => {
              const isCurrent = tier.id === user?.plan ||
                (tier.id === 'premium' && user?.plan === 'annual');
              const isLoading = loading === tier.id;
              return (
                <div key={tier.id} style={s.tierCard(tier.color, isCurrent)}>
                  {tier.badge && (
                    <span style={s.badge(tier.color)}>{tier.badge}</span>
                  )}
                  <p style={{color:'#fff',fontSize:15,fontWeight:700,marginBottom:4,paddingTop:tier.badge?8:0}}>
                    {tier.label}
                  </p>
                  <p style={{color:tier.color,fontSize:26,fontWeight:800,lineHeight:1}}>{tier.price}</p>
                  <p style={{color:'rgba(255,255,255,0.4)',fontSize:12,marginBottom:4}}>{tier.sub}</p>
                  {tier.note && <p style={{color:'rgba(255,255,255,0.35)',fontSize:10,marginBottom:8,lineHeight:1.4}}>{tier.note}</p>}
                  <p style={{color:'rgba(255,255,255,0.5)',fontSize:11,marginBottom:14,borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:10}}>{tier.aiCalls}</p>
                  <ul style={{listStyle:'none',padding:0,margin:'0 0 16px'}}>
                    {tier.features.map(f => (
                      <li key={f} style={{color:f.startsWith('✓')?'rgba(255,255,255,0.75)':'rgba(255,255,255,0.3)',fontSize:12,lineHeight:1.6}}>{f}</li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <div style={s.currentBadge}>✓ You are on {tier.label}</div>
                  ) : tier.id === 'free' ? (
                    <div style={s.currentBadge}>—</div>
                  ) : (
                    <button
                      style={s.upgradeBtn(tier.color, isLoading)}
                      disabled={isLoading}
                      onClick={() => handleUpgrade(tier.id)}
                    >
                      {isLoading ? 'Redirecting…' : `Upgrade to ${tier.label}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {checkoutError && (
            <p style={{color:'#F87171',fontSize:12,textAlign:'center',marginTop:12,background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,padding:'8px 14px'}}>
              ⚠️ {checkoutError}
            </p>
          )}
          {user?.plan === "free" && (
            <div style={{textAlign:"center",marginTop:16}}>
              <button
                style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",fontSize:11,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}
                onClick={() => {
                  const nextMonth = new Date();
                  nextMonth.setMonth(nextMonth.getMonth()+1);
                  nextMonth.setDate(1);
                  storage.set("phs_remind_premium", nextMonth.toISOString().slice(0,10));
                  onClose();
                }}
              >
                Remind me next month instead
              </button>
            </div>
          )}
          <p style={{color:'rgba(255,255,255,0.3)',fontSize:11,textAlign:'center',marginTop:12}}>
            Secure payment via Stripe · Cancel anytime · Prices in GBP
          </p>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /components/styles.js — shared styles (module-level, not recreated per render)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const S = {
  app:       {fontFamily:"'DM Sans',sans-serif",background:"#0f2540",minHeight:"100vh",color:C.white,display:"flex",flexDirection:"column"},
  header:    {background:"rgba(15,37,64,0.7)",borderBottom:"1px solid rgba(229,184,63,0.18)",padding:"0 20px",display:"flex",alignItems:"center",justifyContent:"space-between",height:60,backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",position:"sticky",top:0,zIndex:100,boxShadow:"0 1px 0 rgba(255,255,255,0.04)"},
  logo:      {display:"flex",alignItems:"center",gap:9,fontSize:16,fontWeight:700,color:C.white,letterSpacing:"-0.3px"},
  badge:     {background:"rgba(229,184,63,0.1)",border:"1px solid "+C.teal,color:C.teal,padding:"3px 9px",borderRadius:20,fontSize:10,fontFamily:"monospace",letterSpacing:"0.9px"},
  premBtn:   {background:"linear-gradient(135deg,#F59E0B,#F97316)",color:C.white,padding:"4px 13px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",border:"none",fontFamily:"inherit",boxShadow:"0 2px 12px rgba(245,158,11,0.35)",position:"relative",overflow:"hidden"},
  navWrap:   {display:"flex",gap:1,padding:"10px 20px 0",borderBottom:"1px solid rgba(255,255,255,0.06)",overflowX:"auto",scrollbarWidth:"none"},
  navBtn:    (a) => ({padding:"8px 14px",borderRadius:"7px 7px 0 0",border:"none",cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:a?700:400,background:a?"rgba(229,184,63,0.1)":"transparent",color:a?C.teal:"rgba(255,255,255,0.4)",borderBottom:a?"2px solid "+C.teal:"2px solid transparent",transition:"all .16s",whiteSpace:"nowrap",boxShadow:a?"0 0 12px rgba(229,184,63,0.2)":"none"}),
  content:   {padding:"18px 20px 48px",maxWidth:920,margin:"0 auto",overflowY:"auto",flex:1},
  card:      {background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:16,padding:18,marginBottom:12,backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",boxShadow:"0 4px 24px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.06)"},
  cardTitle: {fontSize:10,fontWeight:700,color:C.teal,marginBottom:14,textTransform:"uppercase",letterSpacing:"1.3px",display:"flex",alignItems:"center",gap:8,paddingBottom:10,borderBottom:"1px solid",borderImage:"linear-gradient(90deg, rgba(229,184,63,0.4), transparent) 1"},
  pill:      (on) => ({background:on?C.teal:"rgba(255,255,255,0.06)",color:on?C.navy:"rgba(255,255,255,0.58)",border:on?"none":"1px solid rgba(255,255,255,0.1)",padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",fontWeight:on?700:400,fontFamily:"inherit",transition:"all .14s",boxShadow:on?"0 2px 10px rgba(229,184,63,0.25)":"none"}),
  input:     {width:"100%",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.11)",borderRadius:10,padding:"10px 13px",color:C.white,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"},
  btn:       {background:"linear-gradient(135deg,"+C.teal+","+C.tealDark+")",color:C.navy,border:"none",padding:"10px 20px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(229,184,63,0.3)",transition:"box-shadow 0.2s, transform 0.1s"},
  btnGhost:  (col) => ({background:"transparent",color:col||C.teal,border:"1px solid "+(col||C.teal),padding:"6px 12px",borderRadius:9,fontSize:11,cursor:"pointer",fontFamily:"inherit"}),
  btnAmber:  {background:"linear-gradient(135deg,#F59E0B,#F97316)",color:C.white,border:"none",padding:"10px 20px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"},
  suppRow:   {display:"flex",alignItems:"flex-start",justifyContent:"space-between",padding:"11px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"},
  tag:       (col) => ({background:col+"1e",color:col,border:"1px solid "+col+"38",padding:"2px 7px",borderRadius:7,fontSize:10,display:"inline-block"}),
  checkbox:  (on) => ({width:26,height:26,borderRadius:7,border:on?"none":"2px solid rgba(255,255,255,0.18)",background:on?C.teal:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0,transition:"all .14s",userSelect:"none"}),
  ratingBtn: (v,cur) => ({width:33,height:33,borderRadius:"50%",border:cur===v?"2px solid "+C.teal:"1px solid rgba(255,255,255,0.16)",background:cur===v?C.teal+"22":"transparent",color:cur===v?C.teal:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",transition:"all .14s"}),
  modal:     {position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:900,backdropFilter:"blur(5px)"},
  modalCard: {background:C.navyDeep,border:"1px solid "+C.teal,borderRadius:20,padding:36,maxWidth:440,width:"90%",textAlign:"center"},
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /pages/StackPage.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Persistent personalised stack guidance from onboarding — what to add / review.
// Stored in localStorage (keyed by uid) so it survives logout/login until dismissed.
function StackAdviceBanner() {
  const { user } = useUser();
  const { stack, toggleStack, setToast } = useApp();
  const [advice, setAdvice] = useState(() => {
    try { return storage.get('phs_stack_advice', null); } catch { return null; }
  });
  if (!advice || !user?.isLoggedIn || advice.uid !== user.id || advice.dismissed) return null;

  const toAdd    = (advice.add    || []).filter(s => !stack.includes(s));
  const toReview = (advice.review || []).filter(s =>  stack.includes(s));
  if (!advice.action && toAdd.length === 0 && toReview.length === 0) return null;

  const dismiss = () => {
    const next = { ...advice, dismissed: true };
    try { storage.set('phs_stack_advice', next); } catch {}
    setAdvice(next);
  };
  const addSupp = (s) => { toggleStack(s); setToast(`Added ${s} to your stack`); };

  return (
    <div style={{position:"relative",background:"linear-gradient(135deg,rgba(245,214,122,0.13),rgba(229,184,63,0.05))",border:"1px solid rgba(229,184,63,0.3)",borderRadius:16,padding:"16px 18px",marginBottom:12,boxShadow:"0 6px 24px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.05)"}}>
      <button onClick={dismiss} title="Dismiss" style={{position:"absolute",top:10,right:12,background:"none",border:"none",color:"rgba(255,255,255,0.35)",cursor:"pointer",fontSize:17,lineHeight:1,fontFamily:"inherit"}}>×</button>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <span style={{fontSize:16}}>📋</span>
        <div style={{fontSize:11,fontWeight:700,color:"#F5D67A",textTransform:"uppercase",letterSpacing:"1.1px"}}>Your personalised stack plan</div>
      </div>
      {advice.action && (
        <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.6,marginBottom:(toAdd.length||toReview.length)?13:0,paddingRight:14}}>
          <span style={{color:"#F5D67A",fontWeight:700}}>Do this first: </span>{advice.action}
        </div>
      )}
      {toAdd.length > 0 && (
        <div style={{marginBottom:toReview.length?13:0}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginBottom:8}}>Recommended for {advice.goal ? `your "${advice.goal}" goal` : "your goal"} — tap to add:</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
            {toAdd.map(s=>(
              <button key={s} onClick={()=>addSupp(s)} style={{display:"inline-flex",alignItems:"center",gap:5,background:"rgba(229,184,63,0.12)",border:"1px solid rgba(229,184,63,0.4)",color:"#F5D67A",borderRadius:30,padding:"6px 13px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                <span style={{fontWeight:800,fontSize:14}}>+</span> {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {toReview.length > 0 && (
        <div style={{fontSize:12,color:"rgba(255,255,255,0.58)",lineHeight:1.55}}>
          <span style={{color:"#FBBF24",fontWeight:700}}>Worth reviewing: </span>
          {toReview.join(", ")} — {toReview.length>1?"these are lower-evidence forms":"this is a lower-evidence form"}; a better-absorbed option may serve your goal better.
        </div>
      )}
      <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:12,fontStyle:"italic"}}>This plan stays here until you dismiss it. Informational only — not medical advice.</div>
    </div>
  );
}

function StackPage({ onOpenDrawer, onOpenBuilder, onShareStack }) {
  const { stack, logs, toggleStack, loadProtocol, customProtocols, activeProtocol, stackDosages, setStackDosages } = useApp();
  const { user, setShowAuth } = useUser();
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = Object.keys(SUPPLEMENT_DB).filter(s =>
    s.toLowerCase().includes(search.toLowerCase()) ||
    SUPPLEMENT_DB[s].tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );
  const displayLimit = showAll ? filtered.length : 24;
  const warnings = getInteractions(stack).filter(i => i.severity !== "none");

  return (
    <>
      <StackAdviceBanner />
      {user.isLoggedIn && stack.length > 0 && (() => {
        const liveScore = computeLiveScore(stack, logs);
        const recommendation = logs.length < 3
          ? `Log ${3 - logs.length} more day${3 - logs.length !== 1 ? "s" : ""} to unlock pattern analysis.`
          : `Based on ${logs.length} days of data · ${liveScore >= 58 ? `↑ ${liveScore - 58} above average` : `↓ ${58 - liveScore} below average`}`;
        return (
          <>
            <StackScoreHero
              stackSize={stack.length}
              score={liveScore}
              maxScore={100}
              recommendation={recommendation}
            />
            <div style={{fontSize:9,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:-12,marginBottom:12}}>
              Score is a relative quality indicator only · {DISCLAIMER_SHORT}
            </div>
          </>
        );
      })()}
      {user.isLoggedIn && user.onboarding_assessment && logs.length < 7 && (
        <details style={{...S.card, marginBottom:12}}>
          <summary style={{cursor:"pointer", fontSize:12, fontWeight:700, color:"#F5C84A", listStyle:"none", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
            📋 Your Starting Analysis
            <span style={{fontSize:10, color:"rgba(255,255,255,0.3)", fontWeight:400}}>tap to expand</span>
          </summary>
          <div style={{marginTop:12}}>
            {user.onboarding_assessment.headline && (
              <p style={{fontSize:13, fontStyle:"italic", color:"rgba(255,255,255,0.8)", borderLeft:"3px solid #F5C84A", paddingLeft:10, marginBottom:12}}>
                "{user.onboarding_assessment.headline}"
              </p>
            )}
            <div style={{fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.6, marginBottom:6}}>
              <span style={{color:"#F5C84A", fontWeight:700}}>Working: </span>{user.onboarding_assessment.working}
            </div>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.6, marginBottom:6}}>
              <span style={{color:"#FBBF24", fontWeight:700}}>Gap: </span>{user.onboarding_assessment.gap ?? user.onboarding_assessment.improve}
            </div>
            <div style={{fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.6}}>
              <span style={{color:"#A78BFA", fontWeight:700}}>Day 1 move: </span>{user.onboarding_assessment.action}
            </div>
          </div>
        </details>
      )}
      <div style={S.card}>
        <div style={S.cardTitle}>
          My Stack
          <span style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
            {stack.length > 0 && onShareStack && (
              <button onClick={onShareStack} style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.6)",borderRadius:8,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:500}}>
                Share My Stack
              </button>
            )}
            <span style={{fontSize:11,color:"rgba(255,255,255,0.32)",fontWeight:400,textTransform:"none",letterSpacing:0}}>
              {stack.length} supplement{stack.length !== 1 ? "s" : ""}
              {warnings.length > 0 && <span style={{color:C.orange,marginLeft:8}}>· {warnings.length} timing note{warnings.length !== 1 ? "s" : ""}</span>}
            </span>
          </span>
        </div>
        {stack.length === 0
          ? <div style={{textAlign:"center",padding:"40px 20px",border:"1px dashed rgba(229,184,63,0.18)",borderRadius:16,background:"rgba(229,184,63,0.02)"}}><div style={{fontSize:32,marginBottom:12,opacity:0.4}}>📦</div><div style={{fontSize:13,color:"rgba(255,255,255,0.35)",lineHeight:1.6}}>No supplements added — search below or load a protocol</div></div>
          : stack.map(s => {
            const info = SUPPLEMENT_DB[s];
            const dos = stackDosages[s] || {};
            const UNITS = ['mg','g','mcg','IU','ml','capsules','tablets','drops'];
            return (
              <div key={s} style={{...S.suppRow, borderLeft:`3px solid ${evidenceColor(info?.evidence)}`, paddingLeft:10}}>
                <div style={{flex:1,minWidth:0,paddingRight:8}}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:3}}>{s}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:4}}>{info?.dose} · {info?.timing}</div>
                  {/* Dosage inline editor */}
                  <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:5}}>
                    <input
                      type="number" min="0" step="any"
                      placeholder="e.g. 300"
                      value={dos.dosage_amount ?? ''}
                      onChange={e => {
                        const val = e.target.value === '' ? null : parseFloat(e.target.value);
                        setStackDosages(prev => ({ ...prev, [s]: { dosage_amount: val, dosage_unit: prev[s]?.dosage_unit || 'mg' } }));
                      }}
                      style={{width:64,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,padding:"3px 6px",color:C.white,fontSize:11,fontFamily:"inherit",outline:"none"}}
                    />
                    <select
                      value={dos.dosage_unit || 'mg'}
                      onChange={e => setStackDosages(prev => ({ ...prev, [s]: { dosage_amount: prev[s]?.dosage_amount ?? null, dosage_unit: e.target.value } }))}
                      style={{background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,padding:"3px 5px",color:"rgba(255,255,255,0.7)",fontSize:11,fontFamily:"inherit",outline:"none",cursor:"pointer"}}
                    >
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                    <span style={S.tag(evidenceColor(info?.evidence))}>{info?.evidence}</span>
                    <BuyButtons name={s} compact />
                    {PHS_SUPPLEMENT_LINKS[s] && (
                      <a href={PHS_SUPPLEMENT_LINKS[s]} target="_blank" rel="noopener" style={{color:C.teal,fontSize:10,textDecoration:"none",fontWeight:600}}>Learn more ↗</a>
                    )}
                  </div>
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0,alignItems:"center"}}>
                  <button style={{...S.pill(false),fontSize:11,padding:"4px 10px",background:"rgba(229,184,63,0.08)",color:C.teal,border:"1px solid rgba(229,184,63,0.2)"}} onClick={() => onOpenDrawer(s)}>Details</button>
                  <button style={{...S.pill(false),background:"rgba(239,68,68,0.1)",color:C.red,border:"1px solid rgba(239,68,68,0.22)",fontSize:11,padding:"4px 10px"}} onClick={() => toggleStack(s)}>Remove</button>
                </div>
              </div>
            );
          })
        }
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>
          Add Supplements
          <span style={{marginLeft:"auto",fontSize:10,color:"rgba(255,255,255,0.28)",fontWeight:400,textTransform:"none",letterSpacing:0}}>{filtered.length} in database</span>
        </div>
        <input style={S.input} placeholder="Search by name or effect — sleep, joint, energy, stress, tendon..." value={search} onChange={e => {setSearch(e.target.value);setShowAll(false);}} />
        <div style={{marginTop:11,display:"flex",flexWrap:"wrap",gap:6}}>
          {filtered.slice(0,displayLimit).map((s, index) => (<button key={s} style={{...S.pill(stack.includes(s)), animation:"slideInUp 0.35s ease both", animationDelay:`${Math.min(index*0.04,0.5)}s`}} onClick={() => onOpenDrawer(s)}>{stack.includes(s)?"✓ ":""}{s}</button>))}
        </div>
        {filtered.length > displayLimit && <button style={{...S.btnGhost(),marginTop:10,fontSize:11}} onClick={() => setShowAll(true)}>+ Show {filtered.length - displayLimit} more</button>}
      </div>

      {stack.length >= 2 && (() => {
        const _interactions = getInteractions(stack);
        const _warnings  = _interactions.filter(i => i.severity !== "none");
        const _synergies = _interactions.filter(i => i.severity === "none");
        return (
          <>
            <div style={{...S.card,paddingBottom:8}}>
              <div style={S.cardTitle}>⚡ Interactions</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:9,marginBottom:12}}>
                {[{label:"Cautions",count:_warnings.filter(w=>w.severity==="moderate").length,color:C.orange},{label:"Mild Notes",count:_warnings.filter(w=>w.severity==="mild").length,color:C.amber},{label:"Synergies",count:_synergies.length,color:C.green}].map(m => (
                  <div key={m.label} style={{background:"rgba(255,255,255,0.04)",borderRadius:10,textAlign:"center",padding:14}}>
                    <div style={{fontSize:22,fontWeight:700,color:m.color}}>{m.count}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.36)",marginTop:3}}>{m.label}</div>
                  </div>
                ))}
              </div>
              {_warnings.length > 0 && (
                <>
                  <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.5)",marginBottom:8}}>⚠️ Timing Considerations</div>
                  {_warnings.map((w,i) => (
                    <div key={i} style={{padding:"10px 0",borderBottom:i<_warnings.length-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:5,flexWrap:"wrap"}}>
                        <span style={S.tag(severityColor(w.severity))}>{w.severity.toUpperCase()}</span>
                        <span style={{fontSize:13,fontWeight:600}}>{w.pair[0]} + {w.pair[1]}</span>
                      </div>
                      <div style={{fontSize:13,color:"rgba(255,255,255,0.56)",lineHeight:1.6}}>{w.note}</div>
                    </div>
                  ))}
                </>
              )}
              {_synergies.length > 0 && (
                <>
                  <div style={{fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.5)",marginTop:_warnings.length?14:0,marginBottom:8}}>Synergistic Combinations</div>
                  {_synergies.map((s,i) => (
                    <div key={i} style={{padding:"10px 0",borderBottom:i<_synergies.length-1?"1px solid rgba(255,255,255,0.05)":"none"}}>
                      <div style={{fontSize:13,fontWeight:600,marginBottom:4,color:C.green}}>{s.pair[0]} + {s.pair[1]}</div>
                      <div style={{fontSize:13,color:"rgba(255,255,255,0.56)",lineHeight:1.6}}>{s.note}</div>
                    </div>
                  ))}
                </>
              )}
              {!_warnings.length && !_synergies.length && (
                <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:32,marginBottom:8}}>✅</div>
                  <div style={{color:"rgba(255,255,255,0.5)",fontSize:13}}>No known interactions in your current stack.</div>
                </div>
              )}
            </div>
          </>
        );
      })()}

      <div style={S.card}>
        <div style={S.cardTitle}>
          Quick Load a Protocol
          <button style={{marginLeft:"auto",...S.btn,fontSize:11,padding:"5px 12px"}} onClick={() => {
            if (!canAccess("customProtocols", user.plan)) {
              user.isLoggedIn ? null : setShowAuth(true);
            } else {
              onOpenBuilder(null);
            }
          }}>
            {canAccess("customProtocols", user.plan) ? "+ Create Protocol" : user.isLoggedIn ? "⭐ Premium" : "+ Create"}
          </button>
        </div>
        {Object.entries(PROTOCOL_CATEGORIES).map(([cat, names]) => (
          <div key={cat} style={{marginBottom:14}}>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.26)",marginBottom:7,textTransform:"uppercase",letterSpacing:"1.5px"}}>{cat}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {names.filter(n => PROTOCOLS[n]).map(p => (
                <button key={p} style={{...S.btnGhost(PROTOCOLS[p].color),background:activeProtocol===p?PROTOCOLS[p].color+"18":"transparent"}} onClick={() => loadProtocol(p)}>
                  {PROTOCOLS[p].icon} {p}{activeProtocol===p?" ✓":""}
                </button>
              ))}
            </div>
          </div>
        ))}
        {customProtocols.length > 0 && (
          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.26)",marginBottom:7,textTransform:"uppercase",letterSpacing:"1.5px"}}>My Protocols</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {customProtocols.map(p => (<button key={p.name} style={{...S.btnGhost(p.color),background:activeProtocol===p.name?p.color+"18":"transparent"}} onClick={() => loadProtocol(p.name,true)}>{p.icon} {p.name}{activeProtocol===p.name?" ✓":""}</button>))}
            </div>
          </div>
        )}
      </div>

      {stack.length >= 2 && (
        <button style={{width:"100%",background:"rgba(229,184,63,0.08)",color:C.teal,border:"1px dashed rgba(229,184,63,0.3)",borderRadius:12,padding:"11px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}
          onClick={() => {
            if (!canAccess("customProtocols", user.plan)) {
              user.isLoggedIn ? null : setShowAuth(true);
              return;
            }
            onOpenBuilder({name:"",icon:"📋",color:C.teal,description:"",supplements:[...stack],custom:true});
          }}>
          <span style={{fontSize:16}}>＋</span>
          {canAccess("customProtocols", user.plan) ? "Save current stack as a custom protocol" : "Sign in to save custom protocols"}
        </button>
      )}
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function isWithin3DaysOfSignup(user) {
  if (!user?.created_at) return false;
  return (Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24) < 3;
}

function computeStreak(logs) {
  if (!logs.length) return 0;
  const toISO = d => {
    const parts = d.split("/");
    if (parts.length === 3) return `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}`;
    return d;
  };
  const todayISO = toISO(new Date().toLocaleDateString("en-GB"));
  const sorted = [...logs].sort((a,b) => toISO(b.date).localeCompare(toISO(a.date)));
  // Subtract one day from a YYYY-MM-DD string using UTC noon to avoid DST/timezone traps
  const prevDay = (isoDate) => {
    const d = new Date(isoDate + "T12:00:00Z");
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  const walkStreak = (startISO) => {
    let s = 0;
    let expected = startISO;
    for (const log of sorted) {
      const iso = toISO(log.date);
      if (iso === expected && log.taken?.length > 0) {
        s++;
        expected = prevDay(expected);
      } else break;
    }
    return s;
  };
  const streak = walkStreak(todayISO);
  if (streak > 0) return streak;
  // grace period: today not yet logged — show yesterday's streak
  return walkStreak(prevDay(todayISO));
}

function computeMicroInsight(logs) {
  if (logs.length < 5) return null;
  const stackSize = logs[0]?.taken?.length || 1;
  const fullDays    = logs.filter(l => l.taken?.length >= stackSize);
  const partialDays = logs.filter(l => !l.taken?.length || l.taken.length < stackSize);
  const avg = (arr, key) => {
    const vals = arr.map(l => l.effects?.[key]).filter(v => v != null);
    return vals.length ? (vals.reduce((a,b) => a + Number(b), 0) / vals.length).toFixed(1) : null;
  };
  const fullEnergy = avg(fullDays, "Energy");
  const partEnergy = avg(partialDays, "Energy");
  if (!fullEnergy || !partEnergy) return null;
  const diff = (parseFloat(fullEnergy) - parseFloat(partEnergy)).toFixed(1);
  if (Math.abs(diff) < 0.3) return null;
  return `On days you take your full stack, energy averages ${fullEnergy} vs ${partEnergy} on days you miss any.`;
}

// /pages/LogPage.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const EXERCISE_LABELS = ['','Rest','Light','Moderate','Hard','Intense'];

function LogPage() {
  const { stack, logs, saveLog, deleteLog, setToast, customTimings, setCustomTimings, stackDosages } = useApp();
  const { user, setShowAuth } = useUser();
  const { consume, canUse } = useAiUsage();
  const canOptimiseTiming = canAccess("aiAdvisor", user?.plan ?? "guest");

  const todayDateStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone
  const [selectedDate, setSelectedDate] = useState(todayDateStr);
  const isToday = selectedDate === todayDateStr;
  const selectedLog = logs.find(l => l.date === selectedDate);
  const hasExisting = !!selectedLog;

  // Form state — initialised from today's log
  const todayExisting = logs.find(l => l.date === todayDateStr);
  const [takenMap, setTakenMap]             = useState(() => {
    if (!todayExisting) return {};
    const m = {}; todayExisting.taken?.forEach(s => { m[s] = true; }); return m;
  });
  const [effectRatings, setEffectRatings]   = useState(() => {
    const e = todayExisting?.effects || {};
    const out = {};
    EFFECTS.forEach(k => { if (e[k] != null) out[k] = e[k]; });
    return out;
  });
  const [exerciseIntensity, setExerciseIntensity] = useState(() => todayExisting?.effects?.exercise_intensity ?? null);
  const [notes, setNotes]                   = useState(() => todayExisting?.notes ?? "");
  const [advancedMetrics, setAdvancedMetrics] = useState(() => extractAdvancedMetrics(todayExisting?.effects || {}));
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete]   = useState(null);
  // Only show quick check-in if today has NO existing log yet.
  // If the user already saved today (even via check-in), show full form directly.
  const [checkinMode, setCheckinMode]       = useState(() => !todayExisting);
  const [checkinVal, setCheckinVal]         = useState(null);
  const [microInsight, setMicroInsight]     = useState("");
  // Split timing state
  const [timingEditSupp, setTimingEditSupp] = useState(null); // supplement name open in edit modal
  const [timingEditSlots, setTimingEditSlots] = useState([]); // working copy of slots
  const [timingPreview, setTimingPreview]   = useState(null); // AI preview: array of { supplement_name, slots }
  const [timingLoading, setTimingLoading]   = useState(false);
  const [timingError, setTimingError]       = useState("");

  const sortedStack = sortByTiming(stack);
  const takenCount = stack.filter(s => takenMap[s]).length;

  // Re-populate form when selectedDate changes
  useEffect(() => {
    const log = logs.find(l => l.date === selectedDate);
    const e = log?.effects || {};
    const coreR = {};
    EFFECTS.forEach(k => { if (e[k] != null) coreR[k] = e[k]; });
    setTakenMap(log ? (() => { const m = {}; log.taken?.forEach(s => { m[s] = true; }); return m; })() : {});
    setEffectRatings(coreR);
    setExerciseIntensity(e.exercise_intensity ?? null);
    setAdvancedMetrics(extractAdvancedMetrics(e));
    setNotes(log?.notes ?? "");
  }, [selectedDate, logs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync takenMap when stack changes (removes stale items)
  useEffect(() => {
    setTakenMap(prev => {
      const synced = {};
      stack.forEach(s => { if (prev[s]) synced[s] = true; });
      return synced;
    });
  }, [stack]);

  const markAll = () => { const a = {}; stack.forEach(s => { a[s] = true; }); setTakenMap(a); };

  const handleSave = () => {
    if (!stack.length) { setToast("Add supplements to your stack first"); return; }
    // Build effects: start from any EXISTING saved effects for this date (preserves
    // data set in a different session or via the other save path),
    // then overlay what the user has actively set in the current form.
    const existingEffects = selectedLog?.effects ?? {};
    const effects = { ...existingEffects };
    // Overlay core ratings (only keys the user has actively rated)
    Object.entries(effectRatings).forEach(([k, v]) => { if (v != null) effects[k] = v; });
    // Overlay exercise intensity
    if (exerciseIntensity != null) effects.exercise_intensity = exerciseIntensity;
    // Overlay advanced metrics
    Object.entries(advancedMetrics).forEach(([k, v]) => {
      if (v === null || v === '') return;
      const parsed = k === 'sleep_duration_mins' ? parseSleepDuration(v) : (typeof v === 'string' ? parseFloat(v) : v);
      if (parsed != null && !isNaN(parsed)) effects[k] = parsed;
    });
    saveLog({ date: selectedDate, taken: Object.keys(takenMap).filter(k => takenMap[k]), effects, notes, stack_snapshot: stack });
    setToast(hasExisting ? "Log updated ✓" : "Log saved ✓");
    if (!isToday) setSelectedDate(todayDateStr); // return to today after editing past entry
  };

  const handleDelete = (date) => {
    deleteLog(date);
    setConfirmDelete(null);
    setToast("Log deleted");
    if (date === todayDateStr) { setTakenMap({}); setEffectRatings({}); setExerciseIntensity(null); setAdvancedMetrics(extractAdvancedMetrics({})); setNotes(""); }
  };

  // ── Split timing helpers ───────────────────────────────────────────────────
  const getSplitSlots = s => Array.isArray(customTimings[s]) ? customTimings[s] : null;

  const openTimingEdit = s => {
    const existing = getSplitSlots(s);
    const dos = stackDosages?.[s];
    setTimingEditSlots(existing ?? (dos?.dosage_amount ? [{ time_label: SUPPLEMENT_DB[s]?.timing || "Daily", dosage_amount: dos.dosage_amount, dosage_unit: dos.dosage_unit || 'mg', notes: "" }] : [{ time_label: "", dosage_amount: "", dosage_unit: "mg", notes: "" }]));
    setTimingEditSupp(s);
  };

  const saveTimingEdit = () => {
    const valid = timingEditSlots.filter(sl => sl.time_label?.trim());
    if (!valid.length) { setCustomTimings(prev => { const n={...prev}; delete n[timingEditSupp]; return n; }); }
    else setCustomTimings(prev => ({ ...prev, [timingEditSupp]: valid }));
    setTimingEditSupp(null);
    setToast("Timing saved ✓");
  };

  const runTimingAI = async () => {
    if (!stack.length) return;
    if (logs.length < MIN_LOGS_FOR_AI) {
      setTimingError(`Log ${MIN_LOGS_FOR_AI - logs.length} more day${MIN_LOGS_FOR_AI - logs.length !== 1 ? 's' : ''} to unlock AI analysis.`);
      return;
    }
    const commitUsage = consume();
    if (!commitUsage) { setTimingError("AI limit reached — upgrade to continue."); return; }
    setTimingLoading(true); setTimingError("");
    try {
      const stackStr = stack.map(s => { const d = stackDosages?.[s]; return d?.dosage_amount ? `${s} ${d.dosage_amount}${d.dosage_unit||'mg'}` : s; }).join(", ");
      const prompt = `You are a supplement timing expert. The user's stack is: ${stackStr}.\nExercise intensity today: ${exerciseIntensity ?? "unknown"}/5.\nFor each supplement, recommend the optimal split timing considering absorption, interactions, timing relative to exercise, sleep, and meals.\nReturn ONLY valid JSON:\n{"timing":[{"supplement_name":"Magnesium Glycinate","slots":[{"time_label":"After exercise","dosage_amount":200,"dosage_unit":"mg","notes":"Within 30 mins post-workout"},{"time_label":"30 mins before bed","dosage_amount":200,"dosage_unit":"mg","notes":"Supports sleep onset"}]}]}`;
      const raw = await callAI({ system: "Supplement timing expert. Return ONLY valid JSON. No markdown. No other text.", prompt, max_tokens: 700 });
      const parsed = JSON.parse(raw.replace(/```json|```/g,"").trim());
      if (!parsed.timing || !Array.isArray(parsed.timing)) throw new Error("bad_format");
      commitUsage();
      setTimingPreview(parsed.timing);
    } catch(e) {
      setTimingError(e.message === "usage_limit" ? "Monthly AI limit reached." : "AI returned an unexpected format — please try again.");
      console.error("[TimingAI]", e);
    } finally {
      setTimingLoading(false);
    }
  };

  const acceptTimingPreview = () => {
    const updated = { ...customTimings };
    timingPreview.forEach(item => {
      if (stack.includes(item.supplement_name) && Array.isArray(item.slots) && item.slots.length) {
        updated[item.supplement_name] = item.slots;
      }
    });
    setCustomTimings(updated);
    setTimingPreview(null);
    setToast("Timing optimised ✓");
  };

  const groups = TIMING_ORDER.reduce((acc, t) => {
    const s = sortedStack.filter(x => SUPPLEMENT_DB[x]?.timing === t);
    if (s.length) acc.push({ timing: t, items: s });
    return acc;
  }, []);

  // Build past 7 days date options
  const dateOptions = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(); d.setDate(d.getDate() - i);
    dateOptions.push(d.toLocaleDateString('en-CA'));
  }

  const anyAdvanced = Object.values(advancedMetrics).some(v => v !== null && v !== '');
  const streak = computeStreak(logs);
  const logsUntilUnlock = Math.max(0, 7 - logs.length);

  return (
    <>
      {/* Streak + AI countdown header */}
      {(streak > 0 || logsUntilUnlock > 0) && (
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          {streak > 0 && (
            <div style={{fontSize:13,fontWeight:700,color:"#FBBF24"}}>🔥 {streak}-day streak</div>
          )}
          {logsUntilUnlock > 0 && (
            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>
              AI analysis unlocks in <span style={{color:"#F5C84A",fontWeight:700}}>{logsUntilUnlock}</span> more log{logsUntilUnlock!==1?"s":""}
              <div style={{height:4,background:"rgba(255,255,255,0.08)",borderRadius:2,marginTop:4,overflow:"hidden",width:120}}>
                <div style={{height:"100%",width:`${Math.min(logs.length/7*100,100)}%`,background:"#F5C84A",borderRadius:2,transition:"width .4s"}}/>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick check-in card — shown for today when not yet completed */}
      {checkinMode && isToday && (
        <div style={{...S.card,borderColor:"rgba(229,184,63,0.3)",marginBottom:12}}>
          <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>Did you take your stack today?</div>
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            {[
              {val:"all",   label:"✓ Yes, all of them", col:"#F5C84A"},
              {val:"partial",label:"⚡ Partial",          col:"#FBBF24"},
              {val:"skip",  label:"✗ Skipped today",     col:"#EF4444"},
            ].map(o => (
              <button key={o.val}
                style={{flex:1,padding:"10px 6px",borderRadius:9,border:`1px solid ${checkinVal===o.val?o.col:"rgba(255,255,255,0.12)"}`,background:checkinVal===o.val?o.col+"22":"rgba(255,255,255,0.04)",color:checkinVal===o.val?o.col:"rgba(255,255,255,0.6)",fontSize:12,fontWeight:checkinVal===o.val?700:400,cursor:"pointer",fontFamily:"inherit"}}
                onClick={() => {
                  setCheckinVal(o.val);
                  if (o.val==="all") {
                    const newMap = {}; stack.forEach(s => { newMap[s] = true; }); setTakenMap(newMap);
                  } else if (o.val==="skip") {
                    setTakenMap({});
                  }
                  // "partial" leaves takenMap for manual checkbox selection
                }}
              >{o.label}</button>
            ))}
          </div>
          {checkinVal && (
            <div style={{marginTop:4}}>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:10}}>How are you feeling right now?</div>
              {EFFECTS.map(effect => (
                <div key={effect} style={{marginBottom:10}}>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.65)",marginBottom:5}}>{effect}</div>
                  <div style={{display:"flex",gap:5}}>
                    {[1,2,3,4,5].map(v => (
                      <button key={v} style={S.ratingBtn(v, effectRatings[effect])}
                        onClick={() => setEffectRatings(p => ({...p,[effect]:v}))}>{v}</button>
                    ))}
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.26)",marginLeft:4,alignSelf:"center"}}>{effectRatings[effect]?EFFECT_LABELS[effectRatings[effect]]:"Rate 1–5"}</span>
                  </div>
                </div>
              ))}
              <div style={{marginBottom:10}}>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.65)",marginBottom:5}}>Exercise Intensity (optional)</div>
                <div style={{display:"flex",gap:5}}>
                  {[1,2,3,4,5].map(v => (
                    <button key={v} title={EXERCISE_LABELS[v]}
                      style={{...S.ratingBtn(v, exerciseIntensity), minWidth:33}}
                      onClick={() => setExerciseIntensity(prev => prev === v ? null : v)}>{v}</button>
                  ))}
                </div>
              </div>
              <textarea
                style={{...S.input,height:60,resize:"vertical",marginTop:4}}
                placeholder="Notes (optional)..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
              <button style={{...S.btn,width:"100%",marginTop:6}}
                onClick={() => {
                  handleSave();
                  setCheckinMode(false);
                  const freshEntry = { date: selectedDate, taken: Object.keys(takenMap).filter(k => takenMap[k]), effects: { ...effectRatings } };
                  const insight = computeMicroInsight([freshEntry, ...logs]);
                  if (insight) setMicroInsight(insight);
                }}
              >Save Check-In →</button>
            </div>
          )}
        </div>
      )}

      {/* Micro-insight flash after save */}
      {microInsight && (
        <div style={{...S.card,borderColor:"rgba(229,184,63,0.3)",marginBottom:12}}>
          <div style={{fontSize:11,color:"#F5C84A",fontWeight:700,marginBottom:4}}>📊 Day {logs.length} update</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.55}}>{microInsight}</div>
        </div>
      )}

      {/* Date selector (Feature 3) */}
      <div style={{...S.card,marginBottom:10}}>
        <div style={S.cardTitle}>Select Date</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {dateOptions.map((d, i) => {
            const isSelected = d === selectedDate;
            const label = i === 0 ? "Today" : i === 1 ? "Yesterday" : new Date(d + 'T00:00:00').toLocaleDateString("en-GB");
            return (
              <button key={d} onClick={() => setSelectedDate(d)}
                style={{padding:"5px 11px",borderRadius:20,fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:isSelected?700:400,background:isSelected?C.teal:"transparent",color:isSelected?C.navy:"rgba(255,255,255,0.5)",border:"1px solid "+(isSelected?C.teal:"rgba(255,255,255,0.15)"),transition:"all .14s"}}>
                {label}
              </button>
            );
          })}
        </div>
        {!isToday && (
          <div style={{marginTop:10,padding:"7px 10px",background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:8,fontSize:11,color:C.amber,display:"flex",alignItems:"center",gap:6}}>
            ✏️ <strong>Editing: {new Date(selectedDate + 'T00:00:00').toLocaleDateString("en-GB")}</strong> — any AI insights based on this entry may be outdated after saving.
          </div>
        )}
      </div>

      {/* Supplement checklist */}
      <div style={S.card}>
        <div style={S.cardTitle}>
          {isToday ? "Today's Supplements" : `Supplements — ${new Date(selectedDate + 'T00:00:00').toLocaleDateString("en-GB")}`}
          <span style={{marginLeft:"auto",fontSize:11,color:"rgba(255,255,255,0.32)",fontWeight:400,textTransform:"none",letterSpacing:0}}>
            {isToday && new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}
          </span>
        </div>
        {!user.isLoggedIn && (
          <div style={{fontSize:11,color:C.amber,marginBottom:10,padding:"7px 10px",background:"rgba(245,158,11,0.08)",borderRadius:8}}>
            Your log won't be saved — <button onClick={()=>setShowAuth(true)} style={{background:"none",border:"none",color:C.teal,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:700}}>sign in to persist history</button>
          </div>
        )}
        {hasExisting && isToday && (
          <div style={{fontSize:11,color:C.teal,marginBottom:10,padding:"7px 10px",background:"rgba(229,184,63,0.07)",borderRadius:8,display:"flex",alignItems:"center",gap:6}}>
            <span>✓</span> You already logged today — changes will update your existing entry.
          </div>
        )}
        {stack.length === 0
          ? <div style={{color:"rgba(255,255,255,0.3)",fontSize:14,textAlign:"center",padding:"16px 0"}}>Add supplements to your stack first</div>
          : <>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:8,flexWrap:"wrap"}}>
              <span style={{fontSize:12,color:"rgba(255,255,255,0.36)"}}>{takenCount}/{stack.length} taken</span>
              <div style={{display:"flex",gap:6,alignItems:"center",marginLeft:"auto"}}>
                {canOptimiseTiming && (
                  <button
                    style={{...S.btnGhost(C.purple),fontSize:11,padding:"4px 10px",opacity:stack.length===0||timingLoading||logs.length<MIN_LOGS_FOR_AI?0.4:1,cursor:logs.length<MIN_LOGS_FOR_AI?"not-allowed":undefined}}
                    disabled={stack.length===0||timingLoading||!canUse||logs.length<MIN_LOGS_FOR_AI}
                    onClick={runTimingAI}
                    title={logs.length<MIN_LOGS_FOR_AI?"Log 7 days to unlock":!canUse?"AI limit reached":stack.length===0?"Add supplements first":""}>
                    {timingLoading ? "⏳ Optimising..." : "✨ Optimise Timing"}
                  </button>
                )}
                <button style={S.btnGhost()} onClick={markAll}>✓ Mark all taken</button>
              </div>
            </div>
            {timingError && <div style={{fontSize:11,color:C.red,marginBottom:10,padding:"6px 10px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>{timingError}<button style={{float:"right",background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:12}} onClick={()=>setTimingError("")}>✕</button></div>}
            {groups.map(({timing,items}) => (
              <div key={timing}>
                <div style={{fontSize:9,fontWeight:700,color:C.teal,textTransform:"uppercase",letterSpacing:"1.2px",padding:"10px 0 6px",borderTop:"1px solid rgba(229,184,63,0.12)",marginTop:4,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:C.teal,flexShrink:0,opacity:0.7}} /> {timing}
                </div>
                {items.map(s => {
                  const slots = getSplitSlots(s);
                  const dos = stackDosages?.[s];
                  const defaultDoseStr = dos?.dosage_amount ? `${dos.dosage_amount}${dos.dosage_unit||'mg'}` : SUPPLEMENT_DB[s]?.dose;
                  const totalSlotAmount = slots ? slots.reduce((a,sl) => a + (parseFloat(sl.dosage_amount)||0), 0) : null;
                  const slotExceedsTotal = dos?.dosage_amount && totalSlotAmount && totalSlotAmount > parseFloat(dos.dosage_amount);
                  return (
                    <div key={s} style={{padding:"9px 0 9px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:13,fontWeight:500}}>{s}</span>
                            {!slots && <span style={{fontSize:11,color:"rgba(255,255,255,0.36)"}}>{defaultDoseStr}</span>}
                          </div>
                          {!slots && typeof customTimings[s] === "string" && customTimings[s] !== SUPPLEMENT_DB[s]?.timing && (
                            <div style={{fontSize:10,color:C.teal,marginTop:1}}>⏰ {customTimings[s]} <span style={{color:"rgba(255,255,255,0.25)"}}>(AI suggested)</span></div>
                          )}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                          {canOptimiseTiming && (
                            <button style={{background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:14,padding:"2px 4px",lineHeight:1}} title="Edit timing" onClick={() => openTimingEdit(s)}>✏</button>
                          )}
                          <div role="checkbox" aria-checked={!!takenMap[s]} tabIndex={0}
                            style={S.checkbox(!!takenMap[s])}
                            onClick={() => setTakenMap(p => ({...p,[s]:!p[s]}))}
                            onKeyDown={e => (e.key==="Enter"||e.key===" ")&&setTakenMap(p=>({...p,[s]:!p[s]}))}>
                            {takenMap[s]?"✓":""}
                          </div>
                        </div>
                      </div>
                      {slots && (
                        <div style={{marginTop:5,paddingLeft:8,borderLeft:"2px solid rgba(139,92,246,0.3)"}}>
                          {slotExceedsTotal && <div style={{fontSize:10,color:C.amber,marginBottom:4}}>⚠ Split dosages exceed your set total — please review</div>}
                          {slots.map((sl,i) => (
                            <div key={i} style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginBottom:4,display:"flex",flexWrap:"wrap",alignItems:"flex-start",gap:4}}>
                              <span style={{color:C.purple,flexShrink:0}}>⏰</span>
                              <span style={{fontWeight:600}}>{sl.time_label}</span>
                              {sl.dosage_amount && <span style={{color:"rgba(255,255,255,0.38)"}}>— {sl.dosage_amount}{sl.dosage_unit||'mg'}</span>}
                              {sl.notes && <span style={{color:"rgba(255,255,255,0.35)",fontStyle:"italic",fontSize:10,lineHeight:1.4,display:"block",marginTop:2}}>{sl.notes}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        }
      </div>

      {/* How are you feeling */}
      <div style={S.card}>
        <div style={S.cardTitle}>How Are You Feeling{isToday ? " Today" : ` — ${selectedDate}`}?</div>
        {EFFECTS.map(effect => (
          <div key={effect} style={{marginBottom:16}}>
            <div style={{fontSize:12,marginBottom:8,color:"rgba(255,255,255,0.68)",fontWeight:500}}>{effect}</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {[1,2,3,4,5].map(v => (<button key={v} style={S.ratingBtn(v,effectRatings[effect])} onClick={() => setEffectRatings(p=>({...p,[effect]:v}))}>{v}</button>))}
              <span style={{fontSize:11,color:"rgba(255,255,255,0.26)",marginLeft:5}}>{effectRatings[effect]?EFFECT_LABELS[effectRatings[effect]]:"Rate 1-5"}</span>
            </div>
          </div>
        ))}

        {/* Feature 1 — Exercise Intensity */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:12,marginBottom:8,color:"rgba(255,255,255,0.68)",fontWeight:500}}>Exercise Intensity</div>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            {[1,2,3,4,5].map(v => (
              <button key={v}
                title={EXERCISE_LABELS[v]}
                style={{...S.ratingBtn(v, exerciseIntensity), minWidth:33}}
                onClick={() => setExerciseIntensity(prev => prev === v ? null : v)}>
                {v}
              </button>
            ))}
            <span style={{fontSize:11,color:"rgba(255,255,255,0.26)",marginLeft:5}}>
              {exerciseIntensity ? `${EXERCISE_LABELS[exerciseIntensity]}` : "Tap to set (optional)"}
            </span>
          </div>
        </div>

        {/* Feature 6 — Advanced Metrics (collapsible) */}
        <div style={{marginBottom:16}}>
          <button
            onClick={() => setAdvancedExpanded(p => !p)}
            style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontFamily:"inherit",fontSize:12,display:"flex",alignItems:"center",gap:6,padding:"4px 0"}}>
            <span style={{transition:"transform .15s",display:"inline-block",transform:advancedExpanded?"rotate(90deg)":"rotate(0deg)"}}>▶</span>
            Advanced Metrics
            {anyAdvanced && <span style={{width:6,height:6,borderRadius:"50%",background:C.teal,display:"inline-block"}} />}
          </button>
          {advancedExpanded && (
            <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
              {/* Sleep & Recovery */}
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>Sleep &amp; Recovery</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                {[
                  {key:"hrv_overnight",   label:"Overnight HRV", unit:"ms",   placeholder:"e.g. 52"},
                  {key:"resting_hr",      label:"Resting HR",    unit:"bpm",  placeholder:"e.g. 58"},
                  {key:"sleep_duration_mins", label:"Sleep Duration", unit:"", placeholder:"e.g. 7h 30m"},
                  {key:"sleep_score",     label:"Sleep Score",   unit:"/100", placeholder:"e.g. 82"},
                  {key:"deep_sleep_mins", label:"Deep Sleep",    unit:"mins", placeholder:"e.g. 95"},
                  {key:"rem_sleep_mins",  label:"REM Sleep",     unit:"mins", placeholder:"e.g. 110"},
                ].map(({key,label,unit,placeholder}) => (
                  <div key={key}>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:4}}>{label}{unit&&<span style={{color:"rgba(255,255,255,0.22)"}}> {unit}</span>}</div>
                    <input type="text" placeholder={placeholder}
                      value={advancedMetrics[key] ?? ''}
                      onChange={e => setAdvancedMetrics(p => ({...p,[key]:e.target.value||null}))}
                      style={{...S.input,padding:"6px 9px",fontSize:12}} />
                  </div>
                ))}
              </div>
              {/* Vitals */}
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>Vitals</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
                {[
                  {key:"spo2",               label:"SpO2",         unit:"%",     placeholder:"e.g. 97"},
                  {key:"respiration_rate",   label:"Respiration",  unit:"br/min",placeholder:"e.g. 14"},
                  {key:"skin_temp_deviation",label:"Skin Temp Dev",unit:"°C",    placeholder:"e.g. 0.3"},
                ].map(({key,label,unit,placeholder}) => (
                  <div key={key}>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:4}}>{label}<span style={{color:"rgba(255,255,255,0.22)"}}> {unit}</span></div>
                    <input type="text" placeholder={placeholder}
                      value={advancedMetrics[key] ?? ''}
                      onChange={e => {
                        const val = e.target.value || null;
                        setAdvancedMetrics(p => ({...p,[key]:val}));
                      }}
                      style={{...S.input,padding:"6px 9px",fontSize:12}} />
                    {key === 'spo2' && advancedMetrics.spo2 !== null && parseFloat(advancedMetrics.spo2) < 90 && (
                      <div style={{fontSize:10,color:C.red,marginTop:3}}>⚠️ Low SpO2 — if accurate, consider speaking to a doctor</div>
                    )}
                  </div>
                ))}
              </div>
              {/* Activity & Stress */}
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>Activity &amp; Stress</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                {[
                  {key:"body_battery_start",label:"Body Battery Wake",unit:"/100",  placeholder:"e.g. 78"},
                  {key:"body_battery_end",  label:"Body Battery End", unit:"/100",  placeholder:"e.g. 32"},
                  {key:"stress_avg",        label:"Avg Stress",       unit:"/100",  placeholder:"e.g. 28"},
                  {key:"steps",             label:"Steps",            unit:"",      placeholder:"e.g. 8420"},
                  {key:"active_calories",   label:"Active Calories",  unit:"kcal",  placeholder:"e.g. 420"},
                ].map(({key,label,unit,placeholder}) => (
                  <div key={key}>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:4}}>{label}{unit&&<span style={{color:"rgba(255,255,255,0.22)"}}> {unit}</span>}</div>
                    <input type="text" placeholder={placeholder}
                      value={advancedMetrics[key] ?? ''}
                      onChange={e => setAdvancedMetrics(p => ({...p,[key]:e.target.value||null}))}
                      style={{...S.input,padding:"6px 9px",fontSize:12}} />
                  </div>
                ))}
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.28)",padding:"8px 10px",background:"rgba(139,92,246,0.06)",borderRadius:8,border:"1px solid rgba(139,92,246,0.15)"}}>
                ⚡ <strong style={{color:"rgba(139,92,246,0.8)"}}>Coming soon:</strong> Auto-sync with Garmin, Apple Health &amp; Google Fit
              </div>
            </div>
          )}
        </div>

        <textarea style={{...S.input,height:76,resize:"vertical",marginTop:4}} placeholder="Notes — anything notable today (optional)..." value={notes} onChange={e=>setNotes(e.target.value)} />
        <button style={{...S.btn,marginTop:12,width:"100%",padding:11}} onClick={handleSave}>
          {isToday ? (hasExisting ? "Update Today's Log" : "Save Today's Log") : `Save Entry for ${selectedDate}`}
        </button>
      </div>

      {/* Recent logs with delete */}
      {logs.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>Recent Logs</div>
          {logs.slice(0,14).map((log,i) => {
            const coreEffects = Object.entries(log.effects||{}).filter(([k]) => EFFECTS.includes(k));
            const hasAdv = ADVANCED_METRICS_KEYS.some(k => log.effects?.[k] != null);
            return (
              <div key={log.date} style={{padding:"10px 0",borderBottom:i<Math.min(logs.length,14)-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:3}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:600}}>{log.date}</span>
                    {log.date === todayDateStr && <span style={{fontSize:9,color:C.teal,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.8px"}}>Today</span>}
                    {hasAdv && <span style={{fontSize:9,color:"rgba(139,92,246,0.7)"}}>● metrics</span>}
                    {log.effects?.exercise_intensity != null && <span style={{fontSize:9,color:C.amber}}>🏃 {EXERCISE_LABELS[log.effects.exercise_intensity]}</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.36)"}}>{log.taken?.length}{log.stack_snapshot != null ? `/${log.stack_snapshot.length}` : ""}</span>
                    {confirmDelete === log.date ? (
                      <>
                        <button onClick={() => handleDelete(log.date)} style={{background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",color:"#EF4444",padding:"3px 8px",borderRadius:6,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Confirm</button>
                        <button onClick={() => setConfirmDelete(null)} style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",padding:"3px 8px",borderRadius:6,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDelete(log.date)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.22)",cursor:"pointer",fontSize:13,padding:"0 4px",lineHeight:1}} title="Delete">🗑</button>
                    )}
                  </div>
                </div>
                {coreEffects.length > 0 && (
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.36)",marginBottom:3}}>
                    {coreEffects.map(([k,v])=>k+": "+EFFECT_LABELS[v]).join(" · ")}
                  </div>
                )}
                {log.notes && <div style={{fontSize:11,color:"rgba(255,255,255,0.42)",fontStyle:"italic"}}>"{log.notes}"</div>}
                {log.taken?.length > 0 && (
                  <div style={{display:"flex",flexWrap:"wrap",gap:3,marginTop:5}}>
                    {log.taken.map(s=><span key={s} style={{fontSize:9,background:"rgba(229,184,63,0.07)",color:"rgba(255,255,255,0.38)",padding:"2px 6px",borderRadius:6}}>{s}</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Timing edit modal ───────────────────────────────────────────── */}
      {timingEditSupp && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:998,padding:16}} onClick={()=>setTimingEditSupp(null)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(139,92,246,0.3)",borderRadius:16,padding:22,maxWidth:380,width:"100%",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:16,color:C.white}}>⏰ Timing — {timingEditSupp}</div>
            {timingEditSlots.map((sl,i) => (
              <div key={i} style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
                  <input style={{...S.input,flex:2,fontSize:12,padding:"6px 10px"}} placeholder="Time label, e.g. Morning" value={sl.time_label||""} onChange={e=>setTimingEditSlots(p=>p.map((x,j)=>j===i?{...x,time_label:e.target.value}:x))} />
                  <input style={{...S.input,flex:1,fontSize:12,padding:"6px 8px"}} placeholder="Amount" type="number" min="0" value={sl.dosage_amount??""} onChange={e=>setTimingEditSlots(p=>p.map((x,j)=>j===i?{...x,dosage_amount:e.target.value}:x))} />
                  <select style={{...S.input,flex:1,fontSize:12,padding:"6px 6px"}} value={sl.dosage_unit||"mg"} onChange={e=>setTimingEditSlots(p=>p.map((x,j)=>j===i?{...x,dosage_unit:e.target.value}:x))}>
                    {['mg','g','mcg','IU','ml','capsules','tablets','drops'].map(u=><option key={u}>{u}</option>)}
                  </select>
                  <button style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:16,flexShrink:0}} onClick={()=>setTimingEditSlots(p=>p.filter((_,j)=>j!==i))}>✕</button>
                </div>
                <input style={{...S.input,fontSize:11,padding:"5px 10px"}} placeholder="Notes (optional)" value={sl.notes||""} onChange={e=>setTimingEditSlots(p=>p.map((x,j)=>j===i?{...x,notes:e.target.value}:x))} />
              </div>
            ))}
            <button style={{...S.btnGhost(),width:"100%",marginBottom:14,fontSize:12}} onClick={()=>setTimingEditSlots(p=>[...p,{time_label:"",dosage_amount:"",dosage_unit:"mg",notes:""}])}>+ Add slot</button>
            <div style={{display:"flex",gap:9}}>
              <button style={{...S.btn,flex:1}} onClick={saveTimingEdit}>Save</button>
              <button style={{...S.btnGhost(),flex:1}} onClick={()=>setTimingEditSupp(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI timing preview modal ─────────────────────────────────────── */}
      {timingPreview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,padding:16}} onClick={()=>setTimingPreview(null)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(139,92,246,0.4)",borderRadius:16,padding:22,maxWidth:420,width:"100%",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:4,color:C.white}}>✨ AI Timing Recommendations</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:16}}>Review before applying. You can edit slots after accepting.</div>
            {timingPreview.map((item,i) => (
              <div key={i} style={{background:"rgba(139,92,246,0.06)",border:"1px solid rgba(139,92,246,0.15)",borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:8,color:C.white}}>{item.supplement_name}</div>
                {(item.slots||[]).map((sl,j) => (
                  <div key={j} style={{fontSize:11,color:"rgba(255,255,255,0.6)",marginBottom:4,paddingLeft:8,borderLeft:"2px solid rgba(139,92,246,0.3)"}}>
                    <span style={{fontWeight:600,color:C.purple}}>{sl.time_label}</span>
                    {sl.dosage_amount && <span style={{color:"rgba(255,255,255,0.45)"}}> — {sl.dosage_amount}{sl.dosage_unit||'mg'}</span>}
                    {sl.notes && <div style={{fontSize:10,color:"rgba(255,255,255,0.32)",marginTop:2,fontStyle:"italic"}}>{sl.notes}</div>}
                  </div>
                ))}
              </div>
            ))}
            <div style={{display:"flex",gap:9,marginTop:4}}>
              <button style={{...S.btn,flex:1,background:"linear-gradient(135deg,#8B5CF6,#6366F1)"}} onClick={acceptTimingPreview}>Accept &amp; Apply</button>
              <button style={{...S.btnGhost(),flex:1}} onClick={()=>setTimingPreview(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /pages/TrendsPage.jsx — advanced insights gated to premium
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AiTrendsInsights — premium AI-powered analysis of the user's trend data
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function AiTrendsInsights({ logs, stack, weekData, stackDosages }) {
  const { user } = useUser();
  const { setCustomTimings } = useApp();
  const { used, limit, consume, pct } = useAiUsage();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [hasRun, setHasRun]     = useState(false);

  const canRun = canAccess("advancedInsights", user.plan) && limit > 0;
  const hasEnoughLogs = logs.length >= MIN_LOGS_FOR_AI;
  const analysisDisabled = !canRun || !hasEnoughLogs;
  const analysisTitle = !hasEnoughLogs
    ? `Log ${MIN_LOGS_FOR_AI - logs.length} more day${MIN_LOGS_FOR_AI - logs.length !== 1 ? 's' : ''} to unlock (${MIN_LOGS_FOR_AI - logs.length} to go)`
    : !canRun ? "Upgrade to Premium to access AI analysis" : "";

  const runAnalysis = async () => {
    if (logs.length < MIN_LOGS_FOR_AI) {
      setError(`Log ${MIN_LOGS_FOR_AI - logs.length} more day${MIN_LOGS_FOR_AI - logs.length !== 1 ? 's' : ''} to unlock AI analysis.`);
      return;
    }
    const commitUsage = consume();
    if (!commitUsage) { setError("AI limit reached — upgrade to continue."); return; }
    setLoading(true); setError(""); setHasRun(true);
    try {
      const lsum  = logSummary(logs);
      const ssum  = stackSummary(stack, stackDosages || {});
      const contextNotes = logs.slice(0,7).filter(l=>l.notes).map(l=>`${l.date}: "${l.notes}"`).join("; ");
      const advCtx = buildAdvancedMetricsContext(logs[0]);

      const prompt = "Stack: "+ssum+"\n\nTracking summary: "+lsum+(advCtx||"")+"\n\nContextual notes (may explain score variations): "+(contextNotes||"none")+"\n\nAnalyse this data. Note if any journal entries explain unusual scores (e.g. poor sleep from alcohol, illness). Where biometric data is present, use it to contextualise supplement effectiveness and recovery patterns. Return ONLY this JSON:\n{\"headline\":\"one sentence\",\"suggestions\":[{\"type\":\"add|adjust|remove|timing\",\"supplement\":\"name\",\"reason\":\"specific\",\"timeToEffect\":\"X weeks\"}],\"timelineNote\":\"patience note\",\"positives\":[\"observation\"]}";

      const raw = await callAI({
        system: "Evidence-based supplement advisor. Return ONLY valid JSON. No markdown. Max 4 suggestions. Be specific to the actual data. You may include timing type suggestions where relevant. Never recommend specific medical doses for individuals. Always recommend consulting a healthcare professional for medical concerns.",
        prompt,
        max_tokens: 900,
      });
      const parsed = JSON.parse(raw.replace(/```json|```/g,"").trim());
      setAnalysis(parsed);
      // Apply any AI-suggested timing overrides to the stack
      if (parsed.timing_overrides && Object.keys(parsed.timing_overrides).length > 0) {
        setCustomTimings(prev => ({ ...prev, ...parsed.timing_overrides }));
        if (user?.id) {
          // Persist to Supabase asynchronously
          api.loadStack(user.id).then(sd => {
            api.saveStack(user.id, sd.stack ?? [], { ...(sd.custom_timings ?? {}), ...parsed.timing_overrides }, sd.custom_protocols ?? [], sd.active_protocol ?? null).catch(console.warn);
          }).catch(console.warn);
        }
      }
      commitUsage(); // ← only counts after successful response
    } catch(e) {
      if (e.message === "usage_limit") setError("That was your last free insight this month. Upgrade to Premium to continue your analysis and get a weekly report automatically.");
      else setError("Couldn't generate analysis. Try again shortly.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const typeConfig = {
    add:    {icon:"➕", color:C.green,  label:"Consider Adding"},
    adjust: {icon:"⚙",  color:C.teal,   label:"Adjust"},
    remove: {icon:"➖", color:C.red,    label:"Consider Removing"},
    timing: {icon:"⏰", color:C.amber,  label:"Timing Tip"},
  };

  return (
    <div style={{...S.card,borderColor:"rgba(139,92,246,0.3)",background:"linear-gradient(135deg,rgba(139,92,246,0.06),rgba(229,184,63,0.04))"}}>
      <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4}}>
        <span style={{fontSize:18}}>✨</span>
        <div style={S.cardTitle}>AI Trends Analysis</div>
        <span style={{marginLeft:"auto",fontSize:10,color:"rgba(255,255,255,0.3)"}}>{used}/{limit} used</span>
      </div>
      <div style={{fontSize:12,color:"rgba(255,255,255,0.42)",marginBottom:16,lineHeight:1.6}}>
        Personalised recommendations from your real tracking data — including context from your journal notes.
      </div>

      {(!hasRun || (error && !analysis)) && !loading && (
        <button style={{...S.btn,background:"linear-gradient(135deg,#8B5CF6,#6366F1)",width:"100%",padding:12,fontSize:13,opacity:analysisDisabled?0.4:1,cursor:analysisDisabled?"not-allowed":undefined}} onClick={runAnalysis} disabled={analysisDisabled} title={analysisTitle}>
          {hasRun ? "↻ Retry Analysis" : "✨ Analyse My Trends"}
        </button>
      )}

      {loading && (
        <div style={{textAlign:"center",padding:"20px 0",color:"rgba(255,255,255,0.4)"}}>
          <div style={{fontSize:24,marginBottom:8}}>✨</div>
          <div style={{fontSize:13}}>Analysing your data...</div>
          <div style={{fontSize:11,marginTop:4,color:"rgba(255,255,255,0.28)"}}>10–15 seconds</div>
        </div>
      )}

      {error && <div style={{color:C.red,fontSize:12,marginBottom:12,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8}}>{error}</div>}
      {error?.includes("last free insight") && (
        <button style={{...S.btnAmber,fontSize:11,padding:"6px 14px",marginBottom:12}} onClick={onUpgrade}>Upgrade to continue →</button>
      )}

      {analysis && !loading && (
        <>
          <div style={{fontSize:14,fontWeight:600,color:C.white,lineHeight:1.5,marginBottom:16,padding:"12px 14px",background:"rgba(139,92,246,0.1)",borderRadius:10,borderLeft:"3px solid #8B5CF6"}}>
            {analysis.headline}
          </div>
          {analysis.positives?.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>✓ What’s Working</div>
              {analysis.positives.map((p,i)=>(
                <div key={i} style={{fontSize:13,color:"rgba(255,255,255,0.6)",padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",display:"flex",gap:8}}>
                  <span style={{color:C.green,flexShrink:0}}>✓</span>{p}
                </div>
              ))}
            </div>
          )}
          {analysis.suggestions?.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>Recommendations</div>
              {analysis.suggestions.map((s,i)=>{
                const cfg = typeConfig[s.type] ?? typeConfig.adjust;
                return (
                  <div key={i} style={{marginBottom:10,padding:"12px 14px",background:"rgba(255,255,255,0.03)",borderRadius:10,border:"1px solid rgba(255,255,255,0.06)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
                      <span style={{fontSize:14}}>{cfg.icon}</span>
                      <span style={{fontSize:9,fontWeight:700,color:cfg.color,textTransform:"uppercase",letterSpacing:"0.9px"}}>{cfg.label}</span>
                      <span style={{fontSize:13,fontWeight:700,color:C.white,marginLeft:2}}>{s.supplement}</span>
                      {s.timeToEffect && <span style={{fontSize:9,color:"rgba(255,255,255,0.3)",marginLeft:"auto",flexShrink:0}}>~{s.timeToEffect}</span>}
                    </div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.55}}>{s.reason}</div>
                  </div>
                );
              })}
            </div>
          )}
          {analysis.timelineNote && (
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",padding:"10px 14px",background:"rgba(245,158,11,0.06)",borderRadius:9,borderLeft:"3px solid "+C.amber,lineHeight:1.6,marginBottom:14}}>
              ⏱ {analysis.timelineNote}
            </div>
          )}
          <button style={{...S.btnGhost(),width:"100%",fontSize:12,opacity:analysisDisabled?0.4:1,cursor:analysisDisabled?"not-allowed":undefined}} onClick={runAnalysis} disabled={analysisDisabled || loading} title={analysisTitle}>↻ Re-analyse</button>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:10,fontStyle:"italic"}}>AI analysis is informational only. Always consult a healthcare professional.</div>
        </>
      )}
    </div>
  );
}


function TrendsPage({ onUpgrade }) {
  const { user } = useUser();
  const { logs, stack, stackDosages } = useApp();
  const [metric, setMetric] = useState("energy");
  const isPremium = canAccess("advancedInsights", user.plan);
  const isLoggedIn = user.isLoggedIn;

  const weekData = (() => {
    // Build a YYYY-MM-DD → log map for locale-independent lookup.
    // Logs are stored as en-GB "DD/MM/YYYY"; convert once up-front so the
    // per-day lookup is immune to any toLocaleDateString() locale variation.
    const logMap = {};
    for (const log of logs) {
      if (!log.date) continue;
      let isoKey;
      if (log.date.includes("/")) {
        // Legacy DD/MM/YYYY format
        const parts = log.date.split("/");
        if (parts.length !== 3) continue;
        isoKey = `${parts[2]}-${parts[1].padStart(2,"0")}-${parts[0].padStart(2,"0")}`;
      } else {
        // Current YYYY-MM-DD format — use as-is
        isoKey = log.date;
      }
      logMap[isoKey] = log;
    }
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate()-i);
      const label = d.toLocaleDateString("en-GB",{weekday:"short",day:"numeric"});
      const isoKey = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
      const log = logMap[isoKey];
      days.push({ date:label, energy:log?.effects?.["Energy"]??null, sleep:log?.effects?.["Sleep Quality"]??null, mood:log?.effects?.["Mood"]??null, taken:log?log.taken.length>0:false });
    }
    return days;
  })();

  const METRICS = [{key:"energy",label:"Energy",color:C.teal},{key:"sleep",label:"Sleep",color:C.purple},{key:"mood",label:"Mood",color:C.amber}];
  const metricAvg = key => { const vals=weekData.map(d=>d[key]).filter(v=>v!==null); return vals.length?(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1):"—"; };
  const adherencePct = Math.round((weekData.filter(d=>d.taken).length/7)*100);
  const streak = computeStreak(logs);
  const bestMetric = METRICS.map(m=>({...m,avg:parseFloat(metricAvg(m.key))})).filter(m=>!isNaN(m.avg)).sort((a,b)=>b.avg-a.avg)[0];

  const correlationData = (() => {
    if (!isPremium || logs.length < 3) return [];
    return stack.slice(0,6).map(s => {
      const daysWith    = logs.filter(l=>l.taken?.includes(s) && l.effects?.["Energy"]);
      const daysWithout = logs.filter(l=>!l.taken?.includes(s) && l.effects?.["Energy"]);
      const avgWith    = daysWith.length ? (daysWith.reduce((a,l)=>a+l.effects["Energy"],0)/daysWith.length).toFixed(1) : null;
      const avgWithout = daysWithout.length ? (daysWithout.reduce((a,l)=>a+l.effects["Energy"],0)/daysWithout.length).toFixed(1) : null;
      const delta = avgWith && avgWithout ? (parseFloat(avgWith)-parseFloat(avgWithout)).toFixed(1) : null;
      return { name:s, avgWith, avgWithout, delta };
    }).filter(d=>d.delta!==null).sort((a,b)=>Math.abs(b.delta)-Math.abs(a.delta));
  })();

  return (
    <div style={{overflowX:"hidden",maxWidth:"100%"}}>
      <div style={{display:"flex",gap:7,marginBottom:12,flexWrap:"wrap"}}>
        {METRICS.map(m => (<button key={m.key} style={{padding:"6px 14px",borderRadius:20,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:metric===m.key?700:400,background:metric===m.key?m.color:"transparent",color:metric===m.key?C.navy:m.color,border:"1px solid "+m.color,transition:"all .15s"}} onClick={() => setMetric(m.key)}>{m.label}</button>))}
      </div>
      <div style={S.card}>
        <div style={S.cardTitle}>7-Day {METRICS.find(m=>m.key===metric)?.label} Trend</div>
        <div style={{display:"flex",gap:5,alignItems:"flex-end",height:108,marginBottom:10,overflowX:"hidden"}}>
          {weekData.map((d,i) => {
            const val=d[metric]; const col=METRICS.find(m=>m.key===metric)?.color??C.teal;
            return (
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                {val!==null&&<div style={{fontSize:10,color:col,fontWeight:700}}>{val}</div>}
                <div style={{width:"100%",height:val!==null?(val/5*86)+"px":"12px",background:val!==null?(d.taken?"linear-gradient(to top,"+col+","+col+"88)":"rgba(255,255,255,0.07)"):"rgba(255,255,255,0.04)",borderRadius:"4px 4px 0 0",transition:"height .4s ease",minHeight:3}} />
                <div style={{fontSize:9,color:"rgba(255,255,255,0.3)",textAlign:"center",lineHeight:1.2}}>{d.date}</div>
              </div>
            );
          })}
        </div>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.26)",textAlign:"center"}}>{logs.length===0?"Log your supplements daily to see real trends here":"Coloured = supplements taken · Grey = missed or no data"}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:9,marginBottom:12,minWidth:0}}>
        {METRICS.map(m => (<div key={m.key} style={{...S.card,marginBottom:0,textAlign:"center",padding:14,minWidth:0,overflow:"hidden"}}><div style={{fontSize:24,fontWeight:700,color:m.color}}>{metricAvg(m.key)}</div><div style={{fontSize:10,color:"rgba(255,255,255,0.36)",marginTop:4}}>Avg {m.label}</div><div style={{fontSize:9,color:"rgba(255,255,255,0.2)",marginTop:2}}>out of 5</div></div>))}
      </div>

      {isLoggedIn && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:12,minWidth:0}}>
          <div style={{...S.card,marginBottom:0,textAlign:"center",padding:14,minWidth:0,overflow:"hidden"}}>
            <div style={{fontSize:28,fontWeight:700,color:C.teal}}>{adherencePct}%</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.36)",marginTop:4}}>7-Day Adherence</div>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.2)",marginTop:2}}>{weekData.filter(d=>d.taken).length}/7 days taken</div>
          </div>
          <div style={{...S.card,marginBottom:0,textAlign:"center",padding:14,minWidth:0,overflow:"hidden"}}>
            <div style={{fontSize:28,fontWeight:700,color:streak>4?C.teal:C.amber}}>🔥 {streak}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.36)",marginTop:4}}>Day Streak</div>
            <div style={{fontSize:9,color:"rgba(255,255,255,0.2)",marginTop:2}}>consecutive days</div>
          </div>
        </div>
      )}
      {!isLoggedIn && (
        <div style={{...S.card,borderColor:"rgba(229,184,63,0.2)",marginBottom:12}}>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,textAlign:"center"}}>Sign in for free to save your logs and see personal adherence, streaks, and trends.</div>
        </div>
      )}

      {isPremium ? (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>Supplement Effect Correlation</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.38)",marginBottom:14,lineHeight:1.5}}>{correlationData.length>=2?"Based on your logs — how your energy scores differ on days you took each supplement.":logs.length<3?"Log at least 3 days to generate correlations. Keep logging to unlock this insight.":"Not enough variation yet — keep going!"}</div>
            {correlationData.length>0?correlationData.map(d=>(
              <div key={d.name} style={{padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.05)",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <div style={{flex:1,minWidth:0}}><div style={{fontSize:12,fontWeight:600,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name}</div><div style={{fontSize:10,color:"rgba(255,255,255,0.32)"}}>With: {d.avgWith} · Without: {d.avgWithout??"no data"}</div></div>
                <div style={{fontSize:15,fontWeight:700,color:parseFloat(d.delta)>0?C.green:parseFloat(d.delta)<0?C.red:"rgba(255,255,255,0.4)",flexShrink:0}}>{parseFloat(d.delta)>0?"+":""}{d.delta}</div>
              </div>
            )):<div style={{color:"rgba(255,255,255,0.25)",fontSize:13,textAlign:"center",padding:"12px 0"}}>{logs.length===0?"Start logging your daily supplements to see correlations.":"Log more days to unlock this insight."}</div>}
          </div>

          {/* AI-powered trends analysis — Feature 4: moved above best metric */}
          <AiTrendsInsights logs={logs} stack={stack} weekData={weekData} stackDosages={stackDosages} />

          {bestMetric&&(
            <div style={{...S.card,background:"linear-gradient(135deg,rgba(229,184,63,0.07),rgba(229,184,63,0.02))"}}>
              <div style={S.cardTitle}>Your Best Metric This Week</div>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <div style={{fontSize:42,fontWeight:800,color:bestMetric.color}}>{bestMetric.avg}</div>
                <div><div style={{fontSize:14,fontWeight:700,color:bestMetric.color}}>{bestMetric.label}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.38)",lineHeight:1.5,marginTop:2}}>Your highest avg score this week. Keep consistent with your stack and logging to track change over time.</div></div>
              </div>
            </div>
          )}

          {/* Feature 6: HRV + Sleep Score wearable charts (5+ data points) */}
          {(()=>{
            const hrvData = logs.filter(l=>l.effects?.hrv_overnight!=null).slice(0,10).reverse();
            const sleepData = logs.filter(l=>l.effects?.sleep_score!=null).slice(0,10).reverse();
            if (hrvData.length < 5 && sleepData.length < 5) return null;
            const maxHrv = Math.max(...hrvData.map(l=>l.effects.hrv_overnight));
            const maxSleep = 100;
            return (
              <>
                {hrvData.length >= 5 && (
                  <div style={S.card}>
                    <div style={S.cardTitle}>Overnight HRV <span style={{fontSize:9,color:"rgba(255,255,255,0.28)",fontWeight:400,textTransform:"none",letterSpacing:0}}>wearable / manual entry</span></div>
                    <div style={{display:"flex",gap:3,alignItems:"flex-end",height:80,marginBottom:8}}>
                      {hrvData.map((l,i)=>(
                        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                          <div style={{fontSize:9,color:"#A78BFA",fontWeight:700}}>{l.effects.hrv_overnight}</div>
                          <div style={{width:"100%",height:(l.effects.hrv_overnight/maxHrv*64)+"px",background:"linear-gradient(to top,#A78BFA,#A78BFA88)",borderRadius:"3px 3px 0 0",minHeight:2}} />
                          <div style={{fontSize:8,color:"rgba(255,255,255,0.25)",textAlign:"center"}}>{l.date.split("/").slice(0,2).join("/")}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.26)",textAlign:"center"}}>ms · higher = better recovery</div>
                  </div>
                )}
                {sleepData.length >= 5 && (
                  <div style={S.card}>
                    <div style={S.cardTitle}>Sleep Score <span style={{fontSize:9,color:"rgba(255,255,255,0.28)",fontWeight:400,textTransform:"none",letterSpacing:0}}>wearable / manual entry</span></div>
                    <div style={{display:"flex",gap:3,alignItems:"flex-end",height:80,marginBottom:8}}>
                      {sleepData.map((l,i)=>(
                        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                          <div style={{fontSize:9,color:C.purple,fontWeight:700}}>{l.effects.sleep_score}</div>
                          <div style={{width:"100%",height:(l.effects.sleep_score/maxSleep*64)+"px",background:"linear-gradient(to top,"+C.purple+","+C.purple+"88)",borderRadius:"3px 3px 0 0",minHeight:2}} />
                          <div style={{fontSize:8,color:"rgba(255,255,255,0.25)",textAlign:"center"}}>{l.date.split("/").slice(0,2).join("/")}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.26)",textAlign:"center"}}>out of 100</div>
                  </div>
                )}
              </>
            );
          })()}

          <div style={S.card}>
            <div style={S.cardTitle}>Full Log History</div>
            {logs.length===0?<div style={{textAlign:"center",padding:"40px 20px",border:"1px dashed rgba(229,184,63,0.18)",borderRadius:16,background:"rgba(229,184,63,0.02)"}}><div style={{fontSize:32,marginBottom:12,opacity:0.4}}>📅</div><div style={{fontSize:13,color:"rgba(255,255,255,0.35)",lineHeight:1.6}}>No logs yet — use the Daily Log tab to start tracking.</div></div>:logs.map((log,i)=>(
              <div key={i} style={{padding:"10px 0",borderBottom:i<logs.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{fontSize:12,fontWeight:600}}>{log.date}</span><span style={{fontSize:10,color:"rgba(255,255,255,0.32)"}}>{log.taken?.length}{log.stack_snapshot != null ? `/${log.stack_snapshot.length}` : ""} taken</span></div>
                {Object.entries(log.effects||{}).filter(([k])=>EFFECTS.includes(k)).length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>{Object.entries(log.effects).filter(([k])=>EFFECTS.includes(k)).map(([k,v])=>(<span key={k} style={{fontSize:10,background:"rgba(229,184,63,0.07)",color:"rgba(255,255,255,0.45)",padding:"2px 7px",borderRadius:8}}>{k}: {v}/5</span>))}</div>}
                {log.notes&&<div style={{fontSize:11,color:"rgba(255,255,255,0.35)",fontStyle:"italic",marginTop:4}}>"{log.notes}"</div>}
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Stack Composition Insight</div>
            {(()=>{
              const catCounts={}; stack.forEach(s=>{const cat=SUPPLEMENT_DB[s]?.category??"Other";catCounts[cat]=(catCounts[cat]||0)+1;});
              const cats=Object.entries(catCounts).sort((a,b)=>b[1]-a[1]);
              const strongCount=stack.filter(s=>SUPPLEMENT_DB[s]?.evidence==="Strong").length;
              return (<><div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:14,lineHeight:1.6}}>Your stack has <strong style={{color:C.white}}>{strongCount} Strong-evidence</strong> supplements out of {stack.length} total.</div><div style={{display:"flex",flexWrap:"wrap",gap:8}}>{cats.map(([cat,count])=>(<div key={cat} style={{background:"rgba(229,184,63,0.08)",border:"1px solid rgba(229,184,63,0.15)",borderRadius:8,padding:"6px 12px",textAlign:"center"}}><div style={{fontSize:14,fontWeight:700,color:C.teal}}>{count}</div><div style={{fontSize:10,color:"rgba(255,255,255,0.38)",marginTop:2}}>{cat}</div></div>))}</div></>);
            })()}
          </div>

          <div style={{...S.card,borderColor:C.teal+"22"}}>
            <div style={S.cardTitle}>Export Your Data</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:12,lineHeight:1.6}}>Download your full log history as CSV for use in Excel, Google Sheets, or your GP.</div>
            <button style={S.btn} onClick={() => {
              const csvCell = v => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
              const rows=[["Date","Taken Count","Stack Size","Energy","Sleep","Mood","Notes"],...logs.map(l=>[l.date,l.taken?.length,stack.length,l.effects?.["Energy"]??"",l.effects?.["Sleep Quality"]??"",l.effects?.["Mood"]??"",l.notes??""])];
              const csv=rows.map(r=>r.map(csvCell).join(",")).join("\n");
              const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download="supplement-log.csv";a.click();
            }}>⬇ Export CSV</button>
          </div>
        </>
      ) : (
        <div style={{...S.card,borderColor:C.amber+"28"}}>
          <div style={S.cardTitle}>📊 Premium Trends & Insights</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:16,lineHeight:1.7}}>Unlock powerful analytics to understand how your supplements are actually working:</div>
          {["🔬 Supplement vs effect correlation analysis","📅 Full log history & progress timeline","🎯 Timing adherence breakdown by supplement","🧪 Stack composition & evidence quality score","🏆 Best performing metric identification","⬇ CSV export for your GP or nutritionist"].map(item=>(
            <div key={item} style={{display:"flex",alignItems:"center",gap:9,padding:"6px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",fontSize:13,color:"rgba(255,255,255,0.55)"}}>{item}</div>
          ))}
          <button style={{...S.btnAmber,marginTop:16,width:"100%"}} onClick={onUpgrade}>Upgrade to Premium</button>
        </div>
      )}
    </div>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /pages/ProtocolsPage.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ProtocolsPage({ onOpenBuilder, onUpgrade }) {
  const { customProtocols, loadProtocol, deleteCustomProtocol, activeProtocol } = useApp();
  const { user, setShowAuth } = useUser();
  const isPremium = canAccess("customProtocols", user.plan);

  const handlePremiumGate = () => user.isLoggedIn ? onUpgrade() : setShowAuth(true);

  return (
    <div>
      <div style={{marginBottom:26}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:11}}>
          <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.26)",textTransform:"uppercase",letterSpacing:"2px"}}>My Protocols</div>
          <button style={{...S.btn,fontSize:11,padding:"5px 14px",opacity:isPremium?1:0.5}} onClick={() => isPremium ? onOpenBuilder(null) : handlePremiumGate()}>
            {isPremium ? "+ New Protocol" : "⭐ Premium only"}
          </button>
        </div>
        {!isPremium ? (
          <GateBanner type="premium" featureName="Custom Protocol creation" onAction={handlePremiumGate} />
        ) : customProtocols.length === 0 ? (
          <div style={{...S.card,textAlign:"center",padding:"28px 20px",borderStyle:"dashed",borderColor:"rgba(229,184,63,0.2)",cursor:"pointer"}} onClick={() => onOpenBuilder(null)}>
            <div style={{fontSize:32,marginBottom:10}}>✚</div>
            <div style={{fontSize:14,fontWeight:600,color:C.teal,marginBottom:5}}>Create your first custom protocol</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",lineHeight:1.55}}>Build a named protocol from any combination of supplements.</div>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:11}}>
            {customProtocols.map(p => {
              const isActive = activeProtocol === p.name;
              return (
                <div key={p.name} style={{...S.card,borderColor:isActive?p.color:p.color+"28",marginBottom:0,display:"flex",flexDirection:"column",minWidth:0,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8}}>
                    <span style={{fontSize:24,flexShrink:0}}>{p.icon}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:700,color:p.color,display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                        {p.name}
                        {isActive&&<span style={{fontSize:9,background:p.color+"25",color:p.color,padding:"2px 7px",borderRadius:6,textTransform:"uppercase",letterSpacing:"0.8px",fontWeight:700}}>Active</span>}
                      </div>
                      {p.description&&<div style={{fontSize:12,color:"rgba(255,255,255,0.42)",marginTop:2,lineHeight:1.5}}>{p.description}</div>}
                    </div>
                    <div style={{display:"flex",gap:5,flexShrink:0}}>
                      <button onClick={() => onOpenBuilder(p)} style={{background:"rgba(255,255,255,0.07)",border:"none",color:"rgba(255,255,255,0.45)",width:28,height:28,borderRadius:7,cursor:"pointer",fontSize:12}}>✏</button>
                      <button onClick={() => deleteCustomProtocol(p.name)} style={{background:"rgba(239,68,68,0.1)",border:"none",color:"rgba(239,68,68,0.6)",width:28,height:28,borderRadius:7,cursor:"pointer",fontSize:12}}>✕</button>
                    </div>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:10}}>
                    {p.supplements.map(s=><span key={s} style={{background:p.color+"15",color:p.color,border:"1px solid "+p.color+"28",padding:"2px 6px",borderRadius:7,fontSize:9}}>{s}</span>)}
                  </div>
                  <button style={{...S.btn,background:"linear-gradient(135deg,"+p.color+","+p.color+"b0)",width:"100%",fontSize:12,padding:9}} onClick={() => loadProtocol(p.name,true)}>
                    {isActive?"✓ Currently Active":"Load Protocol"}
                  </button>
                </div>
              );
            })}
            <div style={{...S.card,marginBottom:0,display:"flex",alignItems:"center",justifyContent:"center",borderStyle:"dashed",borderColor:"rgba(229,184,63,0.15)",cursor:"pointer",minHeight:80,gap:8}} onClick={() => onOpenBuilder(null)}>
              <div style={{fontSize:20,opacity:.4}}>✚</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.28)"}}>New protocol</div>
            </div>
          </div>
        )}
      </div>

      {Object.entries(PROTOCOL_CATEGORIES).map(([category,names]) => (
        <div key={category} style={{marginBottom:26}}>
          <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.26)",textTransform:"uppercase",letterSpacing:"2px",marginBottom:11}}>{category}</div>
          <div style={{display:"flex",flexDirection:"column",gap:11}}>
            {names.filter(n=>PROTOCOLS[n]).map(name => {
              const p = PROTOCOLS[name]; const isActive = activeProtocol===name;
              return (
                <div key={name} style={{...S.card,borderColor:isActive?p.color:p.color+"22",marginBottom:0,display:"flex",flexDirection:"column",boxShadow:isActive?"0 0 0 1px "+p.color+"44":"none",minWidth:0,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                    <span style={{fontSize:26,flexShrink:0}}>{p.icon}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:700,color:p.color,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        {name}
                        {isActive&&<span style={{fontSize:9,background:p.color+"25",color:p.color,padding:"2px 7px",borderRadius:6,textTransform:"uppercase",letterSpacing:"0.8px",fontWeight:700}}>Active</span>}
                      </div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.42)",lineHeight:1.5,marginTop:2}}>{p.description}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:11}}>
                    {p.supplements.map(s=><span key={s} style={{background:p.color+"15",color:p.color,border:"1px solid "+p.color+"28",padding:"2px 6px",borderRadius:7,fontSize:9}}>{s}</span>)}
                  </div>
                  <div style={{display:"flex",gap:8,marginBottom:8}}>
                    <button style={{...S.btn,background:"linear-gradient(135deg,"+p.color+","+p.color+"b0)",flex:1,fontSize:12,padding:9}} onClick={() => loadProtocol(name)}>
                      {isActive?"✓ Currently Active":"Load Protocol"}
                    </button>
                  </div>
                  <ProtocolShopList protocolName={name} color={p.color} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /pages/AIPage.jsx — gated to premium
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function AIPage({ onUpgrade }) {
  const { stack, logs, setCustomTimings, customProtocols, saveCustomProtocol, setToast, stackDosages } = useApp();
  const { user } = useUser();
  const { used, limit, pct, canUse, consume } = useAiUsage();
  const plan = user?.plan ?? "guest";

  // Chat state (premium context mode)
  const [history,   setHistory]   = useState([]);
  const [question,  setQuestion]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");

  // AI Reports state
  const [weeklyReport,  setWeeklyReport]  = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [wniReport,     setWniReport]     = useState(null); // Why Not Improving
  const [buildStack,    setBuildStack]    = useState(null); // Build My Stack protocol
  const [reportLoading, setReportLoading] = useState("");   // which report is loading
  const [reportError,   setReportError]   = useState("");
  const [savingProtocol, setSavingProtocol] = useState(false);
  const [confirmOverwriteName, setConfirmOverwriteName] = useState(null);

  const endRef = useRef(null);
  // Scroll only the chat box, not the whole page
  useEffect(() => { if (endRef.current) endRef.current.scrollTop = endRef.current.scrollHeight; }, [history, loading]);

  const canUseAdvisor   = canAccess("aiAdvisor", plan);
  const canUseBasicAI   = canAccess("aiBasic", plan);
  const canUsePremium   = canAccess("advancedInsights", plan);
  const canBuildStack   = canAccess("buildMyStack", plan) && logs.length >= 3;
  const canWeekly       = canAccess("weeklyReport", plan);
  const canWNI          = canAccess("whyNotImproving", plan);
  const keepContext     = canAccess("aiContextChat", plan);

  // ── Advisor chat ────────────────────────────────────────────────────────────
  const runAdvisor = async (q) => {
    const commitUsage = consume();
    if (!commitUsage) { setError("AI limit reached — upgrade for more insights."); return; }
    setLoading(true); setError("");
    try {
      const ssum = stackSummary(stack, stackDosages);
      const logLimit = canUseAdvisor ? 30 : 7;
      const lsum = logSummary(logs, logLimit);
      const ctxNotes = logs.slice(0, logLimit).filter(l=>l.notes).map(l=>`${l.date}: "${l.notes}"`).join("; ");
      const advCtx = buildAdvancedMetricsContext(logs[0]);

      // Premium users get conversation history context
      const messages = keepContext && history.length > 0
        ? [...history.slice(-6).map(m=>m.role+": "+m.text), "User: "+q].join("\n\n")
        : q;

      const prompt = "Stack: "+ssum+"\nLog context: "+lsum+(advCtx||"")+"\nJournal notes: "+(ctxNotes||"none")+"\n\nQuestion: "+messages+"\n\nAnswer in 2-3 concise paragraphs. If journal notes suggest context for unusual scores (illness, alcohol, stress), acknowledge this. Where biometric data is present, use it to contextualise supplement effectiveness. Be specific and evidence-based. Format with light markdown — **bold** key supplements, terms and numbers, and use short bullet points (lines starting with - ) where they improve clarity.";

      const text = await callAI({
        system: "You are an evidence-based supplement advisor for Peak Health Stack. Be concise (2-3 short paragraphs max). Use light markdown — bold key terms and short bullet lists — for readability. Never make diagnostic claims. Never recommend specific medical doses for individuals. Always recommend consulting a healthcare professional for any dosage or medical concerns.",
        prompt,
        max_tokens: 900,
      });
      setHistory(p => [...p, {role:"assistant", text}]);
      commitUsage(); // ← only counts after successful response
    } catch(e) {
      setError(e.message === "usage_limit" ? "That was your last free insight this month. Upgrade to Premium to continue your analysis and get a weekly report automatically." : "Connection failed — try again.");
    }
    setLoading(false);
  };

  const handleAnalyse = () => {
    setHistory(p=>[...p,{role:"user",text:"Analyse my current stack"}]);
    runAdvisor("Analyse my current stack based on my log data and notes. Highlight what's working, any patterns you notice, and one key optimisation.");
  };

  const handleAsk = () => {
    if (!question.trim() || loading) return;
    const q = question.trim(); setQuestion("");
    setHistory(p=>[...p,{role:"user",text:q}]);
    runAdvisor(q);
  };

  // ── Reports ─────────────────────────────────────────────────────────────────
  const runReport = async (type) => {
    if (logs.length < MIN_LOGS_FOR_AI) {
      setReportError(`Log ${MIN_LOGS_FOR_AI - logs.length} more day${MIN_LOGS_FOR_AI - logs.length !== 1 ? 's' : ''} to unlock AI analysis.`);
      return;
    }
    const commitUsage = consume();
    if (!commitUsage) { setReportError("AI limit reached — upgrade to continue."); return; }
    setReportLoading(type); setReportError("");
    const ssum = stackSummary(stack, stackDosages);
    const lsum = logSummary(logs, logs.length);
    const ctxNotes = logs.filter(l=>l.notes).slice(0,15).map(l=>`${l.date}: "${l.notes}"`).join("; ");
    const advCtx = buildAdvancedMetricsContext(logs[0]);
    const bioNote = advCtx ? " Where biometric data is present, use it to contextualise supplement effectiveness and identify recovery patterns relative to exercise intensity." : "";

    const prompts = {
      weekly: "Stack: "+ssum+"\n7-day summary: "+lsum+(advCtx||"")+"\nJournal: "+(ctxNotes||"none")+"\n\nWrite a weekly supplement performance report. Cover: 1) How this week went (scores + adherence), 2) What the data suggests is working, 3) One specific adjustment to try next week, 4) Patience reminder for any slow-acting supplements. Use the journal notes to contextualise any unusual days."+bioNote+" 3-4 short paragraphs, practical and direct. Format in markdown: a short **bold heading** before each of the four sections above, **bold** key supplements and numbers, and bullet points (lines starting with - ) for any lists.",
      monthly: "Stack: "+ssum+"\nAll logs summary: "+lsum+(advCtx||"")+"\nJournal: "+(ctxNotes||"none")+"\n\nWrite a comprehensive monthly supplement review. Cover: 1) Overall trend in energy/sleep/mood, 2) Top 2 supplements by apparent impact, 3) Any supplement that appears redundant or mistimed, 4) One protocol adjustment to consider next month, 5) What progress to expect in the next 4 weeks."+bioNote+" 4-5 short paragraphs, evidence-based. Format in markdown: a short **bold heading** before each numbered section, **bold** key supplements and numbers, and bullet points (lines starting with - ) for any lists.",
      wni: "Stack: "+ssum+"\nLogs: "+lsum+(advCtx||"")+"\nJournal: "+(ctxNotes||"none")+"\n\nThe user feels they are not improving as expected. Provide a Why Am I Not Improving analysis. Cover: 1) Realistic timelines for each supplement in their stack (many take 8-12 weeks), 2) Possible missing co-factors or timing issues, 3) Lifestyle factors the journal suggests may be limiting results, 4) Whether their tracking window is long enough to judge, 5) One actionable change."+bioNote+" Be honest and supportive, not dismissive. Format in markdown: a short **bold heading** before each section, **bold** key supplements and numbers, and bullet points (lines starting with - ) for any lists.",
      buildStack: "Stack: "+ssum+"\nLogs: "+lsum+(advCtx||"")+"\nJournal: "+(ctxNotes||"none")+"\n\nBased on this user tracking data and notes, design their optimal supplement protocol. Return ONLY this JSON:\n{\"name\":\"Protocol Name\",\"icon\":\"🧬\",\"color\":\"#E5B83F\",\"description\":\"one sentence\",\"supplements\":[\"Supplement1\",\"Supplement2\"],\"rationale\":\"2-3 sentence explanation\"}\n\nChoose from: "+Object.keys(SUPPLEMENT_DB).slice(0,40).join(", ")+". Max 8 supplements. Base choices on actual scores and patterns.",
    };

    try {
      const text = await callAI({
        system: type === "buildStack"
          ? "Return ONLY valid JSON. No markdown. No preamble."
          : "Evidence-based supplement advisor. Write clear, practical, personalised reports. No diagnostic claims. Never recommend specific medical doses for individuals. Always recommend consulting a healthcare professional for medical concerns.",
        prompt: prompts[type],
        max_tokens: type === "buildStack" ? 600 : (type === "monthly" ? 1200 : 1000),
      });

      if (type === "buildStack") {
        const parsed = JSON.parse(text.replace(/```json|```/g,"").trim());
        setBuildStack(parsed);
      } else if (type === "weekly")  setWeeklyReport(text);
      else if (type === "monthly")   setMonthlyReport(text);
      else if (type === "wni")       setWniReport(text);
      commitUsage(); // ← only counts after successful response
    } catch(e) {
      setReportError(e.message === "usage_limit" ? "Monthly AI limit reached." : "Report generation failed — try again.");
    }
    setReportLoading("");
  };

  // ── Gate views ──────────────────────────────────────────────────────────────
  if (!user.isLoggedIn) {
    return (
      <div style={S.card}>
        <div style={S.cardTitle}>✨ AI Advisor</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.7,marginBottom:16}}>
          Sign in for free to get {AI_LIMITS.free.total} AI insights per month — no payment needed. See what the AI says about your stack before deciding to subscribe.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16,opacity:0.4,pointerEvents:"none"}}>
          {["Sample: Your Magnesium + Vitamin D3 pairing is clinically validated — magnesium activates D3 conversion, so D levels will be meaningfully higher with both supplemented together.", "Sample: Consider adding Vitamin K2 alongside your D3. K2 directs calcium mobilised by D3 to bones rather than arterial walls — a critical safety and efficacy pairing."].map((t,i)=>(
            <div key={i} style={{fontSize:13,color:"rgba(255,255,255,0.6)",padding:"10px 12px",background:"rgba(229,184,63,0.05)",borderRadius:9,borderLeft:"2px solid rgba(229,184,63,0.2)",lineHeight:1.65}}>{t}</div>
          ))}
        </div>
        <GateBanner type="signin" featureName="AI Advisor" onAction={() => {}} />
      </div>
    );
  }

  return (
    <>
      {/* Usage bar */}
      <AiUsageBanner onUpgrade={onUpgrade} />

      <div style={{fontSize:11,color:"rgba(255,255,255,0.28)",lineHeight:1.6,marginBottom:12,padding:"8px 12px",background:"rgba(255,255,255,0.02)",borderRadius:8,border:"1px solid rgba(255,255,255,0.06)"}}>
        ⚖️ {DISCLAIMER_AI}
      </div>

      {/* ── FREE TIER — basic AI taster ─────────────────────────────────── */}
      {plan === "free" && (
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:12}}>
            <div style={S.cardTitle}>✨ AI Insights</div>
            <span style={{fontSize:10,color:used>=limit?C.red:C.amber,fontWeight:700,marginLeft:"auto"}}>{used}/{limit} used</span>
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:16,lineHeight:1.6}}>
            You have <strong style={{color:C.white}}>{Math.max(0,limit-used)} AI insight{Math.max(0,limit-used)!==1?"s":""}</strong> remaining this month. Upgrade for more allowances and advanced features.
          </div>
          {canUse ? (
            <>
              <button style={{...S.btn,width:"100%",marginBottom:10,opacity:stack.length===0?0.4:1}} disabled={stack.length===0||loading}
                onClick={() => { setHistory(p=>[...p,{role:"user",text:"Analyse my stack"}]); runAdvisor("Give me a quick assessment of my supplement stack — what works well together, and one improvement suggestion."); }}>
                {loading ? "Analysing..." : "✨ Quick Stack Analysis"}
              </button>
              {history.length > 0 && (
                <div style={{borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:12,marginTop:4}}>
                  {history.slice(-2).map((msg,i) => (
                    <div key={i} style={{marginBottom:12}}>
                      <div style={{fontSize:10,color:msg.role==="user"?C.teal:"rgba(255,255,255,0.28)",fontWeight:700,marginBottom:5,textTransform:"uppercase",letterSpacing:"0.8px"}}>{msg.role==="user"?"You":"Advisor"}</div>
                      {msg.role==="assistant"&&<div style={{width:28,height:28,borderRadius:"50%",background:"rgba(229,184,63,0.15)",border:"1px solid rgba(229,184,63,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,marginBottom:6}}>⚡</div>}
                      <div style={{fontSize:13,lineHeight:1.7,color:"rgba(255,255,255,0.8)",...(msg.role==="assistant"?{background:"rgba(229,184,63,0.05)",border:"1px solid rgba(229,184,63,0.18)",borderRadius:"4px 14px 14px 14px",padding:"14px 16px",boxShadow:"0 2px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(229,184,63,0.08)",position:"relative"}:{paddingLeft:10})}} dangerouslySetInnerHTML={{__html:formatAiText(msg.text)}} />
                    </div>
                  ))}
                  {loading && <Loader />}
                  {error && <div style={{color:C.red,fontSize:12,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8,marginTop:8}}>{error}</div>}
                  {error?.includes("last free insight") && (
                    <button style={{...S.btnAmber,fontSize:11,padding:"6px 14px",marginTop:8}} onClick={onUpgrade}>Upgrade to continue →</button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{textAlign:"center",padding:"12px 0"}}>
              <div style={{fontSize:13,color:C.red,marginBottom:12}}>You’ve used all {AI_LIMITS.free.total} free AI insights this month.</div>
              <button style={S.btnAmber} onClick={onUpgrade}>Upgrade to get monthly AI allowances</button>
            </div>
          )}
          <div style={{marginTop:16,padding:"12px 14px",background:"rgba(245,158,11,0.06)",borderRadius:10,border:"1px solid rgba(245,158,11,0.15)"}}>
            <div style={{fontSize:11,fontWeight:700,color:C.amber,marginBottom:8}}>Upgrade to Premium — £{PLAN_PRICING.premium.monthly.toFixed(2)}/mo</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",lineHeight:1.6}}>{AI_LIMITS.premium.total} AI calls/month · AI Advisor · Context chat · Build My Stack · Weekly &amp; monthly reports · Why Not Improving · Advanced trends · Custom protocols · CSV export</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",lineHeight:1.6,marginTop:4}}>Or go Annual — £{PLAN_PRICING.premium.annual.toFixed(2)}/yr · Save ~50% · Priority support</div>
            <button style={{...S.btnAmber,marginTop:10,fontSize:11,padding:"6px 14px"}} onClick={onUpgrade}>See all plans →</button>
          </div>
        </div>
      )}

      {/* ── PREMIUM / ANNUAL — all features ─────────────────────────────── */}
      {(plan === "premium" || plan === "annual") && (
        <>
          {/* Context-aware advisor */}
          <div style={S.card}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <div style={S.cardTitle}>✨ AI Stack Advisor</div>
              <span style={{fontSize:9,background:"rgba(139,92,246,0.2)",color:C.purple,padding:"2px 7px",borderRadius:6,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.7px"}}>Context Chat</span>
              <span style={{marginLeft:"auto",fontSize:10,color:used>=limit?C.red:"rgba(255,255,255,0.3)",fontWeight:used>=limit?700:400,flexShrink:0}}>{used}/{limit} used</span>
            </div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:14}}>
              Remembers this conversation. Draws on your logs and journal notes for context.
              {stack.length > 0 ? <span style={{color:C.teal}}> {stack.length} supplements · {logs.length} logs.</span> : " Add supplements to your stack first."}
            </div>
            <button style={{...S.btn,opacity:stack.length===0||!canUse?0.4:1}} onClick={handleAnalyse} disabled={loading||stack.length===0||!canUse}>
              {loading ? "Analysing..." : "Analyse My Stack"}
            </button>
          </div>

          {(history.length > 0 || loading) && (
            <div style={{...S.card,borderColor:C.teal+"20"}}>
              <div style={S.cardTitle}>Conversation <button style={{...S.btnGhost(C.red),marginLeft:"auto",fontSize:10,padding:"3px 8px"}} onClick={()=>setHistory([])}>Clear</button></div>
              <div style={{maxHeight:360,overflowY:"auto",paddingRight:2}}>
                {history.map((msg,i) => (
                  <div key={i} style={{marginBottom:14}}>
                    <div style={{fontSize:10,color:msg.role==="user"?C.teal:"rgba(255,255,255,0.28)",fontWeight:700,marginBottom:5,textTransform:"uppercase",letterSpacing:"0.8px"}}>{msg.role==="user"?"You":"Advisor"}</div>
                    {msg.role==="assistant"&&<div style={{width:28,height:28,borderRadius:"50%",background:"rgba(229,184,63,0.15)",border:"1px solid rgba(229,184,63,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,marginBottom:6}}>⚡</div>}
                    <div style={{fontSize:13,lineHeight:1.72,color:msg.role==="user"?"rgba(255,255,255,0.68)":"rgba(255,255,255,0.84)",...(msg.role==="assistant"?{background:"rgba(229,184,63,0.05)",border:"1px solid rgba(229,184,63,0.18)",borderRadius:"4px 14px 14px 14px",padding:"14px 16px",boxShadow:"0 2px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(229,184,63,0.08)"}:{paddingLeft:12})}} dangerouslySetInnerHTML={{__html:msg.role==="assistant"?formatAiText(msg.text):sanitiseText(msg.text)}} />
                  </div>
                ))}
                {loading && <div style={{marginBottom:14}}><div style={{fontSize:10,color:"rgba(255,255,255,0.28)",fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.8px"}}>Advisor</div><Loader /></div>}
                {error && <div style={{fontSize:13,color:C.red,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8,marginBottom:8}}>{error}</div>}
                <div ref={endRef} />
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",marginTop:10,fontStyle:"italic"}}>For informational purposes only. Consult a healthcare professional.</div>
            </div>
          )}

          <div style={S.card}>
            <div style={S.cardTitle}>Ask a Question</div>
            <div style={{display:"flex",gap:9}}>
              <input style={{...S.input,flex:1}} placeholder="e.g. Why is my sleep score low even when I take magnesium?" value={question} onChange={e=>setQuestion(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!loading&&handleAsk()} disabled={!canUse} />
              <button style={{...S.btn,flexShrink:0,opacity:loading||!canUse?0.4:1}} onClick={handleAsk} disabled={loading||!canUse}>{loading?"...":"Ask"}</button>
            </div>
          </div>

          {/* Build My Optimal Stack */}
          <div style={{...S.card,borderColor:"rgba(16,185,129,0.3)"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
              <span style={{fontSize:20}}>🧬</span>
              <div style={S.cardTitle}>Build My Optimal Stack</div>
            </div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:14}}>
              {logs.length < 3
                ? `Log at least 3 days first (you have ${logs.length}) — the AI needs real data to design a protocol for you.`
                : "The AI analyses your logs, scores, and journal notes to design a custom protocol specifically for you."}
            </div>
            {buildStack ? (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:"rgba(16,185,129,0.08)",borderRadius:10,marginBottom:10}}>
                  <span style={{fontSize:28}}>{buildStack.icon}</span>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:buildStack.color||C.green}}>{buildStack.name}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.5}}>{buildStack.description}</div>
                  </div>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:10}}>
                  {buildStack.supplements?.map(s=><span key={s} style={{fontSize:11,background:"rgba(16,185,129,0.1)",color:C.green,border:"1px solid rgba(16,185,129,0.2)",padding:"3px 8px",borderRadius:7}}>{s}</span>)}
                </div>
                {buildStack.rationale && <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",lineHeight:1.6,marginBottom:12,fontStyle:"italic"}}>"{buildStack.rationale}"</div>}
                <button
                  style={{...S.btn,background:"linear-gradient(135deg,"+C.green+",#059669)",width:"100%",opacity:savingProtocol?0.6:1}}
                  disabled={savingProtocol}
                  onClick={async () => {
                    if (!buildStack) return;
                    const duplicate = customProtocols.find(p => p.name === buildStack.name);
                    if (duplicate) {
                      setConfirmOverwriteName(buildStack.name);
                      return; // Show in-app confirmation instead of window.confirm
                    }
                    setSavingProtocol(true);
                    try {
                      saveCustomProtocol({
                        name: buildStack.name,
                        icon: buildStack.icon ?? "🧬",
                        color: buildStack.color ?? C.green,
                        description: buildStack.description ?? buildStack.rationale ?? "",
                        supplements: buildStack.supplements ?? [],
                        custom: true,
                      });
                      setToast((buildStack.icon ?? "🧬") + " \"" + buildStack.name + "\" saved!");
                    } finally {
                      setSavingProtocol(false);
                    }
                  }}>
                  {savingProtocol ? "Saving..." : "💾 Save as Custom Protocol"}
                </button>
                {confirmOverwriteName && (
                  <div style={{marginTop:10,padding:"12px 14px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:10}}>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",marginBottom:10}}>
                      A protocol named <strong style={{color:C.amber}}>"{confirmOverwriteName}"</strong> already exists. Overwrite it?
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button style={{...S.btn,flex:1,background:C.amber,fontSize:12,padding:"7px 0"}} onClick={async () => {
                        setConfirmOverwriteName(null);
                        setSavingProtocol(true);
                        try {
                          saveCustomProtocol({
                            name: buildStack.name,
                            icon: buildStack.icon ?? "🧬",
                            color: buildStack.color ?? C.green,
                            description: buildStack.description ?? buildStack.rationale ?? "",
                            supplements: buildStack.supplements ?? [],
                            custom: true,
                          });
                          setToast((buildStack.icon ?? "🧬") + " \"" + buildStack.name + "\" saved!");
                        } finally {
                          setSavingProtocol(false);
                        }
                      }}>Overwrite</button>
                      <button style={{...S.btnGhost(C.red),flex:1,fontSize:12,padding:"7px 0"}} onClick={() => setConfirmOverwriteName(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button style={{...S.btn,background:"linear-gradient(135deg,"+C.green+",#059669)",width:"100%",opacity:logs.length<3||!canUse||reportLoading==="buildStack"?0.4:1}}
                disabled={logs.length<3||!canUse||reportLoading==="buildStack"} onClick={()=>runReport("buildStack")}>
                {reportLoading==="buildStack" ? "Building..." : "🧬 Build My Optimal Stack"}
              </button>
            )}
          </div>

          {/* Weekly Report */}
          <div style={{...S.card,borderColor:"rgba(59,130,246,0.25)"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
              <span style={{fontSize:20}}>📋</span>
              <div style={S.cardTitle}>Weekly AI Report</div>
            </div>
            {weeklyReport ? (
              <>
                <div style={{marginBottom:12}} dangerouslySetInnerHTML={{__html:formatAiText(weeklyReport)}} />
                <button style={{...S.btnGhost(),fontSize:11}} onClick={()=>{setWeeklyReport(null);}}>↻ Regenerate</button>
              </>
            ) : (
              <>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:14}}>How did this week go? What worked, what to adjust, what to expect next week.</div>
                <button style={{...S.btn,background:"linear-gradient(135deg,"+C.blue+",#2563EB)",width:"100%",opacity:!canUse||reportLoading==="weekly"||logs.length<MIN_LOGS_FOR_AI?0.4:1,cursor:logs.length<MIN_LOGS_FOR_AI?"not-allowed":undefined}}
                  disabled={!canUse||reportLoading==="weekly"||logs.length<MIN_LOGS_FOR_AI} onClick={()=>runReport("weekly")}
                  title={logs.length<MIN_LOGS_FOR_AI?`Log ${MIN_LOGS_FOR_AI-logs.length} more day${MIN_LOGS_FOR_AI-logs.length!==1?"s":""} to unlock (${MIN_LOGS_FOR_AI-logs.length} to go)`:""}>
                  {reportLoading==="weekly" ? "Generating..." : "📋 Generate Weekly Report"}
                </button>
              </>
            )}
          </div>

          {/* Monthly Report */}
          <div style={{...S.card,borderColor:"rgba(139,92,246,0.25)"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
              <span style={{fontSize:20}}>📅</span>
              <div style={S.cardTitle}>Monthly AI Report</div>
            </div>
            {monthlyReport ? (
              <>
                <div style={{marginBottom:12}} dangerouslySetInnerHTML={{__html:formatAiText(monthlyReport)}} />
                <button style={{...S.btnGhost(),fontSize:11}} onClick={()=>setMonthlyReport(null)}>↻ Regenerate</button>
              </>
            ) : (
              <>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:14}}>Deep review of your month — trends, what’s working, stack adjustments, and what to expect in the next 4 weeks.</div>
                <button style={{...S.btn,background:"linear-gradient(135deg,#8B5CF6,#6366F1)",width:"100%",opacity:!canUse||reportLoading==="monthly"||logs.length<MIN_LOGS_FOR_AI?0.4:1,cursor:logs.length<MIN_LOGS_FOR_AI?"not-allowed":undefined}}
                  disabled={!canUse||reportLoading==="monthly"||logs.length<MIN_LOGS_FOR_AI} onClick={()=>runReport("monthly")}
                  title={logs.length<MIN_LOGS_FOR_AI?`Log ${MIN_LOGS_FOR_AI-logs.length} more day${MIN_LOGS_FOR_AI-logs.length!==1?"s":""} to unlock (${MIN_LOGS_FOR_AI-logs.length} to go)`:""}>
                  {reportLoading==="monthly" ? "Generating..." : "📅 Generate Monthly Report"}
                </button>
              </>
            )}
          </div>

          {/* Why Am I Not Improving */}
          <div style={{...S.card,borderColor:"rgba(245,158,11,0.25)"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:8}}>
              <span style={{fontSize:20}}>🔍</span>
              <div style={S.cardTitle}>Why Am I Not Improving?</div>
            </div>
            {wniReport ? (
              <>
                <div style={{marginBottom:12}} dangerouslySetInnerHTML={{__html:formatAiText(wniReport)}} />
                <button style={{...S.btnGhost(),fontSize:11}} onClick={()=>setWniReport(null)}>↻ Regenerate</button>
              </>
            ) : (
              <>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:14}}>Honest analysis: realistic supplement timelines, missing co-factors, lifestyle factors from your journal, and one actionable change.</div>
                <button style={{...S.btn,background:"linear-gradient(135deg,"+C.amber+","+C.orange+")",width:"100%",opacity:!canUse||reportLoading==="wni"||logs.length<MIN_LOGS_FOR_AI?0.4:1,cursor:logs.length<MIN_LOGS_FOR_AI?"not-allowed":undefined}}
                  disabled={!canUse||reportLoading==="wni"||logs.length<MIN_LOGS_FOR_AI} onClick={()=>runReport("wni")}
                  title={logs.length<MIN_LOGS_FOR_AI?`Log ${MIN_LOGS_FOR_AI-logs.length} more day${MIN_LOGS_FOR_AI-logs.length!==1?"s":""} to unlock (${MIN_LOGS_FOR_AI-logs.length} to go)`:""}>
                  {reportLoading==="wni" ? "Analysing..." : "🔍 Why Am I Not Improving?"}
                </button>
              </>
            )}
          </div>

          {reportError && <div style={{color:C.red,fontSize:12,padding:"8px 12px",background:"rgba(239,68,68,0.08)",borderRadius:8,marginBottom:12}}>{reportError}</div>}
        </>
      )}
    </>
  );
}


// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// /pages/ShopPage.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ShopPage() {
  const { stack } = useApp();
  const categories = Object.keys(SUPPLEMENT_DB).reduce((acc, s) => {
    const cat = SUPPLEMENT_DB[s].category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <>
      <div style={S.card}>
        <div style={S.cardTitle}>Supplement Shop</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.65,marginBottom:4}}>{DISCLAIMER_AFFILIATE}</div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",lineHeight:1.6,marginTop:8}}>{DISCLAIMER_SHORT} Product links are provided for convenience and do not constitute a recommendation that any supplement is suitable for your individual health circumstances.</div>
      </div>
      {stack.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>Your Current Stack</div>
          {stack.map(s => {
            const info = SUPPLEMENT_DB[s];
            const specific = isSpecificLink(s);
            return (
              <div key={s} style={{padding:"12px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8,gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{s}</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.38)",display:"flex",alignItems:"center",gap:6}}>{info?.dose} <span style={S.tag(evidenceColor(info?.evidence))}>{info?.evidence}</span></div>
                  </div>
                  {!specific && <span style={{fontSize:9,color:"rgba(255,255,255,0.22)",fontStyle:"italic",alignSelf:"center"}}>Search link</span>}
                </div>
                <BuyButtons name={s} />
              </div>
            );
          })}
        </div>
      )}
      {Object.entries(categories).map(([cat,supps]) => (
        <div key={cat} style={S.card}>
          <div style={S.cardTitle}>{cat}</div>
          {supps.map((s,i) => {
            const info = SUPPLEMENT_DB[s];
            const links = AFFILIATE_LINKS[s];
            const specific = isSpecificLink(s);
            return (
              <div key={s} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:i<supps.length-1?"1px solid rgba(255,255,255,0.04)":"none",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,marginBottom:2}}>{s}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>{info?.dose}</div>
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  {links?.amazon && <a href={links.amazon} target="_blank" rel="noopener noreferrer nofollow" style={{background:"rgba(255,153,0,0.14)",color:"#FF9900",border:"1px solid rgba(255,153,0,0.28)",padding:"4px 10px",borderRadius:7,fontSize:10,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>{specific?"Buy":"Search"} Amazon</a>}
                  {links?.alt && <a href={links.alt.url} target="_blank" rel="noopener noreferrer nofollow" style={{background:"rgba(229,184,63,0.1)",color:C.teal,border:"1px solid rgba(229,184,63,0.25)",padding:"4px 10px",borderRadius:7,fontSize:10,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>{links.alt.label}</a>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// LandingPage — shown to unauthenticated users before onboarding
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function LandingPage({ onStartOnboarding, onSignIn }) {
  // Always present the landing scrolled to the very top (logo first).
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const styles = {
    page: { minHeight:"100vh", background:"radial-gradient(125% 78% at 50% -8%, #284f78 0%, #1B3A5C 48%, #132b46 100%)", display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"clamp(26px,6.5vh,68px) 18px 44px", boxSizing:"border-box" },
    inner: { width:"100%", maxWidth:520, textAlign:"center", position:"relative" },
    headline: { color:"#fff", fontSize:"clamp(27px,7.2vw,37px)", fontWeight:800, lineHeight:1.18, letterSpacing:"-0.5px", margin:"0 0 16px" },
    subheadline: { color:"rgba(255,255,255,0.66)", fontSize:"clamp(15px,4vw,17px)", lineHeight:1.62, margin:"0 auto 30px", maxWidth:448 },
    trustList: { listStyle:"none", padding:0, margin:"0 auto 32px", display:"flex", flexDirection:"column", gap:12, maxWidth:404, textAlign:"left" },
    trustItem: { color:"rgba(255,255,255,0.82)", fontSize:14.5, display:"flex", alignItems:"flex-start", gap:11, lineHeight:1.45 },
    check: { flexShrink:0, width:22, height:22, borderRadius:"50%", background:"rgba(229,184,63,0.16)", border:"1px solid rgba(229,184,63,0.45)", color:"#F5D67A", fontWeight:800, fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", marginTop:1 },
    ctaBtn: { display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", maxWidth:420, margin:"0 auto 14px", padding:"17px 24px", background:"linear-gradient(135deg,#F7DC8A 0%,#E5B83F 52%,#D2A22F 100%)", color:"#10243c", fontSize:16.5, fontWeight:800, border:"none", borderRadius:14, cursor:"pointer", boxSizing:"border-box", boxShadow:"0 12px 30px rgba(229,184,63,0.34), inset 0 1px 0 rgba(255,255,255,0.45)", fontFamily:"inherit", transition:"transform .15s, box-shadow .15s" },
    microcopy: { color:"rgba(255,255,255,0.42)", fontSize:12.5, marginBottom:18 },
    signInLink: { color:"rgba(255,255,255,0.6)", fontSize:14, cursor:"pointer", background:"none", border:"none", marginBottom:36, fontFamily:"inherit", padding:"6px 12px", transition:"color .15s" },
    divider: { borderTop:"1px solid rgba(255,255,255,0.1)", margin:"30px auto", maxWidth:300 },
    footer: { color:"rgba(255,255,255,0.32)", fontSize:12 },
    footerLink: { color:"rgba(229,184,63,0.78)", textDecoration:"none" },
  };
  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        {/* Soft gold glow behind the logo */}
        <div style={{position:"absolute",top:-46,left:"50%",transform:"translateX(-50%)",width:380,height:300,maxWidth:"94%",background:"radial-gradient(circle at 50% 40%,rgba(229,184,63,0.20) 0%,transparent 66%)",pointerEvents:"none"}} />
        <div style={{position:"relative",display:"flex",justifyContent:"center",marginBottom:"clamp(22px,5vh,40px)"}}>
          <BrandLockup variant="hero" />
        </div>
        <div style={{display:"inline-block",background:"rgba(229,184,63,0.08)",border:"1px solid rgba(229,184,63,0.26)",borderRadius:999,padding:"7px 17px",marginBottom:"clamp(20px,4vh,28px)",backdropFilter:"blur(6px)"}}>
          <span style={{fontSize:12.5,color:"rgba(255,255,255,0.72)"}}>Average new-user stack score: </span>
          <span style={{fontSize:12.5,color:"#F5D67A",fontWeight:800}}>58 / 100</span>
        </div>
        <h1 style={styles.headline}>Your supplements are probably <span style={{color:"#F5D67A"}}>fighting each other</span>.</h1>
        <p style={styles.subheadline}>Tell us what you're taking. In 60 seconds you'll know exactly what's working, what's wasting your money, and the one change that makes the biggest difference.</p>
        <ul style={styles.trustList}>
          {[
            "Finds the form error most people don't know they're making",
            "Detects conflicts between supplements you're already taking",
            "Gives you one change — not a 10-point plan",
          ].map(item => (
            <li key={item} style={styles.trustItem}><span style={styles.check}>✓</span><span>{item}</span></li>
          ))}
        </ul>
        <button style={styles.ctaBtn}
          onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 16px 40px rgba(229,184,63,0.46), inset 0 1px 0 rgba(255,255,255,0.45)";}}
          onMouseOut={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 12px 30px rgba(229,184,63,0.34), inset 0 1px 0 rgba(255,255,255,0.45)";}}
          onClick={onStartOnboarding}>
          Analyse My Stack — Free →
        </button>
        <p style={styles.microcopy}>No signup needed · 3 quick questions · Real AI analysis</p>
        <button style={styles.signInLink} onClick={onSignIn} onMouseOver={e=>e.currentTarget.style.color="#F5D67A"} onMouseOut={e=>e.currentTarget.style.color="rgba(255,255,255,0.6)"}>Already have an account? <span style={{textDecoration:"underline",fontWeight:600}}>Sign in</span></button>
        <hr style={styles.divider} />
        <p style={styles.footer}>Evidence-based supplement guidance by{" "}<a href="https://peakhealthstack.com" target="_blank" rel="noopener" style={styles.footerLink}>peakhealthstack.com</a></p>
        <p style={{color:"rgba(255,255,255,0.22)",fontSize:11,marginTop:12,lineHeight:1.6}}>{DISCLAIMER_SHORT} · Affiliate links present · Results vary by individual.</p>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// OnboardingModal — 3-step flow: supplements → goal → issue → AI assessment
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const ONBOARDING_NAME_MAP = {
  "Magnesium":          "Magnesium Glycinate",
  "Vitamin D3":         "Vitamin D3 + K2",
  "Omega-3 / Fish Oil": "Omega-3 (EPA+DHA)",
  "Zinc":               "Zinc Bisglycinate",
  "Ashwagandha":        "Ashwagandha KSM-66",
  "Creatine":           "Creatine Monohydrate",
  "Collagen":           "Collagen Peptides",
  "CoQ10":              "CoQ10 Ubiquinol",
  "Iron":               "Iron Bisglycinate",
  "Turmeric / Curcumin":"Curcumin",
};

const ONBOARDING_SUPPLEMENTS = [
  "Magnesium","Vitamin D3","Omega-3 / Fish Oil","Vitamin B12",
  "Zinc","Ashwagandha","Creatine","Collagen","CoQ10","Iron",
  "L-Theanine","Vitamin C","Probiotics","Turmeric / Curcumin","Protein Powder",
];
const ONBOARDING_GOALS = [
  {emoji:"💪",label:"More energy"},{emoji:"😴",label:"Better sleep"},{emoji:"🧠",label:"Focus & clarity"},
  {emoji:"🦴",label:"Bone & joint health"},{emoji:"❤️",label:"Heart health"},
  {emoji:"⚖️",label:"Weight management"},{emoji:"🌿",label:"General wellness"},
  {emoji:"🏋️",label:"Muscle Recovery"},
];

// Premium onboarding — optional demographics (weighted heavily by the AI) +
// a current-supplement picker that uses the EXACT stack forms (no "Magnesium"
// guesswork). The full SUPPLEMENT_DB sits behind a "Show all" expander.
const ONBOARDING_AGE_RANGES = ["Under 25","25–34","35–44","45–54","55–64","65+"];
const ONBOARDING_SEXES = ["Female","Male","Prefer not to say"];
const ONBOARDING_MAIN_SUPPLEMENTS = [
  "Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Vitamin B12",
  "Zinc Bisglycinate","Creatine Monohydrate","Ashwagandha KSM-66","Vitamin C",
  "Probiotics","Iron Bisglycinate","Collagen Peptides","L-Theanine",
  "Magnesium L-Threonate","CoQ10 Ubiquinol",
].filter(s => SUPPLEMENT_DB[s]);

// Builds the personalised "what to add / review" note persisted to the Stack page.
function buildStackAdvice(stack, goal, assessment) {
  const peer = PEER_DATA[goal];
  const recommended = peer?.topSupps ?? ["Magnesium Glycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Vitamin B12"];
  const add = recommended.filter(s => !stack.includes(s)).slice(0, 3);
  const review = stack.filter(s => SUPPLEMENT_DB[s]?.evidence === "Limited");
  return { goal: goal || "General wellness", action: assessment?.action || "", add, review };
}
const ONBOARDING_ISSUES = [
  {emoji:"😴",label:"Constant fatigue"},{emoji:"🌫️",label:"Brain fog"},{emoji:"😤",label:"Poor sleep"},
  {emoji:"😰",label:"Stress & anxiety"},{emoji:"🤕",label:"Joint or muscle pain"},
  {emoji:"📉",label:"Low mood"},{emoji:"🤷",label:"Not sure if anything is working"},
 
];
const AI_PROXY_URL = `${SUPABASE_URL}/functions/v1/ai-proxy`;

// ── Onboarding scoring + archetype helpers ────────────────────────────────────
function computeStackScoreDetailed(supplements, goal, issue) {
  // supplements received here are ALREADY mapped to DB keys via ONBOARDING_NAME_MAP

  const GOAL_TAG_MAP = {
    "Better sleep":        ["sleep","stress","cortisol","calm"],
    "More energy":         ["energy","fatigue","mitochondria","NAD+"],
    "Focus & clarity":     ["brain","cognition","memory","focus","nerve"],
    "Bone & joint health": ["bone","joint","cartilage","collagen","connective tissue"],
    "Muscle Recovery":     ["recovery","muscle","performance","strength","tendon"],
    "Weight management":   ["metabolism","blood sugar","gut","thyroid"],
    "Heart health":        ["heart","cardiovascular","blood pressure","inflammation"],
    "General wellness":    ["immunity","antioxidant","longevity","gut"],
  };

  const ISSUE_TAG_MAP = {
    "Constant fatigue":               ["energy","fatigue","mitochondria","NAD+","thyroid"],
    "Brain fog":                      ["brain","cognition","memory","nerve","inflammation"],
    "Poor sleep":                     ["sleep","stress","cortisol","calm","anxiety"],
    "Stress & anxiety":               ["stress","cortisol","anxiety","calm","mood"],
    "Joint or muscle pain":           ["joint","inflammation","tendon","recovery","connective tissue"],
    "Low mood":                       ["mood","serotonin","dopamine","stress","brain"],
    "Not sure if anything is working":[],
  };

  const HIGH_QUALITY_FORMS = [
    "Magnesium Glycinate","Magnesium L-Threonate","Zinc Bisglycinate",
    "Iron Bisglycinate","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Vitamin B12",
    "Ashwagandha KSM-66","CoQ10 Ubiquinol","Collagen Peptides",
    "Creatine Monohydrate","L-Theanine","Vitamin C","Probiotics",
    "Copper Bisglycinate","Calcium Citrate",
  ];

  const LOW_VALUE_FOR_SCORING = ["Protein Powder"];

  const SYNERGY_PAIRS = [
    ["Magnesium Glycinate","Ashwagandha KSM-66"],
    ["Magnesium Glycinate","Vitamin D3 + K2"],
    ["Magnesium Glycinate","L-Theanine"],
    ["Magnesium Glycinate","Vitamin B12"],
    ["Vitamin D3 + K2","Omega-3 (EPA+DHA)"],
    ["Vitamin D3 + K2","Zinc Bisglycinate"],
    ["Omega-3 (EPA+DHA)","CoQ10 Ubiquinol"],
    ["Omega-3 (EPA+DHA)","Collagen Peptides"],
    ["Collagen Peptides","Vitamin C"],
    ["Ashwagandha KSM-66","Zinc Bisglycinate"],
    ["Creatine Monohydrate","Vitamin D3 + K2"],
    ["Creatine Monohydrate","Omega-3 (EPA+DHA)"],
    ["Iron Bisglycinate","Vitamin C"],
    ["Probiotics","Vitamin D3 + K2"],
    ["L-Theanine","Vitamin B12"],
  ];

  const inDB = supplements.filter(s => SUPPLEMENT_DB[s]);
  const n = supplements.length;

  // ── FORM QUALITY (0–10) ──────────────────────────────────────────────────
  const highQualityCount = supplements.filter(s => HIGH_QUALITY_FORMS.includes(s)).length;
  const lowValueCount    = supplements.filter(s => LOW_VALUE_FOR_SCORING.includes(s)).length;
  const unknownCount     = supplements.filter(s => !SUPPLEMENT_DB[s] && !LOW_VALUE_FOR_SCORING.includes(s)).length;
  let formScore;
  if (n === 0) {
    formScore = 0;
  } else {
    const qualityRatio = highQualityCount / n;
    formScore = Math.round(qualityRatio * 10);
    formScore = Math.max(1, formScore - lowValueCount * 2 - Math.floor(unknownCount * 1.5));
    formScore = Math.min(10, formScore);
  }

  // ── TIMING RELEVANCE (0–10) ──────────────────────────────────────────────
  const TIME_SENSITIVE_SUPPS = [
    "Magnesium Glycinate","Ashwagandha KSM-66","L-Theanine","Vitamin D3 + K2",
    "Iron Bisglycinate","CoQ10 Ubiquinol","Omega-3 (EPA+DHA)","Collagen Peptides",
    "Creatine Monohydrate","Vitamin B12",
  ];
  const timingSensitiveCount = supplements.filter(s => TIME_SENSITIVE_SUPPS.includes(s)).length;
  let timingScore;
  if (n === 0) {
    timingScore = 0;
  } else if (n === 1) {
    timingScore = TIME_SENSITIVE_SUPPS.includes(supplements[0]) ? 5 : 3;
  } else {
    const ratio = timingSensitiveCount / n;
    timingScore = Math.round(3 + ratio * 7);
    if (n >= 4) timingScore = Math.min(10, timingScore + 1);
    if (n >= 6) timingScore = Math.min(10, timingScore + 1);
  }

  // ── SYNERGY (0–10) ───────────────────────────────────────────────────────
  const synergyCount = SYNERGY_PAIRS.filter(([a,b]) =>
    supplements.includes(a) && supplements.includes(b)
  ).length;
  let synergyScore;
  if (n <= 1) {
    synergyScore = 0;
  } else if (n === 2) {
    synergyScore = synergyCount > 0 ? 7 : 2;
  } else {
    synergyScore = Math.min(10, Math.round(2 + synergyCount * 2.5));
    if (synergyCount >= 3) synergyScore = Math.min(10, synergyScore + 1);
  }

  // ── GAP COVERAGE (0–10) ──────────────────────────────────────────────────
  const goalTags  = GOAL_TAG_MAP[goal]  ?? [];
  const issueTags = ISSUE_TAG_MAP[issue] ?? [];

  const goalTagHits  = inDB.filter(s => goalTags.some(t  => SUPPLEMENT_DB[s]?.tags?.includes(t))).length;
  const issueTagHits = inDB.filter(s => issueTags.some(t => SUPPLEMENT_DB[s]?.tags?.includes(t))).length;

  let gapScore;
  if (n === 0) {
    gapScore = 0;
  } else {
    const goalCoverage  = goalTags.length  > 0 ? Math.min(goalTagHits  / Math.max(1, goalTags.length  * 0.4), 1) : 0.5;
    const issueCoverage = issueTags.length > 0 ? Math.min(issueTagHits / Math.max(1, issueTags.length * 0.4), 1) : 0.5;
    const rawCoverage   = goalCoverage * 0.6 + issueCoverage * 0.4;
    gapScore = Math.round(rawCoverage * 10);
    if (n === 1) gapScore = Math.min(gapScore, 4);
    if (goalTagHits === 0 && issueTagHits === 0) gapScore = Math.min(2, gapScore);
    gapScore = Math.max(0, Math.min(10, gapScore));
  }

  // ── TOTAL (weights: Form 25% · Timing 20% · Synergy 25% · Gap 30%) ──────
  const total = Math.round(
    (formScore   / 10) * 25 +
    (timingScore / 10) * 20 +
    (synergyScore/ 10) * 25 +
    (gapScore    / 10) * 30
  );

  const finalTotal = Math.max(5, Math.min(95, total));

  const formHint    = formScore    >= 8 ? "High-quality, bioavailable forms"          : formScore    >= 5 ? "Mix of good and generic forms"        : "Upgrade to more bioavailable forms";
  const timingHint  = timingScore  >= 7 ? "Time-sensitive supplements present"         : n <= 1      ? "Add more supplements to improve timing" : "Timing not optimised for your goal";
  const synergyHint = synergyScore >= 7 ? "Strong synergistic combinations"            : synergyScore >= 4 ? "Some synergistic pairs present"     : n <= 1 ? "Single supplement — no pairs possible" : "Few synergistic pairs";
  const gapHint     = gapScore     >= 8 ? "Stack directly targets your goal"           : gapScore    >= 5 ? "Partially addresses your goal"        : "Stack doesn't match your stated goal";

  return {
    total: finalTotal,
    breakdown: [
      { label:"Form quality", score:formScore,    max:10, hint:formHint    },
      { label:"Timing",       score:timingScore,  max:10, hint:timingHint  },
      { label:"Synergy",      score:synergyScore, max:10, hint:synergyHint },
      { label:"Gap coverage", score:gapScore,     max:10, hint:gapHint     },
    ],
  };
}

function computeStackScore(supplements, goal, issue) {
  return computeStackScoreDetailed(supplements, goal, issue).total;
}

function computeArchetype(goal, supplementCount) {
  const map = {
    "More energy":         { name:"Energy Optimiser",   desc:"You're building for sustained performance and vitality.",                    pct:22 },
    "Better sleep":        { name:"Recovery Focused",   desc:"You're prioritising the foundation everything else depends on.",             pct:19 },
    "Focus & clarity":     { name:"Cognitive Builder",  desc:"You're optimising the system that runs everything else.",                   pct:14 },
    "Bone & joint health": { name:"Longevity Focused",  desc:"You're playing the long game — and that's the right game.",                 pct:11 },
    "Muscle Recovery":     { name:"Performance Stack",  desc:"You're building for output, resilience, and faster recovery.",              pct:16 },
    "Weight management":   { name:"Metabolic Focus",    desc:"You're targeting the hormonal and metabolic drivers.",                      pct:9  },
    "Heart health":        { name:"Foundation Builder", desc:"You're prioritising the systems that everything else depends on.",           pct:5  },
    "General wellness":    { name:"Foundation Builder", desc:"You're covering your bases before going deeper. Smart approach.",           pct:34 },
  };
  return map[goal] ?? { name:"Foundation Builder", desc:"You're covering your bases before going deeper.", pct:34 };
}

function computeProfileFromStack(stack) {
  const TAG_TO_GOAL = {
    energy:"More energy", sleep:"Better sleep", brain:"Focus & clarity",
    cognition:"Focus & clarity", bone:"Bone & joint health", joint:"Bone & joint health",
    recovery:"Muscle Recovery", muscle:"Muscle Recovery", metabolism:"Weight management",
    heart:"Heart health",
  };
  const counts = {};
  for (const name of stack) {
    for (const tag of (SUPPLEMENT_DB[name]?.tags ?? [])) {
      counts[tag] = (counts[tag] ?? 0) + 1;
    }
  }
  const topTag = Object.entries(counts).sort((a,b) => b[1]-a[1])[0]?.[0];
  return computeArchetype(TAG_TO_GOAL[topTag] ?? "General wellness", stack.length);
}

function computeLiveScore(stack, logs) {
  const base = computeStackScoreDetailed(stack, "General wellness", "Not sure if anything is working").total;
  let bonus = 0;
  if (logs.length >= 3) {
    const last7 = logs.slice(0, 7);
    const adherence = last7.filter(l => l.taken?.length > 0).length / last7.length;
    if (adherence >= 0.8) bonus += 8;
    else if (adherence >= 0.5) bonus += 4;
  }
  if (logs.length >= 7) bonus += 5;
  return Math.min(base + bonus, 95);
}

const PEER_DATA = {
  "Better sleep":        { topSupps:["Magnesium Glycinate","Vitamin D3 + K2","Ashwagandha KSM-66","L-Theanine"],         pct:[81,67,54,43], userCount:892  },
  "More energy":         { topSupps:["Vitamin B12","Vitamin D3 + K2","Omega-3 (EPA+DHA)","Magnesium Glycinate"],          pct:[78,72,61,58], userCount:1043 },
  "Focus & clarity":     { topSupps:["Omega-3 (EPA+DHA)","Magnesium L-Threonate","L-Theanine","Vitamin B12"],             pct:[74,52,48,44], userCount:634  },
  "Bone & joint health": { topSupps:["Vitamin D3 + K2","Calcium Citrate","Magnesium Glycinate","Omega-3 (EPA+DHA)"],      pct:[89,71,64,55], userCount:412  },
  "Muscle Recovery":     { topSupps:["Creatine Monohydrate","Omega-3 (EPA+DHA)","Magnesium Glycinate","Vitamin D3 + K2"],pct:[83,69,61,57], userCount:758  },
  "Weight management":   { topSupps:["Omega-3 (EPA+DHA)","Vitamin D3 + K2","Magnesium Glycinate","Ashwagandha KSM-66"],  pct:[70,65,59,47], userCount:521  },
  "Heart health":        { topSupps:["Omega-3 (EPA+DHA)","Vitamin D3 + K2","CoQ10 (Ubiquinol)","Magnesium Glycinate"],   pct:[88,76,62,58], userCount:387  },
  "General wellness":    { topSupps:["Vitamin D3 + K2","Omega-3 (EPA+DHA)","Magnesium Glycinate","Vitamin B12"],          pct:[79,74,68,61], userCount:1204 },
};

function computePeerPercentile(supplements, goal) {
  const peer = PEER_DATA[goal];
  if (!peer) return null;
  const matches = peer.topSupps.filter(s => supplements.includes(s)).length;
  const percentiles = [20,40,60,75,85];
  return { percentile: percentiles[Math.min(matches, 4)], userCount: peer.userCount };
}

const _assessmentCache = {};

function EmailConfirmationScreen({ email, onCheckConfirmed, onBackToSignUp, checkLoading, checkError }) {
  const [resendStatus, setResendStatus] = useState("idle");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendStatus("sending");
    try {
      const res = await fetch(SUPABASE_URL + "/auth/v1/resend", {
        method: "POST",
        headers: { "apikey": SUPABASE_ANON, "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "signup",
          email,
          options: { emailRedirectTo: "https://stack.peakhealthstack.com/" }
        }),
      });
      if (!res.ok) throw new Error("Server error");
      setResendStatus("sent");
      setResendCooldown(60);
    } catch {
      setResendStatus("error");
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#1B3A5C",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
      boxSizing: "border-box",
    }}>
      <div style={{ width: "100%", maxWidth: 440, textAlign: "center" }}>
        <div style={{ fontSize: 52, marginBottom: 20 }}>📧</div>
        <h1 style={{ color: "#fff", fontSize: 26, fontWeight: 800, lineHeight: 1.2, margin: "0 0 12px" }}>
          Check your email
        </h1>
        <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 15, lineHeight: 1.6, margin: "0 0 8px" }}>
          We've sent a confirmation link to:
        </p>
        <p style={{ color: "#F5C84A", fontSize: 15, fontWeight: 700, margin: "0 0 28px", wordBreak: "break-all" }}>
          {email}
        </p>
        <div style={{
          background: "rgba(229,184,63,0.08)",
          border: "1px solid rgba(229,184,63,0.2)",
          borderRadius: 12,
          padding: "16px 20px",
          marginBottom: 28,
          textAlign: "left",
        }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.7 }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: "#F5C84A", fontWeight: 700 }}>1.</span> Open your inbox and find the email from Your Peak Stack
            </div>
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: "#F5C84A", fontWeight: 700 }}>2.</span> Click the confirmation link
            </div>
            <div>
              <span style={{ color: "#F5C84A", fontWeight: 700 }}>3.</span> Come back here and tap the button below
            </div>
          </div>
        </div>
        <button
          style={{
            display: "block",
            width: "100%",
            padding: "15px 24px",
            background: "#F5C84A",
            color: "#1B3A5C",
            fontSize: 15,
            fontWeight: 800,
            border: "none",
            borderRadius: 12,
            cursor: checkLoading ? "default" : "pointer",
            marginBottom: 12,
            boxSizing: "border-box",
            opacity: checkLoading ? 0.7 : 1,
            fontFamily: "inherit",
          }}
          onClick={onCheckConfirmed}
          disabled={checkLoading}
        >
          {checkLoading ? "Checking…" : "I've confirmed my email — continue →"}
        </button>
        {checkError && (
          <div style={{
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 13,
            color: "#FCA5A5",
            lineHeight: 1.5,
            textAlign: "left",
          }}>
            {checkError}
          </div>
        )}
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
          Can't find it? Check your spam or junk folder.
        </p>
        <button
          style={{
            background: "none",
            border: "1px solid rgba(255,255,255,0.15)",
            color: resendCooldown > 0 ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.55)",
            borderRadius: 9,
            padding: "8px 18px",
            fontSize: 12,
            cursor: resendCooldown > 0 ? "default" : "pointer",
            fontFamily: "inherit",
            marginBottom: 8,
            display: "block",
            width: "100%",
          }}
          onClick={handleResend}
          disabled={resendCooldown > 0 || resendStatus === "sending"}
        >
          {resendStatus === "sending"
            ? "Sending…"
            : resendStatus === "sent" && resendCooldown > 0
              ? `✓ Email resent — wait ${resendCooldown}s to resend again`
              : resendStatus === "error"
                ? "Failed to resend — try again"
                : "Resend confirmation email"}
        </button>
        <button
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.3)",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
            textDecoration: "underline",
            marginTop: 8,
            display: "block",
            width: "100%",
          }}
          onClick={onBackToSignUp}
        >
          Wrong email address? Start over
        </button>
      </div>
    </div>
  );
}

function OnboardingModal({ onComplete, onSignUp }) {
  const [step, setStep]               = useState(1);
  const [ageRange, setAgeRange]       = useState("");
  const [sex, setSex]                 = useState("");
  const [goals, setGoals]             = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [showAllSupps, setShowAllSupps] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const [assessment, setAssessment]   = useState(null);
  const [aiError, setAiError]         = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx]   = useState(0);
  const [displayScore, setDisplayScore]     = useState(0);
  const [showShareCard, setShowShareCard]   = useState(false);

  const primaryGoal = goals[0] || "General wellness";
  const mappedSupps = supplements.map(s => ONBOARDING_NAME_MAP[s] ?? s);

  const LOADING_MSGS = [
    "Checking supplement forms and bioavailability...",
    "Scanning for timing conflicts in your stack...",
    `Tailoring to your goals: ${goals.join(", ") || "general wellness"}...`,
  ];

  useEffect(() => {
    if (step !== "loading") return;
    const id = setInterval(() => setLoadingMsgIdx(i => (i + 1) % 3), 1200);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step !== "result" || aiError) return;
    const target = computeStackScore(mappedSupps, primaryGoal, "");
    let current = 0;
    const stepSize = Math.max(1, Math.ceil(target / 30));
    const id = setInterval(() => {
      current = Math.min(current + stepSize, target);
      setDisplayScore(current);
      if (current >= target) clearInterval(id);
    }, 30);
    return () => clearInterval(id);
  }, [step]);

  const toggleSupplement = (name) =>
    setSupplements(prev => prev.includes(name) ? prev.filter(s=>s!==name) : [...prev,name]);
  const toggleGoal = (label) =>
    setGoals(prev => prev.includes(label) ? prev.filter(g=>g!==label) : [...prev,label]);

  const addCustomSupplement = () => {
    const trimmed = customInput.trim();
    if (trimmed && !supplements.includes(trimmed)) setSupplements(prev=>[...prev,trimmed]);
    setCustomInput("");
  };

  const runAssessment = async () => {
    setStep("loading"); setAiError(false);
    const demo = [ageRange && `Age ${ageRange}`, sex && sex].filter(Boolean).join(", ") || "not specified";
    const cacheKey = [[...supplements].sort().join("|"), [...goals].sort().join("|"), ageRange, sex].join(":");
    if (_assessmentCache[cacheKey]) {
      setAssessment(_assessmentCache[cacheKey]);
      setStep("result");
      return;
    }
    try {
      const resp = await fetch(EDGE + "/ai-proxy", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON}`,"apikey":SUPABASE_ANON,"x-onboarding":"true"},
        body:JSON.stringify({
          max_tokens:600,
          system:"You are a clinical supplement analyst. Return ONLY valid JSON. No markdown. No preamble.",
          prompt:`Demographics: ${demo}.
Current supplements: ${supplements.length ? supplements.join(", ") : "none yet"}.
30-day goals: ${goals.length ? goals.join(", ") : "general wellness"}.
Stack size: ${supplements.length} supplement${supplements.length !== 1 ? "s" : ""}.

You are a clinical supplement analyst. Analyse this specific combination against the stated goals. Age and sex matter strongly — weigh them heavily (e.g. iron for menstruating women, B12 and D3 for older adults, creatine for muscle preservation over 40). Be precise — name the actual supplements and exact forms from their list.

Rules:
- If they take nothing yet: name the 1-2 highest-impact supplements to start, specific to their goals, age and sex.
- If only 1 supplement: focus on whether it addresses the goals, what's missing, and the most impactful addition.
- If a chosen form is suboptimal for a goal (e.g. Magnesium Oxide for sleep, cheap folic acid), say so and name the better form.
- Never give generic advice — every sentence must reference their specific stack, goals, age or sex.

Return ONLY valid JSON, no other text:
{
  "headline": "One specific sentence identifying the most important gap, conflict or opportunity in their stack relative to their goals, age and sex.",
  "working": "What in their stack genuinely helps their goals and why mechanistically. If nothing does, say so. 2 sentences max.",
  "gap": "The most likely thing holding back their goals given what they are and aren't taking, and their age/sex. Name the missing supplement or wrong form. 2 sentences max.",
  "action": "One concrete change: exact supplement name, dose, and timing. Not generic advice."
}`,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const jsonMatch = (data.text ?? "").match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("Invalid format");
      const parsed = JSON.parse(jsonMatch[0]);
      if (!parsed.working || !parsed.gap || !parsed.action) throw new Error("Incomplete");
      _assessmentCache[cacheKey] = parsed;
      setAssessment(parsed);
      setStep("result");
    } catch(err) {
      console.error("[OnboardingModal]", err);
      setAiError(true);
      setStep("result");
    }
  };

  const st = {
    overlay: {position:"fixed",inset:0,background:"rgba(8,18,32,0.78)",backdropFilter:"blur(6px)",WebkitBackdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16},
    modal:   {background:"linear-gradient(180deg,#1f4063 0%,#15304d 100%)",border:"1px solid rgba(229,184,63,0.28)",borderRadius:22,padding:"30px 28px",width:"100%",maxWidth:520,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 30px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)"},
    eyebrow: {color:"#E5B83F",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"1.6px",marginBottom:10},
    heading: {color:"#fff",fontSize:22,fontWeight:800,lineHeight:1.25,marginBottom:8,letterSpacing:"-0.3px"},
    sub:     {color:"rgba(255,255,255,0.62)",fontSize:14,lineHeight:1.55,marginBottom:22},
    fieldLabel:{color:"rgba(255,255,255,0.55)",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"1px",marginBottom:10},
    pillGrid:{display:"flex",flexWrap:"wrap",gap:8,marginBottom:22},
    pill:(sel)=>({padding:"9px 15px",borderRadius:30,fontSize:13,cursor:"pointer",fontWeight:sel?700:500,border:`1px solid ${sel?"#E5B83F":"rgba(255,255,255,0.18)"}`,background:sel?"linear-gradient(135deg,rgba(245,214,122,0.22),rgba(229,184,63,0.14))":"rgba(255,255,255,0.03)",color:sel?"#F5D67A":"rgba(255,255,255,0.78)",fontFamily:"inherit",transition:"all .14s",boxShadow:sel?"0 2px 12px rgba(229,184,63,0.18)":"none"}),
    optGrid: {display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:22},
    optCard:(sel)=>({position:"relative",padding:"13px 15px",borderRadius:13,cursor:"pointer",textAlign:"left",border:`1px solid ${sel?"#E5B83F":"rgba(255,255,255,0.13)"}`,background:sel?"linear-gradient(135deg,rgba(245,214,122,0.16),rgba(229,184,63,0.07))":"rgba(255,255,255,0.035)",color:sel?"#F5D67A":"rgba(255,255,255,0.82)",fontSize:13.5,fontWeight:sel?700:500,fontFamily:"inherit",transition:"all .14s",boxShadow:sel?"0 4px 16px rgba(229,184,63,0.16)":"none"}),
    optCheck:{position:"absolute",top:9,right:10,width:18,height:18,borderRadius:"50%",background:"#E5B83F",color:"#10243c",fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center"},
    btn:    {width:"100%",padding:"15px 24px",borderRadius:12,background:"linear-gradient(135deg,#F7DC8A 0%,#E5B83F 52%,#D2A22F 100%)",color:"#10243c",fontSize:15,fontWeight:800,border:"none",cursor:"pointer",fontFamily:"inherit",boxShadow:"0 10px 26px rgba(229,184,63,0.32), inset 0 1px 0 rgba(255,255,255,0.4)"},
    btnDis: {width:"100%",padding:"15px 24px",borderRadius:12,background:"rgba(229,184,63,0.22)",color:"rgba(16,36,60,0.55)",fontSize:15,fontWeight:800,border:"none",cursor:"not-allowed",fontFamily:"inherit"},
    showAll:{width:"100%",padding:"10px 0",marginBottom:16,marginTop:-6,background:"rgba(229,184,63,0.07)",border:"1px dashed rgba(229,184,63,0.3)",borderRadius:10,color:"#F5D67A",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"},
    catLabel:{color:"#E5B83F",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"1px",margin:"4px 0 8px",opacity:0.85},
    warn:   {color:"#FBBF24",fontSize:12.5,marginBottom:12,marginTop:-8},
    skip:   {color:"rgba(255,255,255,0.4)",fontSize:12.5,textAlign:"center",marginTop:12,cursor:"pointer",fontFamily:"inherit"},
    dots:   {display:"flex",gap:6,marginBottom:22},
    dot:(a,d)=>({width:d||a?22:8,height:8,borderRadius:4,background:d||a?"#E5B83F":"rgba(255,255,255,0.2)",transition:"all 0.2s"}),
    aCard:(col)=>({background:"rgba(255,255,255,0.05)",borderRadius:12,padding:"14px 16px",marginBottom:12,borderLeft:`3px solid ${col}`}),
    aLabel:(col)=>({color:col,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:6}),
    aText:  {color:"rgba(255,255,255,0.85)",fontSize:14,lineHeight:1.55},
    cinput: {background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:9,padding:"10px 13px",color:"#fff",fontSize:13,width:"100%",boxSizing:"border-box",marginBottom:8,outline:"none",fontFamily:"inherit"},
  };

  // Step 1 — About you (age + sex, both optional but weighted heavily by the AI)
  if (step===1) return (
    <div style={st.overlay}><div style={st.modal}>
      <div style={st.dots}>{[1,2,3].map(n=><div key={n} style={st.dot(n===1,n<1)}/>)}</div>
      <div style={st.eyebrow}>Step 1 of 3 · About you</div>
      <p style={st.heading}>First, a little about you</p>
      <p style={st.sub}>Both optional — but your age and sex change what's genuinely worth taking, so the AI weighs them heavily.</p>
      <div style={st.fieldLabel}>Age range</div>
      <div style={st.pillGrid}>
        {ONBOARDING_AGE_RANGES.map(a=><button key={a} style={st.pill(ageRange===a)} onClick={()=>setAgeRange(ageRange===a?"":a)}>{a}</button>)}
      </div>
      <div style={st.fieldLabel}>Sex</div>
      <div style={st.pillGrid}>
        {ONBOARDING_SEXES.map(s=><button key={s} style={st.pill(sex===s)} onClick={()=>setSex(sex===s?"":s)}>{s}</button>)}
      </div>
      <button style={st.btn} onClick={()=>setStep(2)}>Continue →</button>
      <p style={st.skip} onClick={()=>setStep(2)}>Skip — I'd rather not say</p>
    </div></div>
  );

  // Step 2 — Goals (multi-select)
  if (step===2) return (
    <div style={st.overlay}><div style={st.modal}>
      <div style={st.dots}>{[1,2,3].map(n=><div key={n} style={st.dot(n===2,n<2)}/>)}</div>
      <div style={st.eyebrow}>Step 2 of 3 · Your goals</div>
      <p style={st.heading}>What do you want to improve in the next 30 days?</p>
      <p style={st.sub}>Choose as many as you like — picking more than one (e.g. energy + sleep) makes your plan sharper.</p>
      <div style={st.optGrid}>
        {ONBOARDING_GOALS.map(g=>{const sel=goals.includes(g.label);return(
          <button key={g.label} style={st.optCard(sel)} onClick={()=>{toggleGoal(g.label);setShowValidation(false);}}>
            {sel&&<span style={st.optCheck}>✓</span>}
            <span style={{fontSize:19,display:"block",marginBottom:5}}>{g.emoji}</span>{g.label}
          </button>);})}
      </div>
      {showValidation && <p style={st.warn}>Pick at least one goal to continue.</p>}
      <button style={goals.length===0?st.btnDis:st.btn} disabled={goals.length===0}
        onClick={()=>{ if(!goals.length){setShowValidation(true);return;} setShowValidation(false); setStep(3); }}>
        Continue →
      </button>
    </div></div>
  );

  // Step 3 — Current supplements (exact stack forms; full SUPPLEMENT_DB behind "Show all")
  if (step===3) {
    const otherSupps = Object.keys(SUPPLEMENT_DB).filter(s => !ONBOARDING_MAIN_SUPPLEMENTS.includes(s));
    const grouped = {};
    otherSupps.forEach(s => { const c = SUPPLEMENT_DB[s].category || "Other"; (grouped[c] = grouped[c] || []).push(s); });
    return (
      <div style={st.overlay}><div style={st.modal}>
        <div style={st.dots}>{[1,2,3].map(n=><div key={n} style={st.dot(n===3,n<3)}/>)}</div>
        <div style={st.eyebrow}>Step 3 of 3 · Your stack</div>
        <p style={st.heading}>What are you currently taking?</p>
        <p style={st.sub}>Pick the exact forms — form matters as much as the supplement itself. Taking nothing yet? That's fine, just skip ahead.</p>
        <div style={st.pillGrid}>
          {ONBOARDING_MAIN_SUPPLEMENTS.map(s=><button key={s} style={st.pill(supplements.includes(s))} onClick={()=>toggleSupplement(s)}>{supplements.includes(s)?"✓ ":""}{s}</button>)}
        </div>
        <button style={st.showAll} onClick={()=>setShowAllSupps(v=>!v)}>
          {showAllSupps ? "▴  Hide full list" : `▾  Show all ${otherSupps.length} supplements`}
        </button>
        {showAllSupps && (
          <div style={{marginBottom:18,maxHeight:250,overflowY:"auto",paddingRight:4}}>
            {Object.entries(grouped).map(([cat,list])=>(
              <div key={cat} style={{marginBottom:8}}>
                <div style={st.catLabel}>{cat}</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:6}}>
                  {list.map(s=><button key={s} style={{...st.pill(supplements.includes(s)),fontSize:12,padding:"7px 12px"}} onClick={()=>toggleSupplement(s)}>{supplements.includes(s)?"✓ ":""}{s}</button>)}
                </div>
              </div>
            ))}
          </div>
        )}
        <input style={st.cinput} placeholder="+ Add something not listed" value={customInput}
          onChange={e=>setCustomInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addCustomSupplement()} />
        {customInput.trim()&&<button style={{...st.pill(false),marginBottom:16}} onClick={addCustomSupplement}>Add "{customInput.trim()}"</button>}
        {supplements.length>0&&<div style={{marginBottom:16,color:"rgba(255,255,255,0.55)",fontSize:12,lineHeight:1.5}}><strong style={{color:"#F5D67A"}}>{supplements.length} selected:</strong> {supplements.join(", ")}</div>}
        <button style={st.btn} onClick={runAssessment}>{supplements.length===0 ? "See what I should take →" : "Analyse my stack →"}</button>
      </div></div>
    );
  }

  // Loading screen — rotating messages
  if (step==="loading") return (
    <div style={st.overlay}><div style={{...st.modal,textAlign:"center",padding:"48px 32px"}}>
      <div style={{width:48,height:48,border:"3px solid rgba(229,184,63,0.2)",borderTopColor:"#F5C84A",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 24px"}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{color:"#fff",fontSize:17,fontWeight:600,marginBottom:8}}>Analysing your stack…</p>
      <p style={{color:"rgba(255,255,255,0.5)",fontSize:14,minHeight:20}}>{LOADING_MSGS[loadingMsgIdx]}</p>
    </div></div>
  );

  // Result screen
  const breakdown = (!aiError && supplements.length > 0)
    ? computeStackScoreDetailed(mappedSupps, primaryGoal, "").breakdown
    : [];
  const archetype = computeArchetype(primaryGoal, supplements.length);
  const peer = PEER_DATA[primaryGoal];
  const percentileData = computePeerPercentile(mappedSupps, primaryGoal);

  return (
    <div style={st.overlay}><div style={st.modal}>
      {aiError ? (
        <>
          <p style={st.heading}>Something went wrong</p>
          <p style={{color:"rgba(255,255,255,0.65)",fontSize:14,marginBottom:24}}>Sign up free and run your full assessment from inside the app — it only takes 30 seconds and uses your saved stack.</p>
        </>
      ) : assessment && (
        <>
          {/* Stack Score */}
          <div style={{textAlign:"center",marginBottom:16}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:4}}>Your Stack Score</div>
            <div style={{fontSize:56,fontWeight:800,color:"#F5C84A",lineHeight:1}}>{displayScore}<span style={{fontSize:24,color:"rgba(255,255,255,0.4)"}}>/100</span></div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:4}}>Average new user score: 58/100</div>
          </div>

          {/* Score breakdown */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:16}}>
            {breakdown.map(d => (
              <div key={d.label} style={{background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"8px 10px"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.45)"}}>{d.label}</span>
                  <span style={{fontSize:10,fontWeight:700,color:d.score>=7?"#F5C84A":d.score>=5?"#FBBF24":"#EF4444"}}>{d.score}/{d.max}</span>
                </div>
                <div style={{height:3,background:"rgba(255,255,255,0.08)",borderRadius:2,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${d.score/d.max*100}%`,background:d.score>=7?"#F5C84A":d.score>=5?"#FBBF24":"#EF4444",borderRadius:2}}/>
                </div>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.28)",marginTop:3}}>{d.hint}</div>
              </div>
            ))}
          </div>

          {/* Archetype */}
          <div style={{background:"rgba(229,184,63,0.08)",border:"1px solid rgba(229,184,63,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:16,textAlign:"center"}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:2}}>Your Stack Profile</div>
            <div style={{fontSize:16,fontWeight:700,color:"#F5C84A"}}>{archetype.name}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",marginTop:2,marginBottom:6}}>{archetype.desc}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{archetype.pct}% of Your Peak Stack users are {archetype.name} focused</div>
          </div>

          {/* Peer comparison */}
          {peer && percentileData && (
            <div style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"12px 14px",marginBottom:16}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>
                Among {peer.userCount.toLocaleString()} people tracking "{primaryGoal}"
              </div>
              {peer.topSupps.slice(0,3).map((s,i) => {
                const hasIt = mappedSupps.includes(s);
                return (
                  <div key={s} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span style={{fontSize:11,color:hasIt?"#F5C84A":"rgba(255,255,255,0.25)",width:14,flexShrink:0}}>{hasIt?"✓":"○"}</span>
                    <span style={{fontSize:12,color:hasIt?"rgba(255,255,255,0.8)":"rgba(255,255,255,0.4)",flex:1}}>{s}</span>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{peer.pct[i]}% take this</span>
                  </div>
                );
              })}
              <div style={{marginTop:10,paddingTop:8,borderTop:"1px solid rgba(255,255,255,0.06)",fontSize:11,color:"#F5C84A",fontWeight:700}}>
                Your stack is in the top {100-percentileData.percentile}% for {primaryGoal.toLowerCase()}
              </div>
            </div>
          )}

          {/* AI headline */}
          {assessment.headline && (
            <p style={{fontSize:15,color:"#fff",fontStyle:"italic",lineHeight:1.5,marginBottom:16,borderLeft:"3px solid #F5C84A",paddingLeft:12}}>
              "{assessment.headline}"
            </p>
          )}

          {/* Cards */}
          <div style={st.aCard("#F5C84A")}><p style={st.aLabel("#F5C84A")}>✅ What's genuinely working</p><p style={st.aText}>{assessment.working}</p></div>
          <div style={st.aCard("#FBBF24")}><p style={st.aLabel("#FBBF24")}>⚠️ The gap holding you back</p><p style={st.aText}>{assessment.gap}</p></div>
          <div style={st.aCard("#A78BFA")}><p style={st.aLabel("#A78BFA")}>💡 One move — do this today</p><p style={st.aText}>{assessment.action}</p></div>

          <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",lineHeight:1.6,padding:"10px 14px",background:"rgba(255,255,255,0.03)",borderRadius:8,marginBottom:12,marginTop:4}}>
            ⚖️ {DISCLAIMER_AI}
          </div>

          {/* Loss-aversion block */}
          <div style={{border:"1px solid rgba(229,184,63,0.25)",borderRadius:10,padding:"12px 14px",marginBottom:16,marginTop:4}}>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:8}}>📍 When you save this:</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",lineHeight:1.7}}>
              • Your assessment is stored to your account<br/>
              • Your stack is pre-loaded — no setup needed<br/>
              • In 7 days: your first AI update on whether this is working
            </div>
            <div style={{fontSize:11,color:"#FBBF24",marginTop:8,fontWeight:600}}>This result disappears if you close this window.</div>
          </div>
        </>
      )}
      {assessment && (
        <button
          style={{width:"100%",background:"rgba(229,184,63,0.12)",border:"1px solid rgba(229,184,63,0.35)",borderRadius:10,color:"#F5C84A",fontWeight:600,fontSize:14,padding:"12px 0",cursor:"pointer",fontFamily:"inherit",marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}
          onClick={() => setShowShareCard(true)}
        >📤 Share Your Score</button>
      )}
      <button style={st.btn} onClick={()=>{
        const advice = buildStackAdvice(mappedSupps, primaryGoal, assessment);
        onSignUp({ supplements: mappedSupps, goal: primaryGoal, goals, age: ageRange, sex, issue: "", assessment: assessment ? {...assessment, age: ageRange, sex, goals} : assessment, advice });
      }}>
        Save This + Start Tracking Free →
      </button>
      <p style={{color:"rgba(255,255,255,0.35)",fontSize:11,textAlign:"center",marginTop:8}}>Free forever · No credit card · 20 seconds</p>
      {showShareCard && (
        <OnboardingShareCard
          score={displayScore}
          archetype={archetype}
          topGap={assessment?.gap}
          onClose={() => setShowShareCard(false)}
        />
      )}
    </div></div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// OnboardingShareCard — pre-signup share card shown on onboarding result screen
// Uses html2canvas rules: inline SVG logo, system fonts, useCORS:true
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ── Native sharing helpers (shared by both share cards) ───────────────────────
// Renders a card node to a PNG File so it can be shared as an image.
async function captureCardFile(node, fileName) {
  if (!node) return null;
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#0F2640", useCORS: true, allowTaint: false, logging: false });
    const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
    return blob ? new File([blob], fileName, { type: "image/png" }) : null;
  } catch (e) { console.warn("[share] capture failed", e); return null; }
}

// Opens the OS share sheet (Instagram, Facebook, Pinterest, email, Messages, …).
// Prefers sharing the image card; falls back to text+link, then to download+copy
// on desktop browsers without the Web Share API. Returns shared|cancelled|fallback.
async function shareImageOrFallback({ file, text, url }) {
  try {
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text });
      return "shared";
    }
  } catch (e) { if (e && e.name === "AbortError") return "cancelled"; }
  try {
    if (navigator.share) {
      await navigator.share({ title: "Your Peak Stack", text, url });
      return "shared";
    }
  } catch (e) { if (e && e.name === "AbortError") return "cancelled"; }
  try {
    if (file) {
      const u = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = u; a.download = file.name; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 4000);
    }
    if (navigator.clipboard) await navigator.clipboard.writeText(text).catch(() => {});
  } catch (e) { console.warn("[share] fallback failed", e); }
  return "fallback";
}

function OnboardingShareCard({ score, archetype, topGap, onClose }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareFile, setShareFile] = useState(null);
  const [shareNote, setShareNote] = useState("");
  const APP_URL = "https://stack.peakhealthstack.com";

  const caption = `My Stack Score is ${score}/100 and my profile is ${archetype.name}.\nPowered by Your Peak Stack.\nGet your free assessment at ${APP_URL}\n#supplements #health #yourpeakstack #peakhealthstack`;

  const handleDownload = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: "#0F2640",
        useCORS: true,
        allowTaint: false,
        logging: false,
      });
      const a = document.createElement("a");
      a.download = `my-peak-stack-score-${score}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    } catch (err) {
      console.error("[OnboardingShareCard]", err);
      alert("Download failed — please screenshot instead.");
    } finally {
      setDownloading(false);
    }
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(APP_URL); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = APP_URL;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Pre-render the card to an image on open so the Share button can fire instantly
  // within the user gesture (required by iOS Safari's Web Share API).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await captureCardFile(cardRef.current, `my-peak-stack-score-${score}.png`);
      if (!cancelled) setShareFile(f);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true); setShareNote("");
    const file = shareFile || await captureCardFile(cardRef.current, `my-peak-stack-score-${score}.png`);
    const result = await shareImageOrFallback({ file, text: caption, url: APP_URL });
    if (result === "fallback") {
      setShareNote("No share menu here — image saved & caption copied. Paste into any app.");
      setTimeout(() => setShareNote(""), 5000);
    }
    setSharing(false);
  };

  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1100,padding:"40px 16px 16px",overflowY:"auto"}}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{background:"#132D4A",borderRadius:16,width:"100%",maxWidth:480,position:"relative"}}>
        <div style={{position:"absolute",top:12,right:12,zIndex:2}}>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.1)",border:"none",color:"rgba(255,255,255,0.7)",width:30,height:30,borderRadius:"50%",cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>✕</button>
        </div>
        <div style={{padding:"28px 24px 24px"}}>
          <p style={{color:"#fff",fontSize:17,fontWeight:700,marginBottom:4}}>Share Your Score</p>
          <p style={{color:"rgba(255,255,255,0.45)",fontSize:12,marginBottom:20}}>Let your followers discover their own stack score — free.</p>

          {/* Card captured by html2canvas */}
          <div ref={cardRef} style={{
            background:"linear-gradient(145deg, #0F2640 0%, #1B3A5C 60%, #0F2640 100%)",
            borderRadius:16,padding:"28px 24px 24px",marginBottom:20,
            overflow:"hidden",position:"relative",
            fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
          }}>
            {/* Top accent */}
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg, #F5C84A, #1B3A5C, #F5C84A)"}}/>

            {/* Brand lockup */}
            <div style={{display:"flex",justifyContent:"center",marginBottom:22}}>
              <ShareBrand size={46} />
            </div>

            {/* Big score */}
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"1.5px",marginBottom:6}}>Stack Score</div>
              <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",gap:2}}>
                <span style={{fontSize:72,fontWeight:900,color:"#F5C84A",lineHeight:1}}>{score}</span>
                <span style={{fontSize:24,color:"rgba(255,255,255,0.3)",fontWeight:400}}>/100</span>
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:4}}>Average new user: 58/100</div>
            </div>

            {/* Archetype */}
            <div style={{background:"rgba(229,184,63,0.1)",border:"1px solid rgba(229,184,63,0.3)",borderRadius:10,padding:"12px 16px",textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:4}}>Stack Profile</div>
              <div style={{fontSize:16,fontWeight:700,color:"#F5C84A"}}>{archetype.name}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:3}}>{archetype.desc}</div>
            </div>

            {/* Top gap teaser */}
            {topGap && (
              <div style={{background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:8,padding:"10px 14px",marginBottom:4}}>
                <div style={{fontSize:9,color:"#FBBF24",fontWeight:700,textTransform:"uppercase",letterSpacing:"1px",marginBottom:5}}>⚠️ Top gap holding me back</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{topGap}</div>
              </div>
            )}

            {/* CTA line */}
            <div style={{marginTop:16,textAlign:"center"}}>
              <div style={{fontSize:10,color:"#F5C84A",fontWeight:700}}>Get your free stack score → stack.peakhealthstack.com</div>
            </div>

            {/* Bottom accent */}
            <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:"linear-gradient(90deg, transparent, #F5C84A, transparent)"}}/>
          </div>

          {/* Share (primary) + secondary actions */}
          <button
            onClick={handleShare}
            disabled={sharing}
            style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:"linear-gradient(135deg,#F7DC8A 0%,#E5B83F 55%,#D2A22F 100%)",color:"#10243c",border:"none",borderRadius:12,fontWeight:800,fontSize:15,padding:"14px 0",cursor:sharing?"default":"pointer",fontFamily:"inherit",marginBottom:10,boxShadow:"0 8px 22px rgba(229,184,63,0.3), inset 0 1px 0 rgba(255,255,255,0.4)"}}
          >{sharing ? "Preparing…" : "📤 Share my score"}</button>
          <div style={{display:"flex",gap:10,marginBottom:shareNote?8:14}}>
            <button
              onClick={handleDownload}
              disabled={downloading}
              style={{flex:1,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(229,184,63,0.3)",borderRadius:10,color:"#F5D67A",fontWeight:600,fontSize:13,padding:"11px 0",cursor:downloading?"default":"pointer",fontFamily:"inherit"}}
            >{downloading ? "Saving…" : "↓ Download"}</button>
            <button
              onClick={handleCopy}
              style={{flex:1,background:copied?"rgba(229,184,63,0.15)":"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:10,color:"#fff",fontWeight:600,fontSize:13,padding:"11px 0",cursor:"pointer",fontFamily:"inherit"}}
            >{copied ? "✓ Copied!" : "🔗 Copy Link"}</button>
          </div>
          {shareNote && <div style={{fontSize:11,color:"#F5D67A",textAlign:"center",marginBottom:12,lineHeight:1.5}}>{shareNote}</div>}

          {/* Suggested caption */}
          <div style={{background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"10px 12px"}}>
            <p style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginBottom:5,textTransform:"uppercase",letterSpacing:"1px"}}>Suggested caption:</p>
            <p
              style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.6,margin:0,userSelect:"all",cursor:"text",whiteSpace:"pre-wrap"}}
              onClick={e => {
                const range = document.createRange();
                range.selectNodeContents(e.currentTarget);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
              }}
            >{caption}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ShareStackModal — download stack as branded image card
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ShareStackModal({ stack, goal, logs, onClose }) {
  const cardRef = useRef(null);
  const [copied, setCopied]         = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing]       = useState(false);
  const [shareFile, setShareFile]   = useState(null);
  const [shareNote, setShareNote]   = useState("");
  const APP_URL = "https://stack.peakhealthstack.com";
  const MAX_VISIBLE = 8;
  const visibleStack = stack.slice(0, MAX_VISIBLE);
  const overflow     = stack.length - MAX_VISIBLE;
  const archetype    = stack.length > 0 ? computeProfileFromStack(stack) : computeArchetype(goal, stack.length);
  const shareScore   = computeLiveScore(stack, logs ?? []);
  const captionText  = `My Stack Score is ${shareScore}/100 and my profile is ${archetype.name}.\nPowered by Your Peak Stack.\nGet your free assessment at ${APP_URL}\n#supplements #health #yourpeakstack #peakhealthstack`;

  const handleDownload = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {scale:2,backgroundColor:"#0F2640",useCORS:true,allowTaint:false,logging:false});
      const link = document.createElement("a");
      link.download = "my-stack-peakhealthstack.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch(err) {
      console.error("[ShareStackModal]", err);
      alert("Download failed — please screenshot the card instead.");
    } finally { setDownloading(false); }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
    } catch {
      const el = document.createElement("textarea");
      el.value = APP_URL;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(()=>setCopied(false), 2000);
  };

  // Pre-render the card to an image so Share fires instantly inside the gesture (iOS Safari).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await captureCardFile(cardRef.current, "my-stack-peakhealthstack.png");
      if (!cancelled) setShareFile(f);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true); setShareNote("");
    const file = shareFile || await captureCardFile(cardRef.current, "my-stack-peakhealthstack.png");
    const result = await shareImageOrFallback({ file, text: captionText, url: APP_URL });
    if (result === "fallback") {
      setShareNote("No share menu here — image saved & caption copied. Paste into any app.");
      setTimeout(() => setShareNote(""), 5000);
    }
    setSharing(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,paddingTop:40,paddingLeft:16,paddingRight:16,paddingBottom:16,overflowY:"auto"}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:"#132D4A",borderRadius:16,width:"100%",maxWidth:520,maxHeight:"90vh",overflowY:"auto",position:"relative"}}>
        <div style={{position:"sticky",top:0,background:"#132D4A",padding:"14px 14px 0",display:"flex",justifyContent:"flex-end",zIndex:2}}>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.1)",border:"none",color:"rgba(255,255,255,0.7)",width:30,height:30,borderRadius:"50%",cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>✕</button>
        </div>
        <div style={{padding:"4px 28px 28px"}}>
        <p style={{color:"#fff",fontSize:18,fontWeight:700,marginBottom:6}}>Share Your Stack</p>
        <p style={{color:"rgba(255,255,255,0.5)",fontSize:13,marginBottom:20}}>Download your stack card or copy the link to share.</p>
        {/* Card captured by html2canvas */}
        <div ref={cardRef} style={{background:"linear-gradient(145deg, #0F2640 0%, #1B3A5C 50%, #0F2640 100%)",borderRadius:12,padding:"24px 28px",marginBottom:20,overflow:"hidden",position:"relative"}}>
          <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg, #F5C84A, #1B3A5C, #F5C84A)",borderRadius:"12px 12px 0 0"}}/>
          <div style={{display:"flex",justifyContent:"center",marginBottom:18}}>
            <ShareBrand size={44} />
          </div>
          <p style={{color:"#fff",fontSize:18,fontWeight:800,marginBottom:14,textAlign:"center"}}>My Supplement Stack</p>
          <div style={{
            background:"linear-gradient(135deg,rgba(229,184,63,0.2),rgba(229,184,63,0.08))",
            border:"1px solid rgba(229,184,63,0.35)",
            borderRadius:8,
            padding:"6px 12px",
            marginBottom:12,
            display:"inline-flex",
            alignItems:"center",
            gap:6,
          }}>
            <span style={{fontSize:9,color:"rgba(255,255,255,0.5)",textTransform:"uppercase",letterSpacing:"1px"}}>Stack Profile</span>
            <span style={{width:1,height:10,background:"rgba(255,255,255,0.2)"}}/>
            <span style={{fontSize:11,color:"#F5C84A",fontWeight:700}}>{archetype.name}</span>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:goal?14:0}}>
            {visibleStack.map(s=><span key={s} style={{background:"rgba(229,184,63,0.15)",border:"1px solid rgba(229,184,63,0.35)",color:"#F5C84A",borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:600}}>{s}</span>)}
            {overflow>0&&<span style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.5)",borderRadius:20,padding:"5px 12px",fontSize:11}}>+{overflow} more</span>}
          </div>
          {goal&&<div><span style={{display:"inline-block",background:"rgba(167,139,250,0.15)",border:"1px solid rgba(167,139,250,0.3)",color:"#A78BFA",borderRadius:6,padding:"5px 12px",fontSize:11,fontWeight:600,marginBottom:10}}>Goal: {goal}</span></div>}
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:14}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Stack Score</div>
            <div style={{fontSize:32,fontWeight:800,color:"#F5C84A",lineHeight:1}}>{shareScore}<span style={{fontSize:13,color:"rgba(255,255,255,0.35)"}}>/100</span></div>
          </div>
          <div style={{borderTop:"1px solid rgba(255,255,255,0.08)",paddingTop:14,marginTop:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{color:"rgba(255,255,255,0.4)",fontSize:11}}>Rated by Your Peak Stack · peakhealthstack.com</span>
            <span style={{color:"#F5C84A",fontSize:11,fontWeight:700}}>Free assessment →</span>
          </div>
          <div style={{fontSize:8,color:"rgba(255,255,255,0.3)",textAlign:"center",marginTop:8,lineHeight:1.4}}>
            {DISCLAIMER_SHARE_CARD}
          </div>
          <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:"linear-gradient(90deg, transparent, #F5C84A, transparent)"}}/>
        </div>
        <button onClick={handleShare} disabled={sharing}
          style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"14px 0",borderRadius:10,background:"linear-gradient(135deg,#F7DC8A 0%,#E5B83F 55%,#D2A22F 100%)",color:"#10243c",fontSize:15,fontWeight:800,border:"none",cursor:sharing?"default":"pointer",fontFamily:"inherit",marginBottom:10,boxShadow:"0 8px 22px rgba(229,184,63,0.3), inset 0 1px 0 rgba(255,255,255,0.4)"}}>
          {sharing?"Preparing…":"📤 Share my stack"}
        </button>
        <div style={{display:"flex",gap:10,marginBottom:shareNote?8:16}}>
          <button style={{flex:1,padding:"11px 0",borderRadius:8,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(229,184,63,0.3)",color:"#F5D67A",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}} onClick={handleDownload} disabled={downloading}>
            {downloading?"Generating…":"⬇ Download"}
          </button>
          <button style={{flex:1,padding:"11px 0",borderRadius:8,background:copied?"rgba(229,184,63,0.15)":"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}} onClick={handleCopyLink}>
            {copied?"✓ Copied!":"🔗 Copy Link"}
          </button>
        </div>
        {shareNote && <div style={{fontSize:11,color:"#F5D67A",textAlign:"center",marginBottom:12,lineHeight:1.5}}>{shareNote}</div>}
        <p style={{color:"rgba(255,255,255,0.35)",fontSize:11,marginBottom:6}}>Suggested caption (tap to select all):</p>
        <div style={{background:"rgba(255,255,255,0.05)",borderRadius:8,padding:12,color:"rgba(255,255,255,0.6)",fontSize:12,lineHeight:1.6,whiteSpace:"pre-wrap",cursor:"text",userSelect:"all",border:"1px solid rgba(255,255,255,0.1)"}}>{captionText}</div>
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BUTTON COMPONENTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BtnPrimary = ({ children, onClick, disabled, style, ...props }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: disabled ? "rgba(255,255,255,0.08)" : GRADIENTS.primary,
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      fontSize: "14px",
      fontWeight: "600",
      color: disabled ? "rgba(255,255,255,0.4)" : "#0f2540",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
      boxShadow: disabled ? "none" : SHADOWS.glow,
      minHeight: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontFamily: "inherit",
      ...style,
    }}
    onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = SHADOWS.glowLg; } }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = disabled ? "none" : SHADOWS.glow; }}
    {...props}
  >
    {children}
  </button>
);

const BtnSecondary = ({ children, onClick, style, ...props }) => (
  <button
    onClick={onClick}
    style={{
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: "10px",
      padding: "12px 20px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#fff",
      cursor: "pointer",
      transition: "all 0.2s ease",
      minHeight: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontFamily: "inherit",
      ...style,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
    {...props}
  >
    {children}
  </button>
);

const BtnUpsell = ({ children, onClick, style, ...props }) => (
  <button
    onClick={onClick}
    style={{
      background: GRADIENTS.upsell,
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#fff",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
      minHeight: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontFamily: "inherit",
      ...style,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(245,158,11,0.4)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(245,158,11,0.3)"; }}
    {...props}
  >
    {children}
  </button>
);

const BtnTertiary = ({ children, onClick, style, ...props }) => (
  <button
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      color: "rgba(255,255,255,0.5)",
      fontSize: "13px",
      cursor: "pointer",
      transition: "color 0.2s ease",
      fontFamily: "inherit",
      ...style,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.75)")}
    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
    {...props}
  >
    {children}
  </button>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ANIMATED STACK SCORE HERO
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function StackScoreHero({ stackSize, score, maxScore, recommendation }) {
  const percentage = Math.min((score / maxScore) * 100, 100);
  const [displayScore, setDisplayScore] = useState(score);

  useEffect(() => {
    const start = displayScore;
    const target = score;
    if (start === target) return;
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
      setDisplayScore(Math.round(start + (target - start) * easeProgress));
      if (progress < 1) requestAnimationFrame(animate);
    };
    animate();
  }, [score]); // eslint-disable-line react-hooks/exhaustive-deps

  let zoneColor, zoneMessage;
  if (percentage >= 70) { zoneColor = "#10B981"; zoneMessage = "Strong Foundation"; }
  else if (percentage >= 50) { zoneColor = "#F59E0B"; zoneMessage = "Solid Foundation"; }
  else { zoneColor = "#3B82F6"; zoneMessage = "Good Start"; }

  const circumference = 2 * Math.PI * 110;
  const dashArray = `${(percentage / 100) * circumference} ${circumference}`;

  return (
    <div style={{
      background: GRADIENTS.secondary,
      border: "1px solid rgba(229,184,63,0.3)",
      borderRadius: "16px",
      padding: "32px 24px",
      textAlign: "center",
      boxShadow: SHADOWS.hero,
      marginBottom: SPACING.lg,
      animation: "slideInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
    }}>
      <div style={{ fontSize: "13px", fontWeight: "600", color: "rgba(229,184,63,0.8)", textTransform: "uppercase", letterSpacing: "1.2px", marginBottom: SPACING.sm }}>
        Your Supplement Stack
      </div>

      <div style={{ position: "relative", width: "200px", height: "200px", margin: "16px auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="200" height="200" viewBox="0 0 280 280" style={{ position: "absolute", filter: "drop-shadow(0 8px 20px rgba(229,184,63,0.15))" }}>
          <defs>
            <linearGradient id="phsGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: "#E5B83F", stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: "#C2912B", stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <circle cx="140" cy="140" r="110" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />
          <circle cx="140" cy="140" r="110" fill="none" stroke="url(#phsGrad1)" strokeWidth="10"
            strokeDasharray={dashArray} strokeLinecap="round"
            style={{ transformOrigin: "140px 140px", transform: "rotate(-90deg)", transition: "stroke-dasharray 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          />
          <circle cx="140" cy="140" r="35" fill="rgba(229,184,63,0.08)" />
        </svg>
        <div style={{ position: "relative", zIndex: 10, textAlign: "center" }}>
          <div style={{ fontSize: "44px", fontWeight: "900", background: GRADIENTS.primary, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1 }}>
            {displayScore}
          </div>
          <div style={{ fontSize: "11px", color: "rgba(229,184,63,0.8)", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", marginTop: "4px" }}>
            / {maxScore}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: SPACING.md }}>
        <div style={{ display: "inline-block", padding: "6px 14px", background: `${zoneColor}20`, color: zoneColor, borderRadius: "20px", fontSize: "12px", fontWeight: "700" }}>
          {zoneMessage} Zone
        </div>
      </div>

      {recommendation && (
        <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", lineHeight: "1.6", marginBottom: SPACING.md }}>
          {recommendation}
        </div>
      )}

    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// App.jsx — root component, wires everything together
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function App() {
  const { user, setUser, signOut, showAuth, setShowAuth, showPasswordReset, isSigningIn, isAuthLoading, pendingConfirmEmail, setPendingConfirmEmail, confirmCheckLoading, confirmCheckError, checkEmailConfirmed } = useUser();
  const { stack, setStack, logs, toggleStack, customProtocols, saveCustomProtocol, activeProtocol, toast, setToast, saveStack, onboardingAppliedRef } = useApp();
  const [tab, setTab] = useState(() => {
    const savedUser = storage.get("phs_user", null);
    let savedPage = storage.get("phs_current_page", null);
    if (savedPage === "insights") {
      savedPage = "trends";
      storage.set("phs_current_page", "trends");
    }
    const VALID_TABS = ["stack", "log", "trends", "protocols", "ai", "shop"];
    const urlTab = new URLSearchParams(window.location.search).get("tab");
    if (urlTab && VALID_TABS.includes(urlTab)) return urlTab;
    if (savedUser?.isLoggedIn && savedPage && VALID_TABS.includes(savedPage)) return savedPage;
    return "stack";
  });
  const [drawerSupp, setDrawerSupp]         = useState(null);
  const [showBuilder, setShowBuilder]       = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [showPremium, setShowPremium]       = useState(false);
  const [showAbout, setShowAbout]           = useState(false);
  const [showAccount, setShowAccount]       = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showAuditBanner, setShowAuditBanner] = useState(false);
  const [day1Action, setDay1Action]           = useState(null);
  const [showDay7Unlock, setShowDay7Unlock]   = useState(false);
  const [showDay4Prompt, setShowDay4Prompt]   = useState(false);
  const [weeklyMicroInsight, setWeeklyMicroInsight] = useState(null);
  // Onboarding / landing page state
  const [showOnboarding, setShowOnboarding]           = useState(false);
  // Persisted in localStorage so it survives page reload during email confirmation
  const [pendingOnboardingData, setPendingOnboardingData] = useState(() => {
    const raw = storage.get('phs_pending_onboarding', null);
    return raw ? JSON.parse(raw) : null;
  });
  // If returning from Stripe checkout while logged out, auto-open sign-in
  const [showAuthMode, setShowAuthMode] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('upgraded') === 'true' ? 'signin' : null;
  });
  const contentRef                          = useRef(null);
  // Header collapses to a burger menu on narrow screens (About/Account/Sign out)
  const [isHeaderMobile, setIsHeaderMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsHeaderMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Must be declared here (not after conditional returns) — hooks must be called
  // unconditionally on every render. Moving it below the isAuthLoading/isSigningIn
  // early-returns caused React error #310 (hooks order mismatch between renders).
  const openBuilder = useCallback((proto) => { setEditingProtocol(proto); setShowBuilder(true); }, []);

  // Scroll to top whenever the tab changes
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [tab]);

  // Show success toast when plan upgrade confirmed after Stripe return
  useEffect(() => {
    const handler = () => setToast(`🎉 You're now on Premium! Enjoy ${AI_LIMITS.premium.total} AI insights per month.`);
    window.addEventListener("phs:plan-upgraded", handler);
    return () => window.removeEventListener("phs:plan-upgraded", handler);
  }, []);

  // Show toast when user cancels Stripe checkout and returns to the app
  useEffect(() => {
    const handler = () => setToast("No problem — your free account is still active.");
    window.addEventListener("phs:checkout-cancelled", handler);
    return () => window.removeEventListener("phs:checkout-cancelled", handler);
  }, []);

  // Persist current page to localStorage whenever it changes
  useEffect(() => {
    if (user?.isLoggedIn) storage.set("phs_current_page", tab);
  }, [tab, user?.isLoggedIn]);

  // Existing users land on Check-In immediately after a fresh sign-in.
  // (New users are routed to Stack by the post-onboarding effect below.)
  useEffect(() => {
    const handler = () => setTab("log");
    window.addEventListener("phs:signed-in", handler);
    return () => window.removeEventListener("phs:signed-in", handler);
  }, []);

  // Handle ?unsubscribe=1&uid=X links from weekly report emails
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("unsubscribe") !== "1") return;
    const uid = params.get("uid");
    if (!uid || !user?.isLoggedIn || user.id !== uid) return;
    window.history.replaceState({}, "", "/");
    fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${uid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON,
        "Authorization": getAuthHeader(),
        "Prefer": "return=minimal",
      },
      body: JSON.stringify({ weekly_report_enabled: false }),
    }).catch(e => console.error("[Unsubscribe] PATCH failed:", e));
    setToast("You've been unsubscribed from weekly reports.");
  }, [user?.id]);

  // Post-auth: save stack + goal collected during onboarding
  useEffect(() => {
    if (!user?.isLoggedIn || !user?.id || !pendingOnboardingData) return;
    const { supplements, goal, issue, assessment, advice } = pendingOnboardingData;
    if (supplements && supplements.length > 0) {
      const normalised = supplements.map(s => ONBOARDING_NAME_MAP[s] ?? s);
      // Mark before setStack so AppProvider's async load won't overwrite with STARTER_STACK
      onboardingAppliedRef.current = true;
      setStack(normalised);
      api.saveStack(user.id, normalised, {}, [], null).catch(console.error);
    }
    if (goal && user.id) {
      fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON,
          "Authorization": getAuthHeader(),
          "Prefer": "return=minimal",
        },
        body: JSON.stringify({
          onboarding_complete: true,
          goal,
          issue: issue ?? null,
          onboarding_assessment: assessment ? JSON.stringify(assessment) : null,
        }),
      }).catch(console.error);
    }
    if (assessment) {
      setUser(prev => ({ ...prev, onboarding_assessment: assessment }));
    }
    // Persist the personalised "what to add / review" note to the Stack page.
    // Keyed by uid + kept in localStorage so it survives logout/login until dismissed.
    if (advice && (advice.action || advice.add?.length || advice.review?.length)) {
      try { storage.set('phs_stack_advice', { uid: user.id, ...advice, dismissed: false }); } catch {}
    }
    setPendingOnboardingData(null);
    storage.remove('phs_pending_onboarding');
    setTab("stack");
    setShowAuditBanner(true);
    if (assessment?.action) setDay1Action(assessment.action);
    setToast("Welcome! Your stack is saved.");
  }, [user?.isLoggedIn, user?.id]);

  // Day 7 unlock overlay — triggers when logs.length reaches 7 for the first time
  useEffect(() => {
    if (!user?.isLoggedIn) return;
    if (logs.length >= 7 && !storage.get("phs_day7_shown", false)) {
      setShowDay7Unlock(true);
      storage.set("phs_day7_shown", true);
    }
  }, [logs.length, user?.isLoggedIn]);

  // Day 4 / 3rd-log pattern prompt — inline card shown on Check-In tab
  useEffect(() => {
    if (!user?.isLoggedIn) return;
    if (logs.length >= 3 && !storage.get("phs_day4_shown", false)) {
      setShowDay4Prompt(true);
      storage.set("phs_day4_shown", true);
    }
  }, [logs.length, user?.isLoggedIn]);

  // Weekly micro-insight — merged useEffect (records last-seen THEN checks if due)
  useEffect(() => {
    if (!user?.isLoggedIn) return;
    const now = new Date().toISOString();
    const lastSeen = storage.get("phs_last_seen", null);
    storage.set("phs_last_seen", now);
    if (logs.length < 3) return;
    const lastInsightDate = storage.get("phs_last_weekly_insight", null);
    const daysSince = lastSeen
      ? (Date.now() - new Date(lastSeen).getTime()) / (1000 * 60 * 60 * 24)
      : 999;
    const daysSinceInsight = lastInsightDate
      ? (Date.now() - new Date(lastInsightDate).getTime()) / (1000 * 60 * 60 * 24)
      : 999;
    if (daysSince < 1 || daysSinceInsight < 6) return;
    const insight = computeMicroInsight(logs);
    if (!insight) return;
    const last7 = logs.slice(0, 7);
    const energyAvg = (last7.reduce((a,l) => { const v = l.effects?.Energy; return v ? a + Number(v) : a; }, 0) / Math.max(last7.filter(l => l.effects?.Energy).length, 1)).toFixed(1);
    const adherence = last7.length > 0 ? Math.round(last7.filter(l => l.taken?.length > 0).length / last7.length * 100) : 0;
    setWeeklyMicroInsight({ insight, energyAvg, adherence, logCount: logs.length });
    storage.set("phs_last_weekly_insight", now);
  }, [user?.isLoggedIn, logs.length]);

  // Remind-me premium check — show modal on remind date, clear if already premium
  useEffect(() => {
    if (!user?.isLoggedIn) return;
    const remindDate = storage.get("phs_remind_premium", null);
    if (!remindDate) return;
    if (user.plan === "premium" || user.plan === "annual") { storage.remove("phs_remind_premium"); return; }
    const today = new Date().toISOString().slice(0,10);
    if (today >= remindDate) { storage.remove("phs_remind_premium"); setShowPremium(true); }
  }, [user?.isLoggedIn, user?.plan]);

  // ── Loading screens ───────────────────────────────────────────────────────
  if (isSigningIn) {
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:C.navyDeep,color:C.white,flexDirection:"column",gap:12}}>
        <Loader />
        <div style={{fontSize:14,color:"rgba(255,255,255,0.6)"}}>Logging in...</div>
      </div>
    );
  }
  if (isAuthLoading) {
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:C.navyDeep,color:C.white,flexDirection:"column",gap:12}}>
        <Loader />
        <div style={{fontSize:14,color:"rgba(255,255,255,0.6)"}}>Setting up your stack...</div>
      </div>
    );
  }

  // ── Unauthenticated routing ────────────────────────────────────────────────

  // Password reset screen — shown for both logged-in and logged-out users
  if (showPasswordReset) {
    return <PasswordResetModal />;
  }

  // Email confirmation screen — must be first unauthenticated check
  if (!user?.isLoggedIn && pendingConfirmEmail) {
    return (
      <EmailConfirmationScreen
        email={pendingConfirmEmail}
        onCheckConfirmed={() => { setShowAuthMode(null); checkEmailConfirmed(); }}
        onBackToSignUp={() => { setPendingConfirmEmail(null); }}
        checkLoading={confirmCheckLoading}
        checkError={confirmCheckError}
      />
    );
  }

  if (!user?.isLoggedIn && !showOnboarding && showAuthMode === null && !showAuth) {
    return (
      <>
        <LandingPage
          onStartOnboarding={() => setShowOnboarding(true)}
          onSignIn={() => setShowAuthMode("signin")}
        />
      </>
    );
  }

  if (!user?.isLoggedIn && showOnboarding) {
    return (
      <>
        <LandingPage
          onStartOnboarding={() => {}}
          onSignIn={() => { setShowOnboarding(false); setShowAuthMode("signin"); }}
        />
        <OnboardingModal
          onComplete={() => setShowOnboarding(false)}
          onSignUp={(data) => {
            storage.set('phs_pending_onboarding', JSON.stringify(data));
            setPendingOnboardingData(data);
            setShowOnboarding(false);
            setShowAuthMode("signup");
          }}
        />
      </>
    );
  }

  if (!user?.isLoggedIn && (showAuthMode !== null || showAuth)) {
    return (
      <AuthModal
        initialMode={showAuthMode ?? "signin"}
        onClose={() => { setShowAuthMode(null); setShowAuth(false); }}
      />
    );
  }
  // ── End unauthenticated routing ───────────────────────────────────────────

  const TABS = [
    {id:"log",          label:"✅ Check-In"},
    {id:"trends",       label:"📈 Trends"},
    {id:"ai",           label:"✨ AI Analysis" + (!canAccess("advancedInsights",user.plan)?" ⭐":"")},
    {id:"stack",        label:"🧪 Stack (" + stack.length + ")"},
    {id:"protocols",    label:"🎯 Protocols"},
    {id:"shop",         label:"🛒 Shop"},
  ];

  return (
    <div style={S.app}>
      {/* Animated mesh background orbs */}
      <div style={{ position: "fixed", top: "-500px", left: "-500px", width: "1000px", height: "1000px", background: "radial-gradient(circle, rgba(229,184,63,0.07) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0, animation: "meshOrb1 25s ease-in-out infinite" }} />
      <div style={{ position: "fixed", bottom: "-400px", right: "-400px", width: "800px", height: "800px", background: "radial-gradient(circle, rgba(27,58,92,0.5) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0, animation: "meshOrb2 30s ease-in-out infinite" }} />

      {/* Load fonts safely */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600;700&display=swap');`}</style>

      {/* Header */}
      <div style={{...S.header, flexDirection:"column", gap:0, padding:"14px 16px 10px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
          <BrandLockup variant="header" />
          <div style={{display:"flex",gap:6,alignItems:"center",position:"relative",flexShrink:0}}>
            {/* About — inline on desktop; moves into the burger on mobile */}
            {!isHeaderMobile && (
              <button onClick={() => setShowAbout(true)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.35)",fontSize:11,cursor:"pointer",fontFamily:"inherit",padding:"4px 8px",borderRadius:6,transition:"color .15s"}} onMouseOver={e=>e.target.style.color="rgba(255,255,255,0.7)"} onMouseOut={e=>e.target.style.color="rgba(255,255,255,0.35)"}>About</button>
            )}
            {user.isLoggedIn
              ? <>
                  {/* Plan badge / Upgrade — always inline, unchanged */}
                  {planNormalise(user.plan) === "premium"
                    ? <span style={{...S.badge,background:"rgba(245,158,11,0.15)",borderColor:C.amber,color:C.amber}}>{user.plan === "annual" ? "ANNUAL" : "PREMIUM"}</span>
                    : !isWithin3DaysOfSignup(user) && <button style={S.premBtn} onClick={() => setShowPremium(true)}>⭐ Upgrade<span style={{position:"absolute",inset:0,background:"linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.18) 50%,transparent 60%)",animation:"goldShimmer 3s ease infinite",pointerEvents:"none"}} /></button>
                  }
                  {!isHeaderMobile && <>
                    <button style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",padding:"4px 10px",borderRadius:8,fontSize:10,cursor:"pointer",fontFamily:"inherit"}} onClick={() => setShowAccount(true)}>Account</button>
                    <button style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",padding:"4px 10px",borderRadius:8,fontSize:10,cursor:"pointer",fontFamily:"inherit"}} onClick={signOut}>Sign out</button>
                  </>}
                </>
              : <button style={S.premBtn} onClick={() => setShowAuth(true)}>Sign in / Sign up<span style={{position:"absolute",inset:0,background:"linear-gradient(105deg,transparent 40%,rgba(255,255,255,0.18) 50%,transparent 60%)",animation:"goldShimmer 3s ease infinite",pointerEvents:"none"}} /></button>
            }
            {/* Burger (mobile only) */}
            {isHeaderMobile && (
              <button aria-label="Menu" aria-expanded={headerMenuOpen} onClick={() => setHeaderMenuOpen(o=>!o)} style={{background:headerMenuOpen?"rgba(229,184,63,0.15)":"rgba(255,255,255,0.06)",border:"1px solid "+(headerMenuOpen?"rgba(229,184,63,0.4)":"rgba(255,255,255,0.12)"),color:headerMenuOpen?"#F5D67A":"rgba(255,255,255,0.75)",width:34,height:30,borderRadius:8,cursor:"pointer",fontSize:15,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit",flexShrink:0}}>☰</button>
            )}
            {isHeaderMobile && headerMenuOpen && (
              <>
                <div onClick={() => setHeaderMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:150}} />
                <div style={{position:"absolute",top:"calc(100% + 8px)",right:0,minWidth:172,background:C.navyDeep,border:"1px solid rgba(229,184,63,0.3)",borderRadius:12,padding:6,zIndex:200,boxShadow:"0 14px 36px rgba(0,0,0,0.5)"}}>
                  {[
                    {label:"About",    action:() => setShowAbout(true),   show:true},
                    {label:"Account",  action:() => setShowAccount(true), show:user.isLoggedIn},
                    {label:"Sign out", action:signOut,                    show:user.isLoggedIn, danger:true},
                  ].filter(i=>i.show).map((i,idx)=>(
                    <button key={i.label} onClick={() => { setHeaderMenuOpen(false); i.action(); }}
                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",borderTop:idx>0?"1px solid rgba(255,255,255,0.06)":"none",color:i.danger?"#FCA5A5":"rgba(255,255,255,0.82)",fontSize:13,fontWeight:500,padding:"11px 12px",borderRadius:8,cursor:"pointer",fontFamily:"inherit"}}
                      onMouseOver={e=>e.currentTarget.style.background="rgba(229,184,63,0.1)"} onMouseOut={e=>e.currentTarget.style.background="none"}>
                      {i.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* About modal */}
      {showAbout && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={() => setShowAbout(false)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(229,184,63,0.2)",borderRadius:16,padding:28,maxWidth:340,width:"100%"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:700,fontFamily:"'DM Serif Display',serif",marginBottom:6}}>{APP_NAME}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:16}}>Built by Peak Health Stack — evidence-based guides on supplements, nutrition and healthy living.</div>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
              <a href="https://peakhealthstack.com" target="_blank" rel="noopener noreferrer" style={{color:C.teal,fontSize:13,textDecoration:"none",display:"flex",alignItems:"center",gap:7}}>
                <span>🌐</span> peakhealthstack.com
              </a>
              <a href="mailto:support@peakhealthstack.com" style={{color:C.teal,fontSize:13,textDecoration:"none",display:"flex",alignItems:"center",gap:7}}>
                <span>💬</span> support@peakhealthstack.com
              </a>
              <a href="mailto:suggestions@peakhealthstack.com" style={{color:C.teal,fontSize:13,textDecoration:"none",display:"flex",alignItems:"center",gap:7}}>
                <span>💡</span> suggestions@peakhealthstack.com
              </a>
            </div>
            <button style={{...S.btn,width:"100%"}} onClick={() => setShowAbout(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Guest save-data reminder */}
      <GuestBanner />

      {/* Floating glassmorphic tab nav */}
      <div style={{
        position: "fixed",
        bottom: "16px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 100,
        background: "rgba(15,37,64,0.75)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: "50px",
        border: "1px solid rgba(255,255,255,0.1)",
        padding: "8px",
        display: "flex",
        gap: "4px",
        boxShadow: SHADOWS.hero,
        maxWidth: "calc(100% - 32px)",
        overflowX: "auto",
        scrollbarWidth: "none",
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: "0 0 auto",
              background: tab === t.id ? "rgba(229,184,63,0.2)" : "transparent",
              border: tab === t.id ? "1px solid rgba(229,184,63,0.35)" : "1px solid transparent",
              color: tab === t.id ? "#E5B83F" : "rgba(255,255,255,0.5)",
              fontSize: "11px",
              fontWeight: tab === t.id ? "700" : "500",
              padding: "8px 12px",
              borderRadius: "40px",
              cursor: "pointer",
              transition: "all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
              whiteSpace: "nowrap",
              minHeight: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => { if (tab !== t.id) e.currentTarget.style.color = "rgba(255,255,255,0.75)"; }}
            onMouseLeave={(e) => { if (tab !== t.id) e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Pages — contentRef scrolls to top on tab change */}
      <div style={S.content} ref={contentRef}>
        {/* Weekly micro-insight banner — shown for logged-in non-premium users */}
        {user?.isLoggedIn && weeklyMicroInsight && !canAccess("advancedInsights", user.plan) && (
          <div style={{background:"rgba(229,184,63,0.06)",border:"1px solid rgba(229,184,63,0.2)",borderRadius:12,padding:"14px 16px",marginBottom:12,position:"relative"}}>
            <button onClick={() => setWeeklyMicroInsight(null)} style={{position:"absolute",top:10,right:12,background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:16}}>×</button>
            <div style={{fontSize:11,color:"#F5C84A",fontWeight:700,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.8px"}}>📈 Weekly Insight</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.55,marginBottom:10}}>{weeklyMicroInsight.insight}</div>
            <div style={{display:"flex",gap:16,marginBottom:10}}>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>⚡ Energy avg: <span style={{color:"rgba(255,255,255,0.9)",fontWeight:600}}>{weeklyMicroInsight.energyAvg}/5</span></div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>✅ Adherence: <span style={{color:"rgba(255,255,255,0.9)",fontWeight:600}}>{weeklyMicroInsight.adherence}%</span></div>
            </div>
            <button onClick={() => { setWeeklyMicroInsight(null); setShowPremium(true); }} style={{...S.btnAmber,fontSize:11,padding:"6px 14px"}}>Unlock Full Trends →</button>
          </div>
        )}

        {tab === "stack" && showAuditBanner && (
          <div style={{background:"rgba(229,184,63,0.06)",border:"1px solid rgba(229,184,63,0.18)",borderRadius:12,padding:"14px 16px",margin:"0 0 12px",position:"relative"}}>
            <button onClick={() => setShowAuditBanner(false)} style={{position:"absolute",top:10,right:12,background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:16}}>×</button>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.9}}>
              ✅ Your stack is set up — {stack.length} supplement{stack.length!==1?"s":""} added<br/>
              {getInteractions(stack).filter(i=>i.severity!=="none").length > 0 && (
                <span>⚡ {getInteractions(stack).filter(i=>i.severity!=="none").length} timing note(s) detected<br/></span>
              )}
              💡 Your Day 1 action has been saved
            </div>
            {day1Action && (
              <div style={{background:"rgba(229,184,63,0.08)",border:"1px solid rgba(229,184,63,0.2)",borderRadius:9,padding:"10px 13px",marginTop:10}}>
                <div style={{fontSize:10,color:"#F5C84A",fontWeight:700,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.8px"}}>💡 Your Day 1 Move</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.55}}>{day1Action}</div>
                <button onClick={() => setTab("log")} style={{marginTop:8,background:"none",border:"1px solid rgba(229,184,63,0.3)",color:"#F5C84A",borderRadius:7,padding:"5px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Log today to start tracking →</button>
              </div>
            )}
          </div>
        )}
        {tab === "stack"        && <StackPage onOpenDrawer={setDrawerSupp} onOpenBuilder={openBuilder} onShareStack={() => setShowShareModal(true)} />}
        {tab === "log"          && <LogPage key={"log-" + stack.join(",")} />}
        {tab === "log" && showDay4Prompt && (() => {
          const last3 = logs.slice(0, 3);
          const energyAvg = (last3.reduce((a, l) => { const v = l.effects?.Energy; return v ? a + Number(v) : a; }, 0) / Math.max(last3.filter(l => l.effects?.Energy).length, 1)).toFixed(1);
          const adherence = Math.round(last3.filter(l => l.taken?.length > 0).length / last3.length * 100);
          return (
            <div style={{background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:12,padding:"16px",marginTop:12,position:"relative"}}>
              <button onClick={() => setShowDay4Prompt(false)} style={{position:"absolute",top:10,right:12,background:"none",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:16}}>×</button>
              <div style={{fontSize:11,color:"#F59E0B",fontWeight:700,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.8px"}}>🔍 Early Pattern Detected</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",lineHeight:1.55,marginBottom:10}}>
                You've logged {last3.length} days. Your early data shows:
              </div>
              <div style={{display:"flex",gap:16,marginBottom:12}}>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>⚡ Energy avg: <span style={{color:"rgba(255,255,255,0.9)",fontWeight:600}}>{energyAvg}/5</span></div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>✅ Adherence: <span style={{color:"rgba(255,255,255,0.9)",fontWeight:600}}>{adherence}%</span></div>
              </div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",marginBottom:12}}>Unlock Trends to see your full pattern as it builds.</div>
              <button onClick={() => { setShowDay4Prompt(false); setShowPremium(true); }} style={{...S.btnAmber,fontSize:12,padding:"8px 16px",width:"100%"}}>See My Trends — Upgrade →</button>
            </div>
          );
        })()}
        {tab === "trends"       && <TrendsPage onUpgrade={() => setShowPremium(true)} />}
        {tab === "protocols"    && <ProtocolsPage onOpenBuilder={openBuilder} onUpgrade={() => setShowPremium(true)} />}
        {tab === "shop"         && <ShopPage />}
        {tab === "ai"           && <AIPage onUpgrade={() => setShowPremium(true)} />}
      </div>

      {/* Overlays */}
      {drawerSupp && <SupplementDrawer name={drawerSupp} onAdd={toggleStack} onClose={() => setDrawerSupp(null)} inStack={stack.includes(drawerSupp)} />}
      {showBuilder && <ProtocolBuilder key={editingProtocol?.name ?? "new"} existing={editingProtocol} onSave={(p) => { saveCustomProtocol(p); setShowBuilder(false); setEditingProtocol(null); setToast(p.icon + " \"" + p.name + "\" saved!"); }} onClose={() => { setShowBuilder(false); setEditingProtocol(null); }} />}
      {showPremium  && <PremiumModal   onClose={() => setShowPremium(false)} targetPlan={null} logs={logs} />}
      {showAccount  && <AccountModal   onClose={() => setShowAccount(false)} onUpgrade={() => { setShowAccount(false); setShowPremium(true); }} />}
      {showAuth    && <AuthModal onClose={() => setShowAuth(false)} />}
      {showShareModal && <ShareStackModal stack={stack} goal={user?.goal ?? ""} logs={logs} onClose={() => setShowShareModal(false)} />}
	  
      {showDay7Unlock && (() => {
        const last7 = logs.slice(0, 7);
        const energyAvg = (last7.reduce((a, l) => { const v = l.effects?.Energy; return v ? a + Number(v) : a; }, 0) / Math.max(last7.filter(l => l.effects?.Energy).length, 1)).toFixed(1);
        const adherence = Math.round(last7.filter(l => l.taken?.length > 0).length / Math.max(last7.length, 1) * 100);
        const sleepAvg = (last7.reduce((a, l) => { const v = l.effects?.["Sleep Quality"]; return v ? a + Number(v) : a; }, 0) / Math.max(last7.filter(l => l.effects?.["Sleep Quality"]).length, 1)).toFixed(1);
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(10,14,23,0.97)",zIndex:500,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,overflowY:"auto"}}>
            <div style={{maxWidth:380,width:"100%"}}>
              <div style={{fontSize:32,textAlign:"center",marginBottom:8}}>🎉</div>
              <h2 style={{color:C.white,fontWeight:800,fontSize:22,textAlign:"center",margin:"0 0 6px"}}>7-Day Milestone!</h2>
              <p style={{color:"rgba(255,255,255,0.6)",fontSize:13,textAlign:"center",margin:"0 0 24px",lineHeight:1.5}}>You've tracked for a full week. Here's what your data shows:</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:24}}>
                <div style={{background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"12px 8px",textAlign:"center"}}>
                  <div style={{fontSize:20,fontWeight:800,color:C.teal}}>{energyAvg}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:2}}>Avg Energy</div>
                </div>
                <div style={{background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"12px 8px",textAlign:"center"}}>
                  <div style={{fontSize:20,fontWeight:800,color:C.teal}}>{adherence}%</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:2}}>Adherence</div>
                </div>
                <div style={{background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"12px 8px",textAlign:"center"}}>
                  <div style={{fontSize:20,fontWeight:800,color:C.teal}}>{sleepAvg}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:2}}>Avg Sleep</div>
                </div>
              </div>
              {(user.plan === "premium" || user.plan === "annual") ? (
                <>
                  <div style={{background:"rgba(229,184,63,0.08)",border:"1px solid rgba(229,184,63,0.2)",borderRadius:10,padding:"14px",marginBottom:20,fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.55}}>
                    <strong style={{color:"#F5C84A"}}>Your week-one data is in.</strong> Head to AI Advisor to run your first analysis — your 7 days of logs give the AI enough context for a personalised insight.
                  </div>
                  <button onClick={() => { setShowDay7Unlock(false); setTab("ai"); }} style={{...S.btn,width:"100%",padding:14,fontSize:14,marginBottom:12}}>
                    Go to AI Advisor →
                  </button>
                  <button onClick={() => setShowDay7Unlock(false)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.35)",fontSize:12,width:"100%",cursor:"pointer",fontFamily:"inherit"}}>Dismiss</button>
                </>
              ) : (
                <>
                  {(() => {
                    const last7 = logs.slice(0,7);
                    const energyVals = last7.map(l=>l.effects?.Energy).filter(v=>v!=null);
                    const topSupp    = stack.length > 0 ? stack[0] : null;
                    const adherence  = Math.round(last7.filter(l=>l.taken?.length>0).length/Math.max(last7.length,1)*100);
                    let teaser = null;
                    if (energyVals.length >= 3 && topSupp) {
                      const avg = (energyVals.reduce((a,b)=>a+Number(b),0)/energyVals.length).toFixed(1);
                      teaser = `Your energy has averaged ${avg}/5 this week. Premium analysis can show whether ${topSupp} is driving that — or whether a timing change would move the number.`;
                    } else if (adherence >= 80 && topSupp) {
                      teaser = `${adherence}% adherence this week is strong. Premium can tell you which supplements in your stack are actually reflected in your scores.`;
                    } else if (topSupp) {
                      teaser = `You've taken ${topSupp} consistently this week. Premium analysis will show whether it's moving any of your scores — and what to adjust if it isn't.`;
                    }
                    return teaser ? (
                      <div style={{background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:10,padding:"14px",marginBottom:20,fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.55,fontStyle:"italic"}}>
                        "{teaser}"
                      </div>
                    ) : (
                      <div style={{background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:10,padding:"14px",marginBottom:20,fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.55}}>
                        <strong style={{color:"#F59E0B"}}>Your data is building a picture.</strong> Premium analysis will show you exactly which supplements are moving your numbers — and which aren't earning their spot.
                      </div>
                    );
                  })()}
                  <button onClick={() => { setShowDay7Unlock(false); setShowPremium(true); }} style={{...S.btnAmber,width:"100%",padding:14,fontSize:14,marginBottom:12}}>Unlock My Analysis — £9.99/month →</button>
                  <button onClick={() => setShowDay7Unlock(false)} style={{background:"none",border:"none",color:"rgba(255,255,255,0.35)",fontSize:12,width:"100%",cursor:"pointer",fontFamily:"inherit"}}>Maybe later</button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {toast       && <Toast message={toast} onDone={() => setToast("")} />}

      {/* Disclaimer modal */}
      {showDisclaimerModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:9000,backdropFilter:"blur(5px)",padding:16,paddingTop:32,overflowY:"auto"}} onClick={() => setShowDisclaimerModal(false)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(255,255,255,0.12)",borderRadius:16,padding:28,maxWidth:480,width:"100%",fontFamily:"'DM Sans',sans-serif",maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:16,color:C.white}}>⚖️ Legal Disclaimer</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.65)",lineHeight:1.7,marginBottom:16}}>{DISCLAIMER_FULL}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.7,marginBottom:16,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.08)"}}>{DISCLAIMER_AI}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.7,marginBottom:16,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.08)"}}>🇺🇸 {DISCLAIMER_FTC}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.7,marginBottom:16,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.08)"}}>{DISCLAIMER_AFFILIATE}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:20,lineHeight:1.5}}>
              Peak Health Stack is registered in Ireland. This app does not target users under 18. Supplement information is reviewed periodically but individual results vary. Stack Score is a relative indicator only and does not reflect clinical outcomes.
            </div>
            <button onClick={() => setShowDisclaimerModal(false)} style={{...S.btn,width:"100%"}}>Got it</button>
          </div>
        </div>
      )}

      {/* Privacy Policy modal */}
      {showPrivacy && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:9000,backdropFilter:"blur(5px)",padding:16,paddingTop:32,overflowY:"auto"}} onClick={() => setShowPrivacy(false)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(255,255,255,0.12)",borderRadius:16,padding:28,maxWidth:480,width:"100%",fontFamily:"'DM Sans',sans-serif",maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:16,color:C.white}}>🔒 Privacy Policy</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.65)",lineHeight:1.7,marginBottom:20,whiteSpace:"pre-wrap"}}>{PRIVACY_POLICY_TEXT}</div>
            <button onClick={() => setShowPrivacy(false)} style={{...S.btn,width:"100%"}}>Close</button>
          </div>
        </div>
      )}

      {/* Terms of Service modal */}
      {showTerms && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:9000,backdropFilter:"blur(5px)",padding:16,paddingTop:32,overflowY:"auto"}} onClick={() => setShowTerms(false)}>
          <div style={{background:C.navyDeep,border:"1px solid rgba(255,255,255,0.12)",borderRadius:16,padding:28,maxWidth:480,width:"100%",fontFamily:"'DM Sans',sans-serif",maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:16,color:C.white}}>📋 Terms of Service</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.65)",lineHeight:1.7,marginBottom:20,whiteSpace:"pre-wrap"}}>{TERMS_OF_SERVICE_TEXT}</div>
            <button onClick={() => setShowTerms(false)} style={{...S.btn,width:"100%"}}>Close</button>
          </div>
        </div>
      )}

      {/* Global medical disclaimer footer */}
      <div style={{padding:"10px 20px",borderTop:"1px solid rgba(255,255,255,0.05)",textAlign:"center",fontSize:10,color:"rgba(255,255,255,0.22)",lineHeight:1.6,fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
        {DISCLAIMER_SHORT} ·{' '}
        <span style={{textDecoration:"underline",cursor:"pointer"}} onClick={() => setShowDisclaimerModal(true)}>Full Disclaimer</span>
        {' · '}
        <span style={{textDecoration:"underline",cursor:"pointer"}} onClick={() => setShowPrivacy(true)}>Privacy Policy</span>
        {' · '}
        <span style={{textDecoration:"underline",cursor:"pointer"}} onClick={() => setShowTerms(true)}>Terms of Service</span>
      </div>

      {/* Spacer so content clears the floating tab bar */}
      <div style={{ height: "72px", flexShrink: 0 }} />
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ConsentBanner — UK GDPR notice for Sentry error monitoring
// Shows on first visit until user accepts or declines. Enabling Sentry after
// acceptance requires a page reload (Sentry.init enabled flag is read at startup).
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ConsentBanner() {
  const [show, setShow] = useState(() => {
    try { return localStorage.getItem("phs_consent") === null; } catch { return false; }
  });
  if (!show) return null;
  const handleAccept = () => {
    try { localStorage.setItem("phs_consent", "true"); } catch {}
    // Re-init Sentry now — the enabled flag was read at startup before consent was given
    try {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        tracesSampleRate: 0.2,
        sendDefaultPii: false,
        enabled: true,
      });
    } catch { /* non-fatal */ }
    setShow(false);
  };
  const handleDecline = () => {
    try { localStorage.setItem("phs_consent", "false"); } catch {}
    setShow(false);
  };
  return (
    <div style={{position:"fixed",bottom:0,left:0,right:0,background:"rgba(15,37,64,0.97)",borderTop:"1px solid rgba(229,184,63,0.25)",padding:"12px 16px",zIndex:9999,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
      <p style={{margin:0,flex:1,fontSize:12,color:"rgba(255,255,255,0.72)",lineHeight:1.5,minWidth:200}}>
        We use Sentry for error monitoring to improve the app. No advertising data is collected. See our Privacy Policy in the app settings for details.
      </p>
      <div style={{display:"flex",gap:8,flexShrink:0}}>
        <button onClick={handleDecline} style={{padding:"6px 14px",borderRadius:6,border:"1px solid rgba(255,255,255,0.2)",background:"transparent",color:"rgba(255,255,255,0.6)",fontSize:12,cursor:"pointer"}}>Decline</button>
        <button onClick={handleAccept} style={{padding:"6px 14px",borderRadius:6,border:"none",background:C.teal,color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer"}}>Accept</button>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Root export — wraps App in providers
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default function Root() {
  return (
    <Sentry.ErrorBoundary fallback={<div style={{color:"#fff",padding:40,textAlign:"center"}}>Something went wrong. Please refresh the page.</div>}>
      <UserProvider>
        <AppProvider>
          <App />
          <PWAInstallBanner />
          <ConsentBanner />
          <Analytics />
          <SpeedInsights />
        </AppProvider>
      </UserProvider>
    </Sentry.ErrorBoundary>
  );
}
